import { useState, useRef, useEffect, useMemo, useCallback, memo } from "react";
import { flushSync } from "react-dom";
import { supabase, withTimeout } from "./supabaseClient";
import { TESTER_ACTIVITY_ACTIONS, trackTesterActivity } from "./services/testerActivity";
import { ACCESS_MODES, persistAccessMode, readPersistedAccessMode } from "./accessMode";
import { getLegalDocumentKey, LEGAL_DOCUMENT_ORDER, LEGAL_DOCUMENTS } from "./legalDocuments";
import { APP_VERSION, applyPwaUpdate, registerPwaUpdateHandler, removePwaUpdateListener, snoozePwaUpdate } from "./pwaUpdate";
import { useLocationAccess } from "./location/LocationProvider";
import { createPendingLocationAction, hasLocationIntent, LOCATION_ACTION_TYPES } from "./location/locationIntent";
import { withCanonicalGoogleMapsUrl } from "./location/googleMapsUrl";
import { loadGoogleMaps } from "./googleMapsLoader";
import { pushNavigationLayer, seedNavigationGuard } from "./navigationHistory";

import {
  exhaustedRecommendationNotice,
  extractRecommendedPlaces,
  firstRecommendationNotice,
  guestLimitMessage,
  guestListMessage,
  isMultiPlaceRequest,
  isRecommendationRequest,
  readGuestUsage,
  recordSuccessfulRecommendations,
  remainingRecommendations,
  remainingTimeLabel,
  sanitizeRecommendationReply,
  writeGuestUsage,
} from "./guestRecommendationLimit";
import { clearConversation, compactMessagesForModel, conversationIdentity, estimateContextCharacters, readConversation, writeConversation } from "./guestConversationContext";
import { createLogoutController } from "./logoutController";
import { locationMetadata, placeSelectionFromGoogle } from "./placeSelection";
import { buildRelatedPlaces } from "./relatedPlaces";
import { buildCatalogRows, shouldUseCatalogRows } from "./phase6CatalogRows";
import { approvedAdminPlaces, isApprovedPlace, isPendingPlace, pendingModerationPlaces, publicPlaceList } from "./placeVisibility";
import { resolvePlaceCost } from "./phase7PlaceData";
import { excludeRecentRandomPlaces, readRecentRandomPlaceIds, rememberRandomPlaceId } from "./recentRandomRecommendations";
import { averageRatingDimension, buildRatingSummary, RATING_FORMULA_LABEL } from "./phase7Ratings";
import { useActiveFeedbackInvitation } from "./useActiveFeedbackInvitation";
import { getFeedbackInvitationExclusion } from "./feedbackInvitationExclusions";
import { feedbackAudienceForUser } from "./feedbackInvitationPersistence";
import FeedbackForm from "./FeedbackForm";
import { hasSuccessfulFeedback } from "./feedbackRecognition";
import { hasCurrentUserSubmittedFeedback } from "./profileFeedbackRecognition";
import { buildAdminReviewUpdate, canAdminEditOwnPendingReview, findAdminEditableReviewForPlace, normalizeReviewForEditor } from "./adminReviewEditing";
import { buildAdminPlaceImageGallery, captureAdminPreviewContext, restoreAdminPreviewContext } from "./adminPreviewContext";
import { buildAdminContributorProfiles, contributorIdentity } from "./adminContributorIdentity";
import { adminPlaceReviews, resolveAdminBackLayer } from "./adminPlaceContext";
import { ADMIN_PANEL_SHELL_STYLE, ADMIN_PREVIEW_ACTIONS_STYLE, ADMIN_PREVIEW_SCROLL_STYLE, ADMIN_PREVIEW_SHELL_STYLE } from "./adminPreviewLayout";
import AdminFeedbackPanel from "./AdminFeedbackPanel";
import AdminReportsPanel from "./AdminReportsPanel";
import AdminUserMessagingPanel from "./AdminUserMessagingPanel";
import NotificationInbox from "./NotificationInbox";
import NotificationPermissionIntro from "./NotificationPermissionIntro";
import AdminResourceUsagePanel from "./AdminResourceUsagePanel";
import AdminOfficialReviewPanel from "./AdminOfficialReviewPanel";
import FeedbackBrand from "./FeedbackBrand";
import { DON_D_LOGO_URL } from "./brandAssets";
import { randomDonDRecommendationDelay } from "./donDRecommendationTiming";
import { ROUTE_MAP_OPTIONS } from "./location/routeMapOptions";
import { canonicalPlaceLocationContext, isBranchSafeReview } from "./recommendationLocationSafety";
import { verifyCurrentAdminAccess } from "./adminAccess";
import { checkUsernameAvailability, isUsernameConflict, normalizeUsername, USERNAME_TAKEN_MESSAGE, validateUsername } from "./usernamePolicy";
import { buildProfileContributions, buildProfileDetailReviews, resolveProfileContributionTab, visibleProfileContributionTypes } from "./profileContributions";
import { ageFromBirthdate, birthdateInputMax, birthdateValidation, profileAge } from "./birthdate";
import { pushCapability, readPushState, subscribeToVenaqqiPush, unsubscribeFromVenaqqiPush, updateNotificationPreferences } from "./pushNotifications";
import { clearTutorialProgress, clearUiState, isMapsDestination, markActiveNow, markTutorialCompleted, readTutorialProgress, readUiState, saveTutorialProgress, saveUiState, shouldOfferTutorialRefresh } from "./appStatePersistence";
import { clearAddPlaceDraft, defaultAddPlaceForm, hasMeaningfulAddPlaceDraft, readAddPlaceDraft, writeAddPlaceDraft } from "./addPlaceDraft";
import { inferPlaceCategory } from "./categoryInference";
import { trackMarketingEvent } from "./marketingAttribution";
import { trackProductEvent } from "./productAnalytics";
import { uploadReviewImage } from "./reviewImageUpload";
import { loadCatalogPlaceSignals, persistCatalogPlaceFirstImpression, persistCatalogPlaceInterest } from "./placeSignals";

// ── Logs solo en desarrollo: en el build de producción no imprimen nada ──
const isDev = process.env.NODE_ENV !== "production";
const log = (...a) => { if (isDev) console.log(...a); };
const warn = (...a) => { if (isDev) console.warn(...a); };
// Bebas Neue loaded via style tag — build 13

// Hook: cuando el teclado virtual sube en móvil, hace scroll al input activo
// para que no quede tapado. Usar en cualquier modal con inputs.
const useKeyboardAvoid = () => {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const handler = () => {
      const el = document.activeElement;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
      }
    };
    vv.addEventListener("resize", handler);
    return () => vv.removeEventListener("resize", handler);
  }, []);
};


const SPIN_CSS = "@keyframes spin{to{transform:rotate(360deg)}} .pac-container{z-index:99999!important;pointer-events:all!important;} @keyframes hatbob{0%,100%{transform:translateY(0) rotate(-4deg)}50%{transform:translateY(-14px) rotate(4deg)}} @keyframes splashfade{0%{opacity:0}10%{opacity:1}85%{opacity:1}100%{opacity:0}}";
const SHAKE_CSS = "@keyframes vqtextpulse{0%,100%{opacity:1}50%{opacity:0.3}} @keyframes vqtype1{from{width:0}to{width:100%}} @keyframes vqtype2{0%,50%{width:0}50.01%,100%{width:100%}} @keyframes vq-pulse{0%,100%{transform:scale(1);box-shadow:0 0 0 0 rgba(229,57,53,0.7)}50%{transform:scale(1.15);box-shadow:0 0 0 8px rgba(229,57,53,0)}} .vq-pulse{animation:vq-pulse 0.6s ease;} @keyframes vq-shake {0%,100%{transform:translate(0)} 15%{transform:translate(-6px,0)} 30%{transform:translate(6px,0)} 45%{transform:translate(-4px,0)} 60%{transform:translate(4px,0)} 75%{transform:translate(-2px,0)} 90%{transform:translate(2px,0)}} .vq-shake{animation:vq-shake 0.45s ease;}";
// Símbolo efectivo de Don D’: Cloudinary elimina el fondo original; las clases
// del avatar aportan negro sólido y el rojo Venaqqi #e53935.
const LOGO = DON_D_LOGO_URL;
const RELATED_PLACE_FALLBACK_IMAGE = LOGO;
const DON_D_LOGO_CSS = ".don-d-logo-shell{background:#000!important;overflow:hidden;display:flex;align-items:center;justify-content:center}.don-d-logo-symbol{background:#000!important;object-fit:contain}";
// La acción de recomendación de Don D' se mantiene visible en formato mínimo: lugar + Otra + Llévame.
const SHOW_DON_D_RECOMMENDATION_CARD = true;

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const SUPABASE_TIMEOUT_MS = 10000;

const basePlaces = [
  { id: 1, name: "FRULLATTI Angelópolis", tipo: "Cemitas & Tacos Árabes", location: "Niños Héroes 2518, Reserva Territorial Atlixcáyotl", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777703310/FRULLATI_VENAQQI_1_yjdiur.png", maps: "https://maps.app.goo.gl/oP7nGDgk1xeuB3LB6", tags: ["Económico", "Tacos", "Ambiente tranquilo"] },
  { id: 2, name: "TACOQUETO – Galerías Serdán", tipo: "Hamburguesas & Tacos", location: "Blvd. Hermanos Serdán 270, Posadas, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777703256/TACO_QUETO_1_xfmao0.jpg", maps: "https://maps.app.goo.gl/UX6MUfrSdphpXnKZ6", tags: ["Hamburguesas", "Plaza", "Buenas porciones"] },
  { id: 3, name: "Tacos LALO – Vistahermosa", tipo: "Tacos & Alambres", location: "Plaza San José Vistahermosa, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777703477/TACOS_LALO_1_iuixv0.png", maps: "https://maps.app.goo.gl/JAEwtmSQafYefYnSA", tags: ["Alambres", "Económico", "Porciones grandes"] },
  { id: 4, name: "Saporo & Tokio RAMEN", tipo: "Ramen & Japonesa", location: "Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777704228/SAPORO_1_bax0ap.png", maps: "https://maps.app.goo.gl/DxtG8DzJS4xPA1dQ9", tags: ["Ramen", "Japonesa", "Tranquilo"] },
  { id: 5, name: "McCarthy's Irish Pub", tipo: "Pub & Hamburguesas", location: "Av. Juárez 2925, La Paz, Puebla Centro", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777704484/MCARTYS_1_d9iolm.png", maps: "https://maps.app.goo.gl/f2oy2YMvNoqo68ft9", tags: ["Pub", "Música en vivo", "Cervezas artesanales"] },
  { id: 6, name: "La Bici de Cleta", tipo: "Mariscos & Botanas", location: "Atlixcáyotl 1899, Angelópolis, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777704763/LA_BICI_DE_CLETA_1_vwnusu.png", maps: "https://maps.app.goo.gl/k7wtaCYToM3tkGX9A", tags: ["Botanas", "Mariscos", "Angelópolis"] },
  { id: 7, name: "La Churrasca Buffet", tipo: "Buffet & Carnes", location: "Autopista México-Puebla S/N, Sanctorum, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777704942/LA_CHURRASCA_1_ow7vqi.png", maps: "https://maps.app.goo.gl/pSd3uXhDv29iCnqGA", tags: ["Buffet", "Carnes", "Sanctorum"] },
  { id: 8, name: "La Pizca Angelópolis", tipo: "Desayunos Mexicanos", location: "Osa Mayor 2902, Angelópolis, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777705216/LA_PIZCA_ANGELOPOLIS_1_wzaz2k.png", maps: "https://maps.app.goo.gl/8zUrPuWRyj9dZeJe7", tags: ["Desayunos", "Buffet", "Angelópolis"] },
  { id: 9, name: "Rinconcito Argentino", tipo: "Comida Argentina", location: "Calle 7 Sur esq. Av. 35 Pte., Chulavista, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777705498/RINCONCITO_ARGENTINO_1_tjlys0.png", maps: "https://maps.app.goo.gl/2UvuCbHLPHFr9F1aA", tags: ["Argentino", "Auténtico", "Económico"] },
  { id: 10, name: "La Chata Burgers Gourmet", tipo: "Hamburguesas Gourmet", location: "Privada A Ote 4102, Carmen Huexotitla, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777705954/LA_CHATA_BURGERS_1_k0pv60.png", maps: "https://maps.app.goo.gl/uaacnECbUGkNB7zf6", tags: ["Hamburguesas", "Aire libre", "Económico"] },
  { id: 11, name: "Las Delicias de Mamá Eva", tipo: "Comida Casera", location: "Independencia, San Miguel la Rosa, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777706161/LAS_DELICIAS_DE_MAMA_EVA_1_iwqebv.png", maps: "https://maps.app.goo.gl/1bCM7LkWJHALT1wN9", tags: ["Económico", "Casero", "Familiar"] },
  { id: 12, name: "El Asador Restaurante", tipo: "Carnes & Parrilla", location: "Carr. Izúcar de Matamoros 1505, Tehuixpango, Pue.", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777706416/EL_ASADOR_1_ys8lub.png", maps: "https://maps.app.goo.gl/A43JcorGSSFnu62CA", tags: ["Parrilla", "Aire libre", "Atlixco"] },
  { id: 13, name: "Cajun African Chicken Food", tipo: "Pollo & BBQ", location: "Calz. Ignacio Zaragoza 410, La Ciénega, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777706588/CAJUN_CHICKEN_1_iskht5.png", maps: "https://maps.app.goo.gl/wXDTeSEB7Z3E73bM8", tags: ["BBQ", "Pollo", "Centro comercial"] },
  { id: 14, name: "Corazón de Brasil", tipo: "Buffet Brasileño", location: "Cto. Juan Pablo II, La Noria, Atlixcáyotl, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777706882/CORAZON_DE_BRASIL_1_av4ent.png", maps: "https://maps.app.goo.gl/1MZDy5TBLKZGJTbi8", tags: ["Buffet", "Brasileño", "Música en vivo"] },
  { id: 15, name: "Le bon Manger Restaurante", tipo: "Desayunos & Brunch", location: "Blvrd Atlixco 3156, Galería las Ánimas, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777707192/LE_BON_MANGER_1_g9hv6v.png", maps: "https://maps.app.goo.gl/epXDne9MAm9ZbDpH7", tags: ["Desayunos", "Las Ánimas", "Brunch"] },
  { id: 16, name: "Chikielotes", tipo: "Tacos", location: "Blvrd Atlixco 37, San José Vista Hermosa, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777707485/CHIKIELOTES_1_m4ewrj.png", maps: "https://maps.app.goo.gl/3zAzSRJsm7D5wYDP9", tags: ["Económico", "Tacos", "Antojo rápido"] },
  { id: 17, name: "Trípoli Outlet", tipo: "Tacos Árabes", location: "Autopista México-Puebla km. 115, Ocotlán, Pue.", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777707665/TRIPOLI_2_whz4oj.png", maps: "https://maps.app.goo.gl/MAnfijMFxRd9Gvi16", tags: ["Tacos Árabes", "Outlet", "Ocotlán"] },
  { id: 18, name: "Mi Herencia – Plaza Triángulo", tipo: "Tortas & Sándwiches", location: "Cto Juan Pablo II 11, Ánimas, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777707843/MI_HERENCIA_1_zmrrbz.png", maps: "https://maps.app.goo.gl/wNadHCz596fFtJMCA", tags: ["Tortas", "Económico", "Ánimas"] },
  { id: 19, name: "Asador Los Aguacates", tipo: "Tacos de Asada", location: "Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777708134/LOS_AGUACATES_1_wcmbs5.png", maps: "https://maps.app.goo.gl/MJddceFxZSAvyrKt7", tags: ["Tacos de Asada", "Sirloin", "Para llevar"] },
  { id: 20, name: "Taquería El Maraja", tipo: "Tacos Árabes", location: "Puebla City", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777708407/EL_MARAJA_1_dwfutu.png", maps: "https://maps.app.goo.gl/qWikTjLdMgQ8vmzj6", tags: ["Tacos Árabes", "Económico", "Limpio"] },
  { id: 21, name: "La Fonda de Sta. Clara – Cholula", tipo: "Comida Poblana", location: "Zócalo de Cholula, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777708534/LA_FONDA_DE_STA_CLARA_1_scvznw.png", maps: "https://maps.app.goo.gl/xr2Nbw2vZiGQtgW99", tags: ["Comida Poblana", "Cholula", "Zócalo"] },
  { id: 22, name: "Birriería Martina", tipo: "Birria & Bar", location: "Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777708832/BIRRIERIA_LA_MARTINA_1_sxeavm.png", maps: "https://maps.app.goo.gl/LiNsUqvspTMH393v5", tags: ["Birria", "Bar", "Ambiente"] },
  { id: 23, name: "Mi Viejo Pueblito – FINSA", tipo: "Comida Mexicana", location: "FINSA, Puebla", image: "https://res.cloudinary.com/dgdtlohqw/image/upload/v1777709066/MI_VIEJO_PUEBLITO_1_x9t8tu.png", maps: "https://maps.app.goo.gl/DKBLkh4jAkshtcVy6", tags: ["Mexicano", "Cultural", "Moles"] },
].map(place => withCanonicalGoogleMapsUrl({ ...place, catalogSource: "legacy" }));

const initialReviews = [
  { id: "r1", placeId: 1, userid: "venaqqi", username: "venaqqi", sabor: 5, precio: 2, /* B16 migrado desde precio: 4 */ servicio: 5, satisfaccion: 5, platillo: "🌮 Cemita al pastor y tacos árabes", comentario: "Todos los tacos que quieras por $119. Ambiente tranquilo, cerveza, carne del trompo.", precio_label: "$$ ($120 con bebida)", fecha: "Oct 2025" },
  { id: "r2", placeId: 2, userid: "venaqqi", username: "venaqqi", sabor: 5, precio: 2, /* B16 migrado desde precio: 3 */ servicio: 5, satisfaccion: 5, platillo: "🍔 Hamburguesa de Sirloin", comentario: "Sabor de la carne, buenas porciones y eficacia al atender. Plaza limpia.", precio_label: "$$$ (160 MXN)", fecha: "Oct 2025" },
  { id: "r3", placeId: 3, userid: "venaqqi", username: "venaqqi", sabor: 4, precio: 2, /* B16 migrado desde precio: 4 */ servicio: 5, satisfaccion: 4, platillo: "🌮 Alambre Fortachón", comentario: "Limpieza, orden y atención. Porciones generosas, la salsa buenísima.", precio_label: "$$ ($100–120 MXN)", fecha: "Oct 2025" },
  { id: "r4", placeId: 4, userid: "venaqqi", username: "venaqqi", sabor: 4, precio: 4, /* B16 migrado desde precio: 2 */ servicio: 4, satisfaccion: 4, platillo: "🍜 Ramen Mandu y 🥗 Ensalada de camarón", comentario: "Ubicación y limpieza impecables. Tranquilo y espacioso.", precio_label: "$$$ ($250)", fecha: "Oct 2025" },
  { id: "r5", placeId: 5, userid: "venaqqi", username: "venaqqi", sabor: 4, precio: 3, servicio: 4, satisfaccion: 4, platillo: "🍔 Hamburguesa McCarthy y papas gajo", comentario: "Música en vivo y cervezas artesanales. Ideal para grupos y noches temáticas.", precio_label: "$$$ (200 MXN)", fecha: "Nov 2025" },
  { id: "r6", placeId: 6, userid: "venaqqi", username: "venaqqi", sabor: 5, precio: 2, /* B16 migrado desde precio: 4 */ servicio: 5, satisfaccion: 5, platillo: "🌮 Tostada de atún", comentario: "Frescura y ambiente ideal para precopear. Concepto botanero, muy recomendado.", precio_label: "$$ (100–150 MXN)", fecha: "Nov 2025" },
  { id: "r7", placeId: 7, userid: "venaqqi", username: "venaqqi", sabor: 4, precio: 5, /* B16 migrado desde precio: 2 */ servicio: 5, satisfaccion: 4, platillo: "🍖 Buffet general", comentario: "Variedad de cortes, ambiente con media luz. Gran servicio.", precio_label: "$$$ (351.90)", fecha: "Oct 2025" },
  { id: "r8", placeId: 8, userid: "venaqqi", username: "venaqqi", sabor: 4, precio: 4, /* B16 migrado desde precio: 2 */ servicio: 4, satisfaccion: 4, platillo: "🍳 Buffet desayunos mexicanos", comentario: "Música en vivo durante la merienda. Limpieza y ubicación excelentes.", precio_label: "$$$ (249)", fecha: "Oct 2025" },
  { id: "r9", placeId: 9, userid: "venaqqi", username: "venaqqi", sabor: 5, precio: 3, /* B16 migrado desde precio: 4 */ servicio: 5, satisfaccion: 5, platillo: "🥩 Lomito Cordobés y Choripán", comentario: "Comida auténtica argentina. Incluye empanada y alfajor. Atención grandiosa.", precio_label: "$$ (220 sin bebida)", fecha: "Oct 2025" },
  { id: "r10", placeId: 10, userid: "venaqqi", username: "venaqqi", sabor: 5, precio: 2, /* B16 migrado desde precio: 5 */ servicio: 5, satisfaccion: 5, platillo: "🍔 Burger Inmigrant Bourbon", comentario: "Aire libre, cerca del parque. Pan variado y papas baby. Asado como debe ser.", precio_label: "$$ (95 sin bebida)", fecha: "Oct 2025" },
  { id: "r11", placeId: 11, userid: "venaqqi", username: "venaqqi", sabor: 5, precio: 1, /* B16 migrado desde precio: 5 */ servicio: 5, satisfaccion: 5, platillo: "🫓 Quesadilla de bistec", comentario: "Sabores locales, menú variado. Acepta tarjeta, tortillas recién hechas.", precio_label: "$ (70 con bebida)", fecha: "Oct 2025" },
  { id: "r12", placeId: 12, userid: "venaqqi", username: "venaqqi", sabor: 5, precio: 7, /* B16 migrado desde precio: 1 */ servicio: 4, satisfaccion: 4, platillo: "🥩 Costillas + Arrachera", comentario: "Al aire libre cerca de Atlixco. Música y ambiente fresco.", precio_label: "$$$$ (650)", fecha: "Oct 2025" },
  { id: "r13", placeId: 13, userid: "venaqqi", username: "venaqqi", sabor: 4, precio: 3, /* B16 migrado desde precio: 2 */ servicio: 4, satisfaccion: 3, platillo: "🍗 Costilla BBQ + Pollo Bourbon", comentario: "Vistas del centro comercial. Costillar con papas sazonadas incluidas.", precio_label: "$$$ (238)", fecha: "Oct 2025" },
  { id: "r14", placeId: 14, userid: "venaqqi", username: "venaqqi", sabor: 5, precio: 4, /* B16 migrado desde precio: 3 */ servicio: 5, satisfaccion: 5, platillo: "🍖 Buffet Espadas Brasileñas", comentario: "Buffet ilimitado. Música en vivo y gran variedad de ensaladas.", precio_label: "$$$ (300)", fecha: "Oct 2025" },
  { id: "r15", placeId: 15, userid: "venaqqi", username: "venaqqi", sabor: 5, precio: 3, servicio: 4, satisfaccion: 4, platillo: "🍳 Chilaquiles con Arrachera", comentario: "Paquetes completos con fruta, agua, pan y platillo.", precio_label: "$$$ (180)", fecha: "Oct 2025" },
  { id: "r16", placeId: 16, userid: "venaqqi", username: "venaqqi", sabor: 4, precio: 1, /* B16 migrado desde precio: 5 */ servicio: 4, satisfaccion: 4, platillo: "🌮 Taco Llavero", comentario: "Buen sabor y precio accesible. Antojo rápido, limpio y buena atención.", precio_label: "$ (35 MXN)", fecha: "Oct 2025" },
  { id: "r17", placeId: 17, userid: "venaqqi", username: "venaqqi", sabor: 4, precio: 3, servicio: 5, satisfaccion: 3, platillo: "🌮 Tacos Árabes y Oriental", comentario: "¡En el outlet! Grandes marcas y atracciones alrededor.", precio_label: "$$$ (150–200)", fecha: "Oct 2025" },
  { id: "r18", placeId: 18, userid: "venaqqi", username: "venaqqi", sabor: 5, precio: 2, /* B16 migrado desde precio: 4 */ servicio: 5, satisfaccion: 5, platillo: "🥪 Torta Rancho Grande", comentario: "Ingredientes sobre la barra para personalizar. Limpieza y orden.", precio_label: "$$ (139 MXN)", fecha: "Oct 2025" },
  { id: "r19", placeId: 19, userid: "venaqqi", username: "venaqqi", sabor: 5, precio: 2, /* B16 migrado desde precio: 4 */ servicio: 3, satisfaccion: 2, platillo: "🌮 Volcán de Sirloin y Quesadilla de longaniza", comentario: "Sabor de longaniza increíble. ⚠️ Sin sillas, comes parado.", precio_label: "$$ (100–150 MXN)", fecha: "Sep 2025" },
  { id: "r20", placeId: 20, userid: "venaqqi", username: "venaqqi", sabor: 5, precio: 2, /* B16 migrado desde precio: 4 */ servicio: 5, satisfaccion: 5, platillo: "🌮 Taco Oriental y Taco Árabe", comentario: "Sabor y limpieza excelentes. Buen espacio y humo fuera del lugar.", precio_label: "$$ ($80–100 MXN)", fecha: "Sep 2025" },
  { id: "r21", placeId: 21, userid: "venaqqi", username: "venaqqi", sabor: 4, precio: 2, /* B16 migrado desde precio: 3 */ servicio: 4, satisfaccion: 3, platillo: "🫓 Molotes + Cerveza", comentario: "Ubicación en el zócalo de Cholula. Variedad y ambiente limpio.", precio_label: "$$$ (160)", fecha: "Sep 2025" },
  { id: "r22", placeId: 22, userid: "venaqqi", username: "venaqqi", sabor: 4, precio: 3, servicio: 5, satisfaccion: 4, platillo: "🫔 Chalupas y queso fundido", comentario: "Espacio colorido con música. Pantallas y amplia zona de servicio.", precio_label: "$$$ ($175 con cerveza)", fecha: "Sep 2025" },
  { id: "r23", placeId: 23, userid: "venaqqi", username: "venaqqi", sabor: 4, precio: 5, /* B16 migrado desde precio: 2 */ servicio: 4, satisfaccion: 4, platillo: "🍽️ Enchiladas 3 Moles", comentario: "Ambiente cultural y porciones generosas. Muy buen concepto.", precio_label: "$$$ (300–400)", fecha: "Sep 2025" },
];

const avg = (rs, f) => rs.length ? rs.reduce((s, r) => s + r[f], 0) / rs.length : 0;
// finalScore = (sabor*0.5) + (servicio*0.2) + (satisfaccion*0.3)
// precio queda FUERA — solo indicador visual
const qualityScore = (rs) => {
  if (!rs.length) return 0;
  return avg(rs,"sabor")*0.5 + avg(rs,"servicio")*0.2 + avg(rs,"satisfaccion")*0.3;
};

const overallScore = qualityScore;
const genId = () => Math.random().toString(36).slice(2, 10);
const EMPTY_ARR = []; // referencia estable: [] inline crearía un array nuevo por render y rompería memo(Card)
// Estilo compartido: botón 🗑 eliminar del panel admin (3 usos)
const ADMIN_DEL_BTN = { background: "#3a1a1a", border: "1px solid #5a2d2d", color: "#e57373", borderRadius: 8, padding: "6px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 };
// Fisher-Yates: barajado uniforme (sort(() => Math.random()-0.5) es sesgado)
const shuffle = (arr) => { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };
// 1.1 — Fuente única de formato de precio: "$$$ · $160 – $240" desde el índice de PRECIO_RANGES (slider 1–7)
const precioDisplay = (n) => {
  if (!n || n <= 0) return "-";
  const v = Math.min(Math.max(Math.round(n), 1), 7);
  const rango = PRECIO_RANGES.find(r => r.value === v)?.label || "";
  return "$".repeat(Math.min(v, 5)) + (rango ? " · " + rango : "");
};

// Leyendas de satisfacción (¿volverías?)
const SAT_LABELS = {
  1: "No me convenció.",
  2: "Le faltó algo.",
  3: "Tal vez sí.",
  4: "Vale la pena volver.",
  5: "Vuelvo sí porque sí.",
};

// Rangos de precio en pasos de $80
const PRECIO_RANGES = [
  { value: 1, label: "Hasta $80" },
  { value: 2, label: "$80 – $160" },
  { value: 3, label: "$160 – $240" },
  { value: 4, label: "$240 – $320" },
  { value: 5, label: "$320 – $400" },
  { value: 6, label: "$400 – $480" },
  { value: 7, label: "$480+" },
];

const SABOR_LABELS = {
  1: "No volvería a pedirlo.",
  2: "Le falta mucho.",
  3: "Cumple. Nada más.",
  4: "Está muy bueno.",
  5: "De lo mejor que he probado.",
};

const SERVICIO_LABELS = {
  1: "Pésima atención.",
  2: "Lenta o descuidada.",
  3: "Normal, sin destacar.",
  4: "Buena atención.",
  5: "Excelente, regresaría solo por eso.",
};

const SliderField = ({ emoji, label, field, labels, color = "#e53935", values, set }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
      <span style={{ fontSize: 12, color: "#aaa" }}>{emoji} {label}</span>
      <span style={{ fontSize: 13, color, fontWeight: 700 }}>{values[field]}/5</span>
    </div>
    <input type="range" min={1} max={5} value={values[field]} onChange={e => set(field, +e.target.value)} style={{ width: "100%", accentColor: color }} />
    {labels && <p style={{ margin: "3px 0 0", fontSize: 11, color: "#888", fontStyle: "italic" }}>{labels[values[field]]}</p>}
  </div>
);

const TESTER_STATUS_LABELS = { invited: "Invitado", registered: "Registrado", ready: "Listo para iniciar", in_progress: "En progreso", completed: "Completó 14 días", inactive: "Inactivo" };

const downloadCsv = (filename, rows) => {
  const csv = rows.map(row => row.map(value => `"${String(value ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const TestersAdminPanel = ({ adminUsers }) => {
  const [testers, setTesters] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [email, setEmail] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("last_activity");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [testerError, setTesterError] = useState("");
  const [authorizationError, setAuthorizationError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const [testerResult, authorizationResult] = await Promise.all([
      supabase.rpc("admin_get_tester_activity_summary"),
      supabase.from("tester_authorizations").select("email,is_authorized,authorized_at,authorized_by,revoked_at").order("authorized_at", { ascending: false }),
    ]);
    if (testerResult.error) setTesterError("No se pudieron cargar los testers: " + testerResult.error.message);
    else {
      setTesters(testerResult.data || []);
      setTesterError("");
    }
    if (authorizationResult.error) setAuthorizationError("No se pudieron cargar las autorizaciones: " + authorizationResult.error.message);
    else {
      setAuthorizations(authorizationResult.data || []);
      setAuthorizationError("");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addTester = async () => {
    if (!isValidEmail(email)) { setMsg("Correo inválido"); return; }
    const { error } = await supabase.rpc("authorize_tester", { p_email: email.toLowerCase().trim() });
    if (error) setMsg(error.message); else { setEmail(""); setMsg("Tester agregado"); load(); }
  };

  const patchTester = async (id, changes) => {
    const { error } = await supabase.rpc("admin_update_tester", { p_tester_id: id, p_changes: changes });
    if (error) setMsg("No se guardó ningún cambio: " + error.message);
    else { setMsg("Tester actualizado"); await load(); }
  };

  const editTester = async t => {
    const newEmail = window.prompt("Correo del tester:", t.email || "");
    if (newEmail === null) return;
    const startDate = window.prompt("Fecha de inicio (AAAA-MM-DD, vacío = sin fecha):", t.tester_start_date || "");
    if (startDate === null) return;
    const endDate = window.prompt("Fecha final (AAAA-MM-DD, vacío = sin fecha):", t.tester_end_date || "");
    if (endDate === null) return;
    const status = window.prompt("Estado: invited, registered, ready, in_progress, completed, inactive", t.tester_status || "invited");
    if (status === null) return;
    const notes = window.prompt("Notas de reemplazo/contexto:", t.replacement_notes || "");
    if (notes === null) return;
    await patchTester(t.id, { email: newEmail, tester_start_date: startDate, tester_end_date: endDate, tester_status: status, replacement_notes: notes });
  };

  const revokeTester = async t => {
    if (!window.confirm(`¿Quitar autorización de tester a ${t.email}?`)) return;
    const { error } = await supabase.rpc("admin_revoke_tester", { p_email: t.email });
    if (error) setMsg("No se pudo quitar: " + error.message); else { setMsg("Tester revocado"); await load(); }
  };

  const startToday = (id) => {
    const start = async () => {
      const { error } = await supabase.rpc("admin_start_tester_period", { p_tester_id: id });
      if (error) setMsg("No se inició el periodo: " + error.message);
      else { setMsg("Periodo iniciado con la fecha de Supabase"); await load(); }
    };
    start();
  };

  const visible = useMemo(() => {
    const arr = testers.filter(t => filter === "all" || (filter === "created" ? t.account_created : filter === "replacement" ? t.is_replacement : filter === "active" ? t.is_active : filter === "inactive" ? !t.is_active : t.tester_status === filter));
    return [...arr].sort((a,b) => {
      if (sort === "streak") return (b.current_streak || 0) - (a.current_streak || 0);
      if (sort === "active_days") return (b.active_days_total || 0) - (a.active_days_total || 0);
      if (sort === "start") return String(b.tester_start_date || "").localeCompare(String(a.tester_start_date || ""));
      return String(b.last_activity_at || "").localeCompare(String(a.last_activity_at || ""));
    });
  }, [testers, filter, sort]);

  const exportCsv = () => downloadCsv("venaqqi-testers.csv", [["Correo","Nombre","Estado","Cuenta creada","Fecha inicio","Fecha final","Días activos","Racha actual","Racha máxima","Progreso","Sesiones","Recomendaciones","Lugares abiertos","Reseñas","Mensajes Don D","Acciones relevantes","Última actividad","Reemplazo"], ...visible.map(t => [t.email, t.name || adminUsers[t.user_id] || "", TESTER_STATUS_LABELS[t.tester_status] || t.tester_status, t.account_created ? "Sí" : "No", t.tester_start_date || "", t.tester_end_date || "", t.active_days_total || 0, t.current_streak || 0, t.max_streak || 0, `${t.progress_days || 0}/14 días`, t.total_sessions || 0, t.total_recommendations || 0, t.total_place_views || 0, t.total_reviews || 0, t.total_don_d_messages || 0, t.total_relevant_actions || 0, t.last_activity_at || "", t.is_replacement ? "Sí" : "No"])]);

  return <div>
    <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 14, padding: 14, marginBottom: 14 }}>
      <p style={{ margin: "0 0 8px", color: "#fff", fontWeight: 700 }}>Agregar tester autorizado</p>
      <div style={{ display: "flex", gap: 8 }}>
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="correo@tester.com" style={{ flex: 1, background: "#161616", border: "1px solid #2a2a2a", borderRadius: 10, padding: "10px 12px", color: "#fff" }} />
        <button onClick={addTester} style={{ background: "#e53935", border: "none", color: "#fff", borderRadius: 10, padding: "0 14px", fontWeight: 800 }}>Agregar</button>
      </div>
      {msg && <p style={{ margin: "8px 0 0", color: "#aaa", fontSize: 12 }}>{msg}</p>}
    </div>
    {testerError && <p role="alert" style={{ color: "#e57373", fontSize: 12 }}>{testerError}</p>}
    <div style={{ marginBottom: 18 }}>
      <p style={{ color: "#777", fontWeight: 800, fontSize: 11, letterSpacing: 1 }}>AUTORIZACIONES ({authorizations.length})</p>
      {authorizations.map(a => <div key={a.email} style={{ display: "grid", gridTemplateColumns: "minmax(160px,1fr) auto", gap: 8, padding: "7px 0", borderBottom: "1px solid #181818", color: "#aaa", fontSize: 11 }}>
        <span>{a.email}<br/><small style={{ color: "#555" }}>{a.authorized_at ? new Date(a.authorized_at).toLocaleString() : "Sin fecha"}</small></span>
        <strong style={{ color: a.is_authorized && !a.revoked_at ? "#81c784" : "#e57373" }}>{a.is_authorized && !a.revoked_at ? "Autorizado" : "Revocado"}</strong>
      </div>)}
      {!authorizations.length && !loading && <p style={{ color: "#555", fontSize: 12 }}>Sin autorizaciones registradas.</p>}
      {authorizationError && <p role="alert" style={{ color: "#e57373", fontSize: 12 }}>{authorizationError}</p>}
    </div>
    <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
      <select value={filter} onChange={e => setFilter(e.target.value)} style={{ background: "#111", color: "#ddd", border: "1px solid #2a2a2a", borderRadius: 8, padding: 8 }}><option value="all">Todos</option><option value="active">Activos</option><option value="inactive">Inactivos</option><option value="created">Cuenta creada</option><option value="completed">Completados</option><option value="replacement">Reemplazos</option><option value="invited">Invitados</option><option value="in_progress">En progreso</option></select>
      <select value={sort} onChange={e => setSort(e.target.value)} style={{ background: "#111", color: "#ddd", border: "1px solid #2a2a2a", borderRadius: 8, padding: 8 }}><option value="last_activity">Última actividad</option><option value="streak">Mayor racha</option><option value="active_days">Más días activos</option><option value="start">Fecha inicio</option></select>
      <button onClick={exportCsv} style={{ background: "#1a2a1a", border: "1px solid #2d5a2d", color: "#81c784", borderRadius: 8, padding: "8px 12px", fontWeight: 700 }}>Exportar CSV</button>
      <button onClick={load} style={{ background: "#111", border: "1px solid #2a2a2a", color: "#aaa", borderRadius: 8, padding: "8px 12px" }}>{loading ? "Cargando..." : "Actualizar"}</button>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {visible.map(t => <div key={t.id} style={{ background: "#0d0d0d", border: "1px solid #1e1e1e", borderRadius: 12, padding: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
          <div><p style={{ margin: 0, color: "#fff", fontWeight: 800 }}>{t.email}</p><p style={{ margin: "3px 0", color: "#777", fontSize: 11 }}>{t.name || adminUsers[t.user_id] || "Sin nombre"} · {t.account_created ? "Cuenta creada" : "Sin cuenta"} · {TESTER_STATUS_LABELS[t.tester_status] || t.tester_status}{t.is_replacement ? " · Reemplazo" : ""}</p></div>
          <strong style={{ color: (t.progress_days || 0) >= 14 ? "#4caf50" : "#e53935" }}>{t.progress_days || 0}/14</strong>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 8, marginTop: 8, color: "#aaa", fontSize: 11 }}>
          <span>Inicio: {t.tester_start_date || "—"}</span><span>Fin: {t.tester_end_date || "—"}</span><span>Días: {t.active_days_total || 0}</span><span>Periodo: {t.active_days_in_period || 0}</span><span>Racha: {t.current_streak || 0}</span><span>Máx: {t.max_streak || 0}</span><span>Sesiones: {t.total_sessions || 0}</span><span>Reco: {t.total_recommendations || 0}</span><span>Lugares: {t.total_place_views || 0}</span><span>Reseñas: {t.total_reviews || 0}</span><span>Don D’: {t.total_don_d_messages || 0}</span><span>Acciones: {t.total_relevant_actions || 0}</span><span>Última: {t.last_activity_at ? new Date(t.last_activity_at).toLocaleString() : "—"}</span>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
          <button onClick={() => patchTester(t.id, { is_active: !t.is_active })} style={{ background: t.is_active ? "#3a1a1a" : "#1a3a1a", border: "1px solid #333", color: t.is_active ? "#e57373" : "#81c784", borderRadius: 8, padding: "6px 9px" }}>{t.is_active ? "Desactivar" : "Activar"}</button>
          <button onClick={() => startToday(t.id)} style={{ background: "#1a1a2e", border: "1px solid #2a2a4a", color: "#90caf9", borderRadius: 8, padding: "6px 9px" }}>Iniciar/Reiniciar hoy</button>
          <button onClick={() => patchTester(t.id, { is_replacement: !t.is_replacement })} style={{ background: "#111", border: "1px solid #333", color: "#aaa", borderRadius: 8, padding: "6px 9px" }}>{t.is_replacement ? "Quitar reemplazo" : "Marcar reemplazo"}</button>
          <button onClick={() => editTester(t)} style={{ background: "#111", border: "1px solid #555", color: "#ddd", borderRadius: 8, padding: "6px 9px" }}>✏️ Editar</button>
          <button onClick={() => revokeTester(t)} style={{ background: "#2a1111", border: "1px solid #5a2d2d", color: "#ef9a9a", borderRadius: 8, padding: "6px 9px" }}>Quitar tester</button>
          <span style={{ color: "#555", fontSize: 10, alignSelf: "center" }}>ID: {t.id} · user_id: {t.user_id || "sin vincular"}</span>
        </div>
      </div>)}
      {!visible.length && <p style={{ color: "#555", fontSize: 13 }}>Sin testers para este filtro.</p>}
    </div>
  </div>;
};

const Paginador = ({ page, setPage, total, pageSize }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, margin: "12px 0 20px" }}>
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{ background: "#161616", border: "1px solid #2a2a2a", color: page <= 1 ? "#444" : "#ccc", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: page <= 1 ? "default" : "pointer" }}>← Anterior</button>
      <span style={{ color: "#777", fontSize: 12 }}>Página {page} de {totalPages}</span>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ background: "#161616", border: "1px solid #2a2a2a", color: page >= totalPages ? "#444" : "#ccc", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: page >= totalPages ? "default" : "pointer" }}>Siguiente →</button>
    </div>
  );
};

const ResenaForm = ({ placeName, placeTipo, values, set, toggleArr, showExtras, setShowExtras }) => {
  useKeyboardAvoid();
  const inp = { width: "100%", background: "#161616", border: "1px solid #2a2a2a", borderRadius: 10, padding: "10px 13px", color: "#fff", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 8 };
  const camInputRef = useRef(null);
  const galInputRef = useRef(null);

  const [uploadingImgs, setUploadingImgs] = useState([]); // [{name, progress}]
  const [uploadError, setUploadError] = useState("");

  const handleUpload = async (files) => {
    const imgs = Array.isArray(values.imagenes) ? values.imagenes : [];
    const allowed = Array.from(files).slice(0, 5 - imgs.length);
    if (!allowed.length) return;
    setUploadError("");
    setUploadingImgs(allowed.map(file => ({ name: file.name, progress: 10 })));

    const results = await Promise.all(allowed.map(async (file, index) => {
      try {
        const uploaded = await uploadReviewImage(file);
        setUploadingImgs(previous => previous.map((item, i) => i === index ? { ...item, progress: 100 } : item));
        return uploaded;
      } catch (error) {
        setUploadError(error?.message || "No se pudo procesar una de las imágenes");
        return null;
      }
    }));

    const nuevas = results.filter(Boolean);
    if (!nuevas.length) setUploadError(previous => previous || "No se pudo subir ninguna imagen");
    set("imagenes", [...imgs, ...nuevas].slice(0, 5));
    setUploadingImgs([]);
  };
  return (
    <div>
      <p style={{ margin: "0 0 14px", fontSize: 12, color: "#888" }}>
        Tu reseña de <strong style={{ color: "#e57373" }}>{placeName || "este lugar"}</strong>
      </p>

      {/* Sliders */}
      <div style={{ background: "#161616", borderRadius: 12, padding: 14, marginBottom: 12 }}>
        <SliderField emoji="⭐" label="Sabor" field="sabor" labels={SABOR_LABELS} values={values} set={set} />
        <SliderField emoji="✔️" label="Servicio" field="servicio" labels={SERVICIO_LABELS} values={values} set={set} />
        <SliderField emoji="🔁" label="¿Volverías?" field="satisfaccion" labels={SAT_LABELS} values={values} set={set} />
        <div style={{ marginBottom: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: "#aaa" }}>💲 Precio por persona</span>
            <span style={{ fontSize: 12, color: "#4caf50", fontWeight: 700 }}>{precioDisplay(values.precio)}</span>
          </div>
          <input type="range" min={1} max={7} value={values.precio} onChange={e => set("precio", +e.target.value)} style={{ width: "100%", accentColor: "#4caf50" }} />
        </div>
      </div>

      {/* Fotos (movidas arriba, después de métricas) */}
      <div style={{ marginBottom: 12 }}>
        <p style={{ margin: "0 0 4px", fontSize: 12, color: "#aaa" }}>📷 Fotos (máx. 5)</p>
        <p style={{ margin: "0 0 8px", fontSize: 10, color: "#666", lineHeight: 1.4 }}>Para proteger la privacidad, Venaqqi difumina automáticamente los rostros detectados antes de guardar una foto nueva.</p>
        {uploadingImgs.length > 0 && (
          <div style={{ background: "rgba(229,57,53,0.08)", border: "1px solid rgba(229,57,53,0.2)", borderRadius: 10, padding: "8px 12px", marginBottom: 8 }}>
            <p style={{ margin: "0 0 6px", fontSize: 11, color: "#e57373" }}>⬆️ Subiendo {uploadingImgs.length} foto{uploadingImgs.length > 1 ? "s" : ""}...</p>
            {uploadingImgs.map((u, i) => (
              <div key={i} style={{ marginBottom: 4 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                  <span style={{ fontSize: 10, color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "80%" }}>{u.name}</span>
                  <span style={{ fontSize: 10, color: "#e53935", fontWeight: 700 }}>{u.progress}%</span>
                </div>
                <div style={{ height: 4, background: "#1e1e1e", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: u.progress + "%", background: "#e53935", borderRadius: 4, transition: "width 0.2s" }} />
                </div>
              </div>
            ))}
          </div>
        )}
        {uploadError && (
          <div style={{ background: "rgba(229,57,53,0.15)", border: "1px solid rgba(229,57,53,0.4)", borderRadius: 10, padding: "8px 12px", marginBottom: 8 }}>
            <p style={{ margin: 0, fontSize: 12, color: "#e57373" }}>⚠️ {uploadError}</p>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {(Array.isArray(values.imagenes) ? values.imagenes : []).map((img, i) => (
            <div key={i} style={{ position: "relative", width: 72, height: 72 }}>
              <img src={img.url} alt="" style={{ width: 72, height: 72, borderRadius: 10, objectFit: "cover" }} />
              <button onClick={() => set("imagenes", (Array.isArray(values.imagenes) ? values.imagenes : []).filter((_, j) => j !== i))}
                style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", background: "#e53935", border: "none", color: "#fff", fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
              <div style={{ position: "absolute", bottom: 2, left: 2, right: 2, display: "flex", justifyContent: "space-between" }}>
                <button type="button" aria-label={`Mover imagen ${i + 1} a la izquierda`} disabled={i === 0} onClick={() => set("imagenes", values.imagenes.map((item, index, all) => index === i - 1 ? all[i] : index === i ? all[i - 1] : item))}
                  style={{ border: 0, borderRadius: 5, background: "rgba(0,0,0,.75)", color: i === 0 ? "#555" : "#fff", cursor: i === 0 ? "default" : "pointer" }}>←</button>
                <button type="button" aria-label={`Mover imagen ${i + 1} a la derecha`} disabled={i === values.imagenes.length - 1} onClick={() => set("imagenes", values.imagenes.map((item, index, all) => index === i ? all[i + 1] : index === i + 1 ? all[i] : item))}
                  style={{ border: 0, borderRadius: 5, background: "rgba(0,0,0,.75)", color: i === values.imagenes.length - 1 ? "#555" : "#fff", cursor: i === values.imagenes.length - 1 ? "default" : "pointer" }}>→</button>
              </div>
            </div>
          ))}
          {(Array.isArray(values.imagenes) ? values.imagenes : []).length < 5 && <>
              <input ref={camInputRef} type="file" accept="image/*" capture="environment" id="vq-cam-input" style={{ display: "none" }} onChange={e => { handleUpload(e.target.files); e.target.value = ""; }} />
              <input ref={galInputRef} type="file" accept="image/*" multiple id="vq-gal-input" style={{ display: "none" }} onChange={e => { handleUpload(e.target.files); e.target.value = ""; }} />
              <label htmlFor="vq-cam-input" onClick={e => e.stopPropagation()}
                style={{ width: 72, height: 72, borderRadius: 10, border: "1px dashed #333", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexDirection: "column", gap: 3 }}>
                <span style={{ fontSize: 20 }}>📷</span>
                <span style={{ fontSize: 9, color: "#555" }}>Cámara</span>
              </label>
              <label htmlFor="vq-gal-input" onClick={e => e.stopPropagation()}
                style={{ width: 72, height: 72, borderRadius: 10, border: "1px dashed #333", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexDirection: "column", gap: 3 }}>
                <span style={{ fontSize: 20 }}>🖼️</span>
                <span style={{ fontSize: 9, color: "#555" }}>Galería</span>
              </label>
            </>}
        </div>
      </div>

      {/* Platillo - mandatorio con sugerencias */}
      <p style={{ margin: "0 0 8px", fontSize: 12, color: "#aaa" }}>🍽️ ¿Qué pediste? <span style={{ color: "#e53935" }}>*</span></p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
        {getSugerenciasPlatillo(placeTipo || values.tipo || placeName).map(opt => {
          const cur = (values.platillo || "").split(" · ").filter(Boolean);
          const on = cur.includes(opt);
          // B11 (IDs 67,68) — selección múltiple: toca para agregar/quitar; se guardan unidos con " · "
          return (
          <button key={opt} onClick={() => { const next = on ? cur.filter(x => x !== opt) : [...cur, opt]; set("platillo", next.join(" · ")); }}
            style={{ padding: "5px 12px", borderRadius: 20, border: on ? "1px solid #e53935" : "1px solid #2a2a2a", background: on ? "rgba(229,57,53,0.12)" : "transparent", color: on ? "#e53935" : "#666", fontSize: 11, cursor: "pointer" }}>
            {opt}
          </button>
          );
        })}
      </div>
      <input value={values.platillo} onChange={e => set("platillo", e.target.value)} placeholder="O escribe otro platillo..." style={inp} />

      {/* Lo bueno chips */}
      <p style={{ margin: "0 0 8px", fontSize: 12, color: "#aaa" }}>👍 Lo bueno</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        {LO_BUENO_OPTS.map(opt => (
          <button key={opt} onClick={() => toggleArr("lo_bueno", opt)}
            style={{ padding: "5px 12px", borderRadius: 20, border: values.lo_bueno.includes(opt) ? "1px solid #e53935" : "1px solid #2a2a2a", background: values.lo_bueno.includes(opt) ? "rgba(229,57,53,0.12)" : "transparent", color: values.lo_bueno.includes(opt) ? "#e53935" : "#666", fontSize: 11, cursor: "pointer" }}>
            {opt}
          </button>
        ))}
      </div>

      {/* Comentario - opcional, máx 100 */}
      <div style={{ position: "relative", marginBottom: 10 }}>
        <textarea value={values.comentario} onChange={e => { if (e.target.value.length <= 100) set("comentario", e.target.value); }}
          placeholder="Cuéntanos tu experiencia (máx. 100 caracteres)" rows={3}
          style={{ ...inp, resize: "none", marginBottom: 0 }} />
        <span style={{ position: "absolute", bottom: 8, right: 10, fontSize: 10, color: values.comentario.length >= 90 ? "#e53935" : "#555" }}>{values.comentario.length}/100</span>
      </div>

      {/* Sabor 5 → palabras al chef (movido aquí) */}
      {values.sabor === 5 && (
        <div style={{ background: "#1a1a1a", borderRadius: 10, padding: "10px 12px", marginBottom: 8, border: "1px solid #2a2a2a" }}>
          <p style={{ margin: "0 0 6px", fontSize: 11, color: "#d4a853" }}>👨‍🍳 Deja unas palabras para el chef</p>
          <input value={values.chef_msg} onChange={e => set("chef_msg", e.target.value)} placeholder="Felicitaciones al Chef" maxLength={80} style={{ ...inp, marginBottom: 0 }} />
        </div>
      )}

      {/* Servicio 5 → palabras al staff (movido aquí) */}
      {values.servicio === 5 && (
        <div style={{ background: "#1a1a1a", borderRadius: 10, padding: "10px 12px", marginBottom: 10, border: "1px solid #2a2a2a" }}>
          <p style={{ margin: "0 0 6px", fontSize: 11, color: "#d4a853" }}>🌟 Deja unas palabras para quien te atendió</p>
          <input value={values.staff_msg} onChange={e => set("staff_msg", e.target.value)} placeholder="¡Excelente servicio!" maxLength={80} style={{ ...inp, marginBottom: 0 }} />
        </div>
      )}

      {/* Extras colapsables */}
      <button onClick={() => setShowExtras(p => !p)}
        style={{ width: "100%", padding: "9px 14px", background: "transparent", border: "1px solid #2a2a2a", borderRadius: 10, color: "#888", fontSize: 12, cursor: "pointer", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>✨ Extras del lugar {values.extras.length > 0 ? "(" + values.extras.length + ")" : ""}</span>
        <span>{showExtras ? "▲" : "▼"}</span>
      </button>
      {showExtras && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
          {EXTRAS_LIST.map(ex => (
            <button key={ex.id} onClick={() => toggleArr("extras", ex.id)}
              style={{ padding: "8px 4px", borderRadius: 10, border: values.extras.includes(ex.id) ? "1px solid #e53935" : "1px solid #2a2a2a", background: values.extras.includes(ex.id) ? "rgba(229,57,53,0.12)" : "#161616", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 20 }}>{ex.emoji}</span>
              <span style={{ fontSize: 9, color: values.extras.includes(ex.id) ? "#e53935" : "#666", textAlign: "center", lineHeight: 1.2 }}>{ex.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};



const EXTRAS_LIST = [
  { id: "estacionamiento", emoji: "🅿️", label: "Estacionamiento" },
  { id: "banos", emoji: "🚻", label: "Baños" },
  { id: "ac", emoji: "❄️", label: "Aire acondicionado" },
  { id: "tarjeta", emoji: "💳", label: "Acepta tarjeta" },
  { id: "exterior", emoji: "🌿", label: "Área exterior" },
  { id: "wifi", emoji: "📶", label: "WiFi" },
  { id: "alcohol", emoji: "🍺", label: "Venden alcohol" },
  { id: "accesible", emoji: "♿", label: "Accesible" },
  { id: "petfriendly", emoji: "🐾", label: "Pet friendly" },
  { id: "musica", emoji: "🎵", label: "Música en vivo" },
  { id: "reserva", emoji: "📞", label: "Se puede apartar" },
];

const LO_BUENO_OPTS = ["Disponibilidad de mesas", "Limpieza", "Rápido de atender", "Buenas porciones", "Buen ambiente"];

const PRECIO_DETALLES = [];  // eslint-disable-line no-unused-vars


const PLATILLO_SUGS_POR_TIPO = {
  tacos: ["Tacos al pastor", "Tacos árabes", "Alambre", "Quesadilla", "Gringa", "Taco de asada"],
  hamburguesas: ["Hamburguesa clásica", "Con tocino", "Doble carne", "Burger gourmet", "Papas a la francesa"],
  mariscos: ["Tostada de atún", "Ceviche", "Aguachile", "Coctel de camarón", "Pescado a la plancha"],
  buffet: ["Buffet completo", "Cortes de carne", "Ensaladas", "Postres"],
  desayunos: ["Chilaquiles", "Huevos al gusto", "Molletes", "Hot cakes", "Enchiladas"],
  carnes: ["Arrachera", "Costillas BBQ", "Sirloin", "Cortes finos", "Parrillada mixta"],
  ramen: ["Ramen tonkotsu", "Ramen shoyu", "Gyozas", "Edamame", "Tempura"],
  argentina: ["Choripán", "Empanadas", "Bife de chorizo", "Asado", "Provoleta"],
  brasil: ["Espadas brasileñas", "Picaña", "Pollo en espada", "Buffet de ensaladas"],
  pollo: ["Pollo BBQ", "Costillar de cerdo", "Pollo a la parrilla", "Alitas"],
  poblana: ["Mole poblano", "Chiles en nogada", "Cemita", "Chalupas", "Enchiladas"],
  birria: ["Birria de res", "Quesabirria", "Consomé", "Tacos de birria"],
  brunch: ["Hot cakes", "Waffles", "Eggs benedict", "Avocado toast", "Pan dulce"],
  tortas: ["Torta de milanesa", "Torta cubana", "Torta de pierna", "Torta de jamón"],
  bar: ["Botana", "Alitas", "Nachos", "Quesadillas", "Tabla de quesos"],
  default: ["Platillo del día", "Especialidad de la casa", "Combo familiar", "Menú económico"]
};

const getSugerenciasPlatillo = (tipo) => {
  if (!tipo) return PLATILLO_SUGS_POR_TIPO.default;
  const t = tipo.toLowerCase();
  if (t.includes("taco") || t.includes("árabe") || t.includes("arabe") || t.includes("alambre")) return PLATILLO_SUGS_POR_TIPO.tacos;
  if (t.includes("hamburgues") || t.includes("burger")) return PLATILLO_SUGS_POR_TIPO.hamburguesas;
  if (t.includes("marisco") || t.includes("botana")) return PLATILLO_SUGS_POR_TIPO.mariscos;
  if (t.includes("buffet")) return PLATILLO_SUGS_POR_TIPO.buffet;
  if (t.includes("desayuno")) return PLATILLO_SUGS_POR_TIPO.desayunos;
  if (t.includes("brunch")) return PLATILLO_SUGS_POR_TIPO.brunch;
  if (t.includes("carne") || t.includes("parrilla") || t.includes("asador")) return PLATILLO_SUGS_POR_TIPO.carnes;
  if (t.includes("ramen") || t.includes("japon")) return PLATILLO_SUGS_POR_TIPO.ramen;
  if (t.includes("argentin")) return PLATILLO_SUGS_POR_TIPO.argentina;
  if (t.includes("brasil")) return PLATILLO_SUGS_POR_TIPO.brasil;
  if (t.includes("pollo") || t.includes("bbq") || t.includes("cajun")) return PLATILLO_SUGS_POR_TIPO.pollo;
  if (t.includes("poblan") || t.includes("mexican") || t.includes("casera")) return PLATILLO_SUGS_POR_TIPO.poblana;
  if (t.includes("birria")) return PLATILLO_SUGS_POR_TIPO.birria;
  if (t.includes("torta") || t.includes("sándwich") || t.includes("sandwich")) return PLATILLO_SUGS_POR_TIPO.tortas;
  if (t.includes("pub") || t.includes("bar")) return PLATILLO_SUGS_POR_TIPO.bar;
  return PLATILLO_SUGS_POR_TIPO.default;
};

const Stars = ({ n, max = 5 }) => <span>{Array.from({ length: max }, (_, i) => <span key={i} style={{ fontSize: 12, opacity: i < Math.round(n) ? 1 : 0.2 }}>⭐</span>)}<span style={{ fontSize: 11, color: "#666", marginLeft: 3 }}>({n.toFixed(1)})</span></span>;
const Chks = ({ n, max = 5 }) => <span>{Array.from({ length: max }, (_, i) => <span key={i} style={{ fontSize: 11, opacity: i < Math.round(n) ? 1 : 0.2 }}>✔️</span>)}<span style={{ fontSize: 10, color: "#666", marginLeft: 3 }}>({n.toFixed(1)})</span></span>;
const Scr = ({ n }) => <div style={{ background: n >= 4.5 ? "#e53935" : n >= 3.5 ? "#c62828" : "#555", borderRadius: "50%", width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 3px 12px rgba(229,57,53,0.45)", flexShrink: 0 }}><span style={{ fontSize: 13, fontWeight: 900, color: "#fff" }}>{n.toFixed(1)}</span></div>;

// Auth para reseñar (solo correo)
const PasswordResetModal = ({ onClose }) => {
  useKeyboardAvoid();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const showToast = (type, text) => { setToast({ type, text }); setTimeout(() => setToast(null), 4000); };

  const toastStyle = { success: { bg: "rgba(76,175,80,0.12)", border: "rgba(76,175,80,0.4)", color: "#81c784", icon: "✅" }, error: { bg: "rgba(229,57,53,0.12)", border: "rgba(229,57,53,0.4)", color: "#ef9a9a", icon: "❌" } };

  const isStrong = newPassword.length >= 6;
  const matches = newPassword === confirmPassword && confirmPassword.length > 0;

  const save = async () => {
    if (!isStrong) { showToast("error", "La contraseña debe tener al menos 6 caracteres"); return; }
    if (!matches) { showToast("error", "Las contraseñas no coinciden"); return; }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) { showToast("error", "No se pudo actualizar. Intenta de nuevo."); }
      else { showToast("success", "Contraseña actualizada. Ya puedes entrar."); setDone(true); setTimeout(() => onClose(), 2500); }
    } catch (e) {
      showToast("error", "Error de conexión. Intenta de nuevo.");
    }
    setLoading(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 4100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(8px)" }}>
      <div style={{ background: "#0d0d0d", borderRadius: 20, maxWidth: 360, width: "100%", padding: 28, border: "1px solid #1e1e1e" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔐</div>
          <h2 style={{ margin: "0 0 6px", fontFamily: "'Arial Black',sans-serif", color: "#fff", fontSize: 18 }}>Nueva contraseña</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#666" }}>Elige una contraseña nueva para tu cuenta</p>
        </div>
        {!done ? <>
          <div style={{ position: "relative", marginBottom: 4 }}>
            <input value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Nueva contraseña" type={showNew ? "text" : "password"}
              style={{ width: "100%", background: "#161616", border: "1px solid " + (newPassword.length > 0 ? (isStrong ? "#4caf50" : "#e53935") : "#2a2a2a"), borderRadius: 10, padding: "11px 14px", paddingRight: 40, color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
            <button onClick={() => setShowNew(!showNew)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 16 }}>{showNew ? "🙈" : "👁️"}</button>
          </div>
          {newPassword.length > 0 && <p style={{ margin: "0 0 10px", fontSize: 11, color: isStrong ? "#4caf50" : "#e53935" }}>{isStrong ? "✓ Contraseña válida" : "Mínimo 6 caracteres"}</p>}
          <div style={{ position: "relative", marginBottom: 4 }}>
            <input value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirmar contraseña" type={showConfirm ? "text" : "password"}
              style={{ width: "100%", background: "#161616", border: "1px solid " + (confirmPassword.length > 0 ? (matches ? "#4caf50" : "#e53935") : "#2a2a2a"), borderRadius: 10, padding: "11px 14px", paddingRight: 40, color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
            <button onClick={() => setShowConfirm(!showConfirm)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 16 }}>{showConfirm ? "🙈" : "👁️"}</button>
          </div>
          {confirmPassword.length > 0 && <p style={{ margin: "0 0 12px", fontSize: 11, color: matches ? "#4caf50" : "#e53935" }}>{matches ? "✓ Coinciden" : "Las contraseñas no coinciden"}</p>}
          {toast && (
            <div style={{ background: toastStyle[toast.type].bg, border: "1px solid " + toastStyle[toast.type].border, borderRadius: 10, padding: "10px 14px", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
              <span>{toastStyle[toast.type].icon}</span>
              <span style={{ fontSize: 13, color: toastStyle[toast.type].color }}>{toast.text}</span>
            </div>
          )}
          <button onClick={save} disabled={loading || !isStrong || !matches}
            style={{ width: "100%", padding: 13, background: (loading || !isStrong || !matches) ? "#333" : "#e53935", border: "none", borderRadius: 10, color: (isStrong && matches) ? "#fff" : "#555", fontSize: 14, fontWeight: 700, cursor: (loading || !isStrong || !matches) ? "not-allowed" : "pointer" }}>
            {loading ? "Guardando..." : "Guardar contraseña"}
          </button>
        </> : (
          <div style={{ textAlign: "center", padding: "10px 0" }}>
            {toast && (
              <div style={{ background: toastStyle[toast.type].bg, border: "1px solid " + toastStyle[toast.type].border, borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                <span>{toastStyle[toast.type].icon}</span>
                <span style={{ fontSize: 13, color: toastStyle[toast.type].color }}>{toast.text}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// Perfil inicial: todos los usuarios nuevos (email/password u OAuth) nacen con
// preferencias vacías y trazables para personalizar recomendaciones sin inventar datos.
const INITIAL_USER_PREFERENCES = {
  favorite_place_ids: [],
  favorite_food_types: [],
  dietary_preferences: [],
  avoided_food_types: [],
  max_budget: null,
  preferred_zones: [],
  recommendation_notes: "",
};

const buildInitialUserProfile = ({ id, email, username, edad = null, fechaNacimiento = null, nombre = null, provider = "email" }) => ({
  id,
  email,
  username,
  edad,
  fecha_nacimiento: fechaNacimiento,
  nombre,
  auth_provider: provider,
  preferences: INITIAL_USER_PREFERENCES,
});

const getAuthUsername = (authUser) => {
  const raw = authUser?.user_metadata?.username || authUser?.user_metadata?.name || authUser?.email?.split("@")[0] || "user";
  const base = String(raw).replace(/[^a-zA-Z0-9_]/g, "").slice(0, 16) || "user";
  return (base.length >= 3 ? base : base + "123").toLowerCase();
};

const getAuthUsernameFallback = (authUser) => {
  const suffix = String(authUser?.id || "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase()
    .slice(0, 6) || "000000";
  const base = getAuthUsername(authUser).slice(0, 13);
  return `${base}_${suffix}`.slice(0, 20);
};

const getProfileErrorMessage = (error) => error?.message || error?.details || error?.hint || "Error desconocido al crear perfil";

const toAppUser = (authUser, profile) => ({
  email: authUser.email,
  userId: authUser.id,
  username: profile.username,
  edad: profileAge({ edad: profile.edad, fecha_nacimiento: profile.fecha_nacimiento }),
  fechaNacimiento: profile.fecha_nacimiento || null,
  nombre: profile.nombre,
  testerAuthorized: profile.testerAuthorized === true,
  fullReg: true,
});

const ensureUserProfile = async (authUser, profileOverrides = {}) => {
  if (!authUser?.id) throw new Error("No hay session.user para crear perfil");

  const { data: existing, error: selectError } = await supabase
    .from("users")
    .select("*")
    .eq("id", authUser.id)
    .maybeSingle();

  if (selectError) {
    console.error("ensureUserProfile SELECT public.users failed:", selectError);
    throw new Error("No se pudo consultar public.users: " + getProfileErrorMessage(selectError));
  }
  let userProfile = existing;

  if (!userProfile) {
    let profile = buildInitialUserProfile({
      id: authUser.id,
      email: authUser.email || profileOverrides.email,
      username: profileOverrides.username ? normalizeUsername(profileOverrides.username) : getAuthUsername(authUser),
      edad: profileOverrides.edad ?? authUser.user_metadata?.edad ?? null,
      fechaNacimiento: profileOverrides.fechaNacimiento ?? authUser.user_metadata?.fecha_nacimiento ?? null,
      nombre: profileOverrides.nombre ?? authUser.user_metadata?.name ?? null,
      provider: profileOverrides.provider || authUser.app_metadata?.provider || "email",
    });

    const insertProfile = candidate => supabase
      .from("users")
      .insert([candidate])
      .select("*")
      .single();

    let { data: inserted, error: insertError } = await insertProfile(profile);

    // OAuth no pide username manualmente. Si el derivado del correo/nombre ya existe,
    // reintenta con un sufijo estable del user id sin quitar la protección única real.
    if (insertError && isUsernameConflict(insertError) && !profileOverrides.username) {
      profile = { ...profile, username: getAuthUsernameFallback(authUser) };
      ({ data: inserted, error: insertError } = await insertProfile(profile));
    }

    if (insertError) {
      console.error("ensureUserProfile INSERT public.users failed:", insertError);
      if (isUsernameConflict(insertError)) throw new Error(USERNAME_TAKEN_MESSAGE);
      throw new Error("No se pudo insertar public.users: " + getProfileErrorMessage(insertError));
    }
    userProfile = inserted;
  }

  const { data: testerProvisioning, error: testerError } = await supabase.rpc("provision_current_tester");
  if (testerError) {
    console.error("provision_current_tester RPC failed:", testerError);
    throw new Error("Perfil cargado, pero falló el alta de tester: " + getProfileErrorMessage(testerError));
  }
  const testerResult = Array.isArray(testerProvisioning) ? testerProvisioning[0] : testerProvisioning;
  return { ...userProfile, testerAuthorized: testerResult?.authorized === true };
};

const signInWithGoogle = async () => {
  return withTimeout(
    supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    }),
    SUPABASE_TIMEOUT_MS,
    "Supabase no respondió al iniciar sesión con Google."
  );
};

const runGoogleSignIn = async (onError) => {
  try {
    const { error } = await signInWithGoogle();
    if (error) throw error;
  } catch (error) {
    console.error("Google OAuth error:", error);
    onError("No se pudo continuar con Google. Intenta de nuevo.");
  }
};

const ALLOWED_DOMAINS = ["gmail.com","hotmail.com","outlook.com","yahoo.com","icloud.com","live.com","protonmail.com","me.com","hotmail.es","yahoo.es","outlook.es","live.com.mx","hotmail.com.mx","gmail.com.mx"];

const getEmailDomain = (email) => email.includes("@") ? email.split("@")[1].toLowerCase().trim() : "";
const isDomainAllowed = (email) => ALLOWED_DOMAINS.includes(getEmailDomain(email));
// 3.2 — Validación real de formato de email (antes: solo includes("@") e includes("."))
const GoogleG = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.5 13.2l7.9 6.2C12.3 13.7 17.7 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6c4.5-4.2 6.9-10.4 6.9-17.7z"/>
    <path fill="#FBBC05" d="M10.4 28.6c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6l-7.9-6.2C.9 16.5 0 20.1 0 24s.9 7.5 2.5 10.8l7.9-6.2z"/>
    <path fill="#34A853" d="M24 48c6.3 0 11.6-2.1 15.6-5.8l-7.7-6c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.7-4.2-13.6-9.9l-7.9 6.2C6.5 42.6 14.6 48 24 48z"/>
  </svg>
);
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(String(e || "").trim());

const AuthModal = ({ onAuth, onClose, onSwitchToRegister }) => {
  useKeyboardAvoid();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryToast, setRecoveryToast] = useState(null);
  const [recoverySent, setRecoverySent] = useState(false);
  const [recoveryLoading, setRecoveryLoading] = useState(false);

  const tryClose = () => { setShake(true); setTimeout(() => setShake(false), 450); };

  const showToast = (type, text) => {
    setRecoveryToast({ type, text });
    setTimeout(() => setRecoveryToast(null), 4000);
  };

  const go = async () => {
    if (!isValidEmail(email)) { setErr("Correo inválido"); return; }
    if (!password || password.length < 6) { setErr("La contraseña debe tener al menos 6 caracteres"); return; }
    setLoading(true);
    try {
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 10000));
      const { data, error } = await Promise.race([supabase.auth.signInWithPassword({ email, password }), timeoutPromise]);
      if (error) { const m = (error.message || "").toLowerCase(); setErr(m.includes("confirm") ? "Tu correo aún no está confirmado. Revisa tu bandeja o reenvía el correo desde el registro." : "Correo o contraseña incorrectos"); setLoading(false); return; }
      const userData = await ensureUserProfile(data.user);
      onAuth(toAppUser(data.user, userData));
      setLoading(false);
    } catch (e) {
      console.error('Login/profile error:', e);
      setErr(e.message === 'Timeout' ? "La conexión tardó mucho. Intenta de nuevo." : (e?.message || "No se pudo cargar o crear el perfil"));
      setLoading(false);
    }
  };

  const sendRecovery = async () => {
    if (!isValidEmail(recoveryEmail)) { showToast("error", "Ingresa un correo válido"); return; }
    setRecoveryLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(recoveryEmail, { redirectTo: window.location.origin });
      if (error) { showToast("error", "No encontramos ese correo registrado"); }
      else { showToast("success", "Correo enviado. Revisa tu bandeja."); setRecoverySent(true); }
    } catch (e) {
      showToast("error", "Error de conexión. Intenta de nuevo.");
    }
    setRecoveryLoading(false);
  };

  const toastColors = { success: { bg: "rgba(76,175,80,0.12)", border: "rgba(76,175,80,0.4)", color: "#81c784", icon: "✅" }, error: { bg: "rgba(229,57,53,0.12)", border: "rgba(229,57,53,0.4)", color: "#ef9a9a", icon: "❌" }, warn: { bg: "rgba(255,152,0,0.12)", border: "rgba(255,152,0,0.4)", color: "#ffb74d", icon: "⚠️" } };

  return (
    <div onClick={tryClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(8px)" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#0d0d0d", borderRadius: 20, maxWidth: 360, width: "100%", padding: 28, border: "1px solid #1e1e1e", position: "relative" }}>
        <button onClick={onClose} className={shake ? "vq-shake" : ""} style={{ position: "absolute", top: 14, right: 14, background: shake ? "#e53935" : "rgba(255,255,255,0.08)", border: shake ? "2px solid #fff" : "1px solid #333", color: "#fff", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}>✕</button>

        {!recovering ? <>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✍️</div>
            <h2 style={{ margin: "0 0 6px", fontFamily: "'Arial Black',sans-serif", color: "#fff", fontSize: 18 }}>Iniciar sesión</h2>
            <p style={{ margin: 0, fontSize: 13, color: "#666" }}>Entra con tu cuenta VENAQQI</p>
          </div>
          <input value={email} onChange={e => { setEmail(e.target.value); setErr(""); }} placeholder="tu@correo.com" type="email"
            style={{ width: "100%", background: "#161616", border: err ? "1px solid #e53935" : "1px solid #2a2a2a", borderRadius: 10, padding: "11px 14px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 8 }} />
          <div style={{ position: "relative", marginBottom: 8 }}>
            <input value={password} onChange={e => { setPassword(e.target.value); setErr(""); }} placeholder="Contraseña" type={showPassword ? "text" : "password"}
              style={{ width: "100%", background: "#161616", border: err ? "1px solid #e53935" : "1px solid #2a2a2a", borderRadius: 10, padding: "11px 14px", paddingRight: 40, color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
            <button onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 16 }}>
              {showPassword ? "🙈" : "👁️"}
            </button>
          </div>
          {err && <p style={{ margin: "0 0 8px", fontSize: 12, color: "#e53935" }}>{err}</p>}
          <div style={{ background: "rgba(76,175,80,0.1)", border: "1px solid rgba(76,175,80,0.3)", borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
            <p style={{ margin: 0, fontSize: 11, color: "#5cb85c", display: "flex", alignItems: "center", gap: 6 }}>
              <span>🔒</span> Tu correo y tu información son confidenciales.
            </p>
          </div>
          <button onClick={go} disabled={loading} style={{ width: "100%", padding: 13, background: loading ? "#555" : "#e53935", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", marginBottom: 8 }}>
            {loading ? "Entrando..." : "Entrar →"}
          </button>
          <button onClick={() => { setRecovering(true); setRecoveryEmail(email); setRecoverySent(false); setRecoveryToast(null); }}
            style={{ width: "100%", padding: 10, background: "transparent", border: "1px solid #e53935", borderRadius: 10, color: "#e53935", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            🔑 Recuperar contraseña
          </button>
          {/* 3.1 — Login con Google (OAuth vía Supabase) */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "12px 0 10px" }}>
            <div style={{ flex: 1, height: 1, background: "#1e1e1e" }} />
            <span style={{ fontSize: 11, color: "#555" }}>o</span>
            <div style={{ flex: 1, height: 1, background: "#1e1e1e" }} />
          </div>
          <button aria-label="Continuar con Google desde iniciar sesión" onClick={() => runGoogleSignIn(setErr)}
            style={{ width: "100%", padding: 12, background: "#fff", border: "none", borderRadius: 10, color: "#1a1a1a", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <GoogleG /> Continuar con Google
          </button>
          <p style={{ margin: "8px 0 0", fontSize: 10, color: "#555", textAlign: "center", lineHeight: 1.5 }}>Al continuar con Google aceptas los <a href="/terminos.html" target="_blank" rel="noopener noreferrer" style={{ color: "#888" }}>Términos</a>, el <a href="/aviso-privacidad.html" target="_blank" rel="noopener noreferrer" style={{ color: "#888" }}>Aviso de Privacidad</a> y la <a href="/politica-ugc.html" target="_blank" rel="noopener noreferrer" style={{ color: "#888" }}>Política de UGC</a>.</p>
          <div style={{ textAlign: "center", marginTop: 12, paddingTop: 12, borderTop: "1px solid #1e1e1e" }}>
            <p style={{ margin: 0, fontSize: 12, color: "#666" }}>
              ¿Aún no tienes cuenta?{" "}
              <button onClick={() => { onClose(); onSwitchToRegister && onSwitchToRegister(); }}
                style={{ background: "none", border: "none", color: "#e53935", fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
                Regístrate aquí
              </button>
            </p>
          </div>
        </> : <>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🔑</div>
            <h2 style={{ margin: "0 0 6px", fontFamily: "'Arial Black',sans-serif", color: "#fff", fontSize: 18 }}>Recuperar contraseña</h2>
            <p style={{ margin: 0, fontSize: 13, color: "#666" }}>Te mandamos un link a tu correo</p>
          </div>
          {!recoverySent ? <>
            <input value={recoveryEmail} onChange={e => setRecoveryEmail(e.target.value)} placeholder="tu@correo.com" type="email"
              style={{ width: "100%", background: "#161616", border: "1px solid #2a2a2a", borderRadius: 10, padding: "11px 14px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 12 }} />
            <button onClick={sendRecovery} disabled={recoveryLoading}
              style={{ width: "100%", padding: 13, background: recoveryLoading ? "#555" : "#e53935", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: recoveryLoading ? "not-allowed" : "pointer", marginBottom: 8 }}>
              {recoveryLoading ? "Enviando..." : "Enviar link de recuperación"}
            </button>
          </> : <p style={{ textAlign: "center", fontSize: 13, color: "#888", margin: "0 0 12px" }}>Revisa tu bandeja de entrada y sigue el link para crear una nueva contraseña.</p>}
          {recoveryToast && (
            <div style={{ background: toastColors[recoveryToast.type].bg, border: "1px solid " + toastColors[recoveryToast.type].border, borderRadius: 10, padding: "10px 14px", marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
              <span>{toastColors[recoveryToast.type].icon}</span>
              <span style={{ fontSize: 13, color: toastColors[recoveryToast.type].color }}>{recoveryToast.text}</span>
            </div>
          )}
          <button onClick={() => setRecovering(false)}
            style={{ width: "100%", padding: 10, background: "transparent", border: "1px solid #2a2a2a", borderRadius: 10, color: "#888", fontSize: 13, cursor: "pointer" }}>
            ← Volver al login
          </button>
        </>}
      </div>
    </div>
  );
};

// Registro completo para agregar lugares
const RegisterModal = ({ onRegister, onClose, onGuest, purpose = "add_place" }) => {
  const [f, setF] = useState({ nombre: "", username: "", email: "", password: "", fechaNacimiento: "" });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [usernameAvailability, setUsernameAvailability] = useState({ status: "idle", message: "" });
  const usernameCheckSeq = useRef(0);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  useEffect(() => {
    const localError = validateUsername(f.username);
    const seq = ++usernameCheckSeq.current;
    if (localError) {
      setUsernameAvailability({ status: "idle", message: "" });
      return undefined;
    }
    setUsernameAvailability({ status: "checking", message: "Comprobando disponibilidad…" });
    const timer = setTimeout(async () => {
      try {
        const result = await checkUsernameAvailability(supabase, f.username);
        if (seq !== usernameCheckSeq.current) return;
        setUsernameAvailability(result.available
          ? { status: "available", message: "✓ Disponible" }
          : { status: "taken", message: USERNAME_TAKEN_MESSAGE });
      } catch (error) {
        if (seq !== usernameCheckSeq.current) return;
        setUsernameAvailability({ status: "error", message: "No se pudo comprobar ahora; se validará al crear la cuenta." });
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [f.username]);
  const validate = () => {
    const e = {};
    if (!f.nombre.trim()) e.nombre = "Ingresa tu nombre";
    const usernameError = validateUsername(f.username);
    if (usernameError) e.username = usernameError;
    else if (usernameAvailability.status === "taken") e.username = USERNAME_TAKEN_MESSAGE;
    if (!isValidEmail(f.email)) e.email = "Correo inválido";
    else if (!isDomainAllowed(f.email)) e.email = "Dominio no compatible. Usa Gmail, Hotmail, Outlook u otro conocido.";
    if (!f.password || f.password.length < 6) e.password = "Mínimo 6 caracteres";
    const birthdateCheck = birthdateValidation(f.fechaNacimiento);
    if (!birthdateCheck.valid) e.fechaNacimiento = birthdateCheck.message;
    if (!acceptedTerms) e.terms = "Debes aceptar los Términos, el Aviso de Privacidad y la Política de UGC";
    return e;
  };
  const go = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErr(e); return; }
    const normalizedUsername = normalizeUsername(f.username);
    setLoading(true);
    try {
      const availability = await checkUsernameAvailability(supabase, normalizedUsername);
      if (!availability.available) {
        setErr({ username: USERNAME_TAKEN_MESSAGE });
        setUsernameAvailability({ status: "taken", message: USERNAME_TAKEN_MESSAGE });
        setLoading(false);
        return;
      }
    } catch (availabilityError) {
      console.error("Username availability check failed:", availabilityError);
      setErr({ general: "No se pudo comprobar el username. Intenta de nuevo." });
      setLoading(false);
      return;
    }
    try {
      // Crear usuario en Supabase Auth con timeout corto
      let data = null;
      let authError = null;
      try {
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout en auth')), 3000)
        );
        const result = await Promise.race([
          supabase.auth.signUp({
            email: f.email.toLowerCase(),
            password: f.password,
            options: { data: { username: normalizedUsername, name: f.nombre.trim(), fecha_nacimiento: f.fechaNacimiento, edad: ageFromBirthdate(f.fechaNacimiento) } },
          }),
          timeoutPromise
        ]);
        data = result.data;
        authError = result.error;
      } catch (err) {
        setErr({ general: err?.message === 'Timeout en auth' ? 'La conexión tardó mucho. Intenta de nuevo.' : (err?.message || 'Error en auth') });
        setLoading(false);
        return;
      }
      
      if (authError) { setErr({ email: authError.message }); setLoading(false); return; }
      if (!data?.user?.id) { setErr({ general: 'Supabase no devolvió usuario para crear perfil' }); setLoading(false); return; }
      
      let registeredProfile = null;
      if (data?.session?.user) {
        try {
          registeredProfile = await ensureUserProfile(data.session.user, {
            email: f.email.toLowerCase(),
            username: normalizeUsername(f.username),
            edad: ageFromBirthdate(f.fechaNacimiento),
            fechaNacimiento: f.fechaNacimiento,
            nombre: f.nombre.trim(),
            provider: "email",
          });
        } catch (profileErr) {
          console.error('Register profile error:', profileErr);
          setErr({ general: profileErr?.message || 'Error al crear perfil' });
          setLoading(false);
          return;
        }
      }
      
      // 3.3 — Si Supabase tiene confirmación por correo activa, signUp no devuelve sesión:
      // no iniciar sesión "fantasma"; solo mostrar la pantalla de confirmación.
      const hasSession = !!data?.session;
      if (hasSession) {
        const registeredUser = toAppUser(data.user, registeredProfile);
        onRegister(registeredUser);
        localStorage.setItem('venaqqi_user', JSON.stringify(registeredUser));
      }
      setSent(true);
      setLoading(false);
    } catch (err) {
      setErr({ email: err?.message || 'Error desconocido' });
      console.error('Register error:', err);
      setErr({ general: 'Error en el registro' });
      setLoading(false);
    }
  };
  const inp = (field, placeholder, type = "text") => <>
    <input value={f[field]} onChange={e => { const v = (field === "username" || field === "email") ? e.target.value.replace(/\s+/g, "") : e.target.value; set(field, v); setErr(p => ({ ...p, [field]: "" })); }} placeholder={placeholder} type={type}
      style={{ width: "100%", background: "#161616", border: err[field] ? "1px solid #e53935" : "1px solid #2a2a2a", borderRadius: 10, padding: "11px 14px", color: "#fff", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 4 }} />
    {err[field] && <p style={{ margin: "0 0 8px", fontSize: 11, color: "#e53935" }}>{err[field]}</p>}
  </>;
  const [shake, setShake] = useState(false);
  const tryClose = () => { setShake(true); setTimeout(() => setShake(false), 450); };
  return (
    <div onClick={tryClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(8px)" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#0d0d0d", borderRadius: 20, maxWidth: 380, width: "100%", padding: 28, border: "1px solid #1e1e1e", position: "relative" }}>
        <button onClick={onClose} className={shake ? "vq-shake" : ""} style={{ position: "absolute", top: 14, right: 14, background: shake ? "#e53935" : "rgba(255,255,255,0.08)", border: shake ? "2px solid #fff" : "1px solid #333", color: "#fff", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}>✕</button>
        {!sent ? <>
          <div style={{ textAlign: "center", marginBottom: 22 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🗺️</div>
            <h2 style={{ margin: "0 0 6px", fontFamily: "'Arial Black',sans-serif", color: "#fff", fontSize: 18 }}>{purpose === "don_d" ? "Crea tu cuenta" : "Agregar un lugar"}</h2>
            <p style={{ margin: 0, fontSize: 13, color: "#666" }}>{purpose === "don_d" ? "Regístrate para conversar con Don D' y recibir recomendaciones personalizadas." : "Para agregar lugares necesitamos un registro básico."}</p>
          </div>
          {inp("nombre", "Tu nombre completo")}
          {inp("username", "@username (mín. 3 caracteres, sin espacios)")}
          {!err.username && usernameAvailability.message && (
            <p aria-live="polite" style={{ margin: "0 0 8px", fontSize: 11, color: usernameAvailability.status === "available" ? "#81c784" : usernameAvailability.status === "taken" ? "#ef9a9a" : "#777" }}>
              {usernameAvailability.message}
            </p>
          )}
          {inp("email", "tu@correo.com", "email")}
          <div style={{ position: "relative", marginBottom: 4 }}>
            <input value={f.password} onChange={e => { set("password", e.target.value); setErr(p => ({ ...p, password: "" })); }} placeholder="Contraseña (mín. 6 caracteres)" type={showPassword ? "text" : "password"}
              style={{ width: "100%", background: "#161616", border: err.password ? "1px solid #e53935" : "1px solid #2a2a2a", borderRadius: 10, padding: "11px 14px", paddingRight: 40, color: "#fff", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 0 }} />
            <button onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 14 }}>
              {showPassword ? "🙈" : "👁️"}
            </button>
          </div>
          {err.password && <p style={{ margin: "0 0 8px", fontSize: 11, color: "#e53935" }}>{err.password}</p>}
          <label style={{ display: "block", margin: "3px 0 5px", fontSize: 11, color: "#777" }}>Fecha de nacimiento</label>
          <input value={f.fechaNacimiento} onChange={e => { set("fechaNacimiento", e.target.value); setErr(p => ({ ...p, fechaNacimiento: "" })); }} type="date" max={birthdateInputMax()} aria-label="Fecha de nacimiento"
            style={{ width: "100%", background: "#161616", border: err.fechaNacimiento ? "1px solid #e53935" : "1px solid #2a2a2a", borderRadius: 10, padding: "11px 14px", color: "#fff", colorScheme: "dark", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 4 }} />
          {err.fechaNacimiento && <p style={{ margin: "0 0 8px", fontSize: 11, color: "#e53935" }}>{err.fechaNacimiento}</p>}
          <div style={{ background: "rgba(76, 175, 80, 0.1)", border: "1px solid rgba(76, 175, 80, 0.3)", borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
            <p style={{ margin: 0, fontSize: 11, color: "#5cb85c", display: "flex", alignItems: "center", gap: 6 }}>
              <span>🔒</span> Tu correo y tu información son confidenciales.
            </p>
          </div>
          <p style={{ margin: "4px 0 10px", fontSize: 11, color: "#444" }}>Te llegará un correo de confirmación.</p>
          {f.email.includes("@") && !isDomainAllowed(f.email) && (
            <div style={{ background: "rgba(255,152,0,0.1)", border: "1px solid rgba(255,152,0,0.3)", borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
              <p style={{ margin: 0, fontSize: 11, color: "#ffb74d" }}>⚠️ Dominio no compatible. Puedes continuar como invitado.</p>
            </div>
          )}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 11, color: "#888", lineHeight: 1.5, marginBottom: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={acceptedTerms} onChange={e => { setAcceptedTerms(e.target.checked); setErr(p => ({ ...p, terms: "" })); }} style={{ marginTop: 2 }} />
            <span>Acepto los <a href="/terminos.html" target="_blank" rel="noopener noreferrer" style={{ color: "#e53935" }} onClick={e => e.stopPropagation()}>Términos y Condiciones</a>, el <a href="/aviso-privacidad.html" target="_blank" rel="noopener noreferrer" style={{ color: "#e53935" }} onClick={e => e.stopPropagation()}>Aviso de Privacidad</a> y la <a href="/politica-ugc.html" target="_blank" rel="noopener noreferrer" style={{ color: "#e53935" }} onClick={e => e.stopPropagation()}>Política de UGC</a> de Venaqqi.</span>
          </label>
          {err.terms && <p style={{ margin: "-6px 0 10px", fontSize: 11, color: "#e53935" }}>{err.terms}</p>}
          <button onClick={go} disabled={loading || !acceptedTerms || (f.email.includes("@") && !isDomainAllowed(f.email))}
            style={{ width: "100%", padding: 13, background: (loading || !acceptedTerms || (f.email.includes("@") && !isDomainAllowed(f.email))) ? "#333" : "#e53935", border: "none", borderRadius: 10, color: (!acceptedTerms || (f.email.includes("@") && !isDomainAllowed(f.email))) ? "#555" : "#fff", fontSize: 14, fontWeight: 700, cursor: (loading || !acceptedTerms || (f.email.includes("@") && !isDomainAllowed(f.email))) ? "not-allowed" : "pointer", marginBottom: 6 }}>
            {loading ? "Registrando..." : "Registrarme →"}
          </button>
          {/* 3.1 — Registro con Google (OAuth vía Supabase) */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0 8px" }}>
            <div style={{ flex: 1, height: 1, background: "#1e1e1e" }} />
            <span style={{ fontSize: 11, color: "#555" }}>o</span>
            <div style={{ flex: 1, height: 1, background: "#1e1e1e" }} />
          </div>
          <button onClick={() => runGoogleSignIn(message => setErr(p => ({ ...p, general: message })))}
            style={{ width: "100%", padding: 12, background: "#fff", border: "none", borderRadius: 10, color: "#1a1a1a", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 4 }}>
            <GoogleG /> Continuar con Google
          </button>
          <p style={{ margin: "4px 0 6px", fontSize: 10, color: "#555", textAlign: "center", lineHeight: 1.5 }}>Al continuar con Google aceptas los Términos, el Aviso de Privacidad y la Política de UGC.</p>
          {err.general && <p style={{ margin: "0 0 8px", fontSize: 11, color: "#e53935", textAlign: "center" }}>{err.general}</p>}
          {f.email.includes("@") && !isDomainAllowed(f.email) && (
            <button onClick={onGuest} style={{ width: "100%", padding: 11, background: "transparent", border: "1px solid #e53935", borderRadius: 10, color: "#e53935", fontSize: 13, fontWeight: 700, cursor: "pointer", marginBottom: 4 }}>
              Continuar como invitado
            </button>
          )}
          <button onClick={onClose} style={{ width: "100%", padding: 10, background: "none", border: "none", color: "#555", fontSize: 13, cursor: "pointer", marginTop: 2 }}>Cancelar</button>
        </> : <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📩</div>
          <h2 style={{ margin: "0 0 8px", fontFamily: "'Arial Black',sans-serif", color: "#fff", fontSize: 18 }}>¡Revisa tu correo!</h2>
          <p style={{ margin: "0 0 18px", fontSize: 13, color: "#888" }}>Te enviamos un link de confirmación a <strong style={{ color: "#fff" }}>{f.email}</strong>. Confírmalo y ya puedes entrar.</p>
          <button onClick={async () => {
            try {
              const { error: resendErr } = await supabase.auth.resend({ type: 'signup', email: f.email.toLowerCase() });
              if (resendErr) throw resendErr;
              alert('✅ Correo reenviado. Revisa tu bandeja (puede tardar 5 minutos).');
            } catch (err) {
              alert('❌ Error: ' + (err?.message || 'No se pudo reenviar'));
            }
          }} style={{ padding: "11px 28px", background: "#666", border: "none", borderRadius: 10, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", marginBottom: 10 }}>🔄 Reenviar correo</button>
          <button onClick={onClose} style={{ padding: "11px 28px", background: "#e53935", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "block", width: "100%", boxSizing: "border-box" }}>Entendido</button>
        </div>}
      </div>
    </div>
  );
};

// 6.1 / 6.2 — Perfil de usuario: datos, preferencia de presupuesto, "Mis aportes" y borrado total de cuenta
export const ProfileModal = ({ user, places, reviews, sessionReady = true, onClose, onAccountDeleted, onOpenFeedback, setSessionToast, resumeState = null, onResumeStateChange, onOpenContribution, onEditContribution, onNotificationNavigate }) => {
  useKeyboardAvoid();
  const panelRef = useRef(null);
  const contributions = buildProfileContributions({ places, reviews, userId: user.userId });
  const myPlaces = contributions.places.map(entry => entry.item);
  const myReviews = contributions.reviews.map(entry => entry.item);
  const contributionTypes = visibleProfileContributionTypes();
  const [contributionTab, setContributionTab] = useState(() => resolveProfileContributionTab(resumeState?.tab, contributions));
  const [presupuesto, setPresupuesto] = useState(() => { const v = parseInt(localStorage.getItem("venaqqi_presupuesto")); return isNaN(v) ? 3 : v; });
  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [hasFeedback, setHasFeedback] = useState(false);
  const [showPushPermissionIntro, setShowPushPermissionIntro] = useState(false);
  const [pushState, setPushState] = useState({ loading: true, supported: false, subscribed: false, permission: "default", preferences: { moderation: true, novedades: true, nuevos_lugares: true, web_push: true } });
  const refreshPushState = useCallback(async () => {
    try { const state = await readPushState({ supabase, userId: user.userId }); setPushState({ loading: false, ...state, preferences: state.preferences || { moderation: true, novedades: true, nuevos_lugares: true, web_push: true } }); }
    catch { setPushState(s => ({ ...s, loading: false })); }
  }, [user.userId]);
  useEffect(() => { refreshPushState(); }, [refreshPushState]);
  const togglePush = async () => {
    setPushState(s => ({ ...s, loading: true }));
    try {
      if (pushState.subscribed) await unsubscribeFromVenaqqiPush({ supabase, userId: user.userId });
      else { await subscribeToVenaqqiPush({ supabase, userId: user.userId }); trackMarketingEvent({ supabase, eventName: "push_enabled" }); }
      await refreshPushState();
    } catch (error) { setPushState(s => ({ ...s, loading: false })); setSessionToast?.({ type: "error", text: `❌ ${error?.message || "No se pudieron configurar las notificaciones"}` }); setTimeout(() => setSessionToast?.(null), 4000); }
  };
  const setPushPreference = async (key, checked) => {
    const next = { ...(pushState.preferences || {}), [key]: checked };
    setPushState(s => ({ ...s, preferences: next }));
    try { const saved = await updateNotificationPreferences({ supabase, userId: user.userId, preferences: { [key]: checked } }); setPushState(s => ({ ...s, preferences: saved })); } catch { await refreshPushState(); }
  };
  const savePresupuesto = (v) => { setPresupuesto(v); try { localStorage.setItem("venaqqi_presupuesto", String(v)); localStorage.setItem("venaqqi_presupuesto_v2", "1"); } catch { /* almacenamiento no disponible */ } };

  useEffect(() => {
    const requestedTab = resolveProfileContributionTab(resumeState?.tab, contributions);
    setContributionTab(requestedTab);
    const timer = setTimeout(() => {
      if (panelRef.current) panelRef.current.scrollTop = Math.max(0, Number(resumeState?.scrollTop) || 0);
    }, 0);
    return () => clearTimeout(timer);
  }, [resumeState?.tab, resumeState?.scrollTop]); // eslint-disable-line react-hooks/exhaustive-deps

  const captureResumeState = (tab = contributionTab, scrollTop = panelRef.current?.scrollTop || 0) => ({ tab, scrollTop });
  const persistResumeState = (state) => { onResumeStateChange && onResumeStateChange(state); return state; };
  const closeProfile = () => { persistResumeState(captureResumeState()); onClose(); };
  const switchContributionTab = key => {
    setContributionTab(key);
    if (panelRef.current) panelRef.current.scrollTop = 0;
    persistResumeState({ tab: key, scrollTop: 0 });
  };
  const openContribution = entry => {
    const state = persistResumeState(captureResumeState());
    onOpenContribution && onOpenContribution({ type: contributionTab, entry, resumeState: state });
  };

  useEffect(() => {
    let active = true;
    let retryTimer;
    let attempt = 0;
    setHasFeedback(false);
    if (!sessionReady) return () => { active = false; };
    const checkFeedback = async () => {
      try {
        const value = await hasCurrentUserSubmittedFeedback(user.userId);
        if (active) setHasFeedback(value);
      } catch (error) {
        if (!active) return;
        setHasFeedback(false);
        console.warn("No se pudo comprobar el reconocimiento de Feedback; se reintentará.", error?.message || error);
        if (attempt < 3) {
          const delay = [500, 1500, 3000][attempt];
          attempt += 1;
          retryTimer = setTimeout(checkFeedback, delay);
        }
      }
    };
    checkFeedback();
    return () => { active = false; if (retryTimer) clearTimeout(retryTimer); };
  }, [sessionReady, user.userId]);

  const handleDelete = async () => {
    if (confirmText.trim().toUpperCase() !== "ELIMINAR") return;
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account", { body: {} });
      if (error) throw error;
      const deletedIds = (data?.deleted_place_ids || []).map(Number);
      await supabase.auth.signOut();
      onAccountDeleted(deletedIds);
    } catch (err) {
      setDeleting(false);
      if (setSessionToast) { setSessionToast({ type: "error", text: "❌ No se pudo eliminar la cuenta: " + (err?.message || "intenta de nuevo") }); setTimeout(() => setSessionToast(null), 4000); }
    }
  };

  const currentEntries = contributions[contributionTab] || [];
  const noContributions = myPlaces.length === 0 && myReviews.length === 0;

  return (
    <div onClick={closeProfile} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, backdropFilter: "blur(6px)" }}>
      <div ref={panelRef} data-testid="profile-scroll" onClick={e => e.stopPropagation()} style={{ background: "#0d0d0d", borderRadius: 20, maxWidth: 420, width: "100%", border: "1px solid #1e1e1e", maxHeight: "92vh", overflowY: "auto", padding: "22px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 46, height: 46, borderRadius: "50%", background: "#e53935", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 900, color: "#fff" }}>{(user.username || "?")[0].toUpperCase()}</div>
            <div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#fff", fontFamily: "'Arial Black',sans-serif" }}>{user.username}</p>
              <p style={{ margin: "2px 0 0", fontSize: 11, color: "#666" }}>{user.email}{profileAge(user) !== null ? " · " + profileAge(user) + " años" : ""}</p>
              {user.fechaNacimiento && <p style={{ margin: "2px 0 0", fontSize: 10, color: "#555" }}>Nacimiento: {user.fechaNacimiento}</p>}
            </div>
          </div>
          <button onClick={closeProfile} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid #333", color: "#fff", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", fontSize: 14 }}>✕</button>
        </div>

        <div style={{ background: "#161616", borderRadius: 12, padding: 14, marginBottom: 14 }}>
          <p style={{ margin: "0 0 8px", fontSize: 11, color: "#e53935", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Mi presupuesto por persona</p>
          <p style={{ margin: "0 0 6px", fontSize: 12, color: "#4caf50", fontWeight: 700 }}>{precioDisplay(presupuesto)}</p>
          <input type="range" min={1} max={7} value={presupuesto} onChange={e => savePresupuesto(+e.target.value)} style={{ width: "100%", accentColor: "#4caf50" }} />
          <p style={{ margin: "6px 0 0", fontSize: 10, color: "#555" }}>Don D' y el botón Venaqqi usan este rango para recomendarte.</p>
        </div>

        <section aria-label="Notificaciones" style={{ background: "#161616", borderRadius: 12, padding: 14, marginBottom: 14, border: "1px solid #222" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <div><p style={{ margin: 0, fontSize: 11, color: "#e53935", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em" }}>Notificaciones del celular</p><p style={{ margin: "4px 0 0", fontSize: 10, color: "#666" }}>Aprobaciones, rechazos y novedades aunque Venaqqi no esté abierta.</p></div>
            <button onClick={() => { if (pushState.subscribed) void togglePush(); else setShowPushPermissionIntro(true); }} disabled={pushState.loading || !pushCapability().supported} style={{ flexShrink: 0, padding: "8px 10px", borderRadius: 9, border: `1px solid ${pushState.subscribed ? "#4caf50" : "#444"}`, background: pushState.subscribed ? "rgba(76,175,80,.12)" : "#111", color: pushState.subscribed ? "#81c784" : "#ddd", fontSize: 10, fontWeight: 800, cursor: pushState.loading ? "wait" : "pointer" }}>{pushState.loading ? "…" : pushState.subscribed ? "Activadas" : "Activar"}</button>
          </div>
          {pushState.subscribed && <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
            {[['moderation','Aprobaciones y rechazos'],['novedades','Novedades Venaqqi'],['nuevos_lugares','Lugares nuevos']].map(([key,label]) => <label key={key} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 10, color: "#aaa" }}><input type="checkbox" checked={pushState.preferences?.[key] !== false} onChange={e => setPushPreference(key, e.target.checked)} /> {label}</label>)}
          </div>}
          {!pushCapability().supported && <p style={{ margin: "8px 0 0", fontSize: 10, color: "#777" }}>Este navegador no ofrece Web Push. En iPhone, instala Venaqqi en pantalla de inicio para habilitarlo cuando el sistema lo permita.</p>}
        </section>

        <NotificationInbox userId={user.userId} onNavigate={onNotificationNavigate} />
        <NotificationPermissionIntro
          open={showPushPermissionIntro}
          onCancel={() => setShowPushPermissionIntro(false)}
          onConfirm={() => { setShowPushPermissionIntro(false); void togglePush(); }}
        />

        <section aria-labelledby="profile-contributions-title" data-testid="profile-contributions">
          <p id="profile-contributions-title" style={{ margin: "0 0 8px", fontSize: 11, color: "#e53935", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Mis aportes</p>
          <div role="tablist" aria-label="Tipos de aportes" style={{ display: "grid", gridTemplateColumns: `repeat(${contributionTypes.length}, minmax(0, 1fr))`, gap: 7, marginBottom: 10 }}>
            {contributionTypes.map(type => {
              const active = contributionTab === type.key;
              const count = contributions[type.key]?.length || 0;
              return (
                <button key={type.key} role="tab" aria-selected={active} onClick={() => switchContributionTab(type.key)}
                  style={{ padding: "9px 8px", background: active ? "rgba(229,57,53,0.12)" : "#141414", border: `1px solid ${active ? "#e53935" : "#2a2a2a"}`, borderRadius: 10, color: active ? "#fff" : "#777", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                  {type.icon} {type.label} <span style={{ color: active ? "#e57373" : "#555" }}>({count})</span>
                </button>
              );
            })}
          </div>

          {noContributions && <p style={{ margin: "0 0 14px", fontSize: 12, color: "#555" }}>Aún no has publicado nada. ¡Tu primera reseña te espera!</p>}
          {!noContributions && currentEntries.length === 0 && <p style={{ margin: "0 0 14px", fontSize: 12, color: "#555" }}>Todavía no tienes aportes en esta sección.</p>}

          {currentEntries.map(entry => {
            const isReview = entry.kind === "review";
            const canOpen = !isReview || !!entry.place;
            const title = isReview ? (entry.place?.name || "Lugar no disponible") : entry.place.name;
            const subtitle = isReview
              ? (entry.review.comentario || entry.review.platillo || "Comentario sin texto")
              : (entry.place.location || entry.place.tipo || "Lugar agregado por ti");
            const contributionItem = isReview ? entry.review : entry.place;
            const rejectReason = contributionItem?.metadata?.reject_reason || contributionItem?.metadata?.rejectReason || "";
            const adminComment = contributionItem?.moderation_comment || contributionItem?.moderationComment || "";
            return (
              <div key={`${entry.kind}:${entry.id}`} style={{ marginBottom: 8 }}>
              <button type="button" disabled={!canOpen} onClick={() => canOpen && openContribution(entry)}
                aria-label={isReview ? `Abrir comentario en ${title}` : `Abrir lugar agregado ${title}`}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, textAlign: "left", background: "#111", border: "1px solid #1f1f1f", borderRadius: 11, padding: "10px 11px", marginBottom: 7, cursor: canOpen ? "pointer" : "not-allowed", opacity: canOpen ? 1 : 0.55 }}>
                <span aria-hidden="true" style={{ fontSize: 16, flexShrink: 0 }}>{isReview ? "💬" : "📍"}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ display: "block", fontSize: 12, color: "#ddd", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</strong>
                  <span style={{ display: "block", marginTop: 3, fontSize: 10, color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</span>
                </span>
                <span style={{ fontSize: 9, color: entry.status.color, border: `1px solid ${entry.status.color}55`, borderRadius: 999, padding: "3px 7px", flexShrink: 0 }}>{entry.status.label}</span>
                <span aria-hidden="true" style={{ color: "#555", fontSize: 17, flexShrink: 0 }}>›</span>
              </button>
              {entry.status.key === "rejected" && <div style={{ marginTop: -3, padding: "9px 10px", border: "1px solid rgba(229,57,53,.28)", borderTop: 0, borderRadius: "0 0 10px 10px", background: "rgba(229,57,53,.05)" }}>
                <p style={{ margin: 0, fontSize: 10, color: "#ef9a9a", lineHeight: 1.45 }}><strong>Motivo:</strong> {rejectReason || "Requiere correcciones antes de publicarse."}</p>
                {adminComment && <p style={{ margin: "5px 0 0", fontSize: 10, color: "#bbb" }}><strong>Nota de Venaqqi:</strong> {adminComment}</p>}
                <button type="button" onClick={() => onEditContribution?.({ type: contributionTab, entry, resumeState: persistResumeState(captureResumeState()) })} style={{ width: "100%", marginTop: 8, padding: 8, borderRadius: 8, border: "1px solid #e53935", background: "transparent", color: "#fff", fontSize: 10, fontWeight: 800, cursor: "pointer" }}>✏️ Corregir y reenviar</button>
              </div>}
              </div>
            );
          })}
        </section>

        {hasFeedback && (
          <section aria-label="Reconocimiento de Feedback" style={{ marginTop: 16, padding: 14, background: "#161616", border: "1px solid #2a2a2a", borderRadius: 12 }}>
            <p style={{ margin: "0 0 5px", color: "#fff", fontSize: 13, fontWeight: 700 }}>Gracias por ayudarnos a mejorar Venaqqi</p>
            <p style={{ margin: "0 0 12px", color: "#888", fontSize: 11 }}>Ya nos compartiste tu feedback.</p>
            <button onClick={onOpenFeedback} style={{ width: "100%", padding: 10, background: "transparent", border: "1px solid #e53935", borderRadius: 10, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Enviar otro feedback</button>
          </section>
        )}

        <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #1a1a1a" }}>
          {!showDelete ? (
            <button onClick={() => setShowDelete(true)} style={{ width: "100%", padding: 11, background: "none", border: "1px solid #5a2d2d", borderRadius: 10, color: "#e57373", fontSize: 12, cursor: "pointer" }}>🗑 Eliminar mi cuenta</button>
          ) : (
            <div style={{ background: "rgba(229,57,53,0.06)", border: "1px solid rgba(229,57,53,0.3)", borderRadius: 12, padding: 14 }}>
              <p style={{ margin: "0 0 8px", fontSize: 12, color: "#e57373", fontWeight: 700 }}>Esta acción es permanente</p>
              <p style={{ margin: "0 0 10px", fontSize: 11, color: "#aaa", lineHeight: 1.5 }}>Se eliminarán tu perfil, tus reseñas y los lugares que registraste (incluidas las reseñas de otros usuarios sobre ellos). El registro de acceso se elimina conforme a la <a href="/eliminacion-datos.html" target="_blank" rel="noopener noreferrer" style={{ color: "#e57373" }}>Política de Eliminación de Datos</a>.</p>
              <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder={'Escribe "ELIMINAR" para confirmar'} style={{ width: "100%", background: "#161616", border: "1px solid #2a2a2a", borderRadius: 10, padding: "10px 12px", color: "#fff", fontSize: 13, outline: "none", boxSizing: "border-box", marginBottom: 10 }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setShowDelete(false); setConfirmText(""); }} style={{ flex: 1, padding: 10, background: "transparent", border: "1px solid #2a2a2a", borderRadius: 10, color: "#888", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
                <button onClick={handleDelete} disabled={deleting || confirmText.trim().toUpperCase() !== "ELIMINAR"}
                  style={{ flex: 1, padding: 10, background: (deleting || confirmText.trim().toUpperCase() !== "ELIMINAR") ? "#3a1a1a" : "#e53935", border: "none", borderRadius: 10, color: "#fff", fontSize: 12, fontWeight: 700, cursor: (deleting || confirmText.trim().toUpperCase() !== "ELIMINAR") ? "default" : "pointer" }}>
                  {deleting ? "Eliminando..." : "Eliminar todo"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Formulario para agregar lugar nuevo
const similarity = (a, b) => {
  const s = (str) => str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
  const sa = s(a); const sb = s(b);
  if (!sa || !sb) return 0;
  if (sa === sb) return 1;
  if (sa.includes(sb) || sb.includes(sa)) return 0.85;
  // Levenshtein simplificado
  const m = sa.length; const n = sb.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => Array.from({ length: n + 1 }, (_, j) => i === 0 ? j : j === 0 ? i : 0));
  for (let i = 1; i <= m; i++) for (let j = 1; j <= n; j++)
    dp[i][j] = sa[i-1] === sb[j-1] ? dp[i-1][j-1] : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
  return 1 - dp[m][n] / Math.max(m, n);
};

const AddPlaceForm = ({ user, onAdd, onClose, places, onOpenReview }) => {
  const { requestLocationForFeature, permissionState } = useLocationAccess();
  useKeyboardAvoid();
  const tipos = ["Tacos", "Hamburguesas & Burgers", "Carnes & Parrilla", "Mariscos & Botanas", "Buffet", "Desayunos & Brunch", "Ramen & Japonesa", "Comida Mexicana", "Comida Casera", "Comida Argentina", "Pub & Bar", "Pizza & Italiana", "Postres & Café", "Otro"];
  const draftOwner = user?.userId || "guest";
  const initialDraftRef = useRef(readAddPlaceDraft(draftOwner));
  const [f, setF] = useState(() => initialDraftRef.current?.form || { ...defaultAddPlaceForm });
  const [step, setStep] = useState(() => initialDraftRef.current?.step || 1);
  const restoredMeaningfulDraft = useRef(hasMeaningfulAddPlaceDraft(initialDraftRef.current?.form)).current;
  const draftCommittedRef = useRef(false);
  const draftRef = useRef({ form: f, step });
  draftRef.current = { form: f, step };
  useEffect(() => {
    const timer = setTimeout(() => { if (!draftCommittedRef.current) writeAddPlaceDraft(draftOwner, f, step); }, 200);
    return () => clearTimeout(timer);
  }, [draftOwner, f, step]);
  useEffect(() => {
    const flushDraft = () => { if (!draftCommittedRef.current) writeAddPlaceDraft(draftOwner, draftRef.current.form, draftRef.current.step); };
    const onVisibility = () => { if (document.visibilityState === "hidden") flushDraft(); };
    window.addEventListener("pagehide", flushDraft);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flushDraft);
      document.removeEventListener("visibilitychange", onVisibility);
      flushDraft();
    };
  }, [draftOwner]);
  useEffect(() => { if (!f.tipo) { const inferred = inferPlaceCategory(f.name, f.location); if (inferred) setF(prev => ({ ...prev, tipo: prev.tipo || inferred })); } }, [f.name, f.location, f.tipo]);
  const [err, setErr] = useState({});
  const [showExtras, setShowExtras] = useState(false);
  const [dupMatch, setDupMatch] = useState(null); // lugar similar detectado
  const [shake, setShake] = useState(false);
  const tryClose = () => { setShake(true); setTimeout(() => setShake(false), 450); };
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setF(p => ({ ...p, [k]: Array.isArray(p[k]) ? (p[k].includes(v) ? p[k].filter(x => x !== v) : [...p[k], v]) : [v] }));

  const googleAcRef = useRef(null);
  const googleFieldRef = useRef(null);
  const googleMapRef = useRef(null);
  const googleMapObjRef = useRef(null);
  const googleMapMarkerRef = useRef(null);
  const placesRef = useRef(places); placesRef.current = places; // B11 — lista actual para el closure del autocomplete
  const [showGoogleMap, setShowGoogleMap] = useState(false);
  const [googleGpsState, setGoogleGpsState] = useState("idle");
  const [manualEntryConfirmed, setManualEntryConfirmed] = useState(false);
  const [showManualGate, setShowManualGate] = useState(false);
  const [mapsLoadError, setMapsLoadError] = useState("");

  useEffect(() => {
    const onMapsAuthError = event => {
      setMapsLoadError(event?.detail?.message || "Google Maps no pudo validarse. Puedes continuar con dirección y ubicación manual.");
      setShowGoogleMap(false);
      googleMapObjRef.current = null;
      googleMapMarkerRef.current = null;
    };
    window.addEventListener("venaqqi:google-maps-auth-error", onMapsAuthError);
    return () => window.removeEventListener("venaqqi:google-maps-auth-error", onMapsAuthError);
  }, []);

  const selectGooglePlace = useCallback(place => {
    const selection = placeSelectionFromGoogle(place);
    if (!selection) {
      setErr(p => ({ ...p, name: "Selecciona un establecimiento de los resultados del buscador." }));
      return false;
    }
    setF(current => ({ ...current, ...selection }));
    setManualEntryConfirmed(false);
    setMapsLoadError("");
    setErr(p => ({ ...p, name: "", location: "" }));
    const match = (placesRef.current || []).filter(p => !p.pending).find(p => similarity(selection.name.trim(), p.name) > 0.70);
    setDupMatch(match || null);
    setShowGoogleMap(true);
    return true;
  }, []);

  // Inicializar autocomplete en el campo nombre al montar
  useEffect(() => {
    if (!GMAPS_KEY) return;
    const initAc = () => {
      if (!googleFieldRef.current || googleAcRef.current) return;
      const ac = new window.google.maps.places.Autocomplete(googleFieldRef.current, {
        componentRestrictions: { country: "mx" },
        fields: ["name", "formatted_address", "address_components", "geometry", "url", "place_id", "formatted_phone_number", "international_phone_number"],
        types: ["establishment"]
      });
      googleAcRef.current = ac;
      ac.addListener("place_changed", () => {
        selectGooglePlace(ac.getPlace());
      });
    };
    loadGoogleMaps(initAc, error => setMapsLoadError(error?.message || "No se pudo cargar Google Maps."));
    return () => { googleAcRef.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showGoogleMap || !Number.isFinite(f.latitude) || !Number.isFinite(f.longitude) || !GMAPS_KEY) return;
    loadGoogleMaps(() => {
      setTimeout(() => {
        if (!googleMapRef.current) return;
        const position = { lat: f.latitude, lng: f.longitude };
        if (!googleMapObjRef.current) {
          googleMapObjRef.current = new window.google.maps.Map(googleMapRef.current, { center: position, zoom: 16 });
          googleMapMarkerRef.current = new window.google.maps.Marker({ position, map: googleMapObjRef.current, draggable: false });
        } else {
          googleMapObjRef.current.setCenter(position);
          googleMapObjRef.current.setZoom(16);
          googleMapMarkerRef.current.setPosition(position);
        }
      }, 0);
    });
  }, [showGoogleMap, f.latitude, f.longitude]);

  const handleGoogleGPS = () => {
    setMapsLoadError("");
    setGoogleGpsState("loading");
    let safetyTimer;
    requestLocationForFeature("register", createPendingLocationAction(LOCATION_ACTION_TYPES.REGISTER_PLACE, { supportsManualLocation: true }), {
      onGranted: async ({ coords }) => {
        safetyTimer = setTimeout(() => {
          setGoogleGpsState("idle");
          setErr(p => ({ ...p, location: "No se pudo cargar el mapa. Verifica tu conexión e intenta de nuevo." }));
        }, 12000);
        const { latitude: lat, longitude: lng } = coords;
        setF(current => ({ ...current, latitude: lat, longitude: lng }));
        set("maps", `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`);
        if (!GMAPS_KEY) { clearTimeout(safetyTimer); setGoogleGpsState("idle"); setErr(p => ({ ...p, location: "Ubicación obtenida. Escribe la dirección manualmente." })); return; }
        loadGoogleMaps(() => {
          clearTimeout(safetyTimer);
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng }, language: "es" }, (results, status) => {
            setGoogleGpsState("idle");
            if (status === "OK" && results[0]) {
              const r = results[0];
              const c = r.address_components;
              const get = (type) => c.find(x => x.types.includes(type))?.long_name || "";
              const street = get("route"); const num = get("street_number"); const col = get("sublocality_level_1") || get("neighborhood") || get("sublocality");
              const parts = [street, num, col].filter(Boolean);
              const addr = parts.length ? parts.join(", ") : r.formatted_address.split(",").slice(0,3).join(",").trim();
              set("location", addr); setErr(p => ({ ...p, location: "" }));
              set("maps", `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`);
            } else {
              setErr(p => ({ ...p, location: "No se pudo obtener la dirección. Intenta de nuevo." }));
            }
          });
        }, () => {
          clearTimeout(safetyTimer);
          setGoogleGpsState("idle");
          setErr(p => ({ ...p, location: "No se pudo cargar el mapa. Verifica tu conexión e intenta de nuevo." }));
        });
      },
      onManual: zone => { clearTimeout(safetyTimer); setGoogleGpsState("idle"); set("location", zone); setErr(p => ({ ...p, location: "Ubicación aproximada: verifica la dirección antes de publicar." })); },
      onCancel: () => { clearTimeout(safetyTimer); setGoogleGpsState("idle"); },
    });
  };

  const mapOnPosRef = useRef(null);
  const handleGoogleMapPin = () => {
    setMapsLoadError("");
    if (!GMAPS_KEY) { setMapsLoadError("El mapa no está configurado. Puedes escribir la dirección y usar tu ubicación actual."); return; }
    setShowGoogleMap(m => {
      if (m) { if (googleMapObjRef.current) { googleMapObjRef.current = null; googleMapMarkerRef.current = null; } return false; }
      loadGoogleMaps(() => {
        setTimeout(() => {
          if (!googleMapRef.current || googleMapObjRef.current) return;
          const map = new window.google.maps.Map(googleMapRef.current, { center: { lat: 19.0414, lng: -98.2063 }, zoom: 13 });
          const marker = new window.google.maps.Marker({ position: { lat: 19.0414, lng: -98.2063 }, map, draggable: true });
          googleMapObjRef.current = map;
          googleMapMarkerRef.current = marker;
          const onPos = (latLng) => {
            const lat = latLng.lat(); const lng = latLng.lng();
            setF(current => ({ ...current, latitude: lat, longitude: lng }));
            set("maps", `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`);
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng }, language: "es" }, (results, status) => {
              if (status === "OK" && results[0]) {
                const r = results[0]; const c = r.address_components;
                const get = (type) => c.find(x => x.types.includes(type))?.long_name || "";
                const street = get("route"); const num = get("street_number"); const col = get("sublocality_level_1") || get("neighborhood") || get("sublocality");
                const parts = [street, num, col].filter(Boolean);
                const addr = parts.length ? parts.join(", ") : r.formatted_address.split(",").slice(0,3).join(",").trim();
                set("location", addr); setErr(p => ({ ...p, location: "" }));
                set("maps", `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`);
              }
            });
          };
          mapOnPosRef.current = onPos;
          marker.addListener("dragend", (e) => onPos(e.latLng));
          map.addListener("click", (e) => {
            // B11 (IDs 63,64) — tap sobre un POI del mapa: rellenar nombre y datos con Place Details
            if (e.placeId && window.google.maps.places) {
              e.stop();
              const svc = new window.google.maps.places.PlacesService(map);
              svc.getDetails({ placeId: e.placeId, fields: ["name", "formatted_address", "geometry", "formatted_phone_number", "international_phone_number", "url", "place_id"] }, (det, status) => {
                if (status === "OK" && det) {
                  if (det.name) {
                    set("name", det.name); setErr(p => ({ ...p, name: "" }));
                    const match = (placesRef.current || []).filter(p => !p.pending).find(p => similarity(det.name.trim(), p.name) > 0.70);
                    setDupMatch(match || null);
                  }
                  if (det.formatted_address) { set("location", det.formatted_address); setErr(p => ({ ...p, location: "" })); }
                  if (det.url) set("maps", det.url);
                  set("gplace_id", det.place_id || "");
                  set("telefono", det.formatted_phone_number || det.international_phone_number || "");
                  if (det.geometry) {
                    marker.setPosition(det.geometry.location); map.setCenter(det.geometry.location);
                    setF(current => ({ ...current, latitude: det.geometry.location.lat(), longitude: det.geometry.location.lng() }));
                  }
                } else {
                  marker.setPosition(e.latLng); onPos(e.latLng);
                }
              });
              return;
            }
            marker.setPosition(e.latLng); onPos(e.latLng);
          });
        }, 80);
      });
      return true;
    });
  };

  const [locatingOnMap, setLocatingOnMap] = useState(false);
  const handleLocateMeOnMap = () => {
    if (!googleMapObjRef.current) return;
    setLocatingOnMap(true);
    requestLocationForFeature("register", createPendingLocationAction(LOCATION_ACTION_TYPES.REGISTER_PLACE, { supportsManualLocation: true }), {
      onGranted: ({ coords }) => {
        const pos = { lat: coords.latitude, lng: coords.longitude };
        googleMapObjRef.current.setCenter(pos);
        googleMapObjRef.current.setZoom(16);
        googleMapMarkerRef.current.setPosition(pos);
        if (mapOnPosRef.current) mapOnPosRef.current(new window.google.maps.LatLng(pos.lat, pos.lng));
        setLocatingOnMap(false);
      },
      onManual: zone => { setLocatingOnMap(false); set("location", zone); setErr(p => ({ ...p, location: "Ubicación aproximada: coloca o corrige el pin manualmente." })); },
      onCancel: () => setLocatingOnMap(false),
    });
  };

  const validateStep1 = () => {
    const e = {};
    if (!f.name.trim()) e.name = "Nombre requerido";
    if (!f.tipo) e.tipo = "Elige un tipo. Venaqqi intenta sugerirlo, pero tú puedes corregirlo.";
    if (!f.location.trim()) e.location = "Ubicación requerida";
    if (!f.selectedPlace && !manualEntryConfirmed) e.name = "Selecciona un establecimiento de Google Maps o confirma el registro manual.";
    return e;
  };

  const handleNext = () => {
    const e = validateStep1();
    if (Object.keys(e).length) { setErr(e); return; }
    setErr({}); setStep(2);
  };

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    // 4.3 — destacar el faltante en vez de ignorar el click
    if (!f.platillo.trim() && !f.comentario.trim() && !(f.imagenes || []).length) { setSubmitError("⚠️ Escribe qué comiste, un comentario o sube una foto real para publicar."); return; }
    if (submitting) return;
    setSubmitting(true);
    setSubmitError("");
    const newPlace = {
      id: genId(), name: f.name.trim(), tipo: f.tipo, primary_category: f.tipo, categories: Array.from(new Set([f.tipo, ...(f.categories || [])])).filter(Boolean), location: f.location.trim(),
      image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=700&q=80",
      maps: f.maps.trim(), tags: [f.tipo.split("&")[0].trim(), "Pendiente de verificación"],
      pending: true, addedBy: user.userId, addedAt: new Date().toISOString(),
      gplace_id: f.gplace_id || null, telefono: f.telefono || null, city: f.city, state: f.state,
      latitude: f.latitude, longitude: f.longitude,
    };
    const firstReview = {
      id: genId(), placeid: newPlace.id, userid: user.userId, username: user.username,
      sabor: f.sabor, precio: f.precio, servicio: f.servicio, satisfaccion: f.satisfaccion,
      total_bill: f.total_bill ? Number(f.total_bill) : null, party_size: f.party_size ? Number(f.party_size) : null,
      platillo: f.platillo, comentario: f.comentario, precio_label: precioDisplay(f.precio),
      chef_msg: f.chef_msg, staff_msg: f.staff_msg, lo_bueno: f.lo_bueno, extras: f.extras, imagenes: f.imagenes,
      fecha: new Date().toLocaleDateString("es-MX", { month: "short", year: "numeric" }),
    };
    try {
      await onAdd(newPlace, firstReview);
      draftCommittedRef.current = true;
      clearAddPlaceDraft(draftOwner);
      setDone(true);
      setTimeout(() => onClose(), 3000);
    } catch (error) {
      setSubmitError(`❌ ${error?.message || "No se pudo guardar el lugar."} Tus datos siguen aquí; puedes reintentar.`);
    } finally {
      setSubmitting(false);
    }
  };

  const inp = { width: "100%", background: "#161616", border: "1px solid #2a2a2a", borderRadius: 10, padding: "10px 13px", color: "#fff", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 4 };

  if (done) return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, backdropFilter: "blur(8px)" }}>
      <div style={{ background: "#0d0d0d", borderRadius: 20, maxWidth: 340, width: "100%", padding: 40, border: "1px solid #1e1e1e", textAlign: "center" }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
        <h2 style={{ margin: "0 0 10px", fontFamily: "'Arial Black',sans-serif", color: "#fff", fontSize: 22 }}>¡Lugar enviado!</h2>
        <p style={{ margin: "0 0 8px", fontSize: 14, color: "#aaa", lineHeight: 1.5 }}>
          Lo revisaremos pronto.<br />Gracias por contribuir a VENAQQI.
        </p>
        <p style={{ margin: "16px 0 0", fontSize: 11, color: "#444" }}>Esta pantalla se cerrará sola…</p>
      </div>
    </div>
  );

  return (
    <div onClick={tryClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, backdropFilter: "blur(8px)" }}>
      {showManualGate && <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="manual-place-title" style={{ position: "fixed", inset: 0, zIndex: 4200, background: "rgba(0,0,0,.78)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ width: "min(360px, 100%)", background: "#0d0d0d", border: "1px solid #2a2a2a", borderRadius: 18, padding: 20, boxShadow: "0 20px 60px rgba(0,0,0,.6)" }}>
          <h3 id="manual-place-title" style={{ margin: "0 0 10px", color: "#fff", fontSize: 17 }}>¿No aparece en Google Maps?</h3>
          <p style={{ margin: "0 0 18px", color: "#aaa", fontSize: 12, lineHeight: 1.55 }}>Antes de registrarlo manualmente, confirma que ya buscaste el lugar en Google Maps y que no aparece.</p>
          <button type="button" onClick={() => { setManualEntryConfirmed(true); setShowManualGate(false); setErr(p => ({ ...p, name: "" })); }} style={{ width: "100%", padding: 12, background: "#e53935", border: "none", borderRadius: 10, color: "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer", marginBottom: 8 }}>Sí, estoy seguro(a)</button>
          <button type="button" onClick={() => setShowManualGate(false)} style={{ width: "100%", padding: 11, background: "transparent", border: "1px solid #444", borderRadius: 10, color: "#bbb", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Volver a buscar</button>
        </div>
      </div>}
      <div onClick={e => e.stopPropagation()} style={{ background: "#0d0d0d", borderRadius: 20, maxWidth: 440, width: "100%", border: "1px solid #1e1e1e", maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ padding: "20px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div>
            <h2 style={{ margin: 0, fontFamily: "'Arial Black',sans-serif", color: "#fff", fontSize: 17 }}>🗺️ Agregar lugar</h2>
            <p style={{ margin: "4px 0 0", fontSize: 11, color: "#555" }}>Paso {step} de 2 · Quedará pendiente de verificación</p>
            <p style={{ margin: "3px 0 0", fontSize: 10, color: restoredMeaningfulDraft ? "#5cb85c" : "#666" }}>{restoredMeaningfulDraft ? "Borrador recuperado · se guarda automáticamente" : "Tus avances se guardan automáticamente"}</p>
          </div>
          <button onClick={onClose} className={shake ? "vq-shake" : ""} style={{ background: shake ? "#e53935" : "rgba(255,255,255,0.08)", border: shake ? "2px solid #fff" : "1px solid #333", color: "#fff", cursor: "pointer", fontSize: 14, width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}>✕</button>
        </div>

        {/* Progress bar */}
        <div style={{ margin: "12px 20px 0", height: 3, background: "#1e1e1e", borderRadius: 2 }}>
          <div style={{ width: step === 1 ? "50%" : "100%", height: "100%", background: "#e53935", borderRadius: 2, transition: "width 0.3s" }} />
        </div>

        <div style={{ padding: "16px 20px 24px" }}>
          {step === 1 ? <>
            <p style={{ margin: "0 0 8px", fontSize: 12, color: "#888" }}>Datos del lugar</p>
            {permissionState === "granted" && <p role="status" style={{ margin: "0 0 12px", fontSize: 11, color: "#66bb6a" }}>📍 Ubicación habilitada</p>}
            {permissionState === "denied" && <p role="status" style={{ margin: "0 0 12px", fontSize: 11, color: "#e57373" }}>📍 Ubicación deshabilitada · puedes registrar la dirección manualmente</p>}
            {/* 4.1 — Card de duplicado ARRIBA del input: las sugerencias de Google (pac-container) se abren hacia abajo y la tapaban cuando estaba debajo */}
            {dupMatch && (
                <div style={{ background: "rgba(255,152,0,0.08)", border: "1px solid rgba(255,152,0,0.35)", borderRadius: 12, padding: 12, marginBottom: 8 }}>
                  <p style={{ margin: "0 0 8px", fontSize: 12, color: "#ffb74d", fontWeight: 700 }}>⚠️ Este lugar puede ya estar en VENAQQI:</p>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                    <img src={dupMatch.image} alt={dupMatch.name} style={{ width: 56, height: 56, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
                    <div>
                      <p style={{ margin: "0 0 2px", fontSize: 13, fontWeight: 700, color: "#fff" }}>{dupMatch.name}</p>
                      <p style={{ margin: "0 0 2px", fontSize: 11, color: "#888" }}>{dupMatch.tipo}</p>
                      <p style={{ margin: 0, fontSize: 10, color: "#666" }}>{dupMatch.location}</p>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => { onOpenReview && onOpenReview(dupMatch); onClose(); }}
                      style={{ flex: 1, padding: "9px 6px", background: "#e53935", border: "none", borderRadius: 8, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      ✓ Sí, agregar reseña aquí
                    </button>
                    <button onClick={() => setDupMatch(null)}
                      style={{ flex: 1, padding: "9px 6px", background: "transparent", border: "1px solid #444", borderRadius: 8, color: "#aaa", fontSize: 12, cursor: "pointer" }}>
                      No, es diferente
                    </button>
                  </div>
                </div>
            )}
            <div style={{ position: "relative", marginBottom: 4 }}>
              <input ref={googleFieldRef} value={f.name} onChange={e => {
                const val = e.target.value;
                setF(current => ({ ...current, name: val, selectedPlace: false, gplace_id: "", city: "", state: "", latitude: null, longitude: null }));
                setShowGoogleMap(false);
                setErr(p => ({ ...p, name: manualEntryConfirmed ? "" : "Selecciona un establecimiento de los resultados del buscador." }));
                // Detección de duplicados en tiempo real
                if (val.trim().length >= 3 && places) {
                  const match = places.filter(p => !p.pending).find(p => similarity(val.trim(), p.name) > 0.70);
                  setDupMatch(match || null);
                } else { setDupMatch(null); }
              }} placeholder="Nombre del lugar *" style={{ ...inp, marginBottom: 0, border: err.name ? "1px solid #e53935" : "1px solid #2a2a2a" }} />
            </div>
            {err.name && <p style={{ margin: "0 0 6px", fontSize: 11, color: "#e53935" }}>{err.name}</p>}
            {!f.selectedPlace && !manualEntryConfirmed && <button type="button" onClick={() => setShowManualGate(true)} style={{ width: "100%", margin: "2px 0 10px", padding: "10px 12px", background: "transparent", border: "1px solid #e53935", borderRadius: 10, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>¿No aparece en Google Maps? Agregar manualmente</button>}
            {manualEntryConfirmed && <div style={{ margin: "2px 0 10px", padding: "9px 11px", borderRadius: 10, background: "rgba(229,57,53,.08)", border: "1px solid rgba(229,57,53,.35)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 11, color: "#ddd" }}>✓ Registro manual habilitado</span>
              <button type="button" onClick={() => { setManualEntryConfirmed(false); setErr(p => ({ ...p, name: "Selecciona un establecimiento de los resultados del buscador." })); }} style={{ background: "transparent", border: 0, color: "#e57373", fontSize: 10, cursor: "pointer", padding: 2 }}>Volver a Maps</button>
            </div>}
            <select value={f.tipo} onChange={e => { set("tipo", e.target.value); setErr(p => ({ ...p, tipo: "" })); }} style={{ ...inp, border: err.tipo ? "1px solid #e53935" : "1px solid #2a2a2a", appearance: "none" }}>
              <option value="">Tipo de comida *</option>
              {tipos.map(t => <option key={t} value={t} style={{ background: "#111" }}>{t}</option>)}
            </select>
            {err.tipo && <p style={{ margin: "0 0 6px", fontSize: 11, color: "#e53935" }}>{err.tipo}</p>}
            {f.tipo && <div style={{ margin: "4px 0 10px" }}>
              <p style={{ margin: "0 0 6px", fontSize: 10, color: "#666" }}>También encaja en… <span style={{ color: "#444" }}>(opcional)</span></p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>{tipos.filter(x => x !== f.tipo).slice(0,10).map(cat => { const active=(f.categories||[]).includes(cat); return <button key={cat} type="button" onClick={() => toggleArr("categories",cat)} style={{ padding: "5px 8px", borderRadius: 999, border: `1px solid ${active?"#e53935":"#333"}`, background: active?"rgba(229,57,53,.12)":"#111", color: active?"#fff":"#777", fontSize: 9, cursor:"pointer" }}>{cat}</button>; })}</div>
            </div>}
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 4 }}>
              <input value={f.location} readOnly={f.selectedPlace && !manualEntryConfirmed} onChange={e => { set("location", e.target.value); setErr(p => ({ ...p, location: "" })); }} placeholder={manualEntryConfirmed ? "Dirección completa *" : "Dirección completa del resultado seleccionado *"} style={{ ...inp, flex: 1, marginBottom: 0, border: err.location ? "1px solid #e53935" : "1px solid #2a2a2a" }} />

              <button onClick={handleGoogleGPS} disabled={googleGpsState === "loading"} title="Usar mi ubicación (Google)" style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 10, background: googleGpsState === "loading" ? "#1e1e1e" : "#1a1a1a", border: "1px solid #4285F4", color: googleGpsState === "loading" ? "#555" : "#4285F4", cursor: googleGpsState === "loading" ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}>
                {googleGpsState === "loading" ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: "spin 1s linear infinite" }}><circle cx="12" cy="12" r="10" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/><circle cx="12" cy="12" r="8" strokeOpacity="0.3"/></svg>
                )}
              </button>
              <button onClick={handleGoogleMapPin} title="Colocar pin en mapa (Google)" style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 10, background: showGoogleMap ? "#4285F4" : "#1a1a1a", border: "1px solid #4285F4", color: showGoogleMap ? "#fff" : "#4285F4", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </button>
            </div>

            {showGoogleMap && (
              <div style={{ marginBottom: 8, borderRadius: 12, overflow: "hidden", border: "1px solid #4285F4", position: "relative" }}>
                <div ref={googleMapRef} style={{ width: "100%", height: 260 }} />
                <button onClick={handleLocateMeOnMap} disabled={locatingOnMap} title="Ubicarme en tiempo real" style={{ position: "absolute", top: 10, right: 10, width: 36, height: 36, borderRadius: "50%", background: "#fff", border: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", cursor: locatingOnMap ? "default" : "pointer", fontSize: 16 }}>{locatingOnMap ? "⏳" : "🎯"}</button>
                <div style={{ background: "#111", padding: "8px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <p style={{ margin: 0, fontSize: 11, color: "#4285F4" }}>👆 Toca el mapa, arrastra el pin, o usa 🎯 para tu ubicación</p>
                  <button onClick={() => setShowGoogleMap(false)} style={{ background: "transparent", border: "1px solid #4285F4", borderRadius: 8, color: "#4285F4", fontSize: 11, padding: "4px 10px", cursor: "pointer" }}>✕ Cerrar mapa</button>
                </div>
              </div>
            )}
            {googleGpsState === "loading" && <p style={{ margin: "0 0 6px", fontSize: 11, color: "#4285F4" }}>📍 Obteniendo ubicación…</p>}
            {mapsLoadError && <p role="alert" style={{ margin: "0 0 8px", padding: "8px 10px", borderRadius: 8, background: "rgba(229,57,53,.08)", border: "1px solid rgba(229,57,53,.25)", fontSize: 10, color: "#e57373", lineHeight: 1.4 }}>{mapsLoadError}</p>}
            {err.location && <p style={{ margin: "0 0 6px", fontSize: 11, color: "#e53935" }}>{err.location}</p>}
            {f.selectedPlace && <div aria-label="Ciudad y estado" style={{ ...inp, color: "#bbb" }}><span style={{ color: "#666" }}>Ciudad, Estado: </span>{[f.city, f.state].filter(Boolean).join(", ") || "No disponible"}</div>}
            <input value={f.maps} onChange={e => set("maps", e.target.value)} placeholder="Link de Google Maps (opcional)" style={inp} />
            <button onClick={handleNext} style={{ width: "100%", padding: 13, background: "#e53935", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", marginTop: 8 }}>Siguiente →</button>
          </> : <>
                        <p style={{ margin: "0 0 14px", fontSize: 12, color: "#888" }}>
              Tu primera reseña de <strong style={{ color: "#e57373" }}>{f.name || "este lugar"}</strong>
            </p>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
              <input type="number" min="0" step="1" value={f.total_bill} onChange={e=>set("total_bill",e.target.value)} placeholder="Total de cuenta $" style={inp}/><input type="number" min="1" max="100" step="1" value={f.party_size} onChange={e=>set("party_size",e.target.value)} placeholder="Personas" style={inp}/>
            </div>
            {f.total_bill && f.party_size && Number(f.party_size)>0 && <p style={{ margin:"-5px 0 10px", fontSize:10, color:"#4caf50" }}>≈ ${Math.round(Number(f.total_bill)/Number(f.party_size))} por persona · calculado desde tu cuenta real</p>}
            <div style={{ background:"#121212", border:"1px solid #2a2a2a", borderRadius:10, padding:"9px 11px", marginBottom:10 }}><p style={{ margin:0, fontSize:10, color:"#888", lineHeight:1.45 }}>📷 Usa fotos reales y originales de tu experiencia. Se permiten recorte o correcciones técnicas, pero no imágenes generadas ni cambios que hagan parecer distinto lo que ofrece el lugar.</p></div>
            <ResenaForm placeName={f.name} placeTipo={f.tipo} values={f} set={set} toggleArr={toggleArr} showExtras={showExtras} setShowExtras={setShowExtras} />
            <div style={{ background: "rgba(229,57,53,0.06)", border: "1px solid rgba(229,57,53,0.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
              <p style={{ margin: 0, fontSize: 11, color: "#e57373" }}>⏳ Este lugar aparecerá como <strong>Pendiente de verificación</strong> hasta que el equipo de Venaqqi lo revise.</p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setStep(1)} style={{ flex: 1, padding: 12, background: "transparent", border: "1px solid #2a2a2a", borderRadius: 10, color: "#888", fontSize: 13, cursor: "pointer" }}>← Atrás</button>
              <button onClick={handleSubmit} disabled={submitting} style={{ flex: 2, padding: 12, background: submitting ? "#2a2a2a" : ((!f.platillo.trim() && !f.comentario.trim()) ? "#3a3a3a" : "#e53935"), border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer" }}>{submitting ? "Guardando..." : "Publicar lugar ✓"}</button>
            </div>
            {submitError && <p style={{ margin: "10px 0 0", fontSize: 12, color: "#e53935", textAlign: "center" }}>{submitError}</p>}
          </>}
        </div>
      </div>
    </div>
  );
};


// ── Componente compartido de reseña ──────────────────────────


const RevForm = ({ placeId, placeName, placeTipo, user, onSubmit, onClose, initialValues, editMode, placeOptions = null, zIndex = 4000 }) => {
  const initF = initialValues || { sabor: 4, servicio: 4, satisfaccion: 4, precio: 3,
    platillo: "", comentario: "", total_bill: "", party_size: "", precio_label: "", chef_msg: "", staff_msg: "",
    lo_bueno: [], extras: [], imagenes: [] };
  const [f, setF] = useState(() => normalizeReviewForEditor(initF));
  const [showExtras, setShowExtras] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setF(p => { const current = Array.isArray(p[k]) ? p[k] : []; return { ...p, [k]: current.includes(v) ? current.filter(x => x !== v) : [...current, v] }; });
  const canSubmit = (f.platillo || "").trim() || (f.comentario || "").trim() || (Array.isArray(f.imagenes) && f.imagenes.length > 0);
  const [missingMsg, setMissingMsg] = useState(""); // 4.3 — aviso de campo faltante
  const [selectedPlaceId, setSelectedPlaceId] = useState(String(placeId));
  const selectedPlace = placeOptions?.find(place => String(place.id) === selectedPlaceId);

  return (
    <div onClick={e => e.stopPropagation()} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex, display: "flex", alignItems: "flex-end", justifyContent: "center", backdropFilter: "blur(8px)" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#0d0d0d", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 480, border: "1px solid #1e1e1e", maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 0" }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: "#333" }} />
        </div>
        <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontFamily: "'Arial Black',sans-serif", color: "#fff", fontSize: 17 }}>Tu reseña</h2>
          <button onClick={onClose}
            style={{ background: "rgba(255,255,255,0.08)", border: "1px solid #333", color: "#fff", cursor: "pointer", fontSize: 14, width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>
        <div style={{ padding: "0 20px 32px" }}>
          {placeOptions && <label style={{ display: "block", marginBottom: 14, color: "#aaa", fontSize: 12 }}>
            Lugar asociado
            <select aria-label="Lugar asociado" value={selectedPlaceId} onChange={event => setSelectedPlaceId(event.target.value)} style={{ display: "block", width: "100%", marginTop: 6, padding: "10px 12px", borderRadius: 9, border: "1px solid #333", background: "#111", color: "#fff" }}>
              {placeOptions.map(place => <option key={place.id} value={place.id}>{place.name}</option>)}
            </select>
          </label>}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
            <input type="number" min="0" step="1" value={f.total_bill || ""} onChange={e=>set("total_bill",e.target.value)} placeholder="Total de cuenta $" style={{ width:"100%", background:"#161616", border:"1px solid #2a2a2a", borderRadius:10, padding:"10px 12px", color:"#fff", boxSizing:"border-box" }}/><input type="number" min="1" max="100" value={f.party_size || ""} onChange={e=>set("party_size",e.target.value)} placeholder="Personas" style={{ width:"100%", background:"#161616", border:"1px solid #2a2a2a", borderRadius:10, padding:"10px 12px", color:"#fff", boxSizing:"border-box" }}/>
          </div>
          {f.total_bill && f.party_size && Number(f.party_size)>0 && <p style={{ margin:"-4px 0 10px", fontSize:10, color:"#4caf50" }}>≈ ${Math.round(Number(f.total_bill)/Number(f.party_size))} por persona · basado en tu cuenta</p>}
          <div style={{ background:"#121212", border:"1px solid #292929", borderRadius:9, padding:"8px 10px", marginBottom:10, fontSize:10, color:"#777", lineHeight:1.4 }}>📷 Sube fotos reales de tu experiencia. No uses imágenes generadas o alteraciones que cambien lo que realmente ofrece el lugar.</div>
          <ResenaForm placeName={selectedPlace?.name || placeName} placeTipo={selectedPlace?.tipo || placeTipo} values={f} set={set} toggleArr={toggleArr} showExtras={showExtras} setShowExtras={setShowExtras} />
          <p style={{ margin: "0 0 12px", fontSize: 11, color: "#444" }}>Publicando como: <span style={{ color: "#888" }}>{user.username}</span></p>
          {missingMsg && !canSubmit && <p style={{ margin: "0 0 8px", fontSize: 12, color: "#e53935", textAlign: "center" }}>{missingMsg}</p>}
          <button onClick={() => { if (!canSubmit) { setMissingMsg("⚠️ Escribe qué comiste, un comentario o sube una foto real para publicar."); return; } onSubmit({ id: genId(), placeid: Number(selectedPlaceId), userid: user.userId, username: user.username, sabor: f.sabor, servicio: f.servicio, satisfaccion: f.satisfaccion, precio: f.precio, total_bill: f.total_bill ? Number(f.total_bill) : null, party_size: f.party_size ? Number(f.party_size) : null, platillo: f.platillo, comentario: f.comentario, precio_label: precioDisplay(f.precio), chef_msg: f.chef_msg, staff_msg: f.staff_msg, lo_bueno: f.lo_bueno, extras: f.extras, imagenes: f.imagenes, fecha: new Date().toLocaleDateString("es-MX", { month: "short", year: "numeric" }) }); }}
            style={{ width: "100%", padding: 13, background: canSubmit ? "#e53935" : "#3a3a3a", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
            {editMode ? "Guardar cambios" : "Publicar reseña"}
          </button>
        </div>
      </div>
    </div>
  );
};


const ShareIcon = ({ size = 18, color = "#666" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

const toSlug = (name) => name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const sharePlace = (place, user) => {
  const slug = toSlug(place.name);
  const ref = user?.username ? "&ref=" + user.username : "";
  const url = "https://venaqqi.vercel.app/?lugar=" + slug + ref;
  const score = "⭐";
  const text = "🍽️ " + place.name + " — " + place.tipo + "\n📍 " + place.location + "\n\n" + score + " Descúbrelo en VENAQQI 👉 " + url + (place.maps ? "\n\n📌 Google Maps: " + place.maps : "");
  if (navigator.share) {
    navigator.share({ title: place.name + " en VENAQQI", text, url }).catch(() => {});
  } else {
    navigator.clipboard?.writeText(text).then(() => {
      // fallback silencioso — el toast se maneja en el caller
    }).catch(() => {});
  }
};

// 1.7 — Galería alineada: portada + fotos de reseñas, con meta (autor/opinión/calificación) por imagen
const buildGallery = (place, revs) => {
  const images = [];
  const metas = [];
  if (place?.image) { images.push(place.image); metas.push({ username: place.name, comentario: "", sabor: null }); }
  (revs || []).forEach(r => (r.imagenes || []).forEach(img => {
    if (!img?.url) return;
    images.push(img.url);
    metas.push({ username: r.username, comentario: r.comentario || "", sabor: r.sabor || null });
  }));
  return { images, metas };
};

// 5.2 — Bloqueo de usuarios por parte del usuario (requisito Google Play para apps con UGC).
// Persistencia local por dispositivo; migrará a Supabase junto con el sistema de perfil.
const getBlockedUsers = () => { try { return JSON.parse(localStorage.getItem("venaqqi_blocked_users") || "[]"); } catch { return []; } };
const saveBlockedUsers = (list) => { try { localStorage.setItem("venaqqi_blocked_users", JSON.stringify(list)); } catch { /* almacenamiento no disponible */ } };

// 1.2 — Favoritos en localStorage (migrará a Supabase con el sistema de perfil / analytics 12.3)
const getFavs = () => { try { return JSON.parse(localStorage.getItem("venaqqi_favs") || "[]"); } catch { return []; } };
const toggleFavStorage = (placeId) => {
  const id = Number(placeId);
  const favs = getFavs();
  const next = favs.includes(id) ? favs.filter(fv => fv !== id) : [...favs, id];
  try { localStorage.setItem("venaqqi_favs", JSON.stringify(next)); } catch { /* almacenamiento no disponible */ }
  return next.includes(id);
};

const Card = memo(({ place, reviews, hl, onClick, onViewImage, user, onShareToast }) => {
  const rating = buildRatingSummary(reviews);
  const ratingReviews = rating.display.reviews;
  const s = rating.display.score;
  const sb = averageRatingDimension(ratingReviews, "sabor");
  const cs = averageRatingDimension(ratingReviews, "servicio");
  const cost = resolvePlaceCost(place, reviews);
  const lr = rating.published[rating.published.length - 1];
  return (
    <div onClick={() => onClick(place)} style={{ position: "relative", cursor: "pointer" }}>
      {hl && <div style={{ position: "absolute", inset: -3, borderRadius: 19, background: "linear-gradient(135deg,#e53935,#b71c1c)", zIndex: 0, opacity: 0.55 }} />}
      <div style={{ position: "relative", zIndex: 1, background: "#111", borderRadius: 16, overflow: "hidden", border: "1px solid #1e1e1e", boxShadow: "0 4px 20px rgba(0,0,0,0.6)", transition: "transform 0.2s,box-shadow 0.2s" }}
        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 12px 36px rgba(0,0,0,0.8)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.6)"; }}>
        <div style={{ position: "relative", height: 185, background: "#1a1a1a", overflow: "hidden" }}>
          <img src={place.image} alt={place.name} onClick={e => { if (onViewImage) { e.stopPropagation(); onViewImage([place.image], 0); } }} style={{ width: "100%", height: "100%", objectFit: "cover", cursor: onViewImage ? "zoom-in" : "pointer" }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,#111 0%,transparent 60%)" }} />
          <div style={{ position: "absolute", top: 10, right: 10, textAlign: "right" }}><Scr n={s} /><span style={{ display: "block", marginTop: 3, fontSize: 8, color: "#bbb" }}>{rating.display.source === "usuarios" ? "Usuarios" : "Venaqqi"}</span></div>
          <div style={{ position: "absolute", top: 10, left: 10 }}>
            <span style={{ fontSize: 10, background: "rgba(229,57,53,0.15)", border: "1px solid rgba(229,57,53,0.35)", color: "#e57373", padding: "3px 8px", borderRadius: 20 }}>{place.tipo}</span>
          </div>
          <div style={{ position: "absolute", bottom: 8, left: 10 }}>
            <span style={{ fontSize: 10, color: "#aaa" }}>{reviews.length} reseña{reviews.length !== 1 ? "s" : ""}</span>
          </div>
        </div>
        <div style={{ padding: 14 }}>
          <h3 style={{ margin: "0 0 3px", fontSize: 14, fontFamily: "'Arial Black',sans-serif", color: "#fff", fontWeight: 900 }}>{place.name}</h3>
          <p style={{ margin: "0 0 10px", fontSize: 11, color: "#555" }}>📍 {place.location}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontSize: 11, color: "#555", minWidth: 42 }}>Sabor</span><Stars n={sb} /></div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}><span style={{ fontSize: 11, color: "#555", minWidth: 42 }}>Precio</span><span style={{ color: "#4caf50", fontSize: 11 }} title={cost.caption}>{cost.label}</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontSize: 11, color: "#555", minWidth: 42 }}>Servicio</span><Chks n={cs} /></div>
          </div>
          {lr?.platillo && <p style={{ margin: "0 0 10px", fontSize: 12, color: "#777", fontStyle: "italic" }}>{lr.platillo}</p>}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {place.tags.map(t => <span key={t} style={{ fontSize: 10, color: "#555", border: "1px solid #1e1e1e", padding: "2px 7px", borderRadius: 20 }}>{t}</span>)}
            </div>
            <button onClick={e => { e.stopPropagation(); sharePlace(place, user); onShareToast && onShareToast(); }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center", flexShrink: 0, marginLeft: 6 }} title="Compartir">
              <ShareIcon size={17} color="#444" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export const RelatedPlaceCards = ({ relatedPlaces = [], onSelectRelated }) => relatedPlaces.length > 0 && <section aria-labelledby="related-places-title" data-testid="related-places" style={{ marginTop: 24 }}>
  <p id="related-places-title" style={{ margin: "0 0 10px", fontSize: 11, color: "#e53935", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>También te puede gustar</p>
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 8 }}>
    {relatedPlaces.map(candidate => <button aria-label={`Abrir ${candidate.name}`} key={candidate.id} onClick={() => onSelectRelated?.(candidate)} style={{ textAlign: "left", background: "#161616", border: "1px solid #292929", borderRadius: 12, color: "#fff", padding: 0, overflow: "hidden", cursor: "pointer" }}>
      <img src={candidate.image || RELATED_PLACE_FALLBACK_IMAGE} onError={e => { if (e.currentTarget.src !== RELATED_PLACE_FALLBACK_IMAGE) { e.currentTarget.src = RELATED_PLACE_FALLBACK_IMAGE; e.currentTarget.style.objectFit = "contain"; } }} alt={candidate.image ? candidate.name : `Imagen alternativa de ${candidate.name}`} style={{ display: "block", width: "100%", height: 86, objectFit: candidate.image ? "cover" : "contain", background: "#0c0c0c" }} />
      <span style={{ display: "block", padding: 10 }}>
        <strong style={{ display: "block", fontSize: 12 }}>{candidate.name}</strong>
        <span style={{ display: "block", color: "#777", fontSize: 10, marginTop: 4 }}>{candidate.tipo} · {candidate.location}</span>
      </span>
    </button>)}
  </div>
</section>;

const Detail = ({ place, reviews, user, onClose, onAdd, onAuth, hideClose = false, onBack = null, openReviewAfterAuth = false, onAfterAuth, onViewImage, onEditReview, setSessionToast, deepLinkRef = null, highlightLlevame = false, onBlockUser = null, relatedPlaces = [], onSelectRelated, highlightReviewId = null, contributionContext = null }) => {
  const { requestLocationForFeature } = useLocationAccess();
  const [showForm, setShowForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null); // review being edited
  const reviewRefs = useRef(new Map());
  const highlightedReviewKey = highlightReviewId === null || highlightReviewId === undefined ? null : String(highlightReviewId);
  useEffect(() => {
    if (!highlightedReviewKey) return undefined;
    const timer = setTimeout(() => {
      const node = reviewRefs.current.get(highlightedReviewKey);
      if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 140);
    return () => clearTimeout(timer);
  }, [place?.id, highlightedReviewKey, reviews.length]);
  useEffect(() => { if (openReviewAfterAuth && user) { setShowForm(true); onAfterAuth && onAfterAuth(); } }, [openReviewAfterAuth, user, onAfterAuth]);
  const [shake, setShake] = useState(false);
  const [routeInfo, setRouteInfo] = useState(null); // { duration, distance }
  const [routeLoading, setRouteLoading] = useState(false);
  const routeMapRef = useRef(null);
  const routeMapInstanceRef = useRef(null);
  const routeRendererRef = useRef(null);
  const placeMapMarkerRef = useRef(null);
  const placeMapCenterRef = useRef(null);
  const routeBoundsRef = useRef(null);
  useEffect(() => {
    if (!highlightLlevame) return undefined;
    const timer = window.setTimeout(() => {
      routeMapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 220);
    return () => window.clearTimeout(timer);
  }, [highlightLlevame, place?.id]);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportMotivo, setReportMotivo] = useState("cerrado");
  const [reportSending, setReportSending] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const [isFav, setIsFav] = useState(() => getFavs().includes(Number(place?.id))); // 1.2 — favoritos
  const [wantToTry, setWantToTry] = useState(false);
  const [visualRating, setVisualRating] = useState(0);
  const [signalSaving, setSignalSaving] = useState(false);
  useEffect(() => {
    let active = true;
    if (!user?.userId || !place?.id) { setWantToTry(false); setVisualRating(0); return () => { active = false; }; }
    loadCatalogPlaceSignals({ supabase, userId: user.userId, place })
      .then(signals => { if (active) { setWantToTry(signals.wantToTry); setVisualRating(signals.visualRating); } })
      .catch(error => { if (active) warn("No se pudieron cargar señales previas del lugar:", error?.message || error); });
    return () => { active = false; };
  }, [user?.userId, place]);
  const saveInterest = async () => {
    if (!user) { onAuth?.(); return; }
    const next = !wantToTry;
    setSignalSaving(true);
    try {
      await persistCatalogPlaceInterest({ supabase, place, wantToTry: next });
      setWantToTry(next);
    } catch (error) {
      setSessionToast?.({ type: "error", text: `❌ No se pudo guardar: ${error?.message || "intenta de nuevo"}` });
      setTimeout(() => setSessionToast?.(null), 3500);
    } finally { setSignalSaving(false); }
  };
  const saveVisualRating = async ratingValue => {
    if (!user) { onAuth?.(); return; }
    setSignalSaving(true);
    try {
      await persistCatalogPlaceFirstImpression({ supabase, place, visualRating: ratingValue });
      setVisualRating(ratingValue);
    } catch (error) {
      setSessionToast?.({ type: "error", text: `❌ No se pudo guardar: ${error?.message || "intenta de nuevo"}` });
      setTimeout(() => setSessionToast?.(null), 3500);
    } finally { setSignalSaving(false); }
  };
  const sendReport = async () => {
    setReportSending(true);
    // Flujo “solicitar menú”/corrección: hoy solo queda registrada en la tabla
    // Supabase `reportes`. No hay correo/cuenta destino en frontend; la notificación
    // debe configurarse en Supabase (trigger/Edge Function/webhook) al insertar aquí.
    const { error } = await supabase.from("reportes").insert([{ place_id: place.id, motivo: reportMotivo, user_id: user?.userId || null }]);
    setReportSending(false);
    if (!error) { setReportSent(true); setTimeout(() => { setShowReportModal(false); setReportSent(false); }, 2000); }
    else { setSessionToast && setSessionToast({ type: "error", text: "❌ No se pudo enviar el reporte" }); setTimeout(() => setSessionToast && setSessionToast(null), 3000); }
  };

  useEffect(() => {
    if (!place?.location || !GMAPS_KEY) return undefined;
    let cancelled = false;
    setMapReady(false);
    setMapError(false);
    setRouteInfo(null);
    routeBoundsRef.current = null;
    loadGoogleMaps(() => {
      if (cancelled || !routeMapRef.current) return;
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: `${place.name}, ${place.location}` }, (results, status) => {
        if (cancelled || !routeMapRef.current) return;
        if (status !== "OK" || !results?.[0]?.geometry?.location) {
          setMapError(true);
          return;
        }
        const center = results[0].geometry.location;
        placeMapCenterRef.current = center;
        const map = new window.google.maps.Map(routeMapRef.current, {
          ...ROUTE_MAP_OPTIONS,
          center,
          zoom: 15,
        });
        routeMapInstanceRef.current = map;
        placeMapMarkerRef.current = new window.google.maps.Marker({ map, position: center, title: place.name });
        setMapReady(true);
      });
    }, () => { if (!cancelled) setMapError(true); });
    return () => {
      cancelled = true;
      routeRendererRef.current?.setMap(null);
      placeMapMarkerRef.current?.setMap(null);
      routeRendererRef.current = null;
      placeMapMarkerRef.current = null;
      routeMapInstanceRef.current = null;
      placeMapCenterRef.current = null;
      routeBoundsRef.current = null;
    };
  }, [place?.id, place?.name, place?.location]);

  const adjustMapZoom = delta => {
    const map = routeMapInstanceRef.current;
    const zoom = Number(map?.getZoom?.());
    if (!map || !Number.isFinite(zoom)) return;
    map.setZoom(Math.max(3, Math.min(20, zoom + delta)));
  };

  const recenterMap = () => {
    const map = routeMapInstanceRef.current;
    if (!map) return;
    if (routeBoundsRef.current) {
      map.fitBounds(routeBoundsRef.current);
      return;
    }
    if (placeMapCenterRef.current) {
      map.setCenter(placeMapCenterRef.current);
      map.setZoom(15);
    }
  };

  const traceRoute = (origin, approximateZone = null) => {
    setRouteLoading(true);
    if (approximateZone) setSessionToast?.({ type: "info", text: `Calculando desde ${approximateZone} como zona aproximada.` });
    loadGoogleMaps(() => {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route({
        origin,
        destination: place.location,
        travelMode: window.google.maps.TravelMode.DRIVING,
      }, (result, status) => {
        setRouteLoading(false);
        if (status === "OK" && result.routes[0]) {
          const leg = result.routes[0].legs[0];
          const center = typeof origin === "object" ? origin : placeMapCenterRef.current;
          let map = routeMapInstanceRef.current;
          if (!map && routeMapRef.current && center) {
            map = new window.google.maps.Map(routeMapRef.current, {
              ...ROUTE_MAP_OPTIONS,
              center,
              zoom: 14,
            });
            routeMapInstanceRef.current = map;
          }
          if (map) {
            placeMapMarkerRef.current?.setMap(null);
            routeRendererRef.current?.setMap(null);
            routeRendererRef.current = new window.google.maps.DirectionsRenderer({ map, directions: result, suppressMarkers: false });
            routeBoundsRef.current = result.routes[0].bounds || null;
            if (routeBoundsRef.current) map.fitBounds(routeBoundsRef.current);
            setMapReady(true);
          }
          setRouteInfo({ duration: leg.duration.text, distance: leg.distance.text });
          void trackProductEvent("route_resolved", { place_id: Number(place.id), distance: leg.distance.text, duration: leg.duration.text, approximate: !!approximateZone });
          if (approximateZone) {
            setSessionToast?.({ type: "info", text: `Ruta aproximada desde ${approximateZone}.` });
            setTimeout(() => setSessionToast?.(null), 3000);
          }
        } else {
          setSessionToast?.({ type: "error", text: "❌ No se pudo trazar la ruta" });
          setTimeout(() => setSessionToast?.(null), 3000);
        }
      });
    }, () => {
      setRouteLoading(false);
      setSessionToast?.({ type: "error", text: "❌ No se pudo cargar el mapa" });
      setTimeout(() => setSessionToast?.(null), 3000);
    });
  };

  const handleLlevame = () => {
    if (!place?.location) return;
    void trackProductEvent("route_intent", { place_id: Number(place.id), source: "place_detail" });
    void trackTesterActivity(TESTER_ACTIVITY_ACTIONS.ROUTE_INTENT, { userId: user?.userId, context: { source: "place_detail", place_id: Number(place.id) } });
    requestLocationForFeature("navigate", createPendingLocationAction(LOCATION_ACTION_TYPES.NAVIGATE_TO_PLACE, { placeId: place.id, destination: place.location, supportsManualLocation: true }), {
      onGranted: ({ coords }) => traceRoute({ lat: coords.latitude, lng: coords.longitude }),
      onManual: (zone, resolved) => traceRoute(
        resolved?.coords ? { lat: resolved.coords.latitude, lng: resolved.coords.longitude } : zone,
        zone,
      ),
      onCancel: () => setRouteLoading(false),
    });
  };
  if (!place) return null;
  const rating = buildRatingSummary(reviews);
  const ratingReviews = rating.display.reviews;
  const s = rating.display.score;
  const sb = averageRatingDimension(ratingReviews, "sabor");
  const cs = averageRatingDimension(ratingReviews, "servicio");
  const cost = resolvePlaceCost(place, reviews);
  const telefono = place.telefono || place.metadata?.telefono || null; // 1.4
  const visibleRevs = user ? reviews : reviews.slice(0, 1); // 1.8 — 1 opinión sin sesión
  const tryClose = () => { setShake(true); setTimeout(() => setShake(false), 450); };
  return (
    <>

      <style>{SPIN_CSS + SHAKE_CSS + '@import url("https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap");@keyframes vq-pulse-ring{0%{box-shadow:0 0 0 0 rgba(229,57,53,0.7)}70%{box-shadow:0 0 0 12px rgba(229,57,53,0)}100%{box-shadow:0 0 0 0 rgba(229,57,53,0)}}'}</style>
      <div onClick={tryClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.93)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, backdropFilter: "blur(6px)" }}>
        <div onClick={e => e.stopPropagation()} style={{ background: "#0d0d0d", borderRadius: 20, maxWidth: 480, width: "100%", overflow: "hidden", border: "1px solid #1e1e1e", maxHeight: "92vh", overflowY: "auto" }}>
          <div style={{ position: "relative", height: 210, background: "#1a1a1a" }}>
            <img src={place.image} alt={place.name} onClick={() => { if (!onViewImage) return; const g = buildGallery(place, reviews); onViewImage(g.images, 0, g.metas); }} style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "zoom-in" }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,#0d0d0d 0%,transparent 55%)", pointerEvents: "none" }} />
            <button onClick={onClose} className={shake ? "vq-shake" : ""} style={{ position: "absolute", top: 12, right: 12, background: shake ? "#e53935" : "rgba(0,0,0,0.6)", border: shake ? "2px solid #fff" : "1px solid #333", color: "#fff", width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: 15, transition: "background 0.2s, border 0.2s" }}>✕</button>
            {onBack && <button onClick={onBack} style={{ position: "absolute", top: 12, left: 12, background: "rgba(0,0,0,0.6)", border: "1px solid #333", color: "#fff", width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center" }}>←</button>}
          </div>
          <div style={{ padding: "18px 20px" }}>
            {contributionContext && (
              <div data-testid="profile-contribution-context" style={{ margin: "0 0 12px", background: "rgba(229,57,53,0.08)", border: "1px solid rgba(229,57,53,0.28)", borderRadius: 10, padding: "9px 11px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <span style={{ color: "#ddd", fontSize: 11, fontWeight: 700 }}>{contributionContext.kind === "place" ? "📍 Lugar agregado por ti" : "💬 Tu comentario"}</span>
                <span style={{ color: contributionContext.status?.color || "#aaa", fontSize: 10, fontWeight: 700 }}>{contributionContext.status?.label || ""}</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
              <div style={{ flex: 1, paddingRight: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <h2 style={{ margin: "0 0 2px", fontFamily: "'Arial Black',sans-serif", color: "#fff", fontSize: 18 }}>{place.name}</h2>
                  <button onClick={() => { const nowFav = toggleFavStorage(place.id); setIsFav(nowFav); if (setSessionToast) { setSessionToast({ type: "info", text: nowFav ? "❤️ Guardado en favoritos" : "Quitado de favoritos" }); setTimeout(() => setSessionToast(null), 2500); } }}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 2, marginBottom: 2, display: "flex", alignItems: "center", fontSize: 15 }} title={isFav ? "Quitar de favoritos" : "Guardar en favoritos"}>
                    {isFav ? "❤️" : "🤍"}
                  </button>
                  <button onClick={() => { sharePlace(place, user); }}
                    style={{ background: "none", border: "none", color: "#555", cursor: "pointer", padding: 2, marginBottom: 2, display: "flex", alignItems: "center" }} title="Compartir lugar">
                    <ShareIcon size={18} color="#666" />
                  </button>
                  <div style={{ position: "relative" }}>
                    <button onClick={() => setShowActionsMenu(v => !v)}
                      style={{ background: "none", border: "none", color: "#555", cursor: "pointer", padding: 2, marginBottom: 2, display: "flex", alignItems: "center", fontSize: 16, letterSpacing: "-1px" }} title="Más opciones">
                      ⋮
                    </button>
                    {showActionsMenu && (
                      <>
                        <div onClick={() => setShowActionsMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 3500 }} />
                        <div style={{ position: "absolute", top: 22, right: 0, background: "#161616", border: "1px solid #2a2a2a", borderRadius: 10, minWidth: 150, maxWidth: "calc(100vw - 40px)", zIndex: 3600, overflow: "hidden" }}>
                          <button onClick={() => { setShowActionsMenu(false); const url = "https://venaqqi.vercel.app/?lugar=" + toSlug(place.name); navigator.clipboard?.writeText(url).catch(() => {}); if (setSessionToast) { setSessionToast({ type: "info", text: "📋 Enlace copiado" }); setTimeout(() => setSessionToast(null), 2500); } }}
                            style={{ width: "100%", textAlign: "left", background: "none", border: "none", color: "#d4d4d4", fontSize: 12.5, padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                            🔗 Copiar enlace
                          </button>
                          <button onClick={() => { setShowActionsMenu(false); setReportMotivo("info_incorrecta"); setShowReportModal(true); }}
                            style={{ width: "100%", textAlign: "left", background: "none", border: "none", color: "#d4d4d4", fontSize: 12.5, padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                            ✏️ Sugerir corrección
                          </button>
                          <button onClick={() => { setShowActionsMenu(false); setReportMotivo("cerrado"); setShowReportModal(true); }}
                            style={{ width: "100%", textAlign: "left", background: "none", border: "none", color: "#e53935", fontSize: 12.5, padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                            🚩 Reportar lugar
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <p style={{ margin: "0 0 2px", fontSize: 12, color: "#e57373" }}>{place.tipo}</p>
                {place.maps ? <a href={place.maps} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ margin: 0, fontSize: 11, color: "#7986cb", textDecoration: "underline", display: "block" }}>📍 {place.location}</a> : <p style={{ margin: 0, fontSize: 11, color: "#555" }}>📍 {place.location}</p>}
              </div>
              <div style={{ textAlign: "center" }}><Scr n={s} /><p style={{ margin: "4px 0 0", fontSize: 10, color: "#777" }}>{rating.display.source === "usuarios" ? "Usuarios" : "Venaqqi"} · {rating.display.count}</p></div>
            </div>
            <div style={{ background:"#141414", border:"1px solid #252525", borderRadius:12, padding:12, marginBottom:14 }}>
              <p style={{ margin:"0 0 8px", fontSize:10, color:"#888", lineHeight:1.45 }}><strong style={{color:"#ddd"}}>Antes de probarlo</strong> · estas señales NO cambian la calificación real del lugar.</p>
              <button disabled={signalSaving} onClick={saveInterest} style={{ width:"100%", padding:9, borderRadius:9, border:`1px solid ${wantToTry?"#e53935":"#333"}`, background:wantToTry?"rgba(229,57,53,.12)":"#0f0f0f", color:wantToTry?"#fff":"#aaa", fontWeight:700, cursor:"pointer", marginBottom:9 }}>{wantToTry?"✓ Quiero probarlo":"＋ Quiero probarlo"}</button>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}><span style={{fontSize:10,color:"#666"}}>Primera impresión por la foto</span><span aria-label="Calificación visual" style={{display:"flex",gap:3}}>{[1,2,3,4,5].map(n=><button key={n} disabled={signalSaving} onClick={()=>saveVisualRating(n)} title="Solo primera impresión visual" style={{background:"none",border:0,padding:0,color:n<=visualRating?"#f9a825":"#444",fontSize:17,cursor:"pointer"}}>★</button>)}</span></div>
            </div>
            <div style={{ background: "#161616", borderRadius: 12, padding: 14, marginBottom: 14 }}>
              <p style={{ margin: "0 0 10px", fontSize: 11, color: "#e53935", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Calificaciones y costo</p>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><span style={{ fontSize: 12, color: "#555", minWidth: 80 }}>Sabor</span><Stars n={sb} /></div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><span style={{ fontSize: 12, color: "#555", minWidth: 80 }}>Servicio</span><Chks n={cs} /></div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 9 }}><span style={{ fontSize: 12, color: "#555", minWidth: 80 }}>Costo</span><span style={{ color: "#4caf50", fontSize: 12 }}><strong>{cost.label}</strong><small style={{ display: "block", color: "#666", marginTop: 2 }}>{cost.caption}</small></span></div>
              {rating.community.count > 0 && <p data-testid="rating-users" style={{ margin: "4px 0", color: "#aaa", fontSize: 11 }}>Usuarios: <strong style={{ color: "#fff" }}>{rating.community.score.toFixed(1)}</strong> · {rating.community.count} reseña{rating.community.count !== 1 ? "s" : ""}</p>}
              {rating.editorial.count > 0 && <p data-testid="rating-editorial" style={{ margin: "4px 0", color: "#888", fontSize: 11 }}>Venaqqi: <strong style={{ color: "#ddd" }}>{rating.editorial.score.toFixed(1)}</strong> · {rating.editorial.count} reseña{rating.editorial.count !== 1 ? "s" : ""}</p>}
              <p style={{ margin: "8px 0 0", color: "#555", fontSize: 10, lineHeight: 1.45 }}>{RATING_FORMULA_LABEL}. El precio no entra en la calificación. Don D’ recomienda por contexto y no modifica estas calificaciones.</p>
            </div>
            {place.location && GMAPS_KEY && (
              <div style={{ marginBottom: 10, borderRadius: 12, overflow: "hidden", border: "1px solid #2a2a2a", position: "relative", height: 160, background: "#161616" }}>
                <div
                  ref={routeMapRef}
                  data-testid="interactive-place-map"
                  onTouchStart={e => e.stopPropagation()}
                  onPointerDown={e => e.stopPropagation()}
                  onWheel={e => e.stopPropagation()}
                  style={{ width: "100%", height: "100%", touchAction: "none", cursor: "grab" }}
                />
                {routeInfo && (
                  <div style={{ position: "absolute", top: 8, left: 8, background: "#0d0d0d", border: "1px solid #e53935", borderRadius: 8, padding: "5px 10px", fontSize: 12, color: "#fff", pointerEvents: "none" }}>
                    🧭 {routeInfo.duration} · {routeInfo.distance}
                  </div>
                )}
                {!mapReady && !mapError && !routeLoading && (
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#777", fontSize: 11, pointerEvents: "none" }}>
                    Cargando mapa...
                  </div>
                )}
                {mapError && !routeInfo && (
                  <div style={{ position: "absolute", inset: 0, background: "#161616", display: "flex", alignItems: "center", justifyContent: "center", color: "#777", fontSize: 11, pointerEvents: "none" }}>
                    Mapa no disponible aquí · usa Google Maps abajo
                  </div>
                )}
                {(mapReady || routeInfo) && (
                  <>
                    <div aria-label="Controles del mapa" style={{ position: "absolute", right: 7, top: 7, display: "flex", flexDirection: "column", gap: 4 }}>
                      <button aria-label="Acercar mapa" onClick={e => { e.stopPropagation(); adjustMapZoom(1); }} style={{ width: 27, height: 27, borderRadius: 7, border: "1px solid #333", background: "rgba(13,13,13,0.88)", color: "#fff", fontSize: 17, lineHeight: 1, padding: 0, cursor: "pointer" }}>+</button>
                      <button aria-label="Alejar mapa" onClick={e => { e.stopPropagation(); adjustMapZoom(-1); }} style={{ width: 27, height: 27, borderRadius: 7, border: "1px solid #333", background: "rgba(13,13,13,0.88)", color: "#fff", fontSize: 17, lineHeight: 1, padding: 0, cursor: "pointer" }}>−</button>
                      <button aria-label="Recentrar mapa" onClick={e => { e.stopPropagation(); recenterMap(); }} style={{ width: 27, height: 27, borderRadius: 7, border: "1px solid #333", background: "rgba(13,13,13,0.88)", color: "#fff", fontSize: 12, lineHeight: 1, padding: 0, cursor: "pointer" }}>◎</button>
                    </div>
                    <div style={{ position: "absolute", left: 8, bottom: 7, background: "rgba(13,13,13,0.78)", borderRadius: 6, padding: "3px 6px", color: "#aaa", fontSize: 9, letterSpacing: "0.03em", pointerEvents: "none" }}>arrastra · zoom</div>
                  </>
                )}
                {routeLoading && (
                  <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12 }}>
                    Trazando ruta...
                  </div>
                )}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {telefono && (
                <a href={"tel:" + telefono} onClick={e => e.stopPropagation()} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 10, padding: 11, textDecoration: "none", color: "#fff", fontSize: 13 }}>📞 Llamar</a>
              )}
              {place.maps && <a href={place.maps} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 10, padding: 11, textDecoration: "none", color: "#fff", fontSize: 13 }}>{telefono ? "📍 Maps" : "📍 Google Maps"}</a>}
              {place.location && (
                <button onClick={handleLlevame} disabled={routeLoading} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: routeInfo ? "#1a1a1a" : "#e53935", border: routeInfo ? "1px solid #2a2a2a" : "none", borderRadius: 10, padding: 11, color: "#fff", fontSize: 13, fontWeight: 700, cursor: routeLoading ? "default" : "pointer", animation: (highlightLlevame && !routeInfo) ? "vq-pulse-ring 1.5s ease-out infinite" : "none" }}>
                  🧭 {routeInfo ? "Ruta trazada" : "Llévame"}
                </button>
              )}
            </div>
            <p style={{ margin: "0 0 12px", fontSize: 11, color: "#e53935", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Lo que dice la gente</p>
            {visibleRevs.map(r => {
              const isHighlightedReview = highlightedReviewKey === String(r.id);
              return (
              <div key={r.id} ref={node => { const key = String(r.id); if (node) reviewRefs.current.set(key, node); else reviewRefs.current.delete(key); }} data-profile-review-id={String(r.id)}
                style={{ background: isHighlightedReview ? "rgba(229,57,53,0.10)" : "#161616", borderRadius: 12, padding: 14, marginBottom: 10, border: isHighlightedReview ? "2px solid #e53935" : "1px solid #1e1e1e", boxShadow: isHighlightedReview ? "0 0 0 3px rgba(229,57,53,0.12)" : "none", scrollMarginTop: 24, transition: "border-color .2s, box-shadow .2s, background .2s" }}>
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: r.username === "venaqqi" ? "#e53935" : "#2a2a2a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                      {r.username === "venaqqi" ? "V" : r.username[0].toUpperCase()}
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 12, color: r.username === "venaqqi" ? "#e57373" : "#aaa", fontWeight: 700 }}>{r.username === "venaqqi" ? "venaqqi ✓" : r.username}</p>
                      <p style={{ margin: 0, fontSize: 10, color: "#444" }}>{r.fecha}</p>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ display: "flex", gap: 6, fontSize: 11 }}><span>⭐ {r.sabor}</span><span>✔️ {r.servicio}</span></div>
                    <button onClick={() => { sharePlace(place, user); }}
                      style={{ background: "none", border: "none", color: "#555", cursor: "pointer", padding: 2, display: "flex", alignItems: "center" }} title="Compartir">
                      <ShareIcon size={15} color="#555" />
                    </button>
                    {user && r.userid === user.userId && (
                      <button onClick={() => setEditingReview(r)}
                        style={{ background: "none", border: "1px solid #2a2a2a", color: "#888", cursor: "pointer", fontSize: 11, padding: "2px 8px", borderRadius: 6 }} title="Editar">✏️ Editar</button>
                    )}
                    {onBlockUser && r.username !== "venaqqi" && (!user || r.userid !== user.userId) && (
                      <button onClick={() => { if (window.confirm(`¿Ocultar todo el contenido de "${r.username}" en este dispositivo?`)) onBlockUser(r.userid, r.username); }}
                        style={{ background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 13, padding: 2 }} title="Bloquear usuario">🚫</button>
                    )}
                  </div>
                </div>
                {/* Platillo y precio */}
                {r.platillo && <p style={{ margin: "0 0 4px", fontSize: 12, color: "#888", fontStyle: "italic" }}>{r.platillo}</p>}
                {r.precio_label && <p style={{ margin: "0 0 6px", fontSize: 11, color: "#4caf50" }}>{r.precio_label}</p>}
                {/* Lo bueno chips */}
                {r.lo_bueno?.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                    {r.lo_bueno.map(lb => <span key={lb} style={{ fontSize: 10, background: "rgba(229,57,53,0.1)", color: "#e57373", border: "1px solid rgba(229,57,53,0.2)", padding: "2px 8px", borderRadius: 20 }}>{lb}</span>)}
                  </div>
                )}
                {/* Comentario */}
                {r.comentario && <p style={{ margin: "0 0 6px", fontSize: 13, color: "#ccc", lineHeight: 1.5 }}>{r.comentario}</p>}
                {/* Chef / Staff msgs */}
                {r.chef_msg && <p style={{ margin: "0 0 4px", fontSize: 11, color: "#d4a853", fontStyle: "italic" }}>👨‍🍳 {r.chef_msg}</p>}
                {r.staff_msg && <p style={{ margin: "0 0 4px", fontSize: 11, color: "#d4a853", fontStyle: "italic" }}>🌟 {r.staff_msg}</p>}
                {/* Extras */}
                {r.extras?.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                    {r.extras.map(exId => { const ex = EXTRAS_LIST.find(e => e.id === exId); return ex ? <span key={exId} title={ex.label} style={{ fontSize: 16 }}>{ex.emoji}</span> : null; })}
                  </div>
                )}
                {/* Imágenes */}
                {r.imagenes?.length > 0 && (
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    {r.imagenes.map((img, i) => (
                      <img key={i} src={img.url} alt="" onClick={() => onViewImage && onViewImage(r.imagenes.map(im=>im.url), i, r.imagenes.map(() => ({ username: r.username, comentario: r.comentario || "", sabor: r.sabor || null })))}
                        style={{ width: 64, height: 64, borderRadius: 8, objectFit: "cover", cursor: "pointer" }} />
                    ))}
                  </div>
                )}
              </div>
              );
            })}
            {!user && reviews.length > 1 && (
              <div style={{ background: "#161616", border: "1px dashed #2a2a2a", borderRadius: 12, padding: 14, marginBottom: 10, textAlign: "center" }}>
                <p style={{ margin: "0 0 8px", fontSize: 12, color: "#888" }}>🔒 Hay {reviews.length - 1} opinión{reviews.length - 1 !== 1 ? "es" : ""} más de este lugar</p>
                <button onClick={onAuth} style={{ background: "none", border: "1px solid #e53935", borderRadius: 20, color: "#e53935", fontSize: 12, fontWeight: 700, padding: "7px 16px", cursor: "pointer" }}>Ingresa para verlas</button>
              </div>
            )}
            <button onClick={() => { if (!user) { onAuth(); return; } setShowForm(true); }} style={{ width: "100%", padding: 13, background: "#e53935", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'Arial Black',sans-serif" }}>✍️ Agregar mi reseña</button>
            {!user && <p style={{ margin: "8px 0 0", fontSize: 11, color: "#444", textAlign: "center" }}>Necesitas ingresar tu correo para reseñar</p>}
            <RelatedPlaceCards relatedPlaces={relatedPlaces} onSelectRelated={onSelectRelated} />
          </div>
        </div>
      </div>
      {showForm && user && <RevForm placeId={place.id} placeName={place.name} placeTipo={place.tipo} user={user} onSubmit={r => { onAdd(r); setShowForm(false); }} onClose={() => setShowForm(false)} />}
      {editingReview && user && (
        <RevForm
          placeId={place.id} placeName={place.name} placeTipo={place.tipo} user={user}
          editMode={true}
          initialValues={editingReview}
          onClose={() => setEditingReview(null)}
          onSubmit={async updated => {
            const payload = {
              sabor: updated.sabor, servicio: updated.servicio, satisfaccion: updated.satisfaccion,
              precio: updated.precio, total_bill: updated.total_bill, party_size: updated.party_size, platillo: updated.platillo, comentario: updated.comentario,
              precio_label: updated.precio_label, chef_msg: updated.chef_msg, staff_msg: updated.staff_msg,
              lo_bueno: updated.lo_bueno, extras: updated.extras, imagenes: updated.imagenes,
              fecha: new Date().toLocaleDateString("es-MX", { month: "short", year: "numeric" })
            };
            const { data, error } = await supabase.rpc("resubmit_review_with_edits", { p_id: String(editingReview.id), p_changes: payload });
            if (!error) {
              onEditReview && onEditReview(data || { ...editingReview, ...payload, pending: true, moderation_status: "pending", moderation_comment: null, moderated_at: null, moderated_by: null, metadata: { ...(editingReview.metadata || {}), rejected: false, notifSeen: true } });
              setEditingReview(null);
              setSessionToast({ type: "info", text: "⏳ Tu cambio será revisado por Venaqqi" });
              setTimeout(() => setSessionToast(null), 3000);
            } else {
              setSessionToast({ type: "error", text: "❌ No se pudo reenviar la reseña: " + error.message });
              setTimeout(() => setSessionToast(null), 4500);
            }
          }}
        />
      )}
      {/* Banner de bienvenida para visitantes que llegaron por deep link */}
      {deepLinkRef && !user && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 4500, background: "#0d0d0d", borderTop: "1px solid #1e1e1e", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <p style={{ margin: "0 0 2px", fontSize: 13, fontWeight: 700, color: "#fff", fontFamily: "'Arial Black',sans-serif" }}>Estás en VENAQQI</p>
            <p style={{ margin: 0, fontSize: 11, color: "#666" }}>Encuentra los mejores lugares para comer en Puebla</p>
          </div>
          <button onClick={onClose} style={{ background: "#e53935", border: "none", borderRadius: 20, color: "#fff", fontSize: 12, fontWeight: 700, padding: "8px 16px", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "'Arial Black',sans-serif" }}>
            Explorar más →
          </button>
        </div>
      )}
      {showReportModal && (
        <div onClick={() => !reportSending && setShowReportModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 4000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#141414", borderTop: "1px solid #2a2a2a", borderRadius: "20px 20px 0 0", padding: 18, width: "100%", maxWidth: 480 }}>
            {reportSent ? (
              <p style={{ margin: 0, fontSize: 13, color: "#4caf50", textAlign: "center", padding: "10px 0" }}>✅ Reporte enviado, gracias</p>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#fff" }}>Reportar lugar</p>
                  <button onClick={() => setShowReportModal(false)} style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 16 }}>✕</button>
                </div>
                {[
                  ["cerrado", "Ya no existe / cerró"],
                  ["info_incorrecta", "Información incorrecta"],
                  ["contenido_inapropiado", "Contenido inapropiado"],
                  ["duplicado", "Lugar duplicado"],
                ].map(([val, label]) => (
                  <label key={val} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#d4d4d4", padding: "7px 0", cursor: "pointer" }}>
                    <input type="radio" name="motivo" checked={reportMotivo === val} onChange={() => setReportMotivo(val)} />
                    {label}
                  </label>
                ))}
                <button onClick={sendReport} disabled={reportSending}
                  style={{ width: "100%", marginTop: 12, background: "#e53935", border: "none", borderRadius: 10, padding: 11, color: "#fff", fontSize: 13, fontWeight: 700, cursor: reportSending ? "default" : "pointer" }}>
                  {reportSending ? "Enviando..." : "Enviar reporte"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const SYSTEM = (places, reviews, user, visibleContext, userMsg, guestRemaining = null, locationContext = null) => {
  // B8 (IDs 1,2,20,83) — Don D' SOLO conoce lugares aprobados: pendientes/rechazados no existen para él
  places = places.filter(isApprovedPlace);
  const norm = s => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const msg = norm(userMsg || "");

  // Buscar lugares que el usuario mencionó explícitamente por nombre
  const mentioned = places.filter(p => msg.includes(norm(p.name.split("–")[0].trim())));

  // Score general de todos los lugares
  const scored = places.map(p => {
    const rs = reviews.filter(r => Number(r.placeid || r.placeId) === Number(p.id));
    const rating = buildRatingSummary(rs);
    return { p, score: rating.display.score, rs };
  }).sort((a, b) => b.score - a.score);

  // 2.1 — Relevancia por tipo de comida: tokens del mensaje contra tipo/tags/platillos reseñados
  const tokens = msg.split(/[^a-z0-9ñ]+/).filter(w => w.length >= 4);
  const matchesText = (txt) => { const n = norm(txt || ""); return tokens.some(tk => n.includes(tk)); };
  const tipoMatches = tokens.length ? scored.filter(x =>
    matchesText(x.p.tipo) || (x.p.tags || []).some(matchesText) || x.rs.some(r => matchesText(r.platillo))
  ).map(x => x.p) : [];

  // 2.1 — Balance establecidos/nuevos: top 10 por score + hasta 5 lugares con 0–1 reseñas (visibilidad para nuevos)
  const top10 = scored.slice(0, 10).map(x => x.p);
  const nuevos = shuffle(scored.filter(x => x.rs.length <= 1).map(x => x.p)).slice(0, 5);

  // Unión sin duplicados, tope 25 lugares para cuidar tokens
  const pool = [...new Map([...mentioned, ...tipoMatches, ...top10, ...nuevos].map(p => [p.id, p])).values()].slice(0, 25);

  const placesData = pool.map(p => {
    const rs = reviews.filter(r => Number(r.placeid || r.placeId) === Number(p.id));
    const rating = buildRatingSummary(rs);
    const cost = resolvePlaceCost(p, rs);
    const last = [...rating.published].reverse().find(isBranchSafeReview);
    // Costo con procedencia + ubicación canónica. Las opiniones marcadas como otra sucursal no alimentan a Don D'.
    return "- " + p.name + " (" + p.tipo + ") | " + canonicalPlaceLocationContext(p) + " | Score " + (rating.display.source === "usuarios" ? "usuarios" : "Venaqqi") + ":" + rating.display.score.toFixed(1) + " | " + rating.published.length + " reseñas publicadas"
      + " | Costo: " + cost.label + " (" + cost.caption + ")"
      + (last?.comentario ? " | Opinión: \"" + String(last.comentario).slice(0, 90) + "\"" : "");
  }).join("\n");

  const contextLine = visibleContext
    ? "\n\nEL USUARIO ESTÁ VIENDO AHORA: **" + visibleContext.placeName + "** (" + visibleContext.tipo + ")" + (visibleContext.comentario ? ". Última reseña: \"" + visibleContext.comentario + "\"" : "") + (visibleContext.tambienVisibles?.length ? ". También ve en el carrusel: " + visibleContext.tambienVisibles.map(p => p.name).join(", ") + "." : "") + " Este lugar es el recomendado actual y el tema central de la conversación: tu primer mensaje y tus respuestas giran en torno a él. NUNCA lo sustituyas por otro lugar ni menciones otro como si fuera el recomendado, salvo que el usuario pida explícitamente otra opción."
    : "";
  const locationLine = locationContext?.zone
    ? `\n\nUBICACIÓN APROXIMADA INDICADA: ${locationContext.zone}. No afirmes que tiene precisión GPS.`
    : locationContext?.coords
      ? `\n\nUBICACIÓN ACTUAL DISPONIBLE PARA ESTA SOLICITUD: latitud ${locationContext.coords.latitude.toFixed(3)}, longitud ${locationContext.coords.longitude.toFixed(3)}. Úsala solo para cercanía y no la repitas al usuario.`
      : "";

  const rules = "\n\nREGLA CRÍTICA DE FORMATO — SIEMPRE SIN EXCEPCIÓN:\n- Cada vez que menciones un lugar por nombre, escríbelo en **negritas**. SIEMPRE. Aunque ya lo hayas mencionado antes. Aunque estés dando ubicación, descripción o cualquier detalle del lugar.\n- SI MENCIONAS 1-2 LUGARES: después de cada lugar incluye en la siguiente línea su ubicación con ⭐ y score. Ejemplo: **Nombre** - Ubicación ⭐ Score\n- SI MENCIONAS 3+ LUGARES: solo lista, sin score por lugar.\n- Nunca omitas las negritas aunque el usuario ya sepa el nombre del lugar.\n- Si el usuario pide algo barato o económico, no te limites a listar: véndele el lugar — usa el rango de precio real del contexto, di qué obtiene por ese dinero y por qué vale la pena.\n- Cuando el contexto incluya Precio u Opinión de un lugar, úsalos en tu respuesta en vez de generalidades.\n\nREGLAS DE DATOS Y PRECIO — OBLIGATORIAS:\n- SOLO usa los datos que aparecen en 'Lugares disponibles' y en el contexto. NUNCA inventes precios, platillos, horarios ni ningún otro dato. Si no tienes el dato, dilo claro: 'No tengo ese dato registrado'.\n- Para ubicación, rutas y sucursales, la dirección y el mapa canónicos del lugar son la fuente de verdad. Una reseña puede describir otra sucursal: NUNCA uses esa mención para cambiar la ubicación canónica ni para dar indicaciones.\n- NUNCA califiques un lugar como 'caro' o 'barato' por tu cuenta, ni hagas advertencias sobre el precio. Si te preguntan por precio, da el rango real del contexto y ya; si no hay dato, dilo.\n- NUNCA cambies de lugar recomendado por tu propia evaluación de precio ni por falta de contexto. El lugar del contexto ES el tema de la conversación.\n\nREGLAS DE BREVEDAD — OBLIGATORIAS:\n- Respuestas BREVES por default: 1-3 oraciones. Solo da detalle si el usuario lo pide.\n- NO alargues la conversación con preguntas innecesarias. Decide con la información disponible. Solo pregunta si es IMPOSIBLE decidir sin ese dato, y máximo UNA pregunta.\n- No cierres cada mensaje con una pregunta de seguimiento.";

  if (user && user.fullReg) {
    const userHistory = reviews.filter(r => r.userid === user.userId);
    const visited = userHistory.map(r => { const p = places.find(pl => pl.id === r.placeid || r.placeId); return p ? p.name : ""; }).filter(Boolean).join(", ");
    return "Eres Don D', el asistente personal de VENAQQI para " + user.username + ". Conoces su historial y lo tratas de forma directa y concisa. Eslogan: \"come donde quieras\".\n\nLugares visitados por " + user.username + ": " + (visited || "ninguno aún") + ".\n\nLugares disponibles (cada renglón empieza con su ID):\n" + pool.map(p => `[${p.id}] ${placesData.split("\n").find(line => line.startsWith("- " + p.name)) || p.name}`).join("\n") + contextLine + locationLine + rules + "\n\nEspañol mexicano casual y familiar. NUNCA uses 'vos', 'querés', 'tenés' ni modismos argentinos. Cuando el usuario pida una recomendación general, otra opción o alternativas, entrega entre 3 y 5 lugares distintos en una sola respuesta y termina con [RECOMMENDATION_IDS:id1,id2,id3] usando exclusivamente IDs del catálogo. Si pregunta por un lugar específico, responde solo sobre ese lugar y termina con [RECOMMENDATION_IDS:]. Si el usuario pregunta de forma vaga, recomienda directo con lo que sabes de él y del contexto; solo haz UNA pregunta si es imposible decidir.";
  }
  const guestRule = guestRemaining === null ? "LÍMITE: máx 3 lugares por respuesta." : `SALDO DEL INVITADO: ${guestRemaining} lugar(es). No recomiendes más de ese saldo; prefiere una sola recomendación principal. Si recomiendas lugares concretos, termina con [RECOMMENDATION_IDS:id1,id2] usando exclusivamente sus IDs del catálogo. Una mención incidental o una pregunta aclaratoria termina con [RECOMMENDATION_IDS:].`;
  return "Eres Don D', el asistente de VENAQQI. Ayudas a visitantes a encontrar dónde comer en Puebla de forma amable y sencilla. Eslogan: \"come donde quieras\".\n\nLugares disponibles (cada renglón empieza con su ID):\n" + pool.map(p => `[${p.id}] ${placesData.split("\n").find(line => line.startsWith("- " + p.name)) || p.name}`).join("\n") + contextLine + locationLine + rules + "\n\nEspañol mexicano amable y accesible. NUNCA uses 'vos', 'querés', 'tenés' ni modismos argentinos. " + guestRule + " Si el usuario pregunta de forma vaga, recomienda directo con lo que sabes de él y del contexto; solo haz UNA pregunta si es imposible decidir.";
};

const Dots = () => <div style={{ display: "flex", gap: 5, alignItems: "center", padding: "3px 0" }}>{[0, 1, 2].map(i => <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: "#e53935", opacity: 0.8, animation: "vqd 1.2s infinite ease-in-out", animationDelay: (i * 0.2) + "s" }} />)}<style>{`@keyframes vqd{0%,80%,100%{transform:scale(0.5)}40%{transform:scale(1)}}`}</style></div>;

const normalize = s => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const findPlace = (name, places) => {
  if (!places) return null;
  return places.find(p => normalize(p.name.split("–")[0].trim()) === name)
    || places.find(p => { const pn = normalize(p.name.split("–")[0].trim()); return pn.length >= 4 && name.length >= 4 && (pn.includes(name) || name.includes(pn)); });
};

const renderMsg = (t, places, onVenaqqi) => {
  const text = typeof t === "string" ? t : String(t ?? "");
  // Contar cuántos lugares aparecen en el mensaje
  const boldMatches = [...text.matchAll(/\*\*([^*]+)\*\*/g)];
  const matchedPlaces = boldMatches.map(m => findPlace(normalize(m[1]), places)).filter(Boolean);
  const showBtn = matchedPlaces.length > 0 && matchedPlaces.length <= 2;

  let placeNameBuffer = null;

  return text.split(/(\*\*[^*]+\*\*)/g).flatMap((c, i) => {
    if (c.startsWith("**") && c.endsWith("**")) {
      const rawName = c.slice(2, -2);
      const place = findPlace(normalize(rawName), places);
      placeNameBuffer = place ? place : null;
      if (place) {
        return [<button key={i} type="button" onClick={() => onVenaqqi && onVenaqqi(place)} style={{ background: "none", border: "none", padding: 0, color: "#e53935", fontWeight: 700, textDecoration: "underline", textDecorationStyle: "dotted", cursor: "pointer", font: "inherit" }}>{rawName}</button>];
      }
      return [<strong key={i} style={{ color: "#e53935" }}>{rawName}</strong>];
    }
    return c.split("\n").flatMap((line, idx) => {
      if (line === "[Ubicación - Venaqqi]") return [];
      const elements = idx === 0 ? [line] : [<br key={`br-${i}-${idx}`} />, line];
      if (showBtn && placeNameBuffer && line.trim() !== "") {
        const place = placeNameBuffer;
        placeNameBuffer = null;
        if (place.maps) {
          elements.push(<div key={`btn-wrap-${i}-${idx}`} style={{ textAlign: "center", marginTop: 10 }}><button key={`btn-${i}-${idx}`} onClick={() => onVenaqqi && onVenaqqi(place)} style={{ display: "inline-block", padding: "10px 20px", borderRadius: 6, background: "#c62828", color: "#fff", fontWeight: 700, fontSize: 13, border: "3px solid #e53935", boxShadow: "0 3px 10px rgba(229, 57, 53, 0.5)", cursor: "pointer", fontFamily: "inherit" }}>{"Venaqqi → " + place.name}</button></div>);
        }
      }
      return elements;
    });
  });
};

const CHIPS_POR_TIPO = [
  { match: /marisco|pescad|camar[oó]n|ostion|ceviche/i, chips: ["🦐 Un coctel", "🔥 ¿Qué tienes de cocina caliente?", "🍤 Una botana de mariscos"] },
  { match: /taco|árabe|arabe/i, chips: ["🌮 ¿Cuál taco recomiendas?", "🫓 Algo sin picante", "🥤 ¿Tienen aguas frescas?"] },
  { match: /hamburgu|burger/i, chips: ["🍔 La más recomendada", "🍟 ¿Las papas son buenas?", "🥓 Algo con tocino"] },
  { match: /pizza/i, chips: ["🍕 ¿Cuál pizza pides tú?", "🧀 Extra queso disponible?", "🥗 ¿Tienen algo ligero?"] },
  { match: /desayun|caf[eé]|brunch/i, chips: ["☕ ¿El café es bueno?", "🥞 ¿Qué desayuno recomiendas?", "🍳 Algo rápido para llevar"] },
  { match: /asad|carne|bbq|costilla|res\b/i, chips: ["🥩 El corte más recomendado", "🍖 ¿Para compartir?", "🌶️ ¿Tienen salsas picosas?"] },
  { match: /postre|nieve|helad|pastel/i, chips: ["🍰 El postre estrella", "🍦 Algo frío para el calor", "🎉 ¿Para una ocasión especial?"] },
  { match: /sushi|ramen|asi[aá]tic|japon|chino|coreano/i, chips: ["🍣 El platillo más pedido", "🌶️ ¿Qué tan picante es?", "🥢 ¿Tienen para compartir?"] },
];
const getChipsForContext = (ctx) => {
  const fallback = ["📍 Cuéntame más de este lugar", "💰 ¿Cuánto se gasta aquí?", "⭐ ¿Vale la pena ir?"];
  if (!ctx) return fallback;
  // 2.4 — chips desde datos reales: platillos reseñados del lugar visible
  if (ctx.platillos?.length) {
    const chips = ctx.platillos.slice(0, 2).map(pl => "🍽️ ¿Qué tal " + String(pl).replace(/^[^\wÁÉÍÓÚÑáéíóúñ]+/, "").slice(0, 32) + "?");
    chips.push("💰 ¿Cuánto se gasta aquí?");
    return chips;
  }
  if (!ctx.tipo) return fallback;
  const found = CHIPS_POR_TIPO.find(c => c.match.test(ctx.tipo));
  return found ? found.chips : fallback;
};

const Donde = ({ isOpen, onClose, onHL, reviews, places, user, isGuest, initialMsg, userInitialMsg, onFirstMessage, onInteractionComplete, visibleContext, onVenaqqi, onNewChat, newChatRequest, onLogin, onRegister, onGoogle }) => {
  const { getLocationForFeature, requestLocationForFeature } = useLocationAccess();
  const [dondeExpanded, setDondeExpanded] = useState(false);
  const [showIASheet, setShowIASheet] = useState(false); // 2.3 — bottom sheet del disclaimer de IA
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const dragStartY = useRef(null);
  const getDefaultGreeting = () => {
    if (user?.fullReg) return "¡Qué onda, " + user.username + "! 👋 ¿Qué se te antoja hoy?";
    return "¡Qué onda! 👋 Soy Don D', tu guía en VENAQQI. ¿Qué se te antoja hoy?";
  };
  const getGreeting = () => {
    if (initialMsg) return initialMsg;
    return getDefaultGreeting();
  };
  const identity = conversationIdentity(user);
  const savedConversation = useMemo(() => readConversation(user), [identity]); // eslint-disable-line react-hooks/exhaustive-deps
  const [msgs, setMsgs] = useState(savedConversation.messages.length ? savedConversation.messages : [{ role: "assistant", content: getGreeting() }]);
  const [hasSentMsg, setHasSentMsg] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(() => readGuestUsage());
  const [recommendationBatch, setRecommendationBatch] = useState([]);
  const [recommendationCursor, setRecommendationCursor] = useState(0);
  const [confirmNewChat, setConfirmNewChat] = useState(false);
  const requestInFlight = useRef(false);
  const requestGeneration = useRef(0);
  const abortController = useRef(null);
  const bot = useRef(null);

  useEffect(() => () => {
    requestGeneration.current += 1;
    abortController.current?.abort();
    requestInFlight.current = false;
  }, [identity]);

  useEffect(() => {
    writeConversation({ ...readConversation(user), messages: msgs, place: visibleContext?.placeName || null, recommendation: visibleContext || null, currentQuestion: [...msgs].reverse().find(m => m.role === "user")?.content || "" }, user);
  }, [msgs, visibleContext, identity]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetConversation = () => {
    // Una recomendación automática llega mediante initialMsg, pero deja de ser
    // contexto en cuanto la persona pide una charla nueva.
    const greeting = { role: "assistant", content: getDefaultGreeting() };
    setMsgs([greeting]); setHasSentMsg(false); setInput(""); setChipSeed(s => s + 1);
    setRecommendationBatch([]); setRecommendationCursor(0);
    clearConversation(user); if (onNewChat) onNewChat(); setConfirmNewChat(false);
  };
  const requestConversationReset = () => msgs.some(m => m.role === "user") ? setConfirmNewChat(true) : resetConversation();

  useEffect(() => { if (newChatRequest) requestConversationReset(); }, [newChatRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) { setUsage(readGuestUsage()); setTimeout(() => bot.current?.scrollIntoView?.({ behavior: "smooth" }), 80); }
  }, [msgs, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (userInitialMsg) {
      const greeting = "¡Qué onda! 👋 ¿En qué te ayudo?";
      send(userInitialMsg, [{ role: "assistant", content: greeting }, { role: "user", content: userInitialMsg }]);
    } else if (initialMsg) {
      setMsgs([{ role: "assistant", content: initialMsg, conversion: initialMsg.includes("Ya utilizaste tus 2 recomendaciones") }]);
    }
  }, [isOpen, initialMsg, userInitialMsg]); // eslint-disable-line react-hooks/exhaustive-deps

  const callDonD = async (nx, locationOverride = null, signal = undefined) => {
    const modelMessages = compactMessagesForModel(nx);
    const latestText = nx[nx.length - 1]?.content || "";
    const currentLocation = locationOverride || (hasLocationIntent(latestText) ? getLocationForFeature("recommendation") : null);
    if (isDev && isGuest) console.info("[DonD guest consumption]", { calls: 1, contextCharacters: estimateContextCharacters(nx), approximateInputTokens: Math.ceil(estimateContextCharacters(nx) / 4), turns: nx.filter(m => m.role === "user").length, maxOutputTokens: 420 });
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("Inicia sesión para usar Don D'");
    const res = await fetch("/api/chat", {
      method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      signal,
      body: JSON.stringify({
        system: SYSTEM(places, reviews, user, visibleContext, latestText, isGuest ? remainingRecommendations(usage) : null, currentLocation),
        messages: modelMessages,
        guest: isGuest
      })
    });
    if (!res.ok) {
      const err = new Error("HTTP " + res.status);
      err.status = res.status;
      throw err;
    }
    return await res.json();
  };

  const send = async (ov, overrideMsgs, locationHandled = false, locationOverride = null) => {
    const t = typeof ov === "string" ? ov.trim() : input.trim(); if ((!t && !overrideMsgs) || loading || requestInFlight.current) return;
    const remaining = remainingRecommendations(usage);
    if (isGuest && !overrideMsgs && isMultiPlaceRequest(t)) {
      setMsgs(p => [...p, { role: "user", content: t }, { role: "assistant", content: guestListMessage, localOnly: true, conversion: true }]); setInput("");
      if (isDev) console.info("[DonD guest consumption] local call avoided: list"); return;
    }
    if (isGuest && remaining === 0) {
      const content = isRecommendationRequest(t) ? guestLimitMessage : "Puedo conservar esta charla y ayudarte a iniciar sesión o registrarte; las nuevas recomendaciones se habilitarán al iniciar el siguiente periodo.";
      setMsgs(p => [...p, { role: "user", content: t }, { role: "assistant", content, localOnly: true, conversion: true }]); setInput("");
      if (isDev) console.info("[DonD guest consumption] local call avoided: exhausted"); return;
    }
    if (!locationHandled && hasLocationIntent(t)) {
      requestLocationForFeature("recommendation", createPendingLocationAction(LOCATION_ACTION_TYPES.DON_D_RECOMMENDATION, { message: t, conversationId: readConversation(user).conversationId, supportsManualLocation: true }), {
        onGranted: position => send(t, overrideMsgs, true, { coords: position.coords }),
        onManual: zone => send(`${t} Mi zona aproximada es ${zone}.`, overrideMsgs, true, { zone }),
      });
      return;
    }
    requestInFlight.current = true;
    const generation = requestGeneration.current;
    const controller = new AbortController();
    abortController.current = controller;
    if (!hasSentMsg) { setHasSentMsg(true); onFirstMessage && onFirstMessage(); }
    const nx = overrideMsgs || [...msgs, { role: "user", content: t }];
    setMsgs(nx); setInput(""); setLoading(true);
    try {
      let d;
      try {
        // Primer intento
        d = await callDonD(nx, locationOverride, controller.signal);
      } catch (e1) {
        // Si fue error de saturación (529) o de red, reintenta una vez en silencio
        if (e1.status === 529 || e1.status === 503 || e1.status === 429 || !e1.status) {
          await new Promise(r => setTimeout(r, 2000));
          d = await callDonD(nx, locationOverride, controller.signal);
        } else {
          throw e1;
        }
      }
      if (generation !== requestGeneration.current) return;
      if (typeof d?.reply !== "string" || !d.reply.trim()) throw new Error("Empty reply");
      const validated = isGuest ? sanitizeRecommendationReply(d.reply, places, remaining) : sanitizeRecommendationReply(d.reply, places, 5);
      const rep = validated.reply;
      if (user?.userId && !overrideMsgs) trackTesterActivity(TESTER_ACTIVITY_ACTIONS.DON_D_MESSAGE, { userId: user.userId, dedupeKey: `don_d_message:${t}:${nx.length}`, dedupeMs: 2000 });
      const nextUsage = isGuest ? recordSuccessfulRecommendations(usage, validated.places.map(place => place.id)) : usage;
      if (isGuest && validated.places.length) { setUsage(writeGuestUsage(nextUsage)); }
      const after = remainingRecommendations(nextUsage);
      const renewalTime = isGuest && validated.places.length ? remainingTimeLabel(nextUsage) : null;
      const notice = isGuest && validated.places.length ? (after === 1 ? firstRecommendationNotice(nextUsage) : exhaustedRecommendationNotice(nextUsage)) : null;
      if (renewalTime) log("[DonD guest quota]", { remainingRecommendations: after, renewalTime });
      setMsgs(p => [...p, { role: "assistant", content: rep }, ...(notice ? [{ role: "assistant", content: notice, localOnly: true, conversion: after === 0 }] : [])]);
      if (validated.places.length) {
        const batch = validated.places.slice(0, isGuest ? Math.max(1, remaining) : 5);
        setRecommendationBatch(batch); setRecommendationCursor(0);
        void trackProductEvent("don_d_recommendation_batch", { candidate_place_ids: batch.map(place => Number(place.id)), count: batch.length, intent: t.slice(0, 160), guest: !!isGuest, context_place_id: visibleContext?.placeId || null });
      }
      if (validated.truncated && isDev) console.warn("[DonD recommendation validation] response reduced to allowed balance", { generated: extractRecommendedPlaces(d.reply, places).length, allowed: isGuest ? remaining : 5 });
      const hit = places.filter(p => validated.reply.toLowerCase().includes(p.name.split("–")[0].trim().toLowerCase()));
      if (hit.length) onHL(hit.map(p => p.id));
    } catch (err) {
      if (generation !== requestGeneration.current || err?.name === "AbortError") return;
      // Si después del reintento sigue fallando, ofrece botón manual
      setMsgs(p => [...p, { role: "assistant", content: "__RETRY__", retryFor: nx }]);
    }
    if (generation === requestGeneration.current) { requestInFlight.current = false; setLoading(false); onInteractionComplete && onInteractionComplete(); }
  };

  const retryLastMessage = async (msgsToRetry) => {
    if (loading || requestInFlight.current) return;
    requestInFlight.current = true;
    const generation = requestGeneration.current;
    // Quita el bubble de retry
    setMsgs(p => p.filter(m => m.content !== "__RETRY__"));
    setLoading(true);
    try {
      const controller = new AbortController();
      abortController.current = controller;
      const d = await callDonD(msgsToRetry, null, controller.signal);
      if (generation !== requestGeneration.current) return;
      if (typeof d?.reply !== "string" || !d.reply.trim()) throw new Error("Empty reply");
      const validated = isGuest ? sanitizeRecommendationReply(d.reply, places, remainingRecommendations(usage)) : sanitizeRecommendationReply(d.reply, places, 5);
      const nextUsage = isGuest ? recordSuccessfulRecommendations(usage, validated.places.map(place => place.id)) : usage;
      if (isGuest && validated.places.length) setUsage(writeGuestUsage(nextUsage));
      setMsgs(p => [...p, { role: "assistant", content: validated.reply }]);
      if (validated.places.length) {
        const batch = validated.places.slice(0, isGuest ? Math.max(1, remainingRecommendations(usage)) : 5);
        setRecommendationBatch(batch); setRecommendationCursor(0);
        const retryIntent = [...msgsToRetry].reverse().find(item => item.role === "user")?.content || "retry";
        void trackProductEvent("don_d_recommendation_batch", { candidate_place_ids: batch.map(place => Number(place.id)), count: batch.length, intent: String(retryIntent).slice(0, 160), guest: !!isGuest, retried: true });
      }
      const hit = places.filter(p => validated.reply.toLowerCase().includes(p.name.split("–")[0].trim().toLowerCase()));
      if (hit.length) onHL(hit.map(p => p.id));
    } catch (err) {
      if (generation !== requestGeneration.current || err?.name === "AbortError") return;
      setMsgs(p => [...p, { role: "assistant", content: "__RETRY__", retryFor: msgsToRetry }]);
    }
    if (generation === requestGeneration.current) { requestInFlight.current = false; setLoading(false); onInteractionComplete && onInteractionComplete(); }
  };

  const [chipSeed, setChipSeed] = useState(0);
  const sug = useMemo(() => {
    const base = visibleContext ? getChipsForContext(visibleContext) : (user?.fullReg
      ? ["¿Qué me recomiendas hoy?", "Algo diferente a lo de siempre", "Sorpréndeme"]
      : ["¿Qué recomiendas?", "Algo rápido", "¿Qué hay cerca?"]);
    return chipSeed > 0 ? shuffle(base) : base; // B9 (ID 23) — nueva charla re-baraja chips
  }, [visibleContext, user, chipSeed]);

  const recommendationPlace = recommendationBatch[recommendationCursor] || null;
  const showAnotherRecommendation = () => {
    if (recommendationBatch.length < 2) return;
    const nextCursor = (recommendationCursor + 1) % recommendationBatch.length;
    setRecommendationCursor(nextCursor);
    void trackProductEvent("don_d_recommendation_next", { candidate_place_ids: recommendationBatch.map(place => Number(place.id)), visible_place_id: Number(recommendationBatch[nextCursor]?.id), cursor: nextCursor });
  };
  const takeRecommendation = () => {
    if (!recommendationPlace) return;
    if (!window.confirm(`¿Te llevo a ${recommendationPlace.name}?`)) return;
    void trackProductEvent("don_d_recommendation_selected", { selected_place_id: Number(recommendationPlace.id), candidate_place_ids: recommendationBatch.map(place => Number(place.id)), cursor: recommendationCursor, action: "route", destination: "venaqqi_detail_map", context_place_id: visibleContext?.placeId || null });
    onVenaqqi && onVenaqqi(recommendationPlace);
  };

  return (
    <>
      <style>{SPIN_CSS + SHAKE_CSS + DON_D_LOGO_CSS + `@import url("https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap");@keyframes hatbob{0%,100%{transform:translateY(0) rotate(-4deg)}50%{transform:translateY(-14px) rotate(4deg)}}@keyframes splashfade{0%{opacity:0}10%{opacity:1}85%{opacity:1}100%{opacity:0}}`}</style>
      <div data-testid="don-d-backdrop" onClick={() => { setDondeExpanded(false); onClose(); }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0)", zIndex: 4900, opacity: isOpen ? 1 : 0, pointerEvents: isOpen && !dondeExpanded ? "all" : "none", transition: "opacity 0.3s" }} />
      <div data-testid="don-d-panel" aria-hidden={!isOpen} style={{
        position: "fixed", zIndex: 5000, display: "flex", flexDirection: "column",
        background: "#0d0d0d", border: "1px solid #1e1e1e",
        boxShadow: "0 -6px 40px rgba(229,57,53,0.2)",
        bottom: 0, left: 0, right: 0, width: "100%", maxWidth: "100%",
        height: dondeExpanded ? "100vh" : "51vh",
        borderRadius: dondeExpanded ? 0 : "20px 20px 0 0",
        transform: isOpen ? ("translateY(" + (dragOffsetY < 0 ? (-dragOffsetY) : 0) + "px)") : "translateY(110%)",
        transition: dragOffsetY !== 0 ? "none" : "transform 0.38s cubic-bezier(0.32,0.72,0,1), height 0.3s ease, border-radius 0.3s ease",
        pointerEvents: isOpen ? "auto" : "none"
      }}>
        {/* Handle bar - drag up=expand, drag down=minimize */}
        {(() => {
          const onDragMouseDown = e => {
            dragStartY.current = e.clientY;
            const onMove = ev => { setDragOffsetY(Math.max(dragStartY.current - ev.clientY, -200)); };
            const onUp = ev => {
              const diff = dragStartY.current - ev.clientY;
              setDragOffsetY(0);
              if (diff > 24) setDondeExpanded(true);
              else if (diff < -24) { if (dondeExpanded) setDondeExpanded(false); else onClose(); }
              dragStartY.current = null;
              window.removeEventListener("mousemove", onMove);
              window.removeEventListener("mouseup", onUp);
            };
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
          };
          const onDragTouchStart = e => { dragStartY.current = e.touches[0].clientY; };
          const onDragTouchMove = e => {
            if (dragStartY.current === null) return;
            const diff = dragStartY.current - e.touches[0].clientY;
            if (Math.abs(diff) > 3) e.preventDefault(); // B15 (ID 38) — captura casi inmediata del gesto
            setDragOffsetY(diff < 0 ? diff : Math.min(diff, 60));
          };
          const onDragTouchEnd = e => {
            if (dragStartY.current === null) return;
            const diff = dragStartY.current - e.changedTouches[0].clientY;
            setDragOffsetY(0);
            if (diff > 24) setDondeExpanded(true); // B15 (ID 38) — umbral más sensible
            else if (diff < -24) { if (dondeExpanded) setDondeExpanded(false); else onClose(); }
            dragStartY.current = null;
          };
          return (<>
            <div
              style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "12px 0 4px", cursor: "ns-resize", flexShrink: 0, touchAction: "none", userSelect: "none" }}
              onMouseDown={onDragMouseDown} onTouchStart={onDragTouchStart} onTouchMove={onDragTouchMove} onTouchEnd={onDragTouchEnd}
            >
              <div style={{ width: 56, height: 6, borderRadius: 3, background: dragOffsetY !== 0 ? "#e53935" : "#888", transition: "background 0.2s" }} />
            </div>
            {/* B15 (ID 37) — TODA la barra de título es drag handle; los botones detienen la propagación */}
            <div style={{ padding: "10px 18px 14px", borderBottom: "1px solid #1a1a1a", display: "flex", alignItems: "center", gap: 12, flexShrink: 0, cursor: "ns-resize", touchAction: "none", userSelect: "none" }}
              onMouseDown={onDragMouseDown} onTouchStart={onDragTouchStart} onTouchMove={onDragTouchMove} onTouchEnd={onDragTouchEnd}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                <span className="don-d-logo-shell" data-testid="don-d-logo-container" style={{ width: 44, height: 44, borderRadius: "50%", border: "2px solid #e53935", flexShrink: 0 }}><img className="don-d-logo-symbol" src={LOGO} alt="Don D'" style={{ width: 38, height: 38, borderRadius: "50%", pointerEvents: "none" }} /></span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontFamily: "'Arial Black',sans-serif", color: "#fff", fontWeight: 900, display: "flex", alignItems: "center", gap: 6 }}>
                    Don D'
                    <button onClick={e => { e.stopPropagation(); setShowIASheet(true); }}
                      onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()}
                      title="Don D' usa inteligencia artificial"
                      style={{ background: "rgba(229,57,53,0.15)", border: "1px solid rgba(229,57,53,0.4)", color: "#e57373", fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", borderRadius: 5, padding: "1px 6px", cursor: "pointer", fontFamily: "inherit", lineHeight: 1.6 }}>
                      IA
                    </button>
                    <span style={{ fontSize: 10, color: "#555", fontFamily: "inherit", fontWeight: 400 }}>by VENAQQI</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#5cb85c", display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#5cb85c", display: "inline-block" }} />
                    {user?.fullReg ? "Modo personal · " + user.username : "Modo visitante"}
                  </div>
                </div>
              </div>
              <button onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()} onClick={requestConversationReset} title="Empezar nueva charla"
                style={{ background: "rgba(229,57,53,0.12)", border: "1px solid #e53935", borderRadius: 8, color: "#ff6b6b", cursor: "pointer", fontSize: 12, padding: "7px 12px", marginRight: 4, fontWeight: 500, display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
                <span style={{ fontSize: 13 }}>↻</span> Nueva charla
              </button>
              <button onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()} onClick={onClose}
                style={{ background: "rgba(255,255,255,0.08)", border: "1px solid #333", color: "#fff", cursor: "pointer", fontSize: 14, width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
            </div>
          </>);
        })()}

        {/* Messages */}
        <div style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          {msgs.map((m, i) => (
            <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 8 }}>
              {m.role === "assistant" && <span className="don-d-logo-shell" style={{ width: 28, height: 28, borderRadius: "50%", border: "1px solid #e53935", flexShrink: 0 }}><img className="don-d-logo-symbol" src={LOGO} alt="D" style={{ width: 24, height: 24, borderRadius: "50%" }} /></span>}
              {m.content === "__RETRY__" ? (
                <div style={{ maxWidth: "80%", padding: "12px 14px", borderRadius: "16px 16px 16px 3px", background: "#161616", border: "1px solid #e53935", fontSize: 13, lineHeight: 1.55, color: "#ccc" }}>
                  <p style={{ margin: "0 0 10px" }}>Mi cabeza está saturada en este momento. ¿Lo intentamos de nuevo?</p>
                  <button onClick={() => retryLastMessage(m.retryFor)}
                    style={{ padding: "8px 16px", background: "#e53935", border: "none", borderRadius: 8, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    🔄 Reintentar
                  </button>
                </div>
              ) : (
                <div style={{ maxWidth: "80%", padding: "9px 13px", borderRadius: m.role === "user" ? "16px 16px 3px 16px" : "16px 16px 16px 3px", background: m.role === "user" ? "#e53935" : "#161616", color: m.role === "user" ? "#fff" : "#ccc", fontSize: 13, lineHeight: 1.55, border: m.role === "assistant" ? "1px solid #222" : "none" }}>
                  {renderMsg(m.content, places, onVenaqqi)}
                  {m.conversion && <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center", marginTop: 10 }}>
                    <button onClick={onLogin} style={{ minHeight: 36, background: "transparent", border: "1px solid #e53935", borderRadius: 7, color: "#fff", padding: "7px 10px", cursor: "pointer", fontWeight: 700 }}>Iniciar sesión</button>
                    <button onClick={onRegister} style={{ minHeight: 36, background: "#e53935", border: 0, borderRadius: 7, color: "#fff", padding: "7px 10px", cursor: "pointer", fontWeight: 700 }}>Registrarse</button>
                    <button aria-label="Continuar con Google" onClick={onGoogle} style={{ minHeight: 36, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, background: "#fff", border: "1px solid #ddd", borderRadius: 7, color: "#222", padding: "7px 10px", cursor: "pointer", fontWeight: 700 }}><GoogleG /> Continuar con Google</button>
                  </div>}
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
              <span className="don-d-logo-shell" style={{ width: 28, height: 28, borderRadius: "50%", border: "1px solid #e53935", flexShrink: 0 }}><img className="don-d-logo-symbol" src={LOGO} alt="D" style={{ width: 24, height: 24, borderRadius: "50%" }} /></span>
              <div style={{ padding: "9px 13px", background: "#161616", borderRadius: "16px 16px 16px 3px", border: "1px solid #222" }}><Dots /></div>
            </div>
          )}
          {SHOW_DON_D_RECOMMENDATION_CARD && recommendationPlace && (
            <div data-testid="don-d-recommendation-batch" style={{ alignSelf: "stretch", margin: "4px 36px 2px" }}>
              <strong style={{ display: "block", color: "#fff", fontSize: 13, marginBottom: 7 }}>{recommendationPlace.name}</strong>
              <div style={{ display: "flex", gap: 8 }}>
                {recommendationBatch.length > 1 && <button type="button" onClick={showAnotherRecommendation} data-analytics-action="don-d-otra-local" style={{ flex: 1, minHeight: 36, background: "#181818", border: "1px solid #444", borderRadius: 8, color: "#ddd", fontWeight: 800, cursor: "pointer" }}>Otra</button>}
                <button type="button" onClick={takeRecommendation} data-analytics-action="don-d-llevame" style={{ flex: 1, minHeight: 36, background: "#e53935", border: "1px solid #e53935", borderRadius: 8, color: "#fff", fontWeight: 900, cursor: "pointer" }}>Llévame</button>
              </div>
            </div>
          )}
          <div ref={bot} />
        </div>

        {/* Sugerencias */}
        {msgs.length <= 2 && (
          <div style={{ padding: "0 14px 10px", display: "flex", gap: 6, flexWrap: "wrap" }}>
            {sug.map(s => (
              <button key={s} onClick={() => send(s)} style={{ fontSize: 11, padding: "5px 10px", background: "transparent", border: "1px solid #2a2a2a", borderRadius: 20, color: "#666", cursor: "pointer", fontFamily: "inherit" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#e53935"; e.currentTarget.style.color = "#e53935"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#2a2a2a"; e.currentTarget.style.color = "#666"; }}>
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div style={{ padding: "10px 14px 14px", borderTop: "1px solid #1a1a1a", display: "flex", gap: 8, alignItems: "flex-end", flexShrink: 0 }}>
          <textarea value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.stopPropagation(); } }}
            placeholder="Cuéntame qué buscas..." rows={1}
            style={{ flex: 1, background: "#161616", border: "1px solid #222", borderRadius: 12, padding: "9px 13px", color: "#fff", fontSize: 13, outline: "none", resize: "none", fontFamily: "inherit", lineHeight: 1.5, maxHeight: 90, overflowY: "auto" }} />
          <button onClick={() => send()} disabled={loading || !input.trim()}
            style={{ width: 40, height: 40, borderRadius: "50%", background: input.trim() && !loading ? "#e53935" : "#1e1e1e", border: "none", cursor: input.trim() && !loading ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0, color: "#fff" }}>➤</button>
        </div>
      </div>
      {/* 2.3 — Bottom sheet: disclaimer de IA (texto exacto acordado en sesión legal, referenciado en T&C §8) */}
      {showIASheet && (
        <div onClick={() => setShowIASheet(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 5300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#141414", borderTop: "1px solid #2a2a2a", borderRadius: "20px 20px 0 0", padding: "18px 20px 22px", width: "100%", maxWidth: 480 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#fff" }}>Sobre Don D'</p>
              <button onClick={() => setShowIASheet(false)} style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 16 }}>✕</button>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: "#ccc", lineHeight: 1.6 }}>
              Don D' es un asistente que genera recomendaciones con inteligencia artificial, a partir del catálogo de Venaqqi y las reseñas de la comunidad. Sus respuestas pueden contener imprecisiones o errores. Estas no constituyen una garantía respaldada por humanos en tiempo real.
            </p>
          </div>
        </div>
      )}
      {confirmNewChat && (
        <div role="dialog" aria-modal="true" aria-label="Confirmar nueva charla" style={{ position: "fixed", inset: 0, zIndex: 5400, background: "rgba(0,0,0,.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ width: "100%", maxWidth: 360, background: "#161616", border: "1px solid #333", borderRadius: 16, padding: 20 }}>
            <h3 style={{ margin: "0 0 8px", color: "#fff" }}>¿Empezar una nueva charla?</h3>
            <p style={{ color: "#aaa", fontSize: 13 }}>Se borrarán los mensajes y el contexto de esta conversación. Tu saldo y periodo de recomendaciones se conservarán.</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setConfirmNewChat(false)} style={{ background: "transparent", border: "1px solid #555", color: "#fff", borderRadius: 8, padding: "8px 12px" }}>Cancelar</button>
              <button onClick={resetConversation} style={{ background: "#e53935", border: 0, color: "#fff", borderRadius: 8, padding: "8px 12px" }}>Confirmar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};


// ── Categorías simplificadas ──────────────────────────────────


const ImageViewer = ({ images, startIndex = 0, onClose, onBack, zIndex = 10000, metas = null }) => {
  const [idx, setIdx] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [lastTap, setLastTap] = useState(0);
  const swipeStartX = useRef(null);
  const pinchStartDistance = useRef(null);
  const pinchStartScale = useRef(1);
  const panStart = useRef(null);

  const resetZoom = () => { setScale(1); setOffset({ x: 0, y: 0 }); };
  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTap < 300) {
      setScale(current => {
        const nextScale = current === 1 ? 2.5 : 1;
        if (nextScale === 1) setOffset({ x: 0, y: 0 });
        return nextScale;
      });
    }
    setLastTap(now);
  };

  const prev = () => { resetZoom(); setIdx(i => (i - 1 + images.length) % images.length); };
  const next = () => { resetZoom(); setIdx(i => (i + 1) % images.length); };
  useEffect(() => { resetZoom(); }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  const touchDistance = touches => Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
  const clampPan = (value, axisScale) => Math.max(-axisScale, Math.min(axisScale, value));
  const handleTouchStart = e => {
    if (e.touches.length >= 2) {
      pinchStartDistance.current = touchDistance(e.touches); pinchStartScale.current = scale;
      swipeStartX.current = null; panStart.current = null; return;
    }
    const touch = e.touches[0];
    if (scale > 1.05) {
      panStart.current = { x: touch.clientX, y: touch.clientY, offsetX: offset.x, offsetY: offset.y };
      swipeStartX.current = null; return;
    }
    swipeStartX.current = touch.clientX;
  };
  const handleTouchMove = e => {
    if (e.touches.length >= 2 && pinchStartDistance.current) {
      e.preventDefault();
      const ratio = touchDistance(e.touches) / pinchStartDistance.current;
      const nextScale = Math.min(4, Math.max(1, pinchStartScale.current * ratio));
      setScale(nextScale);
      if (nextScale <= 1.05) setOffset({ x: 0, y: 0 });
      return;
    }
    if (e.touches.length === 1 && scale > 1.05 && panStart.current) {
      e.preventDefault();
      const touch = e.touches[0];
      const maxX = Math.max(0, window.innerWidth * (scale - 1) / 2);
      const maxY = Math.max(0, window.innerHeight * (scale - 1) / 2);
      setOffset({
        x: clampPan(panStart.current.offsetX + touch.clientX - panStart.current.x, maxX),
        y: clampPan(panStart.current.offsetY + touch.clientY - panStart.current.y, maxY),
      });
    }
  };
  const handleTouchEnd = e => {
    if (pinchStartDistance.current) {
      if (e.touches.length < 2) pinchStartDistance.current = null;
      if (e.touches.length === 1 && scale > 1.05) {
        const touch = e.touches[0];
        panStart.current = { x: touch.clientX, y: touch.clientY, offsetX: offset.x, offsetY: offset.y };
      }
      return;
    }
    if (panStart.current) { panStart.current = null; swipeStartX.current = null; return; }
    if (swipeStartX.current === null || scale > 1.05) { swipeStartX.current = null; return; }
    const diff = swipeStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) { diff > 0 ? next() : prev(); }
    swipeStartX.current = null;
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000", zIndex: zIndex, display: "flex", flexDirection: "column" }}>
      <style>{"@keyframes vqfadein{from{opacity:0}to{opacity:1}}"}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid #333", color: "#fff", width: 34, height: 34, borderRadius: "50%", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>←</button>
        <span style={{ color: "#888", fontSize: 13 }}>{idx + 1} / {images.length}</span>
        <button onClick={onClose} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid #333", color: "#fff", width: 34, height: 34, borderRadius: "50%", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
      </div>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative", touchAction: scale > 1 ? "none" : "pan-y" }}
        onClick={handleDoubleTap}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}>
        <img src={images[idx]} alt="" draggable={false} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${scale})`, transition: panStart.current ? "none" : "transform 0.2s", animation: "vqfadein 0.2s", userSelect: "none", WebkitUserDrag: "none" }} />
      </div>
      {metas && metas[idx] && (
        <div style={{ padding: "10px 16px", flexShrink: 0, borderTop: "1px solid #1e1e1e", background: "rgba(0,0,0,0.85)" }}>
          <p style={{ margin: "0 0 2px", fontSize: 12, color: "#e57373", fontWeight: 700 }}>
            {metas[idx].username}{metas[idx].sabor ? " · ⭐ " + metas[idx].sabor : ""}
          </p>
          {metas[idx].comentario && <p style={{ margin: 0, fontSize: 12, color: "#ccc", lineHeight: 1.4 }}>{metas[idx].comentario}</p>}
        </div>
      )}
      {images.length > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", flexShrink: 0 }}>
          <button onClick={prev} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", width: 44, height: 44, borderRadius: "50%", cursor: "pointer", fontSize: 20 }}>←</button>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {images.map((_, i) => <div key={i} style={{ width: i === idx ? 20 : 6, height: 6, borderRadius: 3, background: i === idx ? "#e53935" : "#555", transition: "all 0.2s" }} />)}
          </div>
          <button onClick={next} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", width: 44, height: 44, borderRadius: "50%", cursor: "pointer", fontSize: 20 }}>→</button>
        </div>
      )}
    </div>
  );
};


// eslint-disable-next-line no-unused-vars
const AlternateCards = ({ slots, reviews, onSelectAlt, onViewImage }) => {
  const scrollRef = useRef(null);
  const [showArrows, setShowArrows] = useState(true);
  const arrowTimerRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    let raf;
    if (el) {
      raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const cards = el.querySelectorAll('[data-card]');
          if (cards.length >= 3) {
            const containerW = el.offsetWidth; // offsetWidth excluye el padding
            const cardW = cards[0].offsetWidth;
            const gap = 8;
            const peek = 20; // cuánto se asoma cada lado
            // Queremos ver cards[2] y cards[3] centradas
            // scrollLeft = posición de cards[2] - espacio de asomo izquierdo
            // posición de cards[2] = peek + (cardW + gap) * 2
            // para que los dos queden centrados: scrollLeft = peek + (cardW+gap)*2 - (containerW - cardW*2 - gap) / 2
            const twoCardWidth = cardW * 2 + gap;
            const offset = (containerW - twoCardWidth) / 2;
            // Las tarjetas empiezan después del peek (marginLeft en primera)
            const card2Left = peek + (cardW + gap) * 2;
            el.scrollLeft = card2Left - offset;
          }
        });
      });
    }
    setShowArrows(true);
    return () => { if (raf) cancelAnimationFrame(raf); if (arrowTimerRef.current) clearTimeout(arrowTimerRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScroll = () => {
    setShowArrows(false);
    if (arrowTimerRef.current) clearTimeout(arrowTimerRef.current);
    arrowTimerRef.current = setTimeout(() => setShowArrows(true), 3000);
  };

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardW = el.querySelector('[data-card]')?.offsetWidth || 140;
    el.scrollBy({ left: dir * (cardW + 8), behavior: "smooth" });
  };

  if (!slots || !slots.length) return null;

  const peek = 20;

  return (
    <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: "44vh", background: "#0d0d0d", borderRadius: "20px 20px 0 0", border: "1px solid #1e1e1e", zIndex: 2500, display: "flex", flexDirection: "column" }}>
      <style>{`
        @keyframes vq-arrow-blink { 0%,100%{opacity:1} 50%{opacity:0.15} }
        .vq-alt-arrow { animation: vq-arrow-blink 0.9s ease infinite; }
        .vq-alt-scroll { scrollbar-width:none; }
        .vq-alt-scroll::-webkit-scrollbar { display:none; }
      `}</style>
      <p style={{ margin: "10px 0 6px", fontSize: 11, color: "#555", textAlign: "center", textTransform: "uppercase", letterSpacing: "0.1em", flexShrink: 0 }}>También te puede gustar</p>
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        {showArrows && (
          <button onClick={() => scrollBy(-1)} className="vq-alt-arrow"
            style={{ position: "absolute", left: 4, top: "50%", transform: "translateY(-50%)", zIndex: 10, background: "rgba(229,57,53,0.85)", border: "none", color: "#fff", width: 26, height: 26, borderRadius: "50%", cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
        )}
        {showArrows && (
          <button onClick={() => scrollBy(1)} className="vq-alt-arrow"
            style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", zIndex: 10, background: "rgba(229,57,53,0.85)", border: "none", color: "#fff", width: 26, height: 26, borderRadius: "50%", cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
        )}
        {/* Snap por tarjeta: conserva el asomo lateral pero evita posiciones intermedias incómodas. */}
        <div ref={scrollRef} className="vq-alt-scroll"
          onScroll={handleScroll}
          style={{ display: "flex", overflowX: "auto", height: "100%", gap: 8, WebkitOverflowScrolling: "touch", alignItems: "stretch", scrollSnapType: "x mandatory", scrollPaddingInline: peek }}>
          {slots.map((place, idx) => {
            const rs = reviews.filter(r => Number(r.placeid || r.placeId) === Number(place.id));
            const s = overallScore(rs);
            const isTitular = idx === 2 || idx === 3;
            const isFirst = idx === 0;
            const isLast = idx === slots.length - 1;
            // Cada tarjeta: (100% - 2*peek - 1*gap) / 2
            // = calc((100% - 48px) / 2) donde 48px = peek*2 + gap*2 aprox
            return (
              <div key={place.id} data-card onClick={() => onSelectAlt(place)}
                style={{
                  flexShrink: 0,
                  width: "calc((100% - 48px) / 2)",
                  marginLeft: isFirst ? peek : 0,
                  marginRight: isLast ? peek : 0,
                  borderRadius: 14, overflow: "hidden", position: "relative", cursor: "pointer",
                  background: "#1a1a1a",
                  border: isTitular ? "1.5px solid #e53935" : "1px solid #1e1e1e",
                  scrollSnapAlign: "center", scrollSnapStop: "always",
                  display: "flex", flexDirection: "column"
                }}>
                <div style={{ padding: "8px 10px 4px", flexShrink: 0 }}>
                  <p style={{ margin: "0 0 1px", fontSize: 11, fontWeight: 700, color: "#fff", fontFamily: "'Arial Black',sans-serif", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{place.name}</p>
                  <p style={{ margin: 0, fontSize: 9, color: "#666" }}>{place.tipo}</p>
                </div>
                <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
                  <img src={place.image} alt={place.name}
                    onClick={e => { e.stopPropagation(); onViewImage && onViewImage([place.image], 0); }}
                    style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "zoom-in" }} />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 50%)" }} />
                  <div style={{ position: "absolute", top: 6, right: 6, background: "#e53935", borderRadius: "50%", width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: 9, fontWeight: 900, color: "#fff" }}>{s.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <p style={{ margin: "4px 0 8px", fontSize: 10, color: "#333", textAlign: "center", flexShrink: 0 }}>desliza · toca para abrir</p>
    </div>
  );
};

const SuerteView = ({ place, reviews, onClose, hideClose, initialCount = 0, onOpenDetail, onCloseDonde, onViewImages, onResena }) => {
  const [pulse, setPulse] = useState(false);
  const [clickCount, setClickCount] = useState(initialCount);
  if (!place) return null;
  const rating = buildRatingSummary(reviews);
  const ratingReviews = rating.display.reviews;
  const s = rating.display.score;
  const sb = averageRatingDimension(ratingReviews, "sabor");
  const cs = averageRatingDimension(ratingReviews, "servicio");
  const cost = resolvePlaceCost(place, reviews);

  const handleOutsideClick = () => {
    if (onCloseDonde) { onCloseDonde(); return; }
    const newCount = clickCount + 1;
    setClickCount(newCount);
    if (newCount === 1) {
      setPulse(true);
      setTimeout(() => setPulse(false), 600);
    } else {
      onClose();
      setClickCount(0);
    }
  };

  return (
    <>
    <div style={{ position: "fixed", inset: 0, background: "#000", zIndex: 1999, pointerEvents: "all" }} />
    <div onClick={handleOutsideClick} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: "44vh", zIndex: 2000, display: "flex", flexDirection: "column" }}>
      <div onClick={e => e.stopPropagation()} style={{ flex: 1, background: "#0d0d0d", borderRadius: 0, overflow: "hidden", position: "relative" }}>
        <div style={{ height: 200, position: "relative", overflow: "hidden", cursor: "pointer" }} onClick={e => { e.stopPropagation(); onViewImages && onViewImages(); }}>
          <img src={place.image} alt={place.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,#0d0d0d 0%,transparent 55%)" }} />
          {!hideClose && (
            <button onClick={e => { e.stopPropagation(); onClose(); }} className={pulse ? "vq-pulse" : ""}
              style={{ position: "absolute", top: 12, right: 12, background: pulse ? "#e53935" : "rgba(0,0,0,0.6)", border: pulse ? "2px solid #fff" : "1px solid #333", color: "#fff", width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: 15, transition: "background 0.2s, border 0.2s" }}>✕</button>
          )}
          <div style={{ position: "absolute", top: 12, left: 12 }}>
            <span style={{ fontSize: 10, background: "rgba(229,57,53,0.15)", border: "1px solid rgba(229,57,53,0.35)", color: "#e57373", padding: "3px 8px", borderRadius: 20 }}>{place.tipo}</span>
          </div>
        </div>
        <div style={{ padding: "14px 20px 32px", overflowY: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div>
              <h2 style={{ margin: "0 0 4px", fontFamily: "'Arial Black',sans-serif", color: "#fff", fontSize: 18 }}>{place.name}</h2>
              {place.maps ? <a href={place.maps} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ margin: 0, fontSize: 11, color: "#7986cb", textDecoration: "underline", display: "block" }}>📍 {place.location}</a> : <p style={{ margin: 0, fontSize: 11, color: "#555" }}>📍 {place.location}</p>}
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ background: "#e53935", borderRadius: "50%", width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 3px 12px rgba(229,57,53,0.45)" }}>
                <span style={{ fontSize: 13, fontWeight: 900, color: "#fff" }}>{s.toFixed(1)}</span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ fontSize: 11, color: "#555" }}>Sabor</span>
              <span style={{ fontSize: 12, color: "#e53935" }}>{"⭐".repeat(Math.round(sb))}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ fontSize: 11, color: "#555" }}>Servicio</span>
              <span style={{ fontSize: 12 }}>{"✔️".repeat(Math.round(cs))}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ fontSize: 11, color: "#555" }}>¿Volvería?</span>
              <span style={{ fontSize: 11, color: "#aaa", fontStyle: "italic" }}>{SAT_LABELS[Math.round(avg(reviews, "satisfaccion"))] || "-"}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ fontSize: 11, color: "#555" }}>Precio</span>
              <span style={{ fontSize: 12, color: "#4caf50" }} title={cost.caption}>{cost.label}</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 4, alignItems: "stretch" }}>
            <button onClick={() => onResena && onResena()}
              style={{ flex: 1, padding: "11px 6px", background: "#e53935", border: "none", borderRadius: 12, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", boxSizing: "border-box" }}>
              ✍️ Reseña
            </button>
            {place.maps && (
              <a href={place.maps} target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, padding: "11px 6px", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 12, textDecoration: "none", color: "#ccc", fontSize: 12, textAlign: "center", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center" }}>
                📍 Google Maps
              </a>
            )}
            <button onClick={() => onOpenDetail && onOpenDetail()}
              style={{ flex: 1, padding: "11px 6px", background: "#1a1a1a", border: "1px solid #e53935", borderRadius: 12, color: "#e53935", fontSize: 12, cursor: "pointer", boxSizing: "border-box" }}>
              Ver más →
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};



const OnboardingScreen = ({ place, msg, onNext, onSkip, reviews, onOpenDetail, onSendMsg, places }) => {
  const [reply, setReply] = useState("");
  if (!place) return null;
  const rs = reviews.filter(r => Number(r.placeid || r.placeId) === Number(place.id));
  const s = overallScore(rs);
  return (
    <div style={{ position: "fixed", inset: 0, background: "#080808", zIndex: 9000, display: "flex", flexDirection: "column" }}>
      {/* Header Don D' */}
      <div style={{ padding: "16px 16px 12px", display: "flex", alignItems: "flex-start", gap: 10, position: "relative" }}>
        <img className="don-d-logo-symbol" src={LOGO} alt="Don D'" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid #e53935", flexShrink: 0, marginTop: 4 }} />
        <div style={{ background: "#1a1a1a", borderRadius: "0 16px 16px 16px", padding: "12px 14px", flex: 1, border: "1px solid #2a2a2a" }}>
          <p style={{ margin: "0 0 4px", fontSize: 11, color: "#e53935", fontWeight: 700 }}>Don D' · VENAQQI</p>
          <p style={{ margin: 0, fontSize: 13, color: "#ccc", lineHeight: 1.55 }}>{renderMsg(msg, places, (p) => { onOpenDetail && onOpenDetail(p); })}</p>
        </div>
        <button onClick={onSkip} style={{ position: "absolute", top: 12, right: 12, background: "rgba(255,255,255,0.08)", border: "1px solid #333", color: "#fff", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
      </div>

      {/* Place card */}
      <div style={{ flex: 1, margin: "0 12px", borderRadius: 20, overflow: "hidden", position: "relative", background: "#1a1a1a" }}>
        <img src={place.image} alt={place.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, #080808 0%, rgba(0,0,0,0.3) 60%, transparent 100%)" }} />
        {/* Score */}
        <div style={{ position: "absolute", top: 14, right: 14, background: "#e53935", borderRadius: "50%", width: 46, height: 46, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 3px 12px rgba(229,57,53,0.5)" }}>
          <span style={{ fontSize: 13, fontWeight: 900, color: "#fff" }}>{s.toFixed(1)}</span>
        </div>
        {/* Type badge */}
        <div style={{ position: "absolute", top: 14, left: 14 }}>
          <span style={{ fontSize: 10, background: "rgba(229,57,53,0.15)", border: "1px solid rgba(229,57,53,0.35)", color: "#e57373", padding: "3px 8px", borderRadius: 20 }}>{place.tipo}</span>
        </div>
        {/* CTA Button + place name below */}
        <div style={{ position: "absolute", bottom: 16, left: 16, right: 16, display: "flex", flexDirection: "column", gap: 8, zIndex: 2600 }}>
          <button onClick={() => { alert("Próximamente: reservas y promociones exclusivas 🎉"); }}
            style={{ width: "100%", padding: "8px 14px", background: "#e53935", border: "none", borderRadius: 14, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, cursor: "pointer" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className="don-d-logo-shell" style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0 }}><img src={LOGO} alt="Venaqqi" style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "contain" }} /></span>
              <span style={{ fontSize: 15, fontWeight: 900, color: "#fff", fontFamily: "'Arial Black',sans-serif" }}>¡Venaqqi por tu cupón!</span>
            </div>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", letterSpacing: "0.03em" }}>Presiona aquí</span>
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            {place.maps && (
              <a href={place.maps} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                style={{ flex: 1, padding: "12px", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 14, textDecoration: "none", color: "#ccc", fontSize: 13, textAlign: "center", boxSizing: "border-box" }}>
                📍 Google Maps
              </a>
            )}
            <button onClick={e => { e.stopPropagation(); onOpenDetail && onOpenDetail(); }}
              style={{ flex: 1, padding: "12px", background: "#1a1a1a", border: "1px solid #e53935", borderRadius: 14, color: "#e53935", fontSize: 13, cursor: "pointer", boxSizing: "border-box" }}>
              Ver más →
            </button>
          </div>
          <div style={{ paddingTop: 6 }}>
            <h2 style={{ margin: "0 0 2px", fontFamily: "'Arial Black',sans-serif", color: "#fff", fontSize: 20, textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>{place.name}</h2>
            <p style={{ margin: 0, fontSize: 12, color: "#aaa" }}>📍 {place.location}</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: "8px 16px 0", display: "flex", gap: 8, alignItems: "center" }}>
        <input value={reply} onChange={e => setReply(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && reply.trim()) { onSendMsg && onSendMsg(reply); setReply(""); } }}
          placeholder="Escríbele a Don D'..."
          style={{ flex: 1, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 20, padding: "9px 14px", color: "#fff", fontSize: 13, outline: "none", fontFamily: "inherit" }} />
        <button onClick={() => { if (reply.trim()) { onSendMsg && onSendMsg(reply); setReply(""); } }}
          style={{ width: 36, height: 36, borderRadius: "50%", background: reply.trim() ? "#e53935" : "#1e1e1e", border: "none", cursor: reply.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, color: "#fff", flexShrink: 0 }}>➤</button>
      </div>
      <div style={{ padding: "8px 16px 24px", display: "flex", gap: 10 }}>
        <button onClick={onNext}
          style={{ flex: 1, padding: "13px", background: "transparent", border: "1px solid #2a2a2a", borderRadius: 14, color: "#888", fontSize: 14, cursor: "pointer" }}>
          Otra Recomendación
        </button>
        <button onClick={onSkip}
          style={{ flex: 1, padding: "13px", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 14, color: "#ccc", fontSize: 14, cursor: "pointer" }}>
          Más Lugares
        </button>
      </div>
    </div>
  );
};

const CATS = [
  { label: "Todos",           match: () => true },
  { label: "Tacos",           match: t => /taco|árabe|arabe|birria/i.test(t) },
  { label: "Burgers",         match: t => /burger|hamburgues|fast food/i.test(t) },
  { label: "Mexicana",        match: t => /casera|poblana|mexican|antojito|cemita|torta|sándwich|sandwich/i.test(t) },
  { label: "Desayunos",       match: t => /desayuno|brunch/i.test(t) },
  { label: "Pizza",           match: t => /pizza|italian/i.test(t) },
  { label: "Mariscos",        match: t => /marisco|ceviche|aguachile|pescad/i.test(t) },
  { label: "Carnes",          match: t => /carne|parrilla|asador|argentin|brasil|bbq/i.test(t) },
  { label: "Ramen & Japonesa",match: t => /ramen|japon|japones|japonesa|asian|asiática|asiatica|sushi/i.test(t) },
  { label: "Café & Postres",  match: t => /café|cafe|postre|panader|helad|dulce/i.test(t) },
  { label: "Pub & Bar",       match: t => /pub|bar|botan|cervec/i.test(t) },
  { label: "Buffet",          match: t => /buffet/i.test(t) },
];

const approvedImagePlaces = places => places.filter(place => isApprovedPlace(place) && place.image);

const categoryRepresentative = (category, places, seed = 0, salt = 0) => {
  const candidates = approvedImagePlaces(places).filter(place => category.match(place.tipo || ""));
  if (!candidates.length) return null;
  return candidates[(seed + salt) % candidates.length];
};

const categoryCollage = (places, seed = 0) => {
  const candidates = approvedImagePlaces(places);
  if (!candidates.length) return [];
  const offset = seed % candidates.length;
  const rotated = [...candidates.slice(offset), ...candidates.slice(0, offset)];
  const selected = [];
  const seenTypes = new Set();
  for (const place of rotated) {
    const typeKey = String(place.tipo || place.id).toLowerCase();
    if (!seenTypes.has(typeKey)) {
      selected.push(place);
      seenTypes.add(typeKey);
    }
    if (selected.length === 4) break;
  }
  if (selected.length < 4) {
    for (const place of rotated) {
      if (!selected.some(item => item.id === place.id)) selected.push(place);
      if (selected.length === 4) break;
    }
  }
  return selected;
};

// Shuffle basePlaces once on module load (random order every page load)
const shuffledBasePlaces = shuffle(basePlaces);

export default function App() {
  const { clearSessionLocation, deferLocationForSession, dismissLocationDialog, initializeStartup, isLocationDialogOpen, requestLocationForFeature } = useLocationAccess();
  const [significantInteractionPending, setSignificantInteractionPending] = useState(false);
  const [places, setPlaces] = useState(shuffledBasePlaces);
  const [deepLinkRef, setDeepLinkRef] = useState(null);
  const [reviews, setReviews] = useState(initialReviews);
  const [cachedUsers, setCachedUsers] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [reviewsLoaded, setReviewsLoaded] = useState(false);
  const [restaurantsLoaded, setRestaurantsLoaded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [newChatRequest, setNewChatRequest] = useState(0);
  const [splashVisible, setSplashVisible] = useState(false);
  const [splashMode, setSplashMode] = useState("buscando"); // "buscando" | "llevando"
  const [splashDuration, setSplashDuration] = useState(1.4);
  const [highlightLlevame, setHighlightLlevame] = useState(false);
  const handleVenaqqi = (place) => {
    setSplashMode("llevando");
    const delay = 400 + Math.random() * 600; // aleatorio, nunca pasa de 1s
    setSplashDuration(delay / 1000);
    setSplashVisible(true);
    setTimeout(() => {
      setSplashVisible(false);
      setChatOpen(false);   // cerrar el chat de Don D' que estaba encima
      setIsSuerte(false);   // asegurar que Detail (selected && !isSuerte) se renderice
      setHighlightLlevame(true); // resaltar botón Llévame al llegar desde Don D'
      setSelected(place);
    }, delay);
  };
  const [donDOnline, setDonDOnline] = useState(true);
  const [isOffline, setIsOffline] = useState(typeof navigator !== "undefined" ? !navigator.onLine : false);
  const [restaurantsError, setRestaurantsError] = useState(null);
  const [hasSpokenToDonde, setHasSpokenToDonde] = useState(false);
  const [chatInitialMsg, setChatInitialMsg] = useState(null);
  const [userInitialMsg, setUserInitialMsg] = useState(null);
  const [isSuerte, setIsSuerte] = useState(false);
  const [suerteSlots, setSuerteSlots] = useState([]);
  const [filter, setFilter] = useState("Todos");
  const search = "";
  const [highlighted, setHighlighted] = useState([]);
  const [user, setUser] = useState(null);
  const [accessMode, setAccessMode] = useState(readPersistedAccessMode);
  const [legalDocument, setLegalDocument] = useState(null);
  const [showPolicies, setShowPolicies] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackOrigin, setFeedbackOrigin] = useState("manual_menu");
  const [feedbackHasPrevious, setFeedbackHasPrevious] = useState(false);
  const [legalOpenedFromPolicies, setLegalOpenedFromPolicies] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [pendingReview, setPendingReview] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showAuthChoice, setShowAuthChoice] = useState(false);
  const [authGateReason, setAuthGateReason] = useState("add_place");
  const openDonD = (initialText = null) => {
    if (initialText) setUserInitialMsg(initialText);
    if (!user?.fullReg) {
      setAuthGateReason("don_d");
      setShowAuthChoice(true);
      return;
    }
    setChatInitialMsg(null);
    setChatOpen(true);
  };
  const [showAddPlace, setShowAddPlace] = useState(false);
  const [showProfile, setShowProfile] = useState(false); // 6.1 — perfil de usuario
  const [profileResumeState, setProfileResumeState] = useState({ tab: "places", scrollTop: 0 });
  const [profileContributionDetail, setProfileContributionDetail] = useState(null);
  const [showExplore, setShowExplore] = useState(false);
  const [showExploreHint, setShowExploreHint] = useState(false);
  const [exploreShake, setExploreShake] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [fromOnboarding, setFromOnboarding] = useState(false);
  const [imageViewer, setImageViewer] = useState(null);
  const [adminImageViewer, setAdminImageViewer] = useState(null);
  const [altSelected, setAltSelected] = useState(null); // { images: [], startIndex: 0 }
  const [onboardingPlace, setOnboardingPlace] = useState(null);
  const [onboardingMsg, setOnboardingMsg] = useState("");
  const [sessionRestoring, setSessionRestoring] = useState(true);
  useEffect(() => {
    if (!sessionRestoring && user?.fullReg && authGateReason === "don_d") {
      setShowAuthChoice(false);
      setAuthGateReason("add_place");
      setChatOpen(true);
    }
  }, [authGateReason, sessionRestoring, user?.fullReg]);
  const feedbackAudience = sessionRestoring ? null : feedbackAudienceForUser(user?.userId);
  const { showInvitation: showFeedbackInvitation, rejectInvitation: rejectFeedbackInvitation, acceptInvitation: acceptFeedbackInvitation, completeSignificantInteraction, deferInvitation: deferFeedbackInvitation } = useActiveFeedbackInvitation(feedbackAudience);
  // Bienvenida — onboarding de primer uso
  const [showWelcome, setShowWelcome] = useState(false);
  const [adminTutorialOpen, setAdminTutorialOpen] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [confirmSkip, setConfirmSkip] = useState(false); // B12 (ID 50)
  const [onboardingPresupuesto, setOnboardingPresupuesto] = useState(null);
  const [registerFromOnboarding, setRegisterFromOnboarding] = useState(false);
  const [authFromOnboarding, setAuthFromOnboarding] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [adminTab, setAdminTab] = useState("lugares");
  const [adminAuthorizedUserId, setAdminAuthorizedUserId] = useState(null);
  const [adminAuthRevision, setAdminAuthRevision] = useState(0);
  const adminVerificationRef = useRef(0);
  const [showRecoModal, setShowRecoModal] = useState(false);
  const [recoVisualSeed, setRecoVisualSeed] = useState(0);
  const openRecoModal = () => {
    setRecoVisualSeed(seed => seed + 1);
    setShowRecoModal(true);
  };
  const [adminUsers, setAdminUsers] = useState({});
  const [adminUsersFecha, setAdminUsersFecha] = useState({});
  const [adminUserEmails, setAdminUserEmails] = useState({});
  const [adminContributorProfiles, setAdminContributorProfiles] = useState({});
  const [adminPreview, setAdminPreview] = useState(null); // { type: "place"|"review", item }
  const [adminEditPlace, setAdminEditPlace] = useState(null);
  const [adminEditReview, setAdminEditReview] = useState(null);
  const [adminPageLugaresAprob, setAdminPageLugaresAprob] = useState(1);
  const [adminPageResenasAprob, setAdminPageResenasAprob] = useState(1);
  const [adminPageUsuarios, setAdminPageUsuarios] = useState(1);
  const [adminUserDetail, setAdminUserDetail] = useState(null); // id de usuario expandido en tab Usuarios
  const adminListScrollRef = useRef(null);
  const adminReturnContextRef = useRef(null);
  const lastTrackedPlaceRef = useRef({ key: null, at: 0 });
  useEffect(() => {
    if (!selected?.id) return;
    void trackProductEvent("place_opened", { place_id: Number(selected.id), place_name: selected.name || null, source: isSuerte ? "recommendation" : "catalog" });
  }, [selected?.id, selected?.name, isSuerte]);
  const ADMIN_PAGE_SIZE = 10;
  const isAdmin = !!user?.userId && adminAuthorizedUserId === user.userId;
  const adminContributor = (userId) => contributorIdentity(adminContributorProfiles[userId]);
  const openAdminPreview = (preview) => {
    adminReturnContextRef.current = captureAdminPreviewContext({
      preview,
      tab: adminTab,
      scrollTop: adminListScrollRef.current?.scrollTop || 0,
      pageLugaresAprob: adminPageLugaresAprob,
      pageResenasAprob: adminPageResenasAprob,
      pageUsuarios: adminPageUsuarios,
    });
    setAdminPreview(preview);
  };
  const closeAdminPreview = () => {
    const context = adminReturnContextRef.current;
    setAdminPreview(null);
    restoreAdminPreviewContext({
      context,
      getScrollElement: () => adminListScrollRef.current,
      onTab: setAdminTab,
      onPages: ({ pageLugaresAprob, pageResenasAprob, pageUsuarios }) => {
        setAdminPageLugaresAprob(pageLugaresAprob);
        setAdminPageResenasAprob(pageResenasAprob);
        setAdminPageUsuarios(pageUsuarios);
      },
    });
  };
  const verifyAdminAccess = useCallback(async () => {
    const verification = ++adminVerificationRef.current;
    const expectedUserId = user?.userId;
    const allowed = !sessionRestoring && expectedUserId
      ? await verifyCurrentAdminAccess(expectedUserId)
      : false;
    if (verification === adminVerificationRef.current) {
      setAdminAuthorizedUserId(allowed ? expectedUserId : null);
    }
    return allowed;
  }, [sessionRestoring, user?.userId]);

  useEffect(() => { verifyAdminAccess(); }, [adminAuthRevision, verifyAdminAccess]);
  useEffect(() => {
    if (isAdmin) return;
    setShowAdmin(false);
    setAdminPreview(null);
    setAdminEditPlace(null);
    setAdminEditReview(null);
    setAdminImageViewer(null);
    setAdminTutorialOpen(false);
    setAdminUsers({});
    setAdminUsersFecha({});
    setAdminUserEmails({});
    setAdminContributorProfiles({});
  }, [isAdmin]);
  const [blockedUsers, setBlockedUsers] = useState(getBlockedUsers); // 5.2 — [{id, username}]
  // Resenas visibles: pending solo se muestran en panel admin
  // 5.2 — el contenido de usuarios bloqueados por este dispositivo no se muestra (el admin sí lo ve)
  const visibleReviews = useMemo(() => {
    const blockedIds = new Set(blockedUsers.map(b => b.id));
    return reviews.filter(r => (!r.pending || isAdmin) && (isAdmin || !blockedIds.has(r.userid)));
  }, [reviews, isAdmin, blockedUsers]);
  // Índice placeId -> reseñas: un solo pase en vez de un filter por card en cada render
  const reviewsByPlace = useMemo(() => {
    const m = new Map();
    visibleReviews.forEach(r => { const k = Number(r.placeid || r.placeId); if (!m.has(k)) m.set(k, []); m.get(k).push(r); });
    return m;
  }, [visibleReviews]);

  useEffect(() => {
    if (isSuerte) return;
    const activePlace = altSelected || selected;
    if (!activePlace) return;
    const activeReviews = visibleReviews.filter(r => Number(r.placeid || r.placeId) === Number(activePlace.id));
    const activeRelated = buildRelatedPlaces(activePlace, places, visibleReviews);
    const nextContext = {
      placeId: activePlace.id,
      placeName: activePlace.name,
      tipo: activePlace.tipo,
      comentario: activeReviews[activeReviews.length - 1]?.comentario || "",
      platillos: activeReviews.map(r => r.platillo).filter(Boolean).slice(-3).reverse(),
      tambienVisibles: activeRelated.map(p => ({ name: p.name, tipo: p.tipo })),
    };
    setDonDContext(prev => {
      const samePlace = String(prev?.placeId ?? "") === String(nextContext.placeId ?? "");
      const sameComment = (prev?.comentario || "") === nextContext.comentario;
      const sameDishes = JSON.stringify(prev?.platillos || []) === JSON.stringify(nextContext.platillos);
      const sameRelated = JSON.stringify(prev?.tambienVisibles || []) === JSON.stringify(nextContext.tambienVisibles);
      return samePlace && sameComment && sameDishes && sameRelated ? prev : nextContext;
    });
  }, [altSelected, selected, isSuerte, visibleReviews, places]);
  // Handler estable: si se recrea en cada render, memo(Card) no sirve de nada
  const handleViewImage = useCallback((imgs, idx, metas) => setImageViewer({ images: imgs, startIndex: idx, metas: metas || null }), []);
  const [showDondeHint, setShowDondeHint] = useState(false);
  const [logoPulsing, setLogoPulsing] = useState(false);
  const [logoPressing, setLogoPressing] = useState(false);
  // B12 (IDs 44-46) — primer uso: aviso 5-6s antes de auto-recomendar; tap al logo adelanta; otra interacción cancela
  const [autoRecoPending, setAutoRecoPending] = useState(false);
  const autoRecoTimer = useRef(null);
  const cancelAutoReco = (keepGoing = false) => {
    if (autoRecoTimer.current) { clearTimeout(autoRecoTimer.current); autoRecoTimer.current = null; }
    setAutoRecoPending(false);
    if (!keepGoing) setLogoPulsing(false);
  };
  const startAutoReco = () => {
    setLogoPulsing(true);
    setAutoRecoPending(true);
    autoRecoTimer.current = setTimeout(() => {
      autoRecoTimer.current = null;
      setAutoRecoPending(false);
      setLogoPulsing(false);
      setLogoPressing(true);
      setTimeout(() => { setLogoPressing(false); handleSuerte(); }, 500);
    }, 5500);
  };
  // Tutorial contextual — 0=inactivo; 1=buscar; 2=abrir menú; 3=Perfil/login.
  const [tutorialStep, setTutorialStep] = useState(0);
  const resumeTutorial = () => setTutorialStep(readTutorialProgress()?.step || 1);
  const pauseTutorial = () => { if (tutorialStep > 0) saveTutorialProgress(tutorialStep); setTutorialStep(0); };
  const finishTutorial = () => { clearTutorialProgress(); markTutorialCompleted(); setTutorialStep(0); };
  useEffect(() => { if (tutorialStep > 0) saveTutorialProgress(tutorialStep); }, [tutorialStep]);
  // Contexto visible para Don D' (lugar abierto en modal o SuerteView)
  const [donDContext, setDonDContext] = useState(null);
  // Don D' FAB arrastrable — side: "left"|"right", y: px desde top (null=posición default)
  const loadFabPos = (key, def) => { try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : def; } catch { return def; } };
  const [arrowPos, setArrowPos] = useState(() => loadFabPos('vq_fab_arrow', { side: "right", y: null, slot: "above_add" }));
  const arrowDragRef = useRef({ dragging: false, startY: 0, startX: 0, startPosY: 0 });
  const [scrollToast, setScrollToast] = useState(false);
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const PLACES_PER_PAGE = 12;
  // Buscador inteligente
  const [smartSearch, setSmartSearch] = useState("");
  // Versión debounced (250ms): el filtrado pesado usa smartQ, el input sigue usando smartSearch
  const [smartQ, setSmartQ] = useState("");
  useEffect(() => { const t = setTimeout(() => setSmartQ(smartSearch), 250); return () => clearTimeout(t); }, [smartSearch]);
  const [smartSearchFocused, setSmartSearchFocused] = useState(false);

  // Pick random place on mount for onboarding

  // Detección de conexión — banner honesto en vez de fallo silencioso
  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  useEffect(() => {
    const normalExperience = !sessionRestoring && !showWelcome && !showAuth && !showRegister && !showPasswordReset && !showPolicies && !showFeedback && !legalDocument && !showAdmin;
    if (normalExperience) initializeStartup();
  }, [initializeStartup, legalDocument, sessionRestoring, showAdmin, showAuth, showFeedback, showPasswordReset, showPolicies, showRegister, showWelcome]);

  // Deep link al montar — resolver con basePlaces si el lugar está ahí
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lugarSlug = params.get("lugar");
    const ref = params.get("ref");
    const openTarget = params.get("open");
    const notificationId = params.get("notification");
    if (openTarget || notificationId) {
      sessionStorage.setItem("venaqqi_pending_notification_route", JSON.stringify({ openTarget, notificationId }));
    }
    if (!lugarSlug) return;
    if (ref) setDeepLinkRef(ref);
    const match = basePlaces.find(p => toSlug(p.name) === lugarSlug);
    if (match) { setSelected(match); window.history.replaceState(window.history.state, "", window.location.pathname); }
    // Si no está en basePlaces, se resolverá cuando carguen los de Supabase
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sessionRestoring) return;
    const raw = sessionStorage.getItem("venaqqi_pending_notification_route");
    if (!raw) return;
    let pending = null;
    try { pending = JSON.parse(raw); } catch { sessionStorage.removeItem("venaqqi_pending_notification_route"); return; }
    if (!user?.fullReg || !user?.userId) {
      if (pending?.openTarget || pending?.notificationId) setShowAuth(true);
      return;
    }
    if (pending.notificationId) {
      supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("id", pending.notificationId).eq("user_id", user.userId).then(() => {});
    }
    if (pending.openTarget === "profile" || pending.openTarget === "notifications") setShowProfile(true);
    if (pending.openTarget === "don-d") { setChatInitialMsg(null); setChatOpen(true); }
    sessionStorage.removeItem("venaqqi_pending_notification_route");
    if (window.location.search) window.history.replaceState(window.history.state, "", window.location.pathname);
  }, [sessionRestoring, user?.fullReg, user?.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // VENAQQI_LOGIN_PUSH_REPAIR — browser subscription can survive logout/relogin.
  // Re-bind it to the authenticated account and replay pending notifications.
  useEffect(() => {
    if (sessionRestoring || !user?.fullReg || !user?.userId) return;
    if (!pushCapability().supported || window.Notification?.permission !== "granted") return;
    readPushState({ supabase, userId: user.userId }).catch(() => {});
  }, [sessionRestoring, user?.fullReg, user?.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar configuracion Don D' desde Supabase
  useEffect(() => {
    const loadSettings = async () => {
      const { data } = await supabase.from("settings").select("value").eq("key", "don_d_online").single();
      if (data) setDonDOnline(data.value === "true");
      else setDonDOnline(true);
    };
    loadSettings();
  }, []);

  useEffect(() => {
    const pool = [...basePlaces].filter(p => initialReviews.filter(r => Number(r.placeid || r.placeId) === Number(p.id)).length > 0);
    if (!pool.length) return;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    const rs = initialReviews.filter(r => Number(r.placeid || r.placeId) === Number(pick.id));
    const best = rs[rs.length - 1];
    setOnboardingPlace(pick);
    setOnboardingMsg("¡Qué onda! 👋 Soy Don D', tu guía en VENAQQI. Hoy te recomiendo **" + pick.name + "** — " + (best?.comentario || pick.tipo + " en " + pick.location) + ". ¿Se te antoja?");
  }, []);

  // Sesión activa — detectar usuario al cargar y escuchar cambios
  useEffect(() => {
    // Primero registrar onAuthStateChange ANTES de cualquier otra llamada
    // para que PASSWORD_RECOVERY se detecte cuando Supabase procese el hash
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setAdminAuthRevision(value => value + 1);
      if (event === "SIGNED_OUT") setAdminAuthorizedUserId(null);
      try {
        if (event === "PASSWORD_RECOVERY") {
          setShowPasswordReset(true);
          return;
        }
        if (event === "SIGNED_IN" && session?.user) {
          const userData = await ensureUserProfile(session.user);
          const u = toAppUser(session.user, userData);
          setUser(u);
          setAccessMode(ACCESS_MODES.AUTHENTICATED);
          persistAccessMode(ACCESS_MODES.AUTHENTICATED);
          localStorage.setItem('venaqqi_user', JSON.stringify(u));
          setShowWelcome(false);
        }
        if (event === "SIGNED_OUT") {
          setUser(null);
          setAccessMode(ACCESS_MODES.GUEST);
          persistAccessMode(ACCESS_MODES.GUEST);
          localStorage.removeItem('venaqqi_user');
        }
      } catch (err) {
        console.error('Auth state change error:', err);
        setSessionToast({ type: "error", text: "❌ " + (err?.message || "Error al crear/cargar perfil") });
        setTimeout(() => setSessionToast(null), 5000);
      }
    });

    const restoreSession = async () => {
      let hasUser = false;
      try {
        // Si hay hash de recuperación en la URL, dejar que onAuthStateChange lo maneje
        // NO limpiar el hash aquí — Supabase necesita leerlo para disparar PASSWORD_RECOVERY
        const hash = window.location.hash;
        if (hash && hash.includes("type=recovery")) {
          setSessionRestoring(false);
          return;
        }
        const { data: { session } } = await withTimeout(
          supabase.auth.getSession(),
          SUPABASE_TIMEOUT_MS,
          "Supabase no respondió al restaurar la sesión."
        );
        if (session?.user) {
          const userData = await ensureUserProfile(session.user);
          const u = toAppUser(session.user, userData);
          setUser(u);
          setAccessMode(ACCESS_MODES.AUTHENTICATED);
          persistAccessMode(ACCESS_MODES.AUTHENTICATED);
          localStorage.setItem('venaqqi_user', JSON.stringify(u));
          setShowWelcome(false);
          hasUser = true;
        }
      } catch (err) {
        console.error('Session restore error:', err);
        setSessionToast({ type: "error", text: "❌ " + (err?.message || "Error al restaurar perfil") });
        setTimeout(() => setSessionToast(null), 5000);
      } finally {
        // Decidir ANTES de quitar el loading — sin hueco
        if (!hasUser) {
          setAccessMode(ACCESS_MODES.GUEST);
          persistAccessMode(ACCESS_MODES.GUEST);
          setShowWelcome(false);
        }
        setSessionRestoring(false);
      }
    };

    restoreSession();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const restoredUiStateRef = useRef(readUiState());
  useEffect(() => {
    const saved = restoredUiStateRef.current;
    if (!saved || sessionRestoring || !restaurantsLoaded) return;
    const selectedMatch = saved.selectedPlaceId ? places.find(place => String(place.id) === saved.selectedPlaceId) : null;
    const alternateMatch = saved.alternatePlaceId ? places.find(place => String(place.id) === saved.alternatePlaceId) : null;
    const savedContextPlaceId = saved.donDContext?.placeId || saved.donDContextPlaceId;
    const contextPlace = savedContextPlaceId ? places.find(place => String(place.id) === String(savedContextPlaceId)) : null;
    const context = saved.donDContext ? {
      ...saved.donDContext,
      placeId: saved.donDContext.placeId || contextPlace?.id || null,
      placeName: saved.donDContext.placeName || contextPlace?.name || "",
      tipo: saved.donDContext.tipo || contextPlace?.tipo || "",
    } : contextPlace ? { placeId: contextPlace.id, placeName: contextPlace.name, tipo: contextPlace.tipo } : null;

    if (saved.smartSearch) setSmartSearch(saved.smartSearch);
    if (saved.filter) setFilter(saved.filter);
    if (selectedMatch) setSelected(selectedMatch);
    if (alternateMatch) setAltSelected(alternateMatch);
    if (selectedMatch && saved.isSuerte) setIsSuerte(true);
    if (saved.suerteSlotIds?.length) setSuerteSlots(saved.suerteSlotIds.map(id => places.find(place => String(place.id) === id)).filter(Boolean));
    if (context) setDonDContext(context);
    if (saved.profileResumeState) setProfileResumeState(saved.profileResumeState);
    if (saved.profileContributionDetail && user?.fullReg) {
      const detailPlace = places.find(place => String(place.id) === saved.profileContributionDetail.placeId);
      if (detailPlace) setProfileContributionDetail({ ...saved.profileContributionDetail, place: detailPlace, entry: null });
    }
    if (saved.showProfile && user?.fullReg) setShowProfile(true);
    if (saved.showAddPlace && user?.fullReg) setShowAddPlace(true);
    if (saved.currentPage) setCurrentPage(saved.currentPage);
    if (saved.chatOpen && user?.fullReg) setChatOpen(true);
    const timer = setTimeout(() => window.scrollTo({ top: saved.scrollY || 0, behavior: "auto" }), 0);
    restoredUiStateRef.current = null;
    return () => clearTimeout(timer);
  }, [places, restaurantsLoaded, sessionRestoring, user?.fullReg]);

  const persistUiSnapshot = useCallback(() => saveUiState({
    scrollY: window.scrollY,
    selectedPlaceId: selected?.id,
    alternatePlaceId: altSelected?.id,
    isSuerte,
    suerteSlotIds: suerteSlots.map(place => place.id),
    showProfile,
    profileResumeState,
    profileContributionDetail: profileContributionDetail ? {
      type: profileContributionDetail.type,
      placeId: profileContributionDetail.place?.id,
      reviewId: profileContributionDetail.reviewId,
    } : null,
    showAddPlace,
    currentPage,
    smartSearch,
    filter,
    chatOpen,
    donDContext,
  }), [altSelected?.id, chatOpen, currentPage, donDContext, filter, isSuerte, profileContributionDetail, profileResumeState, selected?.id, showAddPlace, showProfile, smartSearch, suerteSlots]);

  useEffect(() => {
    const hidden = () => { if (document.visibilityState === "hidden") persistUiSnapshot(); };
    window.addEventListener("pagehide", persistUiSnapshot);
    document.addEventListener("visibilitychange", hidden);
    return () => {
      window.removeEventListener("pagehide", persistUiSnapshot);
      document.removeEventListener("visibilitychange", hidden);
    };
  }, [persistUiSnapshot]);

  useEffect(() => {
    const saveBeforeMaps = event => {
      const anchor = event.target?.closest?.("a[href]");
      if (anchor && isMapsDestination(anchor.href)) persistUiSnapshot();
    };
    document.addEventListener("click", saveBeforeMaps, true);
    return () => document.removeEventListener("click", saveBeforeMaps, true);
  }, [persistUiSnapshot]);
  useEffect(() => {
    const offerRefresh = shouldOfferTutorialRefresh();
    if (offerRefresh && user?.fullReg) setTutorialStep(1);
    markActiveNow();
    const touch = () => markActiveNow();
    window.addEventListener("focus", touch); document.addEventListener("visibilitychange", touch);
    return () => { window.removeEventListener("focus", touch); document.removeEventListener("visibilitychange", touch); };
  }, [user?.fullReg]);

  useEffect(() => { trackMarketingEvent({ supabase, eventName: "landing" }); }, []);
  useEffect(() => { if (selected?.id) trackMarketingEvent({ supabase, eventName: "place_opened", metadata: { place_id: selected.id } }); }, [selected?.id]);

  // Block body scroll when any overlay is open
  useEffect(() => {
    const isBlocked = selected || chatOpen || showAuth || showRegister || showAddPlace || showExplore || showOnboarding || showWelcome || showPolicies || showFeedback || legalDocument || sessionRestoring; // B13 (IDs 74-76) — splash/bienvenida fijas: sin scroll ni contenido visible detrás
    document.body.style.overflow = isBlocked ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selected, chatOpen, showAuth, showRegister, showAddPlace, showExplore, showOnboarding, showWelcome, showPolicies, showFeedback, legalDocument, sessionRestoring]);



  const discoverablePlaces = useMemo(() => publicPlaceList(places), [places]);
  const filtered = useMemo(() => discoverablePlaces.filter(p => {
    // Ocultar pending: solo visible para quien lo subio + admin
    if (isPendingPlace(p) && !isAdmin && p.addedBy !== user?.userId) return false;
    const cat = CATS.find(c => c.label === filter);
    const matchCat = filter === "Todos" || (cat && cat.match(p.tipo));
    const q = normalize(search);
    const matchSearch = !q || normalize(p.name).includes(q) || normalize(p.tipo).includes(q) || p.tags.some(t => normalize(t).includes(q)) || normalize(p.location).includes(q);
    // Búsqueda inteligente adicional (incluye comentarios de reseñas)
    const sq = normalize(smartQ.trim());
    let matchSmart = !sq;
    if (sq) {
      const inPlace = normalize(p.name).includes(sq) || normalize(p.tipo).includes(sq) || (p.tags || []).some(t => normalize(t).includes(sq)) || normalize(p.location || "").includes(sq);
      const inReviews = (reviewsByPlace.get(Number(p.id)) || []).some(r => normalize(r.comentario || "").includes(sq) || normalize(r.platillo || "").includes(sq));
      matchSmart = inPlace || inReviews;
    }
    return matchCat && matchSearch && matchSmart;
  }), [discoverablePlaces, reviewsByPlace, filter, search, smartQ, isAdmin, user]);
  const sorted = useMemo(() => [...filtered].sort((a, b) => (highlighted.includes(b.id) ? 1 : 0) - (highlighted.includes(a.id) ? 1 : 0)), [filtered, highlighted]);
  // Paginación
  const totalPages = Math.max(1, Math.ceil(sorted.length / PLACES_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = sorted.slice((safePage - 1) * PLACES_PER_PAGE, safePage * PLACES_PER_PAGE);
  const showCatalogRows = shouldUseCatalogRows({ smartQuery: smartQ, searchQuery: search });
  const catalogRows = useMemo(() => (showCatalogRows
    ? buildCatalogRows({ places: sorted, categories: CATS, activeFilter: filter })
    : []), [showCatalogRows, sorted, filter]);

  // Sugerencias del buscador inteligente (debounced + memoizado)
  const smartSuggestions = useMemo(() => {
    const q = normalize(smartQ.trim());
    if (!q || q.length < 2) return null;
    const lugares = discoverablePlaces.filter(p => normalize(p.name).includes(q)).slice(0, 3).map(p => ({ tipo: "lugar", id: p.id, name: p.name, sub: `${p.tipo} · ${p.location || ""}` }));
    const tiposSet = new Set();
    discoverablePlaces.forEach(p => { if (normalize(p.tipo || "").includes(q)) tiposSet.add(p.tipo); });
    const tipos = [...tiposSet].slice(0, 2).map(t => ({ tipo: "tipo", label: t, count: discoverablePlaces.filter(p => p.tipo === t).length }));
    const enResenas = reviews.filter(r => normalize(r.comentario || "").includes(q) || normalize(r.platillo || "").includes(q)).slice(0, 2).map(r => {
      const place = discoverablePlaces.find(p => Number(p.id) === Number(r.placeid || r.placeId));
      return place ? { tipo: "resena", placeId: place.id, placeName: place.name, snippet: (r.platillo || r.comentario || "").substring(0, 60) } : null;
    }).filter(Boolean);
    const total = lugares.length + tipos.length + enResenas.length;
    return { lugares, tipos, enResenas, total };
  }, [discoverablePlaces, reviews, smartQ]);

  // Reset paginación cuando cambia filtro o búsqueda
  useEffect(() => { setCurrentPage(1); }, [filter, search, smartQ]);
  const scrollToGrid = () => {
    const el = document.getElementById("venaqqi-grid");
    el?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const promptAdminComment = () => {
    const value = window.prompt("Comentario adicional opcional para el usuario (máx. 150 caracteres):", "");
    if (value === null) return null;
    return value.trim().slice(0, 150);
  };
  const approvePlace = async (item) => {
    const comment = promptAdminComment();
    if (comment === null) return;
    const { data, error } = await supabase.rpc("admin_moderate_place", { p_id: item.id, p_status: "approved", p_reason: null, p_comment: comment || null });
    if (!error) { const updated = data || {}; setPlaces(prev => prev.map(pl => pl.id === item.id ? { ...pl, moderationStatus: "approved", moderation_status: "approved", pending: false, metadata: updated.metadata || { ...(pl.metadata || {}), rejected: false, notifSeen: false }, moderation_comment: updated.moderation_comment || comment || null } : pl)); }
  };
  const rejectPlace = async (item) => {
    const reason = window.prompt(`Motivo del rechazo de "${item.name}" (obligatorio y persistente):`, "");
    if (reason === null || !reason.trim()) return;
    const comment = promptAdminComment();
    if (comment === null) return;
    const { data, error } = await supabase.rpc("admin_moderate_place", { p_id: item.id, p_status: "rejected", p_reason: reason.trim(), p_comment: comment || null });
    if (!error) { const updated = data || {}; setPlaces(prev => prev.map(pl => pl.id === item.id ? { ...pl, moderationStatus: "rejected", moderation_status: "rejected", pending: false, metadata: updated.metadata || { ...(pl.metadata || {}), rejected: true, reject_reason: reason.trim(), notifSeen: false }, moderation_comment: updated.moderation_comment || comment || null } : pl)); }
  };
  const approveReview = async (item) => {
    const comment = promptAdminComment();
    if (comment === null) return;
    const { data, error } = await supabase.rpc("admin_moderate_review", { p_id: item.id, p_status: "approved", p_reason: null, p_comment: comment || null });
    if (!error) { const updated = data || {}; setReviews(prev => prev.map(rv => rv.id === item.id ? { ...rv, pending: false, moderation_status: "approved", metadata: updated.metadata || { ...(rv.metadata || {}), rejected: false, notifSeen: false }, moderation_comment: updated.moderation_comment || comment || null } : rv)); }
  };
  const rejectReview = async (item) => {
    const reason = window.prompt(`Motivo del rechazo de la reseña de "${item.username}" (obligatorio y persistente):`, "");
    if (reason === null || !reason.trim()) return;
    const comment = promptAdminComment();
    if (comment === null) return;
    const { data, error } = await supabase.rpc("admin_moderate_review", { p_id: item.id, p_status: "rejected", p_reason: reason.trim(), p_comment: comment || null });
    if (!error) { const updated = data || {}; setReviews(prev => prev.map(rv => rv.id === item.id ? { ...rv, pending: false, moderation_status: "rejected", metadata: updated.metadata || { ...(rv.metadata || {}), rejected: true, reject_reason: reason.trim(), notifSeen: false }, moderation_comment: updated.moderation_comment || comment || null } : rv)); }
  };
  const editOwnPendingAdminReview = review => {
    if (!canAdminEditOwnPendingReview({ isAdmin, currentUserId: user?.userId, review })) return;
    setAdminEditReview(review);
  };
  const saveOwnPendingAdminReview = async editedReview => {
    const original = adminEditReview;
    if (!canAdminEditOwnPendingReview({ isAdmin, currentUserId: user?.userId, review: original })) {
      setSessionToast({ type: "error", text: "❌ Esta publicación ya no puede editarse" });
      return;
    }
    const { rpcArgs, localReview } = buildAdminReviewUpdate({ review: original, editedReview, placeId: editedReview.placeid });
    const { error } = await supabase.rpc("admin_update_own_pending_review", rpcArgs);
    if (error) {
      setSessionToast({ type: "error", text: `❌ ${error.message || "No se pudo guardar"}` });
      setTimeout(() => setSessionToast(null), 4000);
      return;
    }
    setReviews(previous => previous.map(review => review.id === original.id ? localReview : review));
    setAdminPreview(previous => previous?.type === "review" && previous.item.id === original.id ? { ...previous, item: localReview } : previous);
    setAdminEditReview(null);
    setSessionToast({ type: "success", text: "✅ Publicación actualizada; continúa pendiente" });
    setTimeout(() => setSessionToast(null), 3000);
  };
  const editOwnPendingAdminPlaceReview = place => {
    const review = findAdminEditableReviewForPlace({ isAdmin, currentUserId: user?.userId, place, reviews });
    if (!review) return;
    editOwnPendingAdminReview(review);
  };

  const handleAddPlace = async (newPlace, firstReview) => {
    // Si el usuario subió fotos, usar la primera como imagen del lugar
    const userImages = firstReview.imagenes || [];
    if (userImages.length > 0 && userImages[0].url) {
      newPlace.image = userImages[0].url;
      firstReview.imagenes = userImages.slice(1); // ya quedó como portada, no repetirla dentro de la reseña
    }

    // 1. Guardar lugar en Supabase (sin id, lo genera la BD)
    const placePayload = {
      name: newPlace.name,
      tipo: newPlace.tipo,
      primary_category: newPlace.primary_category || newPlace.tipo,
      categories: newPlace.categories || [newPlace.tipo],
      location: newPlace.location,
      image: newPlace.image,
      maps: newPlace.maps,
      tags: newPlace.tags,
      moderation_status: isAdmin ? "approved" : "pending",
      pending: !isAdmin,
      added_by: newPlace.addedBy,
      metadata: { addedAt: newPlace.addedAt, ...locationMetadata(newPlace) }
    };
    const { data: insertedPlace, error: placeError } = await withTimeout(supabase
      .from("restaurants")
      .insert([placePayload])
      .select()
      .single(), SUPABASE_TIMEOUT_MS, "El servidor no respondió al guardar el lugar.");

    if (placeError) {
      console.error('❌ Error guardando lugar:', placeError.message);
      throw new Error(`No se pudo guardar el lugar: ${placeError.message}`);
    }
    log('✅ Lugar guardado en Supabase con id:', insertedPlace.id);

    // Asociar y persistir la reseña antes de publicar ambos objetos en memoria.
    const placeWithRealId = { ...newPlace, id: insertedPlace.id, catalogSource: "db", moderationStatus: insertedPlace.moderation_status || "pending", pending: insertedPlace.pending ?? true };
    const reviewWithRealPlaceId = { ...firstReview, placeid: insertedPlace.id };

    const { id: _discardId, ...rest } = reviewWithRealPlaceId;
    const reviewPayload = { ...rest, pending: isAdmin ? false : (rest.pending ?? true), moderation_status: isAdmin ? "approved" : "pending", metadata: { ...rest, rejected: false, notifSeen: isAdmin } };
    const { error: reviewError } = await withTimeout(
      supabase.from("reviews").insert([reviewPayload]),
      SUPABASE_TIMEOUT_MS,
      "El servidor no respondió al guardar la reseña."
    );
    if (reviewError) {
      console.error('❌ Error guardando reseña inicial:', reviewError.message, reviewPayload);
      const { error: rollbackError } = await withTimeout(
        supabase.from("restaurants").delete().eq("id", insertedPlace.id),
        SUPABASE_TIMEOUT_MS,
        "No se pudo revertir el lugar incompleto."
      );
      if (rollbackError) warn("No se pudo revertir el lugar sin reseña:", rollbackError.message);
      throw new Error(`No se pudo guardar la reseña: ${reviewError.message}`);
    }
    setPlaces(p => [...p, placeWithRealId]);
    setReviews(p => [...p, reviewWithRealPlaceId]);
    log('✅ Reseña inicial guardada en Supabase (con backup en metadata)');
    trackTesterActivity(TESTER_ACTIVITY_ACTIONS.REVIEW, { userId: user?.userId, dedupeKey: `review:${insertedPlace.id}:${Date.now()}`, dedupeMs: 500 });
    trackMarketingEvent({ supabase, eventName: "place_submitted", metadata: { place_id: insertedPlace.id } });
    return true;
  };




  // ── Fetch reviews from Supabase (con cache en memoria) ───────────────────────────
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        log('🔄 Iniciando fetchReviews...');
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        log('📡 Consultando Supabase...');
        // Trae las 1000 más recientes y las regresa a orden ascendente
        // (el código asume que la última del array es la más nueva)
        const { data, error } = await supabase
          .from('reviews')
          .select('*')
          .order('fecha', { ascending: false })
          .limit(1000)
          .abortSignal(controller.signal);

        clearTimeout(timeoutId);
        log('✅ Respuesta Supabase - data:', data?.length, 'error:', error?.message);

        if (error) {
          warn('Supabase error:', error.message);
          setReviews(initialReviews);
          setReviewsLoaded(true);
        } else if (data && data.length > 0) {
          log('✅ Reviews cargadas:', data.length);
          setReviews(data.reverse().map(r => ({ ...r, imagenes: Array.isArray(r.imagenes) ? r.imagenes : [] })));
          setReviewsLoaded(true);
        } else {
          warn('Supabase sin datos');
          setReviews(initialReviews);
          setReviewsLoaded(true);
        }
      } catch (err) {
        warn('❌ Fetch reviews error:', err.message);
        setReviews(initialReviews);
        setReviewsLoaded(true);
      }
    };
    fetchReviews();
  }, []);

  // ── Fetch restaurants nuevos desde Supabase (se mergean con basePlaces) ───────────────────────────
  useEffect(() => {
    const fetchRestaurants = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), SUPABASE_TIMEOUT_MS);
      try {
        setRestaurantsError(null);
        log('🔄 Iniciando fetchRestaurants...');
        const { data, error } = await supabase
          .from('restaurants')
          .select('*')
          .order('added_at', { ascending: true })
          .limit(1000)
          .abortSignal(controller.signal);

        if (error) {
          warn('Restaurants error:', error.message);
          setRestaurantsError(`No pudimos cargar todos los restaurantes: ${error.message}`);
          return;
        }
        if (data && data.length > 0) {
          log('✅ Restaurants cargados de Supabase:', data.length);
          // Merge: lugares base hardcoded + nuevos de Supabase (sin duplicar IDs)
          const baseIds = new Set(basePlaces.map(p => p.id));
          const nuevos = data.filter(p => !baseIds.has(p.id)).map(p => withCanonicalGoogleMapsUrl({
            id: p.id,
            catalogSource: "db",
            name: p.name,
            tipo: p.tipo,
            primaryCategory: p.primary_category || p.tipo,
            categories: Array.isArray(p.categories) && p.categories.length ? p.categories : [p.tipo].filter(Boolean),
            location: p.location,
            image: p.image,
            maps: p.maps,
            tags: p.tags || [],
            moderationStatus: p.moderation_status,
            pending: p.pending,
            addedBy: p.added_by,
            addedAt: p.added_at,
            metadata: p.metadata || {},
            priceMinMxn: p.price_min_mxn,
            priceMaxMxn: p.price_max_mxn,
            priceVerifiedAt: p.price_verified_at,
          }));
          if (nuevos.length > 0) {
            setPlaces(p => {
              const merged = [...p, ...nuevos];
              // Deep link: resolver ?lugar= ahora que tenemos todos los lugares
              const params = new URLSearchParams(window.location.search);
              const lugarSlug = params.get("lugar");
              const ref = params.get("ref");
              if (lugarSlug) {
                if (ref) setDeepLinkRef(ref);
                const match = merged.find(pl => toSlug(pl.name) === lugarSlug && isApprovedPlace(pl)); // B14 — solo aprobados por link público
                if (match) { setSelected(match); window.history.replaceState(window.history.state, "", window.location.pathname); }
              }
              return merged;
            });
            log(`✅ ${nuevos.length} lugar(es) nuevo(s) agregados`);
          }
        }
      } catch (err) {
        warn('❌ Fetch restaurants error:', err.message);
        const message = err?.name === 'AbortError'
          ? 'Supabase no respondió a tiempo.'
          : (err?.message || 'Error desconocido.');
        setRestaurantsError(`No pudimos cargar todos los restaurantes: ${message}`);
      } finally {
        clearTimeout(timeoutId);
        setRestaurantsLoaded(true);
      }
    };
    fetchRestaurants();
  }, []);

  // ── Fetch users (cache en memoria) ───────────────────────────
  useEffect(() => {
    const fetchUsers = async () => {
      if (cachedUsers.length > 0) return; // Si ya hay cache, no hagas query
      
      const { data, error } = await supabase
        .from('users')
        .select('*');
      if (!error && data) {
        setCachedUsers(data);
      }
    };
    fetchUsers();
  }, [cachedUsers]);

  // ── Scroll hint after 3s inactivity on main screen ───────
  const resetScrollHint = () => {
    setHintFading(false);
    setShowScrollHint(false);
    if (scrollHintTimer.current) clearTimeout(scrollHintTimer.current);
    const isMainScreen = !selected && !isSuerte && !chatOpen && !showOnboarding && !showMenu && !showExplore && !showAuth && !showRegister && !showAddPlace && !imageViewer && !altSelected && !profileContributionDetail && !showPasswordReset && safePage < totalPages;
    if (isMainScreen) {
      const delay = hasInteracted ? 10000 : 3000;
      // 1.10 — máx. 2 apariciones por sesión para no estorbar (en última página ya no se arma: safePage < totalPages)
      scrollHintTimer.current = setTimeout(() => { if (hintShownCount.current < 2) { hintShownCount.current += 1; setShowScrollHint(true); } }, delay);
    }
  };

  const dismissHint = () => {
    if (!showScrollHint) return;
    setHintFading(true);
    setTimeout(() => { setShowScrollHint(false); setHintFading(false); setHintOpacity(1); setHintY(0); }, 600);
  };

  const handleScrollMove = (deltaY) => {
    if (!showScrollHint) return;
    if (deltaY > 0) {
      const newY = Math.min(hintY + deltaY * 0.5, 60);
      const newOp = Math.max(1 - newY / 60, 0);
      setHintY(newY);
      setHintOpacity(newOp);
      if (newOp <= 0) { setShowScrollHint(false); setHintY(0); setHintOpacity(1); }
    }
  };

  useEffect(() => {
    resetScrollHint();
    let lastY = 0;
    const onTouchMove = (e) => { const dy = lastY - e.touches[0].clientY; lastY = e.touches[0].clientY; handleScrollMove(dy); };
    const onTouchStart = (e) => { lastY = e.touches[0].clientY; };
    const onScroll = () => { const dy = window.scrollY - lastScrollY.current; lastScrollY.current = window.scrollY; handleScrollMove(dy); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    return () => {
      if (scrollHintTimer.current) clearTimeout(scrollHintTimer.current);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchstart", onTouchStart);
    };
  }, [selected, isSuerte, chatOpen, showOnboarding, showMenu, showExplore, showAuth, showRegister, showAddPlace, imageViewer, altSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Hardware back button handler ─────────────────────────
  const lastBackPress = useRef(0);
  const [showExitToast, setShowExitToast] = useState(false);
  const [sessionToast, setSessionToast] = useState(null); // {type: 'login'|'logout', text: '...'}
  // B7 — logout con confirmación y bloqueo total (IDs 4,5,40,89)
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const logoutControllerRef = useRef(null);
  if (!logoutControllerRef.current) logoutControllerRef.current = createLogoutController({ signOut: () => supabase.auth.signOut(), timeoutMs: SUPABASE_TIMEOUT_MS });
  const doLogout = async () => {
    if (logoutControllerRef.current.isRunning()) return;
    const userId = user?.userId;
    const userName = user?.username;
    setConfirmLogout(false);
    setLoggingOut(true);            // overlay bloqueante: nada más se puede tocar
    setChatOpen(false);             // ID 40 — Don D' no puede montarse durante el logout
    setShowMenu(false);
    setShowAuth(false); setShowRegister(false); setShowAddPlace(false); setShowProfile(false);
    setSelected(null); setAltSelected(null); setImageViewer(null); setShowPolicies(false); setLegalDocument(null);
    clearSessionLocation();
    // B7 (ID 8) — limpieza selectiva: NO borrar onboarding/presupuesto/gps/favs/bloqueados
    let signedOut = false;
    try {
      await logoutControllerRef.current.run();
      signedOut = true;
      log('✅ Sesión cerrada en Supabase');
    } catch (e) {
      warn('Error cerrando sesión:', e);
      setSessionToast({ type: 'error', text: '❌ No se pudo cerrar sesión. Intenta nuevamente.' });
    } finally {
      setLoggingOut(false);
    }
    if (!signedOut) return;
    try { localStorage.removeItem('venaqqi_user'); } catch { /* noop */ }
    clearUiState();
    setUser(null);
    setAccessMode(ACCESS_MODES.GUEST);
    persistAccessMode(ACCESS_MODES.GUEST);
    setShowWelcome(false);
    setOnboardingStep(0);
    if (userId) {
      supabase.from('user_sessions').insert({ user_id: userId, action: 'logout', timestamp: new Date().toISOString() })
        .then?.(() => {}, e => warn('Error logging logout:', e));
    }
    setSessionToast({ type: 'logout', text: `👋 Hasta luego${userName ? ', ' + userName : ''}` });
    setTimeout(() => setSessionToast(null), 2000);
  };
  // Handler estable para memo(Card)
  const handleShareToast = useCallback(() => { setSessionToast({ type: "info", text: "📋 Link copiado al portapapeles" }); }, []);
  // 5.2 — bloquear/desbloquear usuarios (gestión en menú hamburguesa)
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const handleBlockUser = useCallback((id, username) => {
    setBlockedUsers(prev => {
      if (prev.some(b => b.id === id)) return prev;
      const next = [...prev, { id, username }];
      saveBlockedUsers(next);
      return next;
    });
    setSessionToast({ type: "info", text: `🚫 Contenido de "${username}" oculto. Gestiónalo en el menú.` });
    setTimeout(() => setSessionToast(null), 3000);
  }, []);
  const handleUnblockUser = useCallback((id) => {
    setBlockedUsers(prev => { const next = prev.filter(b => b.id !== id); saveBlockedUsers(next); return next; });
  }, []);
  const [statusNotif, setStatusNotif] = useState(null); // { kind: 'place'|'review', item }
  const [pwaUpdate, setPwaUpdate] = useState(null);
  const [myEditPlace, setMyEditPlace] = useState(null);
  const [myEditReview, setMyEditReview] = useState(null);

  useEffect(() => {
    if (!user?.userId) { setStatusNotif(null); return; }
    const myPlace = places.find(p => p.addedBy === user.userId && p.metadata && p.metadata.notifSeen === false);
    if (myPlace) { setStatusNotif({ kind: "place", item: myPlace }); return; }
    const myReview = reviews.find(r => r.userid === user.userId && r.metadata && r.metadata.notifSeen === false);
    if (myReview) { setStatusNotif({ kind: "review", item: myReview }); return; }
    setStatusNotif(null);
  }, [places, reviews, user]);

  const dismissStatusNotif = async () => {
    if (!statusNotif) return;
    const { kind, item } = statusNotif;
    const meta = { ...(item.metadata || {}), notifSeen: true };
    if (kind === "place") {
      await supabase.from("restaurants").update({ metadata: meta }).eq("id", item.id);
      setPlaces(prev => prev.map(pl => pl.id === item.id ? { ...pl, metadata: meta } : pl));
    } else {
      await supabase.from("reviews").update({ metadata: meta }).eq("id", item.id);
      setReviews(prev => prev.map(rv => rv.id === item.id ? { ...rv, metadata: meta } : rv));
    }
    setStatusNotif(null);
  };



  useEffect(() => {
    let cancelled = false;
    const onUpdateReady = update => {
      if (cancelled) return;
      setPwaUpdate(previous => ({ ...previous, ...update, applying: false, error: null }));
    };
    registerPwaUpdateHandler({
      onUpdateReady,
    });
    return () => { cancelled = true; removePwaUpdateListener(onUpdateReady); };
  }, []);

  // Durante la beta el aviso es visible únicamente para testers autorizados y Master.
  const showPwaUpdateNotice = !!pwaUpdate && (user?.testerAuthorized === true || isAdmin);
  const handlePwaUpdate = async () => {
    setPwaUpdate(previous => previous ? { ...previous, applying: true, error: null } : previous);
    let sent = false;
    try { sent = await applyPwaUpdate(pwaUpdate?.registration); } catch { sent = false; }
    if (!sent) setPwaUpdate(previous => previous ? { ...previous, applying: false, error: "No pudimos preparar la actualización. Revisa tu conexión e inténtalo de nuevo." } : previous);
  };

  // Auto-cierre para notificaciones de aprobación (no aplica a rechazos, que requieren acción manual)
  useEffect(() => {
    if (!statusNotif) return;
    const rejected = !!statusNotif.item.metadata?.rejected;
    if (rejected) return;
    const t = setTimeout(() => { dismissStatusNotif(); }, 4000);
    return () => clearTimeout(t);
  }, [statusNotif]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showScrollHint, setShowScrollHint] = useState(false);
  const previousSelectedPlace = useRef(null);

  // Una interacción puede terminar dentro de una capa (Don D’, detalle o reseña).
  // Se conserva pendiente hasta que la interfaz vuelve a estar libre de diálogos.
  useEffect(() => {
    if (previousSelectedPlace.current && !selected && !altSelected) setSignificantInteractionPending(true);
    previousSelectedPlace.current = selected || altSelected;
  }, [altSelected, selected]);

  const feedbackInvitationExclusion = getFeedbackInvitationExclusion({ showAuth, showPasswordReset, showRegister, showWelcome, showExplore, showPolicies, legalDocument, showOnboarding, adminTutorialOpen, showAdmin, adminPreview, adminEditPlace, adminEditReview, adminImageViewer });
  const feedbackSurfaceIsClear = !feedbackInvitationExclusion && !selected && !altSelected && !profileContributionDetail && !chatOpen && !showAuthChoice && !showAddPlace && !showFeedback && !showMenu && !showRecoModal && !imageViewer && !showBlockedModal && !showProfile && !myEditPlace && !myEditReview && !confirmLogout && !loggingOut && !statusNotif && !sessionToast && !sessionRestoring && !splashVisible;
  useEffect(() => {
    // Una interacción anterior o realizada dentro de una zona excluida no debe
    // disparar la invitación al salir: se requiere una nueva interacción válida.
    if (feedbackInvitationExclusion) {
      setSignificantInteractionPending(false);
      deferFeedbackInvitation();
    }
  }, [deferFeedbackInvitation, feedbackInvitationExclusion]);
  useEffect(() => {
    if (!significantInteractionPending || !feedbackSurfaceIsClear) return;
    completeSignificantInteraction();
    setSignificantInteractionPending(false);
  }, [completeSignificantInteraction, feedbackSurfaceIsClear, significantInteractionPending]);
  const hintShownCount = useRef(0); // 1.10 — contador de apariciones del hint por sesión
  const [hintFading, setHintFading] = useState(false);
  const [hintOpacity, setHintOpacity] = useState(1);
  const [hintY, setHintY] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const lastScrollY = useRef(0);
  const scrollHintTimer = useRef(null);

  // Keep one same-document entry below every visible layer so Android/PWA back
  // reaches this handler before it can close the application.
  const navigationGuardSeededRef = useRef(false);
  useEffect(() => {
    const ready = !sessionRestoring && !showWelcome && !showAuth && !showRegister && !showPasswordReset;
    if (!ready || navigationGuardSeededRef.current) return;
    navigationGuardSeededRef.current = seedNavigationGuard();
  }, [sessionRestoring, showAuth, showPasswordReset, showRegister, showWelcome]);

  // Push a history entry whenever a new layer opens.
  useEffect(() => { if (isLocationDialogOpen) pushNavigationLayer("locationDialog"); }, [isLocationDialogOpen]);
  useEffect(() => { if (imageViewer) pushNavigationLayer("imageViewer"); }, [imageViewer]);
  useEffect(() => { if (altSelected) pushNavigationLayer("altSelected"); }, [altSelected]);
  useEffect(() => { if (showAddPlace) pushNavigationLayer("showAddPlace"); }, [showAddPlace]);
  useEffect(() => { if (showRegister) pushNavigationLayer("showRegister"); }, [showRegister]);
  useEffect(() => { if (showAuth) pushNavigationLayer("showAuth"); }, [showAuth]);
  useEffect(() => { if (showPolicies) pushNavigationLayer("showPolicies"); }, [showPolicies]);
  useEffect(() => { if (showFeedback) pushNavigationLayer("showFeedback"); }, [showFeedback]);
  useEffect(() => { if (legalDocument) pushNavigationLayer("legalDocument"); }, [legalDocument]);
  useEffect(() => {
    if (!showAdmin || !isAdmin) return;
    const loadUsers = async () => {
      const { data, error } = await supabase.from("users").select("id, username, nombre, email, fecha_registro");
      if (error) {
        console.error('\u274c Error cargando users:', error.message);
        setSessionToast({ type: "error", text: "No se pudieron cargar los usuarios: " + error.message });
        return;
      }
      if (data) {
        const map = {};
        const fechas = {};
        const emails = {};
        data.forEach(u => { map[u.id] = u.username; fechas[u.id] = u.fecha_registro; emails[u.id] = u.email; });
        setAdminUsers(map);
        setAdminUsersFecha(fechas);
        setAdminUserEmails(emails);
        setAdminContributorProfiles(buildAdminContributorProfiles(data));
        log('\u2705 adminUsers cargados:', data.length);
      }
    };
    loadUsers();
  }, [showAdmin, isAdmin, places, reviews]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user?.fullReg) return;
    trackTesterActivity(TESTER_ACTIVITY_ACTIONS.APP_OPEN, { userId: user.userId, dedupeKey: `app_open:${user.userId}`, dedupeMs: Number.POSITIVE_INFINITY });
  }, [user]);

  useEffect(() => {
    if (!user?.fullReg || !selected || isSuerte) return;
    const key = `${user.userId}:${selected.id}`;
    const now = Date.now();
    if (lastTrackedPlaceRef.current.key === key && now - lastTrackedPlaceRef.current.at < 5000) return;
    lastTrackedPlaceRef.current = { key, at: now };
    trackTesterActivity(TESTER_ACTIVITY_ACTIONS.PLACE_VIEW, { userId: user.userId, dedupeKey: `place_view:${key}`, dedupeMs: 5000 });
  }, [selected, isSuerte, user]);

  useEffect(() => { if (showExplore) pushNavigationLayer("showExplore"); }, [showExplore]);
  useEffect(() => { if (showOnboarding) pushNavigationLayer("showOnboarding"); }, [showOnboarding]);
  useEffect(() => { if (showMenu) pushNavigationLayer("showMenu"); }, [showMenu]);
  useEffect(() => { if (selected) pushNavigationLayer("selected"); }, [selected]);
  useEffect(() => { if (isSuerte) pushNavigationLayer("isSuerte"); }, [isSuerte]);
  useEffect(() => { if (chatOpen) pushNavigationLayer("chatOpen"); }, [chatOpen]);
  useEffect(() => { if (showAdmin) pushNavigationLayer("showAdmin"); }, [showAdmin]);
  useEffect(() => { if (adminPreview) pushNavigationLayer("adminPreview"); }, [adminPreview]);
  useEffect(() => { if (adminImageViewer) pushNavigationLayer("adminImageViewer"); }, [adminImageViewer]);

  useEffect(() => {
    const handleBack = (e) => {
      if (isLocationDialogOpen) { dismissLocationDialog(); return; }
      if (legalDocument) { setLegalDocument(null); return; }
      if (showPolicies) { setShowPolicies(false); setLegalOpenedFromPolicies(false); return; }
      if (showFeedback) { setShowFeedback(false); return; }
      const adminBackLayer = resolveAdminBackLayer({ adminImageViewer, adminPreview, showAdmin });
      if (adminBackLayer === "image") { setAdminImageViewer(null); return; }
      if (adminBackLayer === "preview") { closeAdminPreview(); return; }
      if (adminBackLayer === "panel") { setShowAdmin(false); return; }
      if (imageViewer) { setImageViewer(null); return; }
      if (altSelected) { setAltSelected(null); return; }
      if (showAddPlace) { setShowAddPlace(false); return; }
      if (loggingOut) { return; } // B7 — durante logout ni el back físico interrumpe
      if (showRegister) { setShowRegister(false); if (!user && accessMode === ACCESS_MODES.PENDING) { setRegisterFromOnboarding(false); setShowWelcome(true); setOnboardingStep(0); } return; } // B7 (ID 9) — back desde registro no concede acceso
      if (showAuth) { setShowAuth(false); if (!user && accessMode === ACCESS_MODES.PENDING) { setAuthFromOnboarding(false); setShowWelcome(true); setOnboardingStep(0); } return; }
      if (showExplore) { setShowExplore(false); return; }
      if (showOnboarding) { setShowOnboarding(false); return; }
      if (showMenu) { setShowMenu(false); return; }
      // 2.2 — Don D' está por encima (z-index 4900+): el back lo minimiza primero, igual que el click fuera
      if (chatOpen) { setChatOpen(false); return; }
      if (selected && fromOnboarding) { setSelected(null); setShowOnboarding(true); setFromOnboarding(false); return; }
      if (selected) { setSelected(null); return; }
      if (isSuerte) { setSelected(null); setIsSuerte(false); return; }

      // Main screen - double press to exit
      const now = Date.now();
      if (now - lastBackPress.current < 2000) {
        window.history.go(-999);
      } else {
        lastBackPress.current = now;
        setShowExitToast(true);
        setTimeout(() => setShowExitToast(false), 2000);
        window.history.pushState(null, "");
      }
    };

    window.addEventListener("popstate", handleBack);
    return () => window.removeEventListener("popstate", handleBack);
  }, [isLocationDialogOpen, dismissLocationDialog, legalDocument, showPolicies, showFeedback, adminImageViewer, adminPreview, showAdmin, imageViewer, altSelected, showAddPlace, showRegister, showAuth, showExplore, showOnboarding, showMenu, selected, fromOnboarding, isSuerte, chatOpen, loggingOut, user, accessMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnboardingGPS = () => {
    requestLocationForFeature("recommendation", createPendingLocationAction(LOCATION_ACTION_TYPES.ONBOARDING_LOCATION, { supportsManualLocation: true, forceGps: true }), {
      onGranted: () => { localStorage.setItem('venaqqi_gps', '1'); setOnboardingStep(2); },
      onManual: zone => { localStorage.setItem('venaqqi_gps', '0'); localStorage.setItem('venaqqi_zona_manual', zone); setOnboardingStep(2); },
    });
  };

  const handleSuerte = ({ respectBudget = true } = {}) => {
    try {
      const guestMode = accessMode === ACCESS_MODES.GUEST && !user;
      const rawPresupuesto = localStorage.getItem('venaqqi_presupuesto');
      // B8 (ID 41) — unificar escala: valores legacy del onboarding (1-4) se traducen a la escala 1-7 de PRECIO_RANGES
      const LEGACY_MAP = { 1: 2, 2: 4, 3: 5, 4: 7 };
      let maxPrecio = rawPresupuesto && rawPresupuesto !== '0' ? parseInt(rawPresupuesto) : null;
      if (maxPrecio && maxPrecio <= 4 && !localStorage.getItem('venaqqi_presupuesto_v2')) { maxPrecio = LEGACY_MAP[maxPrecio] || maxPrecio; }
      // B8 (IDs 1,2,20) — solo lugares APROBADOS entran al pool de recomendación
      let pool = [...places].filter(p => {
        if (!isApprovedPlace(p)) return false;
        const rs = reviews.filter(r => Number(r.placeid || r.placeId) === Number(p.id));
        return buildRatingSummary(rs).published.length > 0;
      });
      if (maxPrecio && respectBudget) {
        pool = pool.filter(p => {
          const rs = buildRatingSummary(reviews.filter(r => Number(r.placeid || r.placeId) === Number(p.id))).published;
          const avgPrecio = rs.reduce((acc, r) => acc + (r.precio || 3), 0) / rs.length;
          return avgPrecio <= maxPrecio;
        });
        if (!pool.length) pool = [...places].filter(p => {
          if (!isApprovedPlace(p)) return false;
          const rs = reviews.filter(r => Number(r.placeid || r.placeId) === Number(p.id));
          return buildRatingSummary(rs).published.length > 0;
        });
      }
      // Evitar ciclos visibles: no repetir ninguno de los últimos cuatro lugares mostrados.
      if (pool.length > 1 && selected) pool = pool.filter(p => Number(p.id) !== Number(selected.id));
      pool = excludeRecentRandomPlaces(pool, readRecentRandomPlaceIds());
      log('handleSuerte - pool:', pool.length, 'reviews:', reviews.length, 'places:', places.length);
      if (!pool.length) { warn('No hay places con reviews'); return; }
      const shuffled = shuffle(pool);
      const pick = shuffled[0];
      rememberRandomPlaceId(pick.id);
      const rest = shuffled.slice(1);
      const slots = rest.slice(0, 6);
      const rs = reviews.filter(r => Number(r.placeid || r.placeId) === Number(pick.id));
      const best = rs[rs.length - 1];
      let msg = "🎲 Te recomiendo **" + pick.name + "** — " + (best?.comentario || pick.tipo + " en " + pick.location) + ". ¿Se te antoja?";
      // Una recomendación aleatoria es exploración pública, no una consulta a Don D'.
      setSuerteSlots(slots);
      setSelected(pick);
      setChatInitialMsg(msg);
      void trackProductEvent("recommendation_shown", { place_id: Number(pick.id), source: "random", guest: guestMode, alternative_place_ids: slots.map(place => Number(place.id)) });
      if (user?.userId) trackTesterActivity(TESTER_ACTIVITY_ACTIONS.RECOMMENDATION, { userId: user.userId, dedupeKey: `recommendation:${pick.id}:${Date.now()}`, dedupeMs: 500 });
      setSignificantInteractionPending(true);
      setIsSuerte(true);
      if (user?.fullReg) setTimeout(() => setChatOpen(true), 50);
      if (user?.fullReg && !sessionStorage.getItem('venaqqi_donde_hint_shown') && !localStorage.getItem('venaqqi_donde_used')) {
        sessionStorage.setItem('venaqqi_donde_hint_shown', '1');
        setTimeout(() => { setShowDondeHint(true); setTimeout(() => setShowDondeHint(false), 3000); }, 800);
      }
    } catch(err) {
      console.error('handleSuerte error:', err);
    }
  };

  const nextOnboarding = () => {
    const pool = [...basePlaces].filter(p => initialReviews.filter(r => Number(r.placeid || r.placeId) === Number(p.id)).length > 0 && (!onboardingPlace || p.id !== onboardingPlace.id));
    if (!pool.length) return;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    const rs = initialReviews.filter(r => Number(r.placeid || r.placeId) === Number(pick.id));
    const best = rs[rs.length - 1];
    setOnboardingPlace(pick);
    setOnboardingMsg("🎲 También te puede gustar **" + pick.name + "** — " + (best?.comentario || pick.tipo + " en " + pick.location) + ". ¿Qué dices?");
  };

  const handleExplore = (asUser) => {
    if (asUser && !user) { setShowAuth(true); return; }
    setShowExplore(false);
    document.getElementById("venaqqi-grid")?.scrollIntoView({ behavior: "smooth" });
    try {
      if (!sessionStorage.getItem("venaqqi_explore_hint_v1")) {
        sessionStorage.setItem("venaqqi_explore_hint_v1", "1");
        setShowExploreHint(true);
        setTimeout(() => setShowExploreHint(false), 5000);
      }
    } catch { /* storage unavailable */ }
  };

  const openLegalDocument = (documentKey, fromPolicies = false) => {
    if (!LEGAL_DOCUMENTS[documentKey]) return;
    setLegalOpenedFromPolicies(fromPolicies);
    setLegalDocument(documentKey);
  };

  // Don D’ (z-index 5000) must be removed before opening Auth (z-index 4000).
  // flushSync also guarantees that OAuth starts only after the chat stops
  // receiving pointer/focus events, while preserving its local conversation.
  const leaveDonDFor = (action) => {
    flushSync(() => setChatOpen(false));
    action();
  };

  const closeLegalDocument = () => {
    setLegalDocument(null);
    if (legalOpenedFromPolicies) {
      setShowPolicies(false);
      setLegalOpenedFromPolicies(false);
    }
  };

  return (
    <div style={{ minHeight: "10vh", background: "#080808", fontFamily: "'Helvetica Neue',Arial,sans-serif", color: "#fff" }} onTouchStart={(e) => { setHasInteracted(true); resetScrollHint(); if (autoRecoPending && !e.target.closest('[data-venaqqi-logo]')) cancelAutoReco(); }} onClickCapture={(e) => { const anchor = e.target.closest?.('a[href]'); const documentKey = anchor && getLegalDocumentKey(anchor.getAttribute('href')); if (documentKey) { e.preventDefault(); e.stopPropagation(); openLegalDocument(documentKey, false); } }} onClick={(e) => { setHasInteracted(true); resetScrollHint(); if (autoRecoPending && !e.target.closest('[data-venaqqi-logo]')) cancelAutoReco(); }} >
     
      <style>{SPIN_CSS + SHAKE_CSS + '@import url("https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap");'}</style>

      {tutorialStep > 0 && <div aria-live="polite" style={{ position: "fixed", inset: 0, zIndex: 11990, pointerEvents: "none", background: "rgba(0,0,0,.68)" }}>
        <div style={{ position: "fixed", left: 16, right: 16, top: "calc(env(safe-area-inset-top, 0px) + 72px)", margin: "0 auto", maxWidth: 420, padding: 13, borderRadius: 12, background: "#111", border: "1px solid #e53935", boxShadow: "0 12px 35px rgba(0,0,0,.65)", color: "#fff", fontSize: 12, lineHeight: 1.45 }}>
          <strong style={{ color: "#e57373" }}>Paso {tutorialStep} de 3</strong><br/>{tutorialStep === 1 ? "Busca algo que se te antoje. Escribe en el buscador para continuar." : tutorialStep === 2 ? "Ahora abre el menú ☰. No avanzaremos hasta que lo hagas." : user?.fullReg ? "Entra a tu Perfil. Ahí viven tus aportes, estados y notificaciones." : "Toca Iniciar sesión para conocer dónde guardarás tus aportes."}
          <button onClick={pauseTutorial} style={{ pointerEvents: "auto", marginTop: 8, background: "transparent", border: 0, color: "#aaa", fontSize: 10, cursor: "pointer" }}>Salir · puedes retomarlo después</button>
        </div>
      </div>}

      {/* Splash de sesión restaurándose */}
      {sessionRestoring && (
        <div style={{ position: "fixed", inset: 0, background: "#080808", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 20 }}>
          <img className="don-d-logo-symbol" src={LOGO} alt="Venaqqi" style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", border: "3px solid #e53935", animation: "vq-pulse 1.5s ease infinite" }} />
          <div style={{ fontSize: 14, color: "#888" }}>Preparando tu mesa...</div>
        </div>
      )}

      {accessMode === ACCESS_MODES.GUEST && !user && !showWelcome && !sessionRestoring && (
        <div role="status" style={{ position: "sticky", top: 0, zIndex: 2500, background: "rgba(20,20,20,0.97)", borderBottom: "1px solid #333", padding: "8px 14px", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, flexWrap: "wrap", fontSize: 12, color: "#bbb" }}>
          <strong style={{ color: "#fff" }}>Modo invitado</strong>
          <span>Exploras sin una cuenta registrada.</span>
          <button onClick={() => setShowAuth(true)} style={{ background: "none", border: "none", color: "#e57373", padding: 2, fontWeight: 700, cursor: "pointer" }}>Iniciar sesión</button>
          <button onClick={() => setShowRegister(true)} style={{ background: "#e53935", border: "none", borderRadius: 12, color: "#fff", padding: "5px 10px", fontWeight: 700, cursor: "pointer" }}>Registrarme</button>
        </div>
      )}

      {/* Banner de sin conexión — fallback honesto sobre shuffledBasePlaces */}
      {isOffline && !sessionRestoring && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9500, background: "#e53935", color: "#fff", textAlign: "center", padding: "8px 12px", fontSize: 13, fontWeight: 600 }}>
          Sin conexión. Te mostramos algunos lugares guardados — conéctate para ver todo Venaqqi.
        </div>
      )}

      {restaurantsError && !sessionRestoring && (
        <div role="alert" style={{ position: "fixed", top: isOffline ? 36 : 0, left: 0, right: 0, zIndex: 9499, background: "#7f1d1d", color: "#fff", textAlign: "center", padding: "8px 36px 8px 12px", fontSize: 13, fontWeight: 600 }}>
          {restaurantsError} Mostramos temporalmente los lugares guardados.
          <button aria-label="Cerrar aviso" onClick={() => setRestaurantsError(null)} style={{ position: "absolute", right: 10, top: 5, border: 0, background: "transparent", color: "#fff", fontSize: 18, cursor: "pointer" }}>×</button>
        </div>
      )}

      {/* DEBUG BADGE — descomenta el bloque de abajo para reactivar */}
      {/*
      <div style={{ position: "fixed", top: 70, right: 10, zIndex: 9999, background: "rgba(0,0,0,0.8)", border: "1px solid #e53935", borderRadius: 8, padding: "8px 12px", fontSize: 11, color: "#aaa", fontFamily: "monospace", lineHeight: "1.4", maxWidth: 180 }}>
        <div>Reviews: {reviews.length}</div>
        <div>Usuario: {user ? "Sí" : "No"}</div>
        <div>Loaded: {reviews.length > 0 ? "✓" : "✗"}</div>
      </div>
      */}

      {/* ── Scroll hint overlay ─────────────────────────────── */}
      {showScrollHint && !selected && !isSuerte && !chatOpen && !showProfile && !showAdmin && !showAuth && !showRegister && !showAddPlace && !showWelcome && !showExplore && !imageViewer && !showMenu && (
        <div onClick={dismissHint} style={{ position: "fixed", bottom: 160, left: 0, right: 0, zIndex: 9900, display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 8, pointerEvents: "all", transition: hintFading ? "opacity 0.6s, transform 0.6s" : "none", opacity: hintFading ? 0 : hintOpacity, transform: "translateY(-" + hintY + "px)", animation: hintFading ? "none" : "vqfadein 0.5s ease" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 0, animation: "vqbounce2 1.2s infinite", flexShrink: 0 }}>
            <svg width="28" height="18" viewBox="0 0 36 22" fill="none"><path d="M4 4 L18 18 L32 4" stroke="#e53935" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <svg width="28" height="18" viewBox="0 0 36 22" fill="none"><path d="M4 4 L18 18 L32 4" stroke="#e53935" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/></svg>
          </div>
          <p style={{ color: "rgba(255,255,255,0.9)", fontSize: 16, fontWeight: 700, fontStyle: "italic", margin: 0, letterSpacing: "0.03em", textAlign: "center", animation: "vqtextpulse 1.2s infinite", flexShrink: 0 }}>Desliza para VER MÁS</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 0, animation: "vqbounce2 1.2s infinite", flexShrink: 0 }}>
            <svg width="28" height="18" viewBox="0 0 36 22" fill="none"><path d="M4 4 L18 18 L32 4" stroke="#e53935" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <svg width="28" height="18" viewBox="0 0 36 22" fill="none"><path d="M4 4 L18 18 L32 4" stroke="#e53935" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/></svg>
          </div>
        </div>
      )}

      {/* ── HERO ─────────────────────────────────────────────── */}
      <div className="vq-hero" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", padding: "20px", paddingTop: "60px", paddingBottom: "12px", textAlign: "center", position: "relative" }}>

        {/* Menú hamburguesa */}
        {showMenu && <div onClick={() => setShowMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 499 }} />}
        <div style={{ position: "absolute", top: 20, right: 20, zIndex: tutorialStep >= 2 ? 12010 : 500 }}>
          <button data-tutorial-target="menu" onClick={() => { setShowMenu(!showMenu); if (tutorialStep === 2) setTutorialStep(3); }} aria-label="Abrir menú" aria-expanded={showMenu} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", gap: 5, padding: 8 }}>
            <div style={{ width: 24, height: 2.5, background: "#e53935", borderRadius: 2 }} />
            <div style={{ width: 24, height: 2.5, background: "#e53935", borderRadius: 2 }} />
            <div style={{ width: 24, height: 2.5, background: "#e53935", borderRadius: 2 }} />
          </button>
          {showMenu && (
            <div role="menu" aria-label="Menú principal" style={{ position: "absolute", top: 44, right: 0, background: "#161616", border: "1px solid #2a2a2a", borderRadius: 14, minWidth: 200, overflow: "hidden", zIndex: 500, boxShadow: "0 8px 24px rgba(0,0,0,0.6)" }}>
              {[
                ...(user && user.fullReg ? [
                  { label: `👤 ${user.username} · Mi perfil`, action: () => { setShowProfile(true); setShowMenu(false); if (tutorialStep === 3) finishTutorial(); } },
                ] : [
                  { label: "🔑 Iniciar Sesión", action: () => { setShowAuth(true); setShowMenu(false); if (tutorialStep === 3) finishTutorial(); } },
                ]),
                { label: "🎲 Dame una recomendación", action: () => { openRecoModal(); setShowMenu(false); } },
                { label: "🗺️ Explorar lugares", action: () => { if (user?.fullReg) { setShowMenu(false); handleExplore(true); } else { setShowExplore(true); setShowMenu(false); } } },
                { label: "🗺️ Agregar lugar", action: () => { if (!user || !user.fullReg) { setAuthGateReason("add_place"); setShowAuthChoice(true); } else { setShowAddPlace(true); } setShowMenu(false); } },
                { label: "💬 Hablar con Don D'", action: () => { openDonD(); if (user?.fullReg) setNewChatRequest(value => value + 1); setShowMenu(false); } },
                ...(isAdmin ? [{ label: "📖 Ver tutorial (admin)", action: () => { setAdminTutorialOpen(true); setShowWelcome(true); setOnboardingStep(0); setShowMenu(false); } }] : []), // vista temporal: la X conserva la sesión admin
                ...(blockedUsers.length ? [{ label: `🚫 Usuarios bloqueados (${blockedUsers.length})`, action: () => { setShowBlockedModal(true); setShowMenu(false); } }] : []),
                ...(isAdmin ? [
                  // 5.4 — aviso de pendientes por aprobar directo en el menú
                  { label: (() => { const n = pendingModerationPlaces(places).length + reviews.filter(r => r.pending && !r.metadata?.rejected).length; return n > 0 ? `🛡️ Panel Admin · 🔴 ${n} por aprobar` : "🛡️ Panel Admin"; })(), action: async () => { setShowMenu(false); if (await verifyAdminAccess()) setShowAdmin(true); } },
                  // 5.6 — OnboardingScreen (cupón) aislado: solo demo de referencia para admin
                  { label: "🎬 Demo cupón (referencia)", action: () => { nextOnboarding(); setShowOnboarding(true); setShowMenu(false); } },
                ] : []),
                { label: readTutorialProgress()?.step ? "🧭 Retomar tutorial" : "🧭 Ver tutorial", action: () => { resumeTutorial(); setShowMenu(false); } },
                { label: "💬 Feedback", action: () => { setFeedbackOrigin("manual_menu"); setFeedbackHasPrevious(hasSuccessfulFeedback(user?.userId)); setShowFeedback(true); setShowMenu(false); } },
                { label: "📜 Políticas", action: () => { setShowPolicies(true); setShowMenu(false); } },
                ...(user && user.fullReg ? [
                  { label: "🔓 Cerrar Sesión", action: () => { setConfirmLogout(true); setShowMenu(false); } },
                ] : []),
              ].map(item => (
                <button role="menuitem" key={item.label} onClick={() => item.action && item.action()} disabled={item.disabled}
                  style={{ width: "calc(100% - 12px)", margin: "6px", padding: "12px 13px", background: item.disabled ? "#111" : "#1b1b1b", border: "1px solid #303030", borderRadius: 10, color: item.disabled ? "#555" : "#eee", fontSize: 13, cursor: item.disabled ? "default" : "pointer", textAlign: "left", fontFamily: "inherit", fontWeight: item.label.includes("Mi perfil") ? 800 : 600, boxShadow: item.label.includes("Mi perfil") ? "0 0 0 1px rgba(229,57,53,.35)" : "none" }}
                  onMouseEnter={e => !item.disabled && (e.currentTarget.style.background = "#1e1e1e")}
                  onMouseLeave={e => !item.disabled && (e.currentTarget.style.background = "#1b1b1b")}>
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Texto arriba */}
        <p style={{ margin: "0 0 6px", fontSize: 20, color: "#ccc", letterSpacing: "0.05em", fontWeight: 600 }}>Dame una recomendación</p>
        <p style={{ margin: "0 0 20px", fontSize: 15, color: "#555", letterSpacing: "0.05em" }}>Click Aqqí ↓</p>

        {/* Hint post-onboarding */}
        {logoPulsing && (
          <div style={{ background: "#fff", color: "#080808", borderRadius: 12, padding: "8px 16px", fontSize: 12, fontWeight: 700, marginBottom: 12, position: "relative", maxWidth: 260, textAlign: "center" }}>
            {autoRecoPending ? "🎲 Este botón te recomienda dónde comer. En unos segundos te muestro un ejemplo — o tócalo tú." : "Toca aquí para tu recomendación"}
            <div style={{ position: "absolute", bottom: -6, left: "50%", transform: "translateX(-50%)", width: 12, height: 12, background: "#fff", clipPath: "polygon(0 0, 100% 0, 50% 100%)" }} />
          </div>
        )}

        {/* Botón logo grande */}
        <style>{`@keyframes vq-pulse-ring{0%{box-shadow:0 0 0 0 rgba(229,57,53,0.7)}70%{box-shadow:0 0 0 20px rgba(229,57,53,0)}100%{box-shadow:0 0 0 0 rgba(229,57,53,0)}} @keyframes vq-press{0%{transform:scale(1)}40%{transform:scale(0.92)}100%{transform:scale(1)}}`}</style>
        <button data-venaqqi-logo="1" onClick={() => { cancelAutoReco(true); const d = randomDonDRecommendationDelay(); setSplashMode("buscando"); setSplashDuration(d / 1000); setSplashVisible(true); setTimeout(() => { setSplashVisible(false); handleSuerte({ respectBudget: false }); }, d); }} style={{ background: "none", border: "none", cursor: "pointer", marginBottom: 20, display: "block" }}
          onMouseEnter={e => !logoPulsing && !logoPressing && (e.currentTarget.querySelector("div").style.transform = "scale(1.04)")}
          onMouseLeave={e => !logoPulsing && !logoPressing && (e.currentTarget.querySelector("div").style.transform = "scale(1)")}>
          <div style={{ width: 240, height: 240, borderRadius: "50%", border: `3px solid ${logoPressing ? "#fff" : "#e53935"}`, overflow: "hidden", background: "#080808", display: "flex", alignItems: "center", justifyContent: "center", transition: "border-color 0.2s, transform 0.2s", animation: logoPulsing ? "vq-pulse-ring 1s ease-out infinite" : logoPressing ? "vq-press 0.5s ease forwards" : "none" }}>
            <img className="don-d-logo-symbol" src={LOGO} alt="Venaqqi" style={{ width: "108%", height: "108%", objectFit: "contain" }} />
          </div>
        </button>

        {/* Eslogan y nombre */}
        <div style={{ fontSize: 18, color: "#888", letterSpacing: "0.1em", fontStyle: "italic", marginBottom: 0 }}>come donde quieras</div>
        <div style={{ fontSize: 72, fontWeight: 400, color: "#e53935", fontFamily: "'Bebas Neue', 'Arial Black', sans-serif", letterSpacing: 2, lineHeight: 0.9, marginBottom: 20, marginTop: 0 }}>VENAQQI</div>
        {/* Eslogan tutorial */}
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <p style={{ margin: "0 0 4px", fontSize: 18, fontStyle: "italic", color: "rgba(255,255,255,0.9)", animation: "vqfadein2 0.5s ease forwards" }}>Hoy tú decides dónde comer...</p>
          <p style={{ margin: 0, fontSize: 18, fontStyle: "italic", color: "rgba(255,255,255,0.9)", opacity: 0, animation: "vqfadein2 0.5s ease 0.5s forwards" }}>Nosotros te ayudamos <span style={{ fontStyle: "normal" }}>😉</span></p>
        </div>
        <style>{"@keyframes vqfadein2{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}"}</style>

        {/* Flecha scroll */}
        <div style={{ marginTop: 16, animation: "vqbounce2 2s infinite" }}>
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M6 10 L16 22 L26 10" stroke="#e53935" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <style>{"@keyframes vqbounce2{0%,100%{transform:translateY(0)}50%{transform:translateY(8px)}}"}</style>
      </div>

            {/* ── GRID (scroll) ────────────────────────────────────── */}
      <div id="venaqqi-grid" style={{ padding: "0 20px 40px" }}>
        {showExploreHint && <div role="status" style={{ maxWidth: 600, margin: "0 auto 12px", padding: "10px 13px", borderRadius: 12, background: "rgba(229,57,53,.10)", border: "1px solid rgba(229,57,53,.38)", color: "#ddd", fontSize: 11, lineHeight: 1.45 }}>🧭 Explora por categoría y desliza las tarjetas horizontalmente. Toca cualquier lugar para ver su reseña y detalles.</div>}
        {/* Buscador inteligente */}
        <div style={{ maxWidth: 600, margin: "0 auto 18px", position: "relative", zIndex: tutorialStep === 1 ? 12010 : "auto" }}>
          <div style={{ background: "#161616", border: `1px solid ${smartSearchFocused || smartSearch ? "#e53935" : "#2a2a2a"}`, borderRadius: 30, padding: "12px 18px", display: "flex", alignItems: "center", gap: 10, transition: "border-color 0.2s" }}>
            <span style={{ color: smartSearchFocused || smartSearch ? "#e53935" : "#666", fontSize: 16 }}>🔍</span>
            <input data-tutorial-target="search" type="text" value={smartSearch} onChange={e => { setSmartSearch(e.target.value); if (tutorialStep === 1 && e.target.value.trim()) setTutorialStep(2); }} onFocus={() => setSmartSearchFocused(true)} onBlur={() => setTimeout(() => setSmartSearchFocused(false), 200)} placeholder="¿Qué desea tu estómago hoy?" style={{ flex: 1, background: "transparent", border: "none", color: "#fff", fontSize: 13, outline: "none", fontFamily: "inherit" }} />
            {smartSearch && <button onClick={() => setSmartSearch("")} style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 16, padding: 0 }}>✕</button>}
          </div>

          {/* Sugerencias en tiempo real */}
          {smartSearchFocused && smartSearch.length >= 2 && smartSuggestions && smartSuggestions.total > 0 && (
            <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "#0d0d0d", border: "1px solid #1e1e1e", borderRadius: 14, overflow: "hidden", zIndex: 100, boxShadow: "0 6px 20px rgba(0,0,0,0.5)" }}>
              {smartSuggestions.lugares.map(s => (
                <div key={"L" + s.id} onClick={() => { const p = places.find(pl => pl.id === s.id); if (p) { setSelected(p); setSmartSearch(""); } }} style={{ padding: "10px 16px", borderBottom: "1px solid #1a1a1a", display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                  <span style={{ color: "#888", fontSize: 14 }}>📍</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: "#fff", fontSize: 13, margin: 0 }}>{s.name}</p>
                    <p style={{ color: "#888", fontSize: 11, margin: "2px 0 0" }}>{s.sub}</p>
                  </div>
                </div>
              ))}
              {smartSuggestions.tipos.map(s => (
                <div key={"T" + s.label} onClick={() => { setSmartSearch(s.label); setSmartSearchFocused(false); }} style={{ padding: "10px 16px", borderBottom: "1px solid #1a1a1a", display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                  <span style={{ color: "#888", fontSize: 14 }}>🥩</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: "#aaa", fontSize: 12, margin: 0 }}>Tipo: <span style={{ color: "#fff" }}>{s.label}</span></p>
                    <p style={{ color: "#888", fontSize: 11, margin: "2px 0 0" }}>{s.count} lugares</p>
                  </div>
                </div>
              ))}
              {smartSuggestions.enResenas.map((s, idx) => (
                <div key={"R" + idx} onClick={() => { const p = places.find(pl => Number(pl.id) === Number(s.placeId)); if (p) { setSelected(p); setSmartSearch(""); } }} style={{ padding: "10px 16px", borderBottom: "1px solid #1a1a1a", display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                  <span style={{ color: "#888", fontSize: 14 }}>💬</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: "#aaa", fontSize: 12, margin: 0 }}>En reseñas: "<span style={{ color: "#fff" }}>{s.snippet}</span>"</p>
                    <p style={{ color: "#888", fontSize: 11, margin: "2px 0 0" }}>{s.placeName}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Sin resultados → Don D' */}
          {smartSearch.length >= 2 && smartSuggestions && smartSuggestions.total === 0 && (
            <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 14, padding: "18px 18px", textAlign: "center", zIndex: 100, boxShadow: "0 6px 20px rgba(0,0,0,0.5)" }}>
              <p style={{ color: "#888", fontSize: 24, margin: "0 0 6px" }}>😶</p>
              <p style={{ color: "#fff", fontSize: 13, margin: "0 0 4px" }}>No encontramos lo que buscas por aquí</p>
              <p style={{ color: "#aaa", fontSize: 11, margin: "0 0 14px" }}>pero seguro Don D' puede ayudarte</p>
              <button onClick={() => { if (!donDOnline) return; const q = smartSearch; setSmartSearch(""); openDonD(q); }} style={{ background: "#e53935", color: "#fff", border: "none", borderRadius: 22, padding: "9px 18px", fontSize: 12, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 16, height: 16, background: "#fff", color: "#e53935", borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 900 }}>D'</span>
                Preguntarle a Don D'
              </button>
            </div>
          )}
        </div>

        {/* Categorías */}
        <div style={{ display: "flex", gap: 7, justifyContent: "center", flexWrap: "wrap", maxWidth: 600, margin: "0 auto 24px" }}>
          {CATS.map(cat => {
            const active = filter === cat.label;
            return <button key={cat.label} onClick={() => { setFilter(cat.label); setSignificantInteractionPending(true); }}
              style={{ padding: "7px 16px", borderRadius: 20, border: active ? "1px solid #e53935" : "1px solid #1e1e1e", background: active ? "rgba(229,57,53,0.1)" : "transparent", color: active ? "#e53935" : "#555", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit", transition: "all 0.18s" }}>
              {cat.label}
            </button>;
          })}
        </div>

        {highlighted.length > 0 && (
          <div style={{ maxWidth: 960, margin: "0 auto 14px" }}>
            <div style={{ background: "rgba(229,57,53,0.07)", border: "1px solid rgba(229,57,53,0.2)", borderRadius: 10, padding: "9px 14px", display: "flex", alignItems: "center", gap: 8 }}>
              <span>✨</span><span style={{ fontSize: 13, color: "#e57373" }}>Don D' encontró {highlighted.length} lugar{highlighted.length > 1 ? "es" : ""} para ti</span>
              <button onClick={() => setHighlighted([])} style={{ marginLeft: "auto", background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 12 }}>Limpiar</button>
            </div>
          </div>
        )}

        {showCatalogRows && (
          <div data-testid="catalog-rows" style={{ maxWidth: 960, margin: "0 auto", display: "flex", flexDirection: "column", gap: 26, paddingBottom: 100 }}>
            {catalogRows.map(row => (
              <section key={row.label} data-testid={`catalog-row-${row.label}`} aria-label={row.label} style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 10, padding: "0 2px" }}>
                  <div>
                    <h2 style={{ margin: 0, color: "#f4f4f4", fontSize: 18, lineHeight: 1.15, fontWeight: 900 }}>{row.label}</h2>
                    <p style={{ margin: "4px 0 0", color: "#666", fontSize: 11 }}>{row.subtitle}</p>
                  </div>
                  <span style={{ color: "#555", fontSize: 11, whiteSpace: "nowrap" }}>{row.places.length} {row.places.length === 1 ? "lugar" : "lugares"}</span>
                </div>
                <div role="list" aria-label={`Lugares de ${row.label}`} style={{ display: "flex", gap: 12, overflowX: "auto", overflowY: "hidden", overscrollBehaviorX: "contain", scrollSnapType: "x proximity", WebkitOverflowScrolling: "touch", padding: "2px 2px 8px", margin: "0 -2px" }}>
                  {row.places.map(p => (
                    <div role="listitem" key={p.id} data-catalog-place-id={String(p.id)} style={{ flex: "0 0 clamp(230px, 72vw, 290px)", scrollSnapAlign: "start", position: "relative" }}>
                      {isPendingPlace(p) && <div style={{ position: "absolute", top: -8, left: 12, zIndex: 10, background: "#f59e0b", borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 700, color: "#1a1a1a" }}>⏳ Pendiente de verificación</div>}
                      <Card place={p} reviews={reviewsByPlace.get(Number(p.id)) || EMPTY_ARR} hl={highlighted.includes(p.id)} onClick={setSelected} onViewImage={handleViewImage} user={user} onShareToast={handleShareToast} />
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Paginación superior */}
        {!showCatalogRows && totalPages > 1 && (
          <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 0 16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, flexWrap: "wrap" }}>
              <button onClick={() => { setCurrentPage(1); scrollToGrid(); }} disabled={safePage === 1} style={{ background: "#161616", border: "1px solid #2a2a2a", color: safePage === 1 ? "#444" : "#888", borderRadius: 8, padding: "8px 10px", fontSize: 12, minWidth: 36, cursor: safePage === 1 ? "default" : "pointer", opacity: safePage === 1 ? 0.5 : 1 }}>⏮</button>
              <button onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); scrollToGrid(); }} disabled={safePage === 1} style={{ background: "#161616", border: "1px solid #2a2a2a", color: safePage === 1 ? "#444" : "#888", borderRadius: 8, padding: "8px 10px", fontSize: 12, minWidth: 36, cursor: safePage === 1 ? "default" : "pointer", opacity: safePage === 1 ? 0.5 : 1 }}>‹</button>
              {(() => {
                const pages = [];
                const maxBtns = 5;
                let start = Math.max(1, safePage - 2);
                let end = Math.min(totalPages, start + maxBtns - 1);
                if (end - start < maxBtns - 1) start = Math.max(1, end - maxBtns + 1);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map(n => {
                  const isActive = n === safePage;
                  return <button key={n} onClick={() => { setCurrentPage(n); scrollToGrid(); }} style={{ background: isActive ? "#e53935" : "#161616", border: `1px solid ${isActive ? "#e53935" : "#2a2a2a"}`, color: isActive ? "#fff" : "#aaa", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: isActive ? 600 : 400, minWidth: 32, cursor: "pointer" }}>{n}</button>;
                });
              })()}
              <button onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); scrollToGrid(); }} disabled={safePage === totalPages} style={{ background: "#161616", border: "1px solid #2a2a2a", color: safePage === totalPages ? "#444" : "#888", borderRadius: 8, padding: "8px 10px", fontSize: 12, minWidth: 36, cursor: safePage === totalPages ? "default" : "pointer", opacity: safePage === totalPages ? 0.5 : 1 }}>›</button>
              <button onClick={() => { setCurrentPage(totalPages); scrollToGrid(); }} disabled={safePage === totalPages} style={{ background: "#161616", border: "1px solid #2a2a2a", color: safePage === totalPages ? "#444" : "#888", borderRadius: 8, padding: "8px 10px", fontSize: 12, minWidth: 36, cursor: safePage === totalPages ? "default" : "pointer", opacity: safePage === totalPages ? 0.5 : 1 }}>⏭</button>
            </div>
            <p style={{ color: "#555", fontSize: 10, textAlign: "center", margin: "8px 0 0" }}>Página {safePage} de {totalPages} · {sorted.length} lugares</p>
          </div>
        )}

        {!showCatalogRows && <div style={{ maxWidth: 960, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(270px,1fr))", gap: 18, paddingBottom: 90 }}>
          {paginated.map(p => (
            <div key={p.id} style={{ position: "relative" }}>
              {isPendingPlace(p) && <div style={{ position: "absolute", top: -8, left: 12, zIndex: 10, background: "#f59e0b", borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 700, color: "#1a1a1a" }}>⏳ Pendiente de verificación</div>}
              <Card place={p} reviews={reviewsByPlace.get(Number(p.id)) || EMPTY_ARR} hl={highlighted.includes(p.id)} onClick={setSelected} onViewImage={handleViewImage} user={user} onShareToast={handleShareToast} />
            </div>
          ))}
        </div>}

        {/* Paginación */}
        {!showCatalogRows && totalPages > 1 && (
          <div style={{ maxWidth: 960, margin: "0 auto", padding: "10px 0 140px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, flexWrap: "wrap" }}>
              <button onClick={() => { setCurrentPage(1); scrollToGrid(); }} disabled={safePage === 1} style={{ background: "#161616", border: "1px solid #2a2a2a", color: safePage === 1 ? "#444" : "#888", borderRadius: 8, padding: "8px 10px", fontSize: 12, minWidth: 36, cursor: safePage === 1 ? "default" : "pointer", opacity: safePage === 1 ? 0.5 : 1 }}>⏮</button>
              <button onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); scrollToGrid(); }} disabled={safePage === 1} style={{ background: "#161616", border: "1px solid #2a2a2a", color: safePage === 1 ? "#444" : "#888", borderRadius: 8, padding: "8px 10px", fontSize: 12, minWidth: 36, cursor: safePage === 1 ? "default" : "pointer", opacity: safePage === 1 ? 0.5 : 1 }}>‹</button>
              {(() => {
                const pages = [];
                const maxBtns = 5;
                let start = Math.max(1, safePage - 2);
                let end = Math.min(totalPages, start + maxBtns - 1);
                if (end - start < maxBtns - 1) start = Math.max(1, end - maxBtns + 1);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map(n => {
                  const isActive = n === safePage;
                  return <button key={n} onClick={() => { setCurrentPage(n); scrollToGrid(); }} style={{ background: isActive ? "#e53935" : "#161616", border: `1px solid ${isActive ? "#e53935" : "#2a2a2a"}`, color: isActive ? "#fff" : "#aaa", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: isActive ? 600 : 400, minWidth: 32, cursor: "pointer" }}>{n}</button>;
                });
              })()}
              <button onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); scrollToGrid(); }} disabled={safePage === totalPages} style={{ background: "#161616", border: "1px solid #2a2a2a", color: safePage === totalPages ? "#444" : "#888", borderRadius: 8, padding: "8px 10px", fontSize: 12, minWidth: 36, cursor: safePage === totalPages ? "default" : "pointer", opacity: safePage === totalPages ? 0.5 : 1 }}>›</button>
              <button onClick={() => { setCurrentPage(totalPages); scrollToGrid(); }} disabled={safePage === totalPages} style={{ background: "#161616", border: "1px solid #2a2a2a", color: safePage === totalPages ? "#444" : "#888", borderRadius: 8, padding: "8px 10px", fontSize: 12, minWidth: 36, cursor: safePage === totalPages ? "default" : "pointer", opacity: safePage === totalPages ? 0.5 : 1 }}>⏭</button>
            </div>
            <p style={{ color: "#555", fontSize: 10, textAlign: "center", margin: "12px 0 0" }}>Mostrando {(safePage - 1) * PLACES_PER_PAGE + 1}–{Math.min(safePage * PLACES_PER_PAGE, sorted.length)} de {sorted.length} lugares</p>
          </div>
        )}
      </div>

      {/* ── Botones fijos: se ocultan cuando el teclado está activo ── */}
      {!smartSearchFocused && <>

        {/* Toast "Sube al inicio" */}
        {scrollToast && (
          <div style={{ position: "fixed", bottom: 100, left: "50%", transform: "translateX(-50%)", background: "rgba(30,30,30,0.95)", border: "1px solid #333", color: "#ccc", fontSize: 12, padding: "8px 16px", borderRadius: 20, zIndex: 9900, pointerEvents: "none", whiteSpace: "nowrap" }}>
            Sube al inicio
          </div>
        )}

        {/* ── Flecha ↑ FAB arrastrable ── */}
        {(() => {
          const fabStyle = arrowPos.y !== null
            ? { position: "fixed", top: arrowPos.y, [arrowPos.side]: 12, bottom: "auto" }
            : { position: "fixed", bottom: 82, [arrowPos.side]: 22 };
          return (
            <button
              onPointerDown={e => { e.currentTarget.setPointerCapture(e.pointerId); const rect = e.currentTarget.getBoundingClientRect(); arrowDragRef.current = { dragging: false, startX: e.clientX, startY: e.clientY, startPosY: rect.top, lastY: rect.top }; }}
              onPointerMove={e => { const d = arrowDragRef.current; if (!d || !e.buttons) return; if (!d.dragging && (Math.abs(e.clientX - d.startX) > 6 || Math.abs(e.clientY - d.startY) > 6)) { d.dragging = true; e.currentTarget.style.transition = "none"; } if (!d.dragging) return; const newY = Math.max(10, Math.min(window.innerHeight - 60, d.startPosY + (e.clientY - d.startY))); d.lastY = newY; e.currentTarget.style.top = newY + "px"; e.currentTarget.style.bottom = "auto"; }}
              onPointerUp={e => { const d = arrowDragRef.current; if (!d) return; if (d.dragging) { e.currentTarget.style.transition = "top 0.25s ease, left 0.25s ease, right 0.25s ease, bottom 0.25s ease"; const side = e.clientX > window.innerWidth / 2 ? "right" : "left"; const np = { y: d.lastY, side }; localStorage.setItem('vq_fab_arrow', JSON.stringify(np)); setArrowPos(np); } else { window.scrollTo({ top: 0, behavior: "smooth" }); setScrollToast(true); setTimeout(() => setScrollToast(false), 2000); } arrowDragRef.current = { dragging: false, startX: 0, startY: 0, startPosY: 0, lastY: null }; }}
              style={{ ...fabStyle, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "50%", width: 44, height: 44, cursor: "grab", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: "#666", zIndex: 800, touchAction: "none", userSelect: "none", transition: "top 0.25s ease, left 0.25s ease, right 0.25s ease, bottom 0.25s ease" }}
              title="Volver al inicio">
              ↑
            </button>
          );
        })()}

        {/* ── Agregar lugar FAB ── */}
        <button
          onClick={() => { if (!user || !user.fullReg) { setAuthGateReason("add_place"); setShowAuthChoice(true); } else { setShowAddPlace(true); } }}
          aria-label="Agregar un lugar"
          style={{ position: "fixed", bottom: "calc(18px + env(safe-area-inset-bottom, 0px))", right: 18, background: "#161616", border: "1px solid #e53935", borderRadius: 28, padding: "0 17px", minHeight: 50, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, zIndex: 800, boxShadow: "0 6px 20px rgba(0,0,0,.45)" }}>
          <span aria-hidden="true" style={{ fontSize: 18 }}>📍</span>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#fff", fontFamily: "'Arial Black',sans-serif" }}>Agregar lugar</span>
        </button>

        {/* ── Don D' FAB / Bubble ── */}
        {!chatOpen && (selected || altSelected || profileContributionDetail || showAddPlace ? (
          <button onClick={() => { if (!donDOnline) { setSessionToast({ type: "info", text: "🔕 Don D' no está disponible ahora" }); setTimeout(() => setSessionToast(null), 3000); return; } openDonD(); }}
            title="Hablar con Don D'"
            style={{ position: "fixed", bottom: 26, left: 22, width: 48, height: 48, borderRadius: "50%", background: "#e53935", border: "2px solid #e53935", cursor: "pointer", boxShadow: "0 6px 24px rgba(229,57,53,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 4600, transition: "transform 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
            onMouseLeave={e => e.currentTarget.style.transform = ""}>
            <img className="don-d-logo-symbol" src={LOGO} alt="Don D'" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
          </button>
        ) : (
          <button onClick={() => { if (!donDOnline) { setSessionToast({ type: "info", text: "🔕 Don D' no está disponible ahora" }); setTimeout(() => setSessionToast(null), 3000); return; } openDonD(); }}
            style={{ position: "fixed", bottom: 26, left: 22, background: "#e53935", border: "none", borderRadius: 28, padding: "0 16px", height: 46, cursor: "pointer", boxShadow: "0 6px 24px rgba(229,57,53,0.5)", display: "flex", alignItems: "center", gap: 8, zIndex: 3900, transition: "all 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
            onMouseLeave={e => e.currentTarget.style.transform = ""}>
            <img className="don-d-logo-symbol" src={LOGO} alt="V" style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover" }} />
            <span style={{ fontSize: 12, fontWeight: 900, color: "#fff", fontFamily: "'Arial Black',sans-serif" }}>
              {hasSpokenToDonde ? "Hablar con Don D'" : "¿Qué como hoy?"}
            </span>
          </button>
        ))}
      </>}

      {/* ── Modal Explorar ────────────────────────────────────── */}
      {showExplore && (
        <div onClick={() => { setExploreShake(true); setTimeout(() => setExploreShake(false), 450); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(8px)" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#0d0d0d", borderRadius: 20, maxWidth: 340, width: "100%", padding: 28, border: "1px solid #1e1e1e", position: "relative", textAlign: "center" }}>
            <button onClick={() => setShowExplore(false)} className={exploreShake ? "vq-shake" : ""}
              style={{ position: "absolute", top: 14, right: 14, background: exploreShake ? "#e53935" : "rgba(255,255,255,0.08)", border: exploreShake ? "2px solid #fff" : "1px solid #333", color: "#fff", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}>✕</button>
            <p style={{ margin: "0 0 6px", fontSize: 12, color: "#666", textTransform: "uppercase", letterSpacing: "0.1em" }}>Explorar como:</p>
            <h2 style={{ margin: "0 0 24px", fontFamily: "'Arial Black',sans-serif", color: "#fff", fontSize: 20 }}>¿Quién eres?</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button onClick={() => handleExplore(true)}
                style={{ padding: "13px", background: "#e53935", border: "none", borderRadius: 12, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'Arial Black',sans-serif" }}>
                {user?.fullReg ? user.username : "Iniciar sesión"}
              </button>
              <button onClick={() => handleExplore(false)}
                style={{ padding: "13px", background: "transparent", border: "1px solid #2a2a2a", borderRadius: 12, color: "#888", fontSize: 14, cursor: "pointer" }}>
                Continuar como Visitante
              </button>
            </div>
          </div>
        </div>
      )}

      {showOnboarding && onboardingPlace && <OnboardingScreen place={onboardingPlace} msg={onboardingMsg} reviews={reviews} places={places} onNext={nextOnboarding} onSkip={() => setShowOnboarding(false)} onOpenDetail={() => { setSelected(onboardingPlace); setShowOnboarding(false); setFromOnboarding(true); }} onSendMsg={(msg) => { setShowOnboarding(false); setChatInitialMsg(null); openDonD(msg); }} />}
      {showExitToast && (
        <div style={{ position: "fixed", bottom: 100, left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.85)", color: "#fff", padding: "10px 20px", borderRadius: 20, fontSize: 13, zIndex: 9999, whiteSpace: "nowrap" }}>
          Presiona de nuevo para salir
        </div>
      )}
      {/* B7 — Confirmación de cierre de sesión (ID 4) */}
      {confirmLogout && !loggingOut && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 12000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(6px)" }}>
          <div style={{ background: "#0d0d0d", border: "1px solid #1e1e1e", borderRadius: 20, maxWidth: 320, width: "100%", padding: 28, textAlign: "center" }}>
            <div style={{ fontSize: 34, marginBottom: 12 }}>🔓</div>
            <p style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#fff" }}>¿Cerrar sesión?</p>
            <p style={{ margin: "0 0 22px", fontSize: 13, color: "#888" }}>Tus preferencias se conservarán para tu próximo ingreso.</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmLogout(false)} style={{ flex: 1, padding: "12px 0", background: "transparent", border: "1px solid #2a2a2a", borderRadius: 12, color: "#ccc", fontSize: 14, cursor: "pointer" }}>No</button>
              <button onClick={doLogout} style={{ flex: 1, padding: "12px 0", background: "#e53935", border: "none", borderRadius: 12, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Sí, salir</button>
            </div>
          </div>
        </div>
      )}
      {/* B7 — Overlay bloqueante durante logout (IDs 4,5,89): captura TODO tap hasta terminar */}
      {loggingOut && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(8,8,8,0.96)", zIndex: 13000, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, pointerEvents: "all", touchAction: "none" }}>
          <div style={{ width: 34, height: 34, border: "3px solid #2a2a2a", borderTopColor: "#e53935", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <p style={{ margin: 0, fontSize: 14, color: "#ccc", fontWeight: 600 }}>Cerrando sesión…</p>
        </div>
      )}
      {sessionToast && (
        <>
          <style>{`@keyframes vq-ghost { 0% { opacity: 0; transform: translate(-50%, 10px); } 15% { opacity: 1; transform: translate(-50%, 0); } 70% { opacity: 1; transform: translate(-50%, 0); } 100% { opacity: 0; transform: translate(-50%, -10px); } }`}</style>
          <div style={{
            position: "fixed", top: "30%", left: "50%", transform: "translateX(-50%)",
            background: sessionToast.type === 'login' ? "rgba(92, 184, 92, 0.95)" : "rgba(40, 40, 40, 0.95)",
            color: "#fff", padding: "14px 28px", borderRadius: 30, fontSize: 15, fontWeight: 600,
            zIndex: 99999, whiteSpace: "nowrap",
            boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
            border: sessionToast.type === 'login' ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(229,57,53,0.4)",
            animation: "vq-ghost 2s ease forwards",
            pointerEvents: "none",
          }}>
            {sessionToast.text}
          </div>
        </>
      )}
      {statusNotif && (() => {
        const { kind, item } = statusNotif;
        const rejected = !!item.metadata?.rejected;
        const label = kind === "place" ? item.name : ("Tu reseña en " + (places.find(p => Number(p.id) === Number(item.placeid || item.placeId))?.name || "un lugar"));
        const approvedMsg = kind === "place"
          ? "🎉 ¡Felicidades! " + item.name + " ya es parte de Venaqqi. Tienes un lugar más en tu lista de registros."
          : "🎉 ¡Gracias! Tu reseña ya es visible para todos.";
        return (
          <div style={{ position: "fixed", top: 16, left: 16, right: 16, zIndex: 9000, background: "#0d0d0d", border: rejected ? "1px solid #5a2d2d" : "1px solid #2d5a2d", borderRadius: 14, padding: "14px 16px", boxShadow: "0 6px 24px rgba(0,0,0,0.5)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <p style={{ margin: 0, fontSize: 13, color: "#fff", fontWeight: 700 }}>
                {rejected ? ("❌ No se aprobó: " + label) : approvedMsg}
              </p>
              <button onClick={dismissStatusNotif} style={{ background: "none", border: "none", color: "#666", fontSize: 16, cursor: "pointer", flexShrink: 0 }}>✕</button>
            </div>
            {rejected && item.metadata?.reject_reason && (
              <p style={{ margin: "6px 0 0", fontSize: 12, color: "#e57373" }}>Motivo: {item.metadata.reject_reason}</p>
            )}
            {rejected && (
              <button onClick={() => { if (kind === "place") setMyEditPlace({ ...item }); else setMyEditReview({ ...item }); dismissStatusNotif(); }}
                style={{ marginTop: 10, width: "100%", padding: 10, background: "#e53935", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>✏️ Reeditar y reenviar</button>
            )}
          </div>
        );
      })()}
      {myEditPlace && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 12000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => { if (e.target === e.currentTarget) setMyEditPlace(null); }}>
          <div style={{ background: "#0d0d0d", border: "1px solid #2a2a2a", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", padding: "24px 20px 40px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>✏️ Reeditar lugar</span>
              <button onClick={() => setMyEditPlace(null)} style={{ background: "none", border: "none", color: "#555", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            {myEditPlace.metadata?.reject_reason && <p style={{ margin: "0 0 16px", fontSize: 12, color: "#e57373" }}>Motivo del rechazo anterior: {myEditPlace.metadata.reject_reason}</p>}
            <p style={{ color: "#555", fontSize: 11, fontWeight: 700, letterSpacing: 1, margin: "0 0 8px" }}>FOTO DE PORTADA</p>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <img src={myEditPlace.image} alt="" style={{ width: 64, height: 64, borderRadius: 10, objectFit: "cover", border: "1px solid #2a2a2a" }} />
              <label style={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, padding: "8px 14px", fontSize: 12, color: "#90caf9", cursor: "pointer", fontWeight: 700 }}>
                📷 Cambiar foto
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={async e => {
                  const file = e.target.files[0]; if (!file) return;
                  const fd = new FormData(); fd.append("file", file); fd.append("upload_preset", "venaqqi_reviews");
                  try {
                    const res = await fetch("https://api.cloudinary.com/v1_1/dgdtlohqw/image/upload", { method: "POST", body: fd });
                    const data = await res.json();
                    if (data.secure_url) { setMyEditPlace(prev => ({ ...prev, image: data.secure_url })); }
                    else { setSessionToast({ type: "error", text: "❌ " + (data.error?.message || "Error al subir foto") }); setTimeout(() => setSessionToast(null), 4000); }
                  } catch { setSessionToast({ type: "error", text: "❌ No se pudo subir la foto" }); setTimeout(() => setSessionToast(null), 4000); }
                }} />
              </label>
            </div>
            {[
              { label: "NOMBRE", key: "name" },
              { label: "TIPO", key: "tipo" },
              { label: "DIRECCIÓN", key: "location" },
              { label: "LINK MAPS", key: "maps" },
            ].map(({ label, key }) => (
              <div key={key} style={{ marginBottom: 14 }}>
                <p style={{ color: "#555", fontSize: 11, fontWeight: 700, letterSpacing: 1, margin: "0 0 6px" }}>{label}</p>
                <input value={myEditPlace[key] || ""} onChange={e => setMyEditPlace(prev => ({ ...prev, [key]: e.target.value }))}
                  style={{ width: "100%", background: "#111", border: "1px solid #2a2a2a", borderRadius: 8, padding: "10px 12px", color: "#fff", fontSize: 13, boxSizing: "border-box", outline: "none" }} />
              </div>
            ))}
            <button onClick={async () => {
              const meta = { ...(myEditPlace.metadata || {}), rejected: false };
              const { error: updateError } = await supabase.from("restaurants").update({ name: myEditPlace.name, tipo: myEditPlace.tipo, location: myEditPlace.location, maps: myEditPlace.maps, image: myEditPlace.image, metadata: meta }).eq("id", myEditPlace.id);
        if (updateError) throw updateError;
        const { error } = await supabase.rpc("mark_place_resubmitted", { p_id: myEditPlace.id });
              if (!error) {
                setPlaces(prev => prev.map(pl => pl.id === myEditPlace.id ? { ...pl, ...myEditPlace, moderationStatus: "pending", pending: true, metadata: meta } : pl));
                setMyEditPlace(null);
                setSessionToast({ type: "info", text: "⏳ Reenviado a revisión" }); setTimeout(() => setSessionToast(null), 3000);
              }
            }} style={{ width: "100%", padding: 13, background: "#e53935", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", marginTop: 8 }}>Reenviar a revisión</button>
          </div>
        </div>
      )}
      {myEditReview && user && (
        <RevForm
          placeId={myEditReview.placeid || myEditReview.placeId} placeName={places.find(p => Number(p.id) === Number(myEditReview.placeid || myEditReview.placeId))?.name || ""} placeTipo="" user={user}
          editMode={true} initialValues={myEditReview} onClose={() => setMyEditReview(null)}
          onSubmit={async updated => {
            const authoredPayload = {
              sabor: updated.sabor, servicio: updated.servicio, satisfaccion: updated.satisfaccion,
              precio: updated.precio, total_bill: updated.total_bill, party_size: updated.party_size, platillo: updated.platillo, comentario: updated.comentario,
              precio_label: updated.precio_label, chef_msg: updated.chef_msg, staff_msg: updated.staff_msg,
              lo_bueno: updated.lo_bueno, extras: updated.extras, imagenes: updated.imagenes
            };
            const { data, error } = await supabase.rpc("resubmit_review_with_edits", { p_id: String(myEditReview.id), p_changes: authoredPayload });
            if (error) { setSessionToast({ type: "error", text: "❌ No se pudo reenviar la corrección: " + error.message }); setTimeout(() => setSessionToast(null), 5000); return; }
            const nextMetadata = { ...(myEditReview.metadata || {}), rejected: false, notifSeen: true };
            delete nextMetadata.reject_reason;
            const nextReview = data || { ...myEditReview, ...authoredPayload, pending: true, moderation_status: "pending", moderation_comment: null, moderated_at: null, moderated_by: null, metadata: nextMetadata };
            setReviews(prev => prev.map(rv => rv.id === myEditReview.id ? nextReview : rv));
            setMyEditReview(null);
            setSessionToast({ type: "info", text: "⏳ Reenviada a revisión" }); setTimeout(() => setSessionToast(null), 3000);
          }}
        />
      )}
      {showAuthChoice && (
        <div onClick={() => { setShowAuthChoice(false); setAuthGateReason("add_place"); }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 4700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 360, background: "#0d0d0d", border: "1px solid #1e1e1e", borderRadius: 16, padding: "28px 22px", position: "relative" }}>
            <button onClick={() => { setShowAuthChoice(false); setAuthGateReason("add_place"); }} style={{ position: "absolute", top: 14, right: 14, width: 28, height: 28, borderRadius: "50%", background: "#1e1e1e", border: "none", color: "#ccc", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>

            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ width: 56, height: 56, margin: "0 auto 12px", borderRadius: "50%", background: "#1a1a1a", border: "2px solid #e53935", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>🍽️</div>
              <h2 style={{ color: "#fff", fontSize: 18, fontWeight: 500, margin: "0 0 6px" }}>{authGateReason === "don_d" ? "Habla con Don D'" : "¡Hola!"}</h2>
              <p style={{ color: "#888", fontSize: 13, margin: 0, lineHeight: 1.5 }}>{authGateReason === "don_d" ? "Explorar Venaqqi es libre. Para conversar con Don D', inicia sesión o crea tu cuenta." : "Para agregar un lugar o dejar una reseña necesitas tu cuenta VENAQQI"}</p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button onClick={() => { setShowAuthChoice(false); setShowAuth(true); }} style={{ width: "100%", padding: 14, background: "#e53935", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer" }}>
                Ya tengo cuenta · Iniciar sesión
              </button>
              <button onClick={() => { setShowAuthChoice(false); setShowRegister(true); }} style={{ width: "100%", padding: 14, background: "transparent", border: "1px solid #444", borderRadius: 10, color: "#ccc", fontSize: 14, fontWeight: 500, cursor: "pointer" }}>
                Soy nuevo · Registrarme
              </button>
            </div>

            <p style={{ textAlign: "center", color: "#555", fontSize: 11, margin: "18px 0 0" }}>Solo te tomará un momento</p>
          </div>
        </div>
      )}
      {showRecoModal && (() => {
        const allCollage = categoryCollage(places, recoVisualSeed);
        const opciones = CATS.map((category, index) => {
          const isAll = category.label === "Todos";
          return {
            ...category,
            representative: isAll ? allCollage[0] || null : categoryRepresentative(category, places, recoVisualSeed, index),
            collage: isAll ? allCollage : null,
          };
        }).filter(category => category.representative);
        return (
          <div onClick={() => setShowRecoModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.94)", zIndex: 4200, display: "flex", alignItems: "flex-end", justifyContent: "center", backdropFilter: "blur(8px)" }}>
            <div role="dialog" aria-modal="true" aria-labelledby="craving-title" onClick={e => e.stopPropagation()} style={{ background: "#080808", borderRadius: 0, width: "100%", maxWidth: 720, height: "100dvh", maxHeight: "100dvh", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)", boxSizing: "border-box", border: "1px solid rgba(229,57,53,0.28)", boxShadow: "0 -18px 50px rgba(0,0,0,0.6)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ flexShrink: 0 }}>
                <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px", background: "#080808" }}>
                  <div style={{ width: 42, height: 4, borderRadius: 2, background: "#333" }} />
                </div>
                <div style={{ padding: "12px 18px 16px", background: "linear-gradient(135deg, rgba(229,57,53,0.34) 0%, rgba(112,23,31,0.20) 42%, rgba(8,8,8,0.98) 100%)", borderBottom: "1px solid rgba(229,57,53,0.28)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div className="don-d-logo-shell" style={{ width: 68, height: 68, borderRadius: "50%", border: "2px solid #e53935", boxShadow: "0 0 24px rgba(229,57,53,0.28)", flexShrink: 0 }}>
                      <img src={DON_D_LOGO_URL} alt="Don D'" className="don-d-logo-symbol" style={{ width: "100%", height: "100%" }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: "0 0 2px", color: "#ff6b68", fontSize: 11, fontWeight: 900, letterSpacing: 1.1 }}>DON D’ RECOMIENDA</p>
                      <p id="craving-title" style={{ margin: 0, fontSize: 22, lineHeight: 1.1, fontWeight: 900, color: "#fff", fontFamily: "'Arial Black', sans-serif" }}>¿Qué se te antoja?</p>
                      <p style={{ margin: "5px 0 0", fontSize: 12, lineHeight: 1.4, color: "#c9c9c9" }}>Soy Don D’. Elige qué se te antoja y te llevo a algo bueno.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ flex: 1, overflowY: "auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(148px, 1fr))", alignContent: "start", gap: 10, padding: "14px 16px max(24px, env(safe-area-inset-bottom))" }}>
                {opciones.map(op => (
                  <button key={op.label} onClick={() => {
                    setShowRecoModal(false);
                    setFilter(op.label);
                    setCurrentPage(1);
                    setSignificantInteractionPending(true);
                    setTimeout(scrollToGrid, 0);
                  }} style={{ minHeight: 112, background: "#111", border: "1px solid #292929", borderRadius: 16, padding: 6, display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: 7, cursor: "pointer", textAlign: "center", fontFamily: "inherit", overflow: "hidden" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "#e53935"; e.currentTarget.style.boxShadow = "0 0 0 1px rgba(229,57,53,0.22)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#292929"; e.currentTarget.style.boxShadow = "none"; }}>
                    {op.collage?.length
                      ? <div aria-hidden="true" style={{ width: "100%", height: 78, display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gap: 2, borderRadius: 12, overflow: "hidden", background: "#151515" }}>
                          {op.collage.map(place => <img key={place.id} src={place.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />)}
                        </div>
                      : <img src={op.representative.image} alt="" style={{ width: "100%", height: 78, borderRadius: 12, objectFit: "cover", flexShrink: 0 }} />}
                    <span style={{ fontSize: 13, lineHeight: 1.15, fontWeight: 800, color: "#f1f1f1", padding: "0 4px 3px" }}>{op.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {showPwaUpdateNotice && (
        <div style={{ position: "fixed", left: 14, right: 14, bottom: 14, zIndex: 9800, display: "flex", justifyContent: "center", pointerEvents: "none" }}>
          <div style={{ width: "100%", maxWidth: 420, background: "rgba(13,13,13,0.96)", border: "1px solid #2a2a2a", borderRadius: 16, padding: 14, boxShadow: "0 12px 30px rgba(0,0,0,0.45)", pointerEvents: "auto" }}>
            <p style={{ margin: 0, color: "#fff", fontWeight: 900, fontSize: 15 }}>Nueva versión disponible</p>
            <p style={{ margin: "4px 0 12px", color: "#aaa", fontSize: 12 }}>Actual: {pwaUpdate.currentVersion || APP_VERSION}{pwaUpdate.newVersion ? ` · Nueva: ${pwaUpdate.newVersion}` : ""}</p>
            {pwaUpdate.preparing && !pwaUpdate.applying && <p style={{ margin: "-6px 0 12px", color: "#bbb", fontSize: 12 }}>La actualización se descargará cuando la confirmes.</p>}
            {pwaUpdate.error && <p role="alert" style={{ margin: "-6px 0 12px", color: "#ff8a80", fontSize: 12 }}>{pwaUpdate.error}</p>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button disabled={pwaUpdate.applying} onClick={() => { snoozePwaUpdate(); setPwaUpdate(null); }} style={{ background: "#151515", border: "1px solid #2a2a2a", color: "#aaa", borderRadius: 10, padding: "9px 12px", fontWeight: 700, opacity: pwaUpdate.applying ? 0.55 : 1 }}>Después</button>
              <button disabled={pwaUpdate.applying} onClick={handlePwaUpdate} style={{ background: "#e53935", border: "none", color: "#fff", borderRadius: 10, padding: "9px 12px", fontWeight: 900, opacity: pwaUpdate.applying ? 0.7 : 1 }}>{pwaUpdate.applying ? (pwaUpdate.preparing ? "Preparando…" : "Actualizando…") : "Actualizar ahora"}</button>
            </div>
          </div>
        </div>
      )}
      {showPasswordReset && <PasswordResetModal onClose={() => setShowPasswordReset(false)} />}
      {showAuth && <AuthModal onAuth={u => { setUser(u); setAccessMode(ACCESS_MODES.AUTHENTICATED); persistAccessMode(ACCESS_MODES.AUTHENTICATED); localStorage.setItem('venaqqi_user', JSON.stringify(u)); localStorage.setItem('venaqqi_onboarding_visto', '1'); setShowAuth(false); setAuthFromOnboarding(false); setShowWelcome(false); setOnboardingStep(0); setSessionToast({ type: 'login', text: `👋 Bienvenido, ${u.username || u.email.split('@')[0]}` }); setTimeout(() => setSessionToast(null), 2000); }} onClose={() => { setShowAuth(false); setPendingReview(false); if (authFromOnboarding) { setAuthFromOnboarding(false); setShowWelcome(true); setOnboardingStep(0); } }} onSwitchToRegister={() => { setShowWelcome(false); setShowRegister(true); if (authFromOnboarding) { setRegisterFromOnboarding(true); } }} />}
      {showRegister && <RegisterModal purpose={authGateReason} onRegister={u => { setUser(u); setAccessMode(ACCESS_MODES.AUTHENTICATED); persistAccessMode(ACCESS_MODES.AUTHENTICATED); localStorage.setItem('venaqqi_user', JSON.stringify(u)); setShowRegister(false); if (authGateReason === "don_d") { setTutorialStep(0); } else { setTutorialStep(1); if (registerFromOnboarding) { setRegisterFromOnboarding(false); setShowWelcome(true); setOnboardingStep(1); } else { setShowAddPlace(true); } } setSessionToast({ type: 'login', text: `🎉 ¡Bienvenido a VENAQQI, ${u.username || u.email.split('@')[0]}!` }); setTimeout(() => setSessionToast(null), 2000); }} onClose={() => { setShowRegister(false); if (registerFromOnboarding) { setRegisterFromOnboarding(false); setShowWelcome(true); setOnboardingStep(0); } }} onGuest={() => { setShowRegister(false); setRegisterFromOnboarding(false); setShowWelcome(true); setOnboardingStep(-1); }} />}
      {showAddPlace && user?.fullReg && <AddPlaceForm user={user} onAdd={handleAddPlace} onClose={() => setShowAddPlace(false)} places={places} onOpenReview={(place) => { setSelected(place); setShowAddPlace(false); }} />}
      {selected && isSuerte && (() => {
        const suerteRevs = visibleReviews.filter(r => Number(r.placeid || r.placeId) === Number(selected.id));
        const suerteCtx = {
          placeName: selected.name, tipo: selected.tipo,
          comentario: suerteRevs[suerteRevs.length-1]?.comentario || "",
          platillos: suerteRevs.map(r => r.platillo).filter(Boolean).slice(-3).reverse(),
          tambienVisibles: suerteSlots.map(p => ({ name: p.name, tipo: p.tipo }))
        };
        if (!donDContext) setTimeout(() => setDonDContext(suerteCtx), 0);
        return <SuerteView key={chatOpen ? "open" : "closed"} initialCount={chatOpen ? 0 : 1} place={selected} reviews={suerteRevs} hideClose={false}
          onClose={() => { setSelected(null); setIsSuerte(false); setChatOpen(false); setDonDContext(null); setSuerteSlots([]); }}
          onOpenDetail={() => { setIsSuerte(false); setChatOpen(false); setFromOnboarding(true); if (!donDContext) setDonDContext(suerteCtx); }}
          onCloseDonde={chatOpen ? () => setChatOpen(false) : null}
          onResena={() => { if (!user) { setPendingReview(true); setShowAuth(true); } else { setIsSuerte(false); setChatOpen(false); setFromOnboarding(true); } }}
          onViewImages={() => { const g = buildGallery(selected, reviews.filter(r => Number(r.placeid || r.placeId) === Number(selected.id))); setImageViewer({ images: g.images, startIndex: 0, metas: g.metas }); }}
        />;
      })()}
      {selected && isSuerte && !chatOpen && !altSelected && suerteSlots.length > 0 && (
        <AlternateCards
          slots={suerteSlots}
          reviews={reviews}
          onSelectAlt={p => setAltSelected(p)}
          onViewImage={(imgs, idx) => setImageViewer({ images: imgs, startIndex: idx })}
        />
      )}
      {selected && !isSuerte && (() => { const detailRevs = visibleReviews.filter(r => Number(r.placeid || r.placeId) === Number(selected.id)); const related = buildRelatedPlaces(selected, places, visibleReviews); if (!donDContext) setTimeout(() => setDonDContext({ placeName: selected.name, tipo: selected.tipo, comentario: detailRevs[detailRevs.length-1]?.comentario || "", platillos: detailRevs.map(r => r.platillo).filter(Boolean).slice(-3).reverse() }), 0); return <Detail place={selected} reviews={detailRevs} relatedPlaces={related} onSelectRelated={setAltSelected} user={user} onClose={() => { setSelected(null); setFromOnboarding(false); setDonDContext(null); setDeepLinkRef(null); setHighlightLlevame(false); }} onAdd={async r => {
        const isAdminRev = !!(user?.email && user.email === 'venaqqi@gmail.com');
        setReviews(p => [...p, { ...r, pending: !isAdminRev, moderation_status: isAdminRev ? "approved" : "pending" }]); 
        const { id, ...rest } = r;
        const payload = { ...rest, pending: !isAdminRev, moderation_status: isAdminRev ? "approved" : "pending", moderated_at: isAdminRev ? new Date().toISOString() : null, moderated_by: isAdminRev ? user?.userId : null, metadata: { ...rest, official: isAdminRev } };
        const { error } = await supabase.from("reviews").insert([payload]);
        if (error) console.error('❌ Error guardando reseña:', error.message, payload);
        else { log('✅ Reseña guardada'); trackTesterActivity(TESTER_ACTIVITY_ACTIONS.REVIEW, { userId: user?.userId, dedupeKey: `review:${r.placeid || r.placeId}:${Date.now()}`, dedupeMs: 500 }); if (!isAdminRev) { setSessionToast({ type: 'info', text: '⏳ Tu reseña será revisada por Venaqqi' }); setTimeout(() => setSessionToast(null), 3500); } }
      }} onAuth={() => { setShowAuth(true); setPendingReview(true); }} openReviewAfterAuth={pendingReview} onAfterAuth={() => setPendingReview(false)} onBack={fromOnboarding ? () => { setSelected(null); setShowOnboarding(true); setFromOnboarding(false); } : null} onViewImage={(imgs, idx, metas) => setImageViewer({ images: imgs, startIndex: idx, metas: metas || null })} onEditReview={updated => setReviews(prev => prev.map(rv => rv.id === updated.id ? updated : rv))} setSessionToast={setSessionToast} deepLinkRef={deepLinkRef} highlightLlevame={highlightLlevame} onBlockUser={handleBlockUser} />; })()} 
      
      {altSelected && <Detail place={altSelected} reviews={visibleReviews.filter(r => Number(r.placeid || r.placeId) === Number(altSelected.id))} relatedPlaces={buildRelatedPlaces(altSelected, places, visibleReviews)} onSelectRelated={setAltSelected} user={user} onClose={() => setAltSelected(null)} onAdd={async r => {
        const isAdminRev = !!(user?.email && user.email === 'venaqqi@gmail.com');
        setReviews(p => [...p, { ...r, pending: !isAdminRev, moderation_status: isAdminRev ? "approved" : "pending" }]); 
        const { id, ...rest } = r;
        const payload = { ...rest, pending: !isAdminRev, moderation_status: isAdminRev ? "approved" : "pending", moderated_at: isAdminRev ? new Date().toISOString() : null, moderated_by: isAdminRev ? user?.userId : null, metadata: { ...rest, official: isAdminRev } };
        const { error } = await supabase.from("reviews").insert([payload]);
        if (error) console.error('❌ Error guardando reseña:', error.message, payload);
        else { log('✅ Reseña guardada'); trackTesterActivity(TESTER_ACTIVITY_ACTIONS.REVIEW, { userId: user?.userId, dedupeKey: `review:${r.placeid || r.placeId}:${Date.now()}`, dedupeMs: 500 }); if (!isAdminRev) { setSessionToast({ type: 'info', text: '⏳ Tu reseña será revisada por Venaqqi' }); setTimeout(() => setSessionToast(null), 3500); } }
      }} onAuth={() => { setShowAuth(true); setPendingReview(true); }} openReviewAfterAuth={pendingReview} onAfterAuth={() => setPendingReview(false)} onBack={() => setAltSelected(null)} onViewImage={(imgs, idx, metas) => setImageViewer({ images: imgs, startIndex: idx, metas: metas || null })} onEditReview={updated => setReviews(prev => prev.map(rv => rv.id === updated.id ? updated : rv))} setSessionToast={setSessionToast} onBlockUser={handleBlockUser} />}
      {profileContributionDetail && user?.fullReg && (() => {
        const detail = profileContributionDetail;
        const detailPlace = detail.place;
        const detailReviews = buildProfileDetailReviews({
          allReviews: reviews,
          visibleReviews,
          placeId: detailPlace.id,
          highlightReviewId: detail.reviewId,
          userId: user.userId,
        });
        const returnToProfile = () => {
          setProfileContributionDetail(null);
          setShowProfile(true);
        };
        return <Detail
          place={detailPlace}
          reviews={detailReviews}
          relatedPlaces={buildRelatedPlaces(detailPlace, places, visibleReviews)}
          onSelectRelated={place => setProfileContributionDetail(prev => ({ ...prev, place, reviewId: null, type: "related", entry: null }))}
          user={user}
          onClose={returnToProfile}
          onBack={returnToProfile}
          highlightReviewId={detail.reviewId}
          contributionContext={detail.type === "related" ? null : { kind: detail.type === "places" ? "place" : "review", status: detail.entry?.status }}
          onAdd={async r => {
            const isAdminRev = !!(user?.email && user.email === 'venaqqi@gmail.com');
            setReviews(prev => [...prev, { ...r, pending: !isAdminRev, moderation_status: isAdminRev ? "approved" : "pending" }]);
            const { id, ...rest } = r;
            const payload = { ...rest, pending: !isAdminRev, moderation_status: isAdminRev ? "approved" : "pending", moderated_at: isAdminRev ? new Date().toISOString() : null, moderated_by: isAdminRev ? user?.userId : null, metadata: { ...rest, official: isAdminRev } };
            const { error } = await supabase.from("reviews").insert([payload]);
            if (error) console.error('❌ Error guardando reseña:', error.message, payload);
            else { trackTesterActivity(TESTER_ACTIVITY_ACTIONS.REVIEW, { userId: user?.userId, dedupeKey: `review:${r.placeid || r.placeId}:${Date.now()}`, dedupeMs: 500 }); if (!isAdminRev) { setSessionToast({ type: 'info', text: '⏳ Tu reseña será revisada por Venaqqi' }); setTimeout(() => setSessionToast(null), 3500); } }
          }}
          onAuth={() => {}}
          onViewImage={(imgs, idx, metas) => setImageViewer({ images: imgs, startIndex: idx, metas: metas || null })}
          onEditReview={updated => setReviews(prev => prev.map(rv => rv.id === updated.id ? updated : rv))}
          setSessionToast={setSessionToast}
          onBlockUser={handleBlockUser}
        />;
      })()}
      {/* 6.1 / 6.2 — Perfil y borrado de cuenta */}
      {showProfile && user?.fullReg && (
        <ProfileModal user={user} places={places} reviews={reviews} sessionReady={!sessionRestoring} setSessionToast={setSessionToast}
          resumeState={profileResumeState}
          onResumeStateChange={setProfileResumeState}
          onEditContribution={({ entry, resumeState }) => { setProfileResumeState(resumeState); if (entry.kind === "review") setMyEditReview(entry.review); else setMyEditPlace(entry.place); setShowProfile(false); }}
          onOpenContribution={({ type, entry, resumeState }) => {
            const place = entry.place;
            if (!place) { setSessionToast({ type: "error", text: "❌ Ese lugar ya no está disponible" }); setTimeout(() => setSessionToast(null), 3000); return; }
            setProfileResumeState(resumeState);
            setProfileContributionDetail({ type, entry, place, reviewId: type === "reviews" ? entry.review.id : null });
            setShowProfile(false);
          }}
          onClose={() => setShowProfile(false)}
          onNotificationNavigate={(deepLink, notificationId) => {
            setShowProfile(false);
            const target = new URL(deepLink || "/", window.location.origin);
            if (notificationId) target.searchParams.set("notification", notificationId);
            window.location.assign(`${target.pathname}${target.search}${target.hash}`);
          }}
          onOpenFeedback={() => { setFeedbackOrigin("manual_menu"); setFeedbackHasPrevious(false); setShowFeedback(true); }}
          onAccountDeleted={(ownIds) => {
            const uid = user.userId;
            const ownSet = new Set(ownIds);
            setReviews(prev => prev.filter(r => r.userid !== uid && !ownSet.has(Number(r.placeid || r.placeId))));
            setPlaces(prev => prev.filter(pl => pl.addedBy !== uid));
            sessionStorage.clear(); localStorage.clear();
            setUser(null); setShowProfile(false);
            setSessionToast({ type: "logout", text: "✅ Tu cuenta y tu contenido fueron eliminados" });
            setTimeout(() => setSessionToast(null), 3000);
          }} />
      )}
      {/* 5.2 — Gestión de usuarios bloqueados */}
      {showBlockedModal && (
        <div onClick={() => setShowBlockedModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9600, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#0d0d0d", border: "1px solid #1e1e1e", borderRadius: 16, padding: 20, width: "100%", maxWidth: 360 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#fff" }}>🚫 Usuarios bloqueados</p>
              <button onClick={() => setShowBlockedModal(false)} style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 16 }}>✕</button>
            </div>
            {blockedUsers.length === 0 ? (
              <p style={{ margin: 0, fontSize: 12, color: "#666" }}>No tienes usuarios bloqueados.</p>
            ) : blockedUsers.map(b => (
              <div key={b.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #1a1a1a" }}>
                <span style={{ fontSize: 13, color: "#ccc" }}>{b.username}</span>
                <button onClick={() => handleUnblockUser(b.id)} style={{ background: "none", border: "1px solid #2a2a2a", borderRadius: 8, color: "#5cb85c", fontSize: 11, padding: "4px 10px", cursor: "pointer" }}>Desbloquear</button>
              </div>
            ))}
          </div>
        </div>
      )}
      {imageViewer && <ImageViewer images={imageViewer.images} startIndex={imageViewer.startIndex} metas={imageViewer.metas || null} onBack={() => setImageViewer(null)} onClose={() => { setImageViewer(null); setSelected(null); setIsSuerte(false); setChatOpen(false); }} />}
      {adminImageViewer && isAdmin && <ImageViewer images={adminImageViewer.images} startIndex={adminImageViewer.startIndex} zIndex={13000} onBack={() => setAdminImageViewer(null)} onClose={() => setAdminImageViewer(null)} />}
      {splashVisible && (
        <div style={{ position: "fixed", inset: 0, zIndex: 19999, background: "#080808", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", animation: `splashfade ${splashDuration}s ease forwards` }}>
          <img className="don-d-logo-symbol" src={LOGO} alt="Venaqqi" style={{ width: 110, height: 110, objectFit: "contain", animation: "hatbob 0.7s ease-in-out infinite", marginBottom: 20 }} />
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 36, color: "#fff", letterSpacing: 4, marginBottom: 6 }}>VENAQQI</div>
          <div style={{ fontSize: 12, color: "#555", letterSpacing: 2, fontWeight: 700, marginBottom: 28 }}>come donde quieras</div>
          <div style={{ fontSize: 13, color: "#aaa", fontWeight: 700, fontFamily: "'Arial Black', sans-serif" }}>{splashMode === "llevando" ? "Te llevamos al lugar..." : "Buscando lo mejor contigo..."}</div>
        </div>
      )}
      <Donde key={conversationIdentity(user)} isOpen={chatOpen && !!user?.fullReg && !loggingOut && !confirmLogout} onClose={() => { setChatOpen(false); setUserInitialMsg(null); }} onHL={setHighlighted} reviews={reviews} places={discoverablePlaces} user={user} isGuest={accessMode === ACCESS_MODES.GUEST && !user} initialMsg={chatInitialMsg} userInitialMsg={userInitialMsg} onFirstMessage={() => { setHasSpokenToDonde(true); localStorage.setItem('venaqqi_donde_used', '1'); }} onInteractionComplete={() => setSignificantInteractionPending(true)} visibleContext={donDContext} onVenaqqi={handleVenaqqi} newChatRequest={newChatRequest} onLogin={() => leaveDonDFor(() => setShowAuth(true))} onRegister={() => leaveDonDFor(() => setShowRegister(true))} onGoogle={() => leaveDonDFor(() => runGoogleSignIn(message => setSessionToast({ type: "error", text: message })))} onNewChat={() => { setSelected(null); setIsSuerte(false); setSuerteSlots([]); setFromOnboarding(false); setDonDContext(null); setChatInitialMsg(null); setUserInitialMsg(null); setDeepLinkRef(null); window.scrollTo({ top: 0, behavior: "smooth" }); }} />

      {/* Panel Admin */}
      {showAdmin && isAdmin && (() => {
        const adminDragStart = { y: null };
        // 5.3 — más reciente primero también en pendientes (aprobados y usuarios ya venían ordenados)
        const pendingPlaces = pendingModerationPlaces(places).sort((a, b) => Number(b.id) - Number(a.id));
        const pendingReviews = reviews.filter(r => r.pending && !r.metadata?.rejected).sort((a, b) => Number(b.id) - Number(a.id));
        const approvedPlaces = approvedAdminPlaces(places).sort((a, b) => Number(b.id) - Number(a.id));
        const approvedReviews = reviews.filter(r => !r.pending && r.userid !== "venaqqi").sort((a, b) => Number(b.id) - Number(a.id));
        const approvedPlacesPage = approvedPlaces.slice((adminPageLugaresAprob - 1) * ADMIN_PAGE_SIZE, adminPageLugaresAprob * ADMIN_PAGE_SIZE);
        const approvedReviewsPage = approvedReviews.slice((adminPageResenasAprob - 1) * ADMIN_PAGE_SIZE, adminPageResenasAprob * ADMIN_PAGE_SIZE);
        const usuariosOrdenados = Object.entries(adminUsers).sort((a, b) => new Date(adminUsersFecha[b[0]] || 0) - new Date(adminUsersFecha[a[0]] || 0));
        const usuariosPage = usuariosOrdenados.slice((adminPageUsuarios - 1) * ADMIN_PAGE_SIZE, adminPageUsuarios * ADMIN_PAGE_SIZE);
        return (
        <div style={ADMIN_PANEL_SHELL_STYLE}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px 12px", borderBottom: "1px solid #1e1e1e", background: "#080808", touchAction: "none" }}
            onTouchStart={e => { adminDragStart.y = e.touches[0].clientY; }}
            onTouchEnd={e => {
              if (adminDragStart.y === null) return;
              if (e.changedTouches[0].clientY - adminDragStart.y > 60) { setShowAdmin(false); setAdminPreview(null); }
              adminDragStart.y = null;
            }}>
            <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: "#e53935", letterSpacing: 2 }}>🛡 Panel Admin</span>
            <button onClick={() => { setShowAdmin(false); setAdminPreview(null); }} style={{ background: "none", border: "none", color: "#888", fontSize: 22, cursor: "pointer" }}>×</button>
          </div>
          {/* Tabs */}
          {!adminPreview && (
            <div style={{ display: "flex", borderBottom: "1px solid #1e1e1e", background: "#0a0a0a", flexShrink: 0 }}>
              {[
                { key: "lugares", label: "LUGARES", badge: pendingPlaces.length },
                { key: "resenas", label: "RESEÑAS", badge: pendingReviews.length, badgeColor: "#f59e0b" },
                { key: "usuarios", label: "USUARIOS", badge: 0 },
                { key: "testers", label: "TESTERS", badge: 0 },
                { key: "feedback", label: "FEEDBACK", badge: 0 },
                { key: "config", label: "CONFIG", badge: 0 },
              ].map(t => (
                <button key={t.key} onClick={() => setAdminTab(t.key)} style={{ flex: 1, padding: "10px 4px", background: "none", border: "none", borderBottom: adminTab === t.key ? "2px solid #e53935" : "2px solid transparent", color: adminTab === t.key ? "#e53935" : "#555", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "'Arial Black', Arial, sans-serif", letterSpacing: 0.5 }}>
                  {t.label}{t.badge > 0 && <span style={{ display: "inline-block", background: t.badgeColor || "#e53935", color: "#fff", fontSize: 8, borderRadius: 8, padding: "1px 5px", marginLeft: 3, fontWeight: 900 }}>{t.badge}</span>}
                </button>
              ))}
            </div>
          )}
          {/* Vista previa - modal evaluacion */}
          {adminPreview && (
            <div style={ADMIN_PREVIEW_SHELL_STYLE}>
              <div style={{ background: "#1a1500", borderBottom: "1px solid #2a2000", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14 }}>👁</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: adminPreview.approved ? "#90caf9" : "#f59e0b", letterSpacing: 1 }}>{adminPreview.approved ? "PANEL ADMIN · CONSULTA" : "EVALUANDO"}</span>
                </div>
                <button onClick={closeAdminPreview} style={{ background: "#111", border: "1px solid #2a2a2a", color: "#aaa", borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                  ← Volver
                </button>
              </div>
              {adminPreview.type === "place" ? (
                <div data-testid="admin-place-preview-scroll" style={{ ...ADMIN_PREVIEW_SCROLL_STYLE, padding: "0 0 24px" }}>
                  {(() => {
                    const gallery = buildAdminPlaceImageGallery(adminPreview.item, reviews);
                    if (!gallery.length) return null;
                    return (
                      <button
                        type="button"
                        aria-label={`Ver imágenes completas de ${adminPreview.item.name}`}
                        onClick={() => setAdminImageViewer({ images: gallery, startIndex: 0 })}
                        style={{ position: "relative", display: "block", width: "100%", padding: 0, border: 0, background: "#000", cursor: "zoom-in" }}
                      >
                        <img src={gallery[0]} alt={adminPreview.item.name || "Lugar"} style={{ display: "block", width: "100%", height: 220, objectFit: "cover" }} />
                        <span style={{ position: "absolute", right: 12, bottom: 12, background: "rgba(0,0,0,.78)", border: "1px solid #444", color: "#fff", borderRadius: 999, padding: "7px 11px", fontSize: 11, fontWeight: 700 }}>
                          🔍 Ver completa{gallery.length > 1 ? ` · ${gallery.length} fotos` : ""}
                        </span>
                      </button>
                    );
                  })()}
                  <div style={{ padding: "16px 20px" }}>
                    <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#fff" }}>{adminPreview.item.name}</p>
                    <p style={{ margin: "4px 0 0", fontSize: 13, color: "#e53935" }}>{adminPreview.item.tipo}</p>
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: "#666" }}>{adminPreview.item.location}</p>
                    <p style={{ margin: "8px 0 0", fontSize: 12, color: "#555" }}>Agregado por: <span style={{ color: "#aaa" }}>{adminContributor(adminPreview.item.addedBy).primary}</span></p>
                    {adminPreview.item.tags?.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
                        {adminPreview.item.tags.map(t => <span key={t} style={{ background: "#1e1e1e", border: "1px solid #2a2a2a", borderRadius: 20, padding: "3px 10px", fontSize: 11, color: "#888" }}>{t}</span>)}
                      </div>
                    )}
                    {(() => {
                      const placeReviews = adminPlaceReviews(adminPreview.item, reviews);
                      return (
                        <div style={{ marginTop: 20 }}>
                          <p style={{ fontSize: 11, color: "#7986cb", letterSpacing: 1, margin: "0 0 8px", fontWeight: 700 }}>CONTEXTO ADMIN · RESEÑAS ({placeReviews.length})</p>
                          {placeReviews.length === 0 ? (
                            <p style={{ margin: 0, color: "#555", fontSize: 12 }}>Este lugar todavía no tiene reseñas activas.</p>
                          ) : placeReviews.map(r => (
                            <div key={r.id} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: 12, padding: "12px 14px", marginBottom: 10 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", marginBottom: 7 }}>
                                <p style={{ margin: 0, fontSize: 12, color: "#aaa", fontWeight: 600 }}>{r.username || "Usuario"} <span style={{ color: "#555", fontWeight: 400 }}>· {r.fecha || "sin fecha"}</span></p>
                                <span style={{ color: r.pending ? "#f59e0b" : "#4caf50", border: `1px solid ${r.pending ? "#5b4600" : "#234d23"}`, borderRadius: 999, padding: "2px 7px", fontSize: 9, fontWeight: 700 }}>{r.pending ? "Pendiente" : "Publicada"}</span>
                              </div>
                              <p style={{ margin: "0 0 6px", fontSize: 12, color: "#bbb" }}>🍽 {r.platillo || "(sin platillo)"}</p>
                              <p style={{ margin: "0 0 9px", fontSize: 12, color: "#ddd", lineHeight: 1.5 }}>{r.comentario || "(sin comentario)"}</p>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, fontSize: 10, color: "#666", marginBottom: r.imagenes?.length ? 10 : 0 }}>
                                <span>Sabor: {r.sabor ?? "—"}/5</span>
                                <span>Servicio: {r.servicio ?? "—"}/5</span>
                                <span>Satisfacción: {r.satisfaccion ?? "—"}/5</span>
                                {r.precio_label && <span>Precio: {r.precio_label}</span>}
                              </div>
                              {r.imagenes?.length > 0 && (
                                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                                  {r.imagenes.map((img, i) => (
                                    <img key={i} src={img.url || img} alt="" onClick={() => setAdminImageViewer({ images: r.imagenes.map(im => im.url || im), startIndex: i })}
                                      style={{ width: 68, height: 68, borderRadius: 8, objectFit: "cover", cursor: "zoom-in" }} />
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <div data-testid="admin-review-preview-scroll" style={{ ...ADMIN_PREVIEW_SCROLL_STYLE, padding: "20px 20px 24px" }}>
                  {adminPreview.approved && (() => { const pl = places.find(p => Number(p.id) === Number(adminPreview.item.placeid || adminPreview.item.placeId)); return pl ? <p style={{ margin: "0 0 16px", fontSize: 12, color: "#7986cb", background: "#1a1a2e", border: "1px solid #2a2a4a", borderRadius: 8, padding: "8px 12px" }}>📍 Lugar: <strong style={{ color: "#90caf9" }}>{pl.name}</strong> · {pl.tipo}</p> : null; })()}
                  <p style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700, color: "#fff" }}>{adminPreview.item.username}</p>
                  <p style={{ margin: "0 0 12px", fontSize: 12, color: "#e53935" }}>{places.find(p => Number(p.id) === Number(adminPreview.item.placeid || adminPreview.item.placeId))?.name || "Lugar desconocido"}</p>
                  <div style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: 12, padding: "14px 16px", marginBottom: 12 }}>
                    <p style={{ margin: "0 0 6px", fontSize: 12, color: "#aaa" }}>🍽 {adminPreview.item.platillo || "(sin platillo)"}</p>
                    <p style={{ margin: "0 0 10px", fontSize: 13, color: "#ddd", lineHeight: 1.5 }}>{adminPreview.item.comentario || "(sin comentario)"}</p>
                    <div style={{ display: "flex", gap: 16, fontSize: 11, color: "#666" }}>
                      <span>Sabor: {adminPreview.item.sabor}/5</span>
                      <span>Servicio: {adminPreview.item.servicio}/5</span>
                      <span>Satisfacción: {adminPreview.item.satisfaccion}/5</span>
                    </div>
                  </div>
                  {adminPreview.item.imagenes?.length > 0 && (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {adminPreview.item.imagenes.map((img, i) => (
                        <img key={i} src={img.url || img} alt="" onClick={() => setAdminImageViewer({ images: adminPreview.item.imagenes.map(im => im.url || im), startIndex: i })}
                          style={{ width: 80, height: 80, borderRadius: 8, objectFit: "cover", cursor: "zoom-in" }} />
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Botones fijos abajo — solo si NO es reseña ya aprobada */}
              {!adminPreview.approved && <div style={ADMIN_PREVIEW_ACTIONS_STYLE}>
                {adminPreview.type === "place" && findAdminEditableReviewForPlace({ isAdmin, currentUserId: user?.userId, place: adminPreview.item, reviews }) && <button onClick={() => editOwnPendingAdminPlaceReview(adminPreview.item)} style={{ flex: 1, background: "#1a1a2e", border: "1px solid #2d2d5a", color: "#90caf9", borderRadius: 10, padding: "13px 0", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>✏️ Editar</button>}
                {adminPreview.type === "review" && canAdminEditOwnPendingReview({ isAdmin, currentUserId: user?.userId, review: adminPreview.item }) && <button onClick={() => editOwnPendingAdminReview(adminPreview.item)} style={{ flex: 1, background: "#1a1a2e", border: "1px solid #2d2d5a", color: "#90caf9", borderRadius: 10, padding: "13px 0", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>✏️ Editar</button>}
                <button onClick={async () => {
                  const item = adminPreview.item;
                  const isPlace = adminPreview.type === "place";
                  if (!window.confirm("¿Aprobar \"" + item.name + (isPlace ? "\"?" : (item.username + "\"?")))  ) return;
                  if (isPlace) await approvePlace(item); else await approveReview(item);
                  closeAdminPreview();
                }} style={{ flex: 1, background: "#1a3a1a", border: "1px solid #2d5a2d", color: "#4caf50", borderRadius: 10, padding: "13px 0", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>✓ Aprobar</button>
                <button onClick={async () => {
                  const item = adminPreview.item;
                  const isPlace = adminPreview.type === "place";
                  if (isPlace) await rejectPlace(item); else await rejectReview(item);
                  closeAdminPreview();
                }} style={{ flex: 1, background: "#3a1a1a", border: "1px solid #5a2d2d", color: "#e57373", borderRadius: 10, padding: "13px 0", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>✕ Rechazar</button>
              </div>}
            </div>
          )}
          {/* Contenido por tab */}
          {!adminPreview && (
          <div ref={adminListScrollRef} style={{ flex: 1, overflowY: "auto", padding: "16px 20px 40px", maxWidth: 700, margin: "0 auto", width: "100%" }}>

            {adminTab === "lugares" && (<>
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {[["Pendientes", pendingPlaces.length, "#e53935"], ["Aprobados", approvedPlaces.length, "#4caf50"]].map(([l, n, c]) => (
                  <div key={l} style={{ flex: 1, background: "#111", border: "1px solid #1e1e1e", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                    <div style={{ color: c, fontSize: 20, fontWeight: 900 }}>{n}</div>
                    <div style={{ color: "#555", fontSize: 9, letterSpacing: 1, marginTop: 2 }}>{l.toUpperCase()}</div>
                  </div>
                ))}
              </div>
              {pendingPlaces.length === 0 ? <p style={{ color: "#555", fontSize: 13, marginBottom: 24 }}>Sin lugares pendientes 👌</p> : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
                  <p style={{ color: "#e53935", fontWeight: 700, fontSize: 11, letterSpacing: 1, margin: "0 0 8px" }}>PENDIENTES</p>
                  {pendingPlaces.map(p => (
                    <div key={p.id} style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 14, padding: "14px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                        <img src={p.image} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, color: "#fff", fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</p>
                          <p style={{ margin: "2px 0 0", color: "#888", fontSize: 11 }}>{p.tipo} · Por: <span style={{ color: "#bbb" }}>{adminContributor(p.addedBy).primary}</span></p>
                        </div>
                        <button onClick={() => openAdminPreview({ type: "place", item: p })} style={{ background: "#1a1a2e", border: "1px solid #2a2a4a", color: "#7986cb", borderRadius: 8, padding: "6px 12px", fontSize: 11, cursor: "pointer", flexShrink: 0 }}>Ver</button>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        {findAdminEditableReviewForPlace({ isAdmin, currentUserId: user?.userId, place: p, reviews }) && <button onClick={() => editOwnPendingAdminPlaceReview(p)} style={{ flex: 1, background: "#1a1a2e", border: "1px solid #2d2d5a", color: "#90caf9", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>✏️ Editar</button>}
                        <button onClick={async () => { if (!window.confirm("¿Aprobar \"" + p.name + "\"?")) return; await approvePlace(p); }} style={{ flex: 1, background: "#1a3a1a", border: "1px solid #2d5a2d", color: "#4caf50", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>✓ Aprobar</button>
                        <button onClick={() => rejectPlace(p)} style={{ flex: 1, background: "#3a1a1a", border: "1px solid #5a2d2d", color: "#e57373", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>✕ Rechazar</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {approvedPlaces.length > 0 && (<>
                <p style={{ color: "#555", fontWeight: 700, fontSize: 11, letterSpacing: 1, margin: "0 0 8px" }}>APROBADOS ({approvedPlaces.length})</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {approvedPlacesPage.map(p => (
                    <div key={p.id} style={{ background: "#0d0d0d", border: "1px solid #1e1e1e", borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                      <img src={p.image} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover", flexShrink: 0, opacity: 0.7 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, color: "#888", fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</p>
                        <p style={{ margin: "2px 0 0", color: "#444", fontSize: 10 }}>Por: {adminContributor(p.addedBy).primary}</p>
                      </div>
                      <button onClick={() => openAdminPreview({ type: "place", item: p, approved: true })} aria-label={`Ver ${p.name} en contexto Admin`} style={{ background: "#1a1a2e", border: "1px solid #2a2a4a", color: "#7986cb", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer", flexShrink: 0, marginRight: 4 }}>👁</button>
                      <button onClick={() => setAdminEditPlace({ ...p })} style={{ background: "#1a1a2a", border: "1px solid #2d2d5a", color: "#90caf9", borderRadius: 8, padding: "6px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0, marginRight: 4 }}>✏️</button>
                      <button onClick={async () => { if (!window.confirm("¿Eliminar \"" + p.name + "\"?")) return; const { error } = await supabase.from("restaurants").delete().eq("id", p.id); if (!error) setPlaces(prev => prev.filter(pl => pl.id !== p.id)); }} style={ADMIN_DEL_BTN}>🗑</button>
                    </div>
                  ))}
                </div>
                <Paginador page={adminPageLugaresAprob} setPage={setAdminPageLugaresAprob} total={approvedPlaces.length} pageSize={ADMIN_PAGE_SIZE} />
              </>)}
            </>)}

            {adminTab === "resenas" && (<>
              <AdminOfficialReviewPanel user={user} places={places} onCreated={review => setReviews(prev => [...prev, review])} setSessionToast={setSessionToast} />
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {[["Pendientes", pendingReviews.length, "#f59e0b"], ["Aprobadas", approvedReviews.length, "#4caf50"]].map(([l, n, c]) => (
                  <div key={l} style={{ flex: 1, background: "#111", border: "1px solid #1e1e1e", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                    <div style={{ color: c, fontSize: 20, fontWeight: 900 }}>{n}</div>
                    <div style={{ color: "#555", fontSize: 9, letterSpacing: 1, marginTop: 2 }}>{l.toUpperCase()}</div>
                  </div>
                ))}
              </div>
              {pendingReviews.length === 0 ? <p style={{ color: "#555", fontSize: 13, marginBottom: 24 }}>Sin reseñas pendientes 👌</p> : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
                  <p style={{ color: "#f59e0b", fontWeight: 700, fontSize: 11, letterSpacing: 1, margin: "0 0 8px" }}>PENDIENTES</p>
                  {pendingReviews.map(r => (
                    <div key={r.id} style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 14, padding: "14px 16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, color: "#fff", fontSize: 13, fontWeight: 600 }}>{r.username} <span style={{ color: "#888", fontWeight: 400 }}>· {places.find(p => Number(p.id) === Number(r.placeid || r.placeId))?.name || "Lugar #" + (r.placeid || r.placeId)}</span></p>
                          <p style={{ margin: "4px 0 0", color: "#aaa", fontSize: 12, lineHeight: 1.4 }}>{r.comentario || "(sin comentario)"}</p>
                        </div>
                        <button onClick={() => openAdminPreview({ type: "review", item: r })} style={{ background: "#1a1a2e", border: "1px solid #2a2a4a", color: "#7986cb", borderRadius: 8, padding: "6px 12px", fontSize: 11, cursor: "pointer", flexShrink: 0 }}>Ver</button>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        {canAdminEditOwnPendingReview({ isAdmin, currentUserId: user?.userId, review: r }) && <button onClick={() => editOwnPendingAdminReview(r)} style={{ flex: 1, background: "#1a1a2e", border: "1px solid #2d2d5a", color: "#90caf9", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>✏️ Editar</button>}
                        <button onClick={async () => { if (!window.confirm("¿Aprobar reseña de \"" + r.username + "\"?")) return; await approveReview(r); }} style={{ flex: 1, background: "#1a3a1a", border: "1px solid #2d5a2d", color: "#4caf50", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>✓ Aprobar</button>
                        <button onClick={() => rejectReview(r)} style={{ flex: 1, background: "#3a1a1a", border: "1px solid #5a2d2d", color: "#e57373", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>✕ Rechazar</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {approvedReviews.length > 0 && (<>
                <p style={{ color: "#555", fontWeight: 700, fontSize: 11, letterSpacing: 1, margin: "0 0 8px" }}>APROBADAS ({approvedReviews.length})</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {approvedReviewsPage.map(r => (
                    <div key={r.id} style={{ background: "#0d0d0d", border: "1px solid #1e1e1e", borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, color: "#777", fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.username} · {places.find(p => Number(p.id) === Number(r.placeid || r.placeId))?.name || "?"}</p>
                        <p style={{ margin: "2px 0 0", color: "#444", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.comentario || ""}</p>
                      </div>
                      <button onClick={() => openAdminPreview({ type: "review", item: r, approved: true })} style={{ background: "#1a1a2e", border: "1px solid #2a2a4a", color: "#7986cb", borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer", flexShrink: 0, marginRight: 4 }}>👁</button>
                      <button onClick={async () => { if (!window.confirm("¿Eliminar reseña de \"" + r.username + "\"?")) return; const { error } = await supabase.from("reviews").delete().eq("id", r.id); if (!error) setReviews(prev => prev.filter(rv => rv.id !== r.id)); }} style={ADMIN_DEL_BTN}>🗑</button>
                    </div>
                  ))}
                </div>
                <Paginador page={adminPageResenasAprob} setPage={setAdminPageResenasAprob} total={approvedReviews.length} pageSize={ADMIN_PAGE_SIZE} />
              </>)}
            </>)}

            {adminTab === "usuarios" && (<>
              <AdminUserMessagingPanel adminUsers={adminUsers} adminUserEmails={adminUserEmails} />
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <div style={{ flex: 1, background: "#111", border: "1px solid #1e1e1e", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                  <div style={{ color: "#7986cb", fontSize: 20, fontWeight: 900 }}>{Object.keys(adminUsers).length}</div>
                  <div style={{ color: "#555", fontSize: 9, letterSpacing: 1, marginTop: 2 }}>REGISTRADOS</div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {usuariosPage.map(([id, username]) => {
                  const userReviews = reviews.filter(r => r.userid === id);
                  const userPlaces = places.filter(p => p.addedBy === id);
                  const expanded = adminUserDetail === id;
                  return (
                  <div key={id} style={{ background: "#0d0d0d", border: "1px solid #1e1e1e", borderRadius: 12, padding: "12px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: "#1a2a3a", border: "1px solid #2a3a5a", display: "flex", alignItems: "center", justifyContent: "center", color: "#7986cb", fontSize: 13, fontWeight: 900, flexShrink: 0 }}>{(username || "?")[0].toUpperCase()}</div>
                      <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => setAdminUserDetail(expanded ? null : id)}>
                        <p style={{ margin: 0, color: "#aaa", fontSize: 13, fontWeight: 700 }}>{username}</p>
                        <p style={{ margin: "2px 0 0", color: "#666", fontSize: 10 }}>{adminUserEmails[id] || "Sin correo"} · {adminUsersFecha[id] ? new Date(adminUsersFecha[id]).toLocaleString() : "Sin fecha"}</p>
                        <p style={{ margin: "2px 0 0", color: "#444", fontSize: 10 }}>user_id: {id} · {userReviews.length} reseñas · {userPlaces.length} lugares {expanded ? "▲" : "▼"}</p>
                      </div>
                      <button onClick={async () => {
                        // 5.1 — cascada: reseñas del usuario + lugares que registró + reseñas de terceros sobre esos lugares
                        if (!window.confirm(`¿Eliminar la cuenta de "${username}" y TODO su contenido?\n\nSe borran: sus reseñas, los lugares que registró y las reseñas de otros usuarios sobre esos lugares.\n\nEsta acción no se puede deshacer.`)) return;
                        const ownPlaceIds = places.filter(p => p.addedBy === id).map(p => p.id);
                        try {
                          if (ownPlaceIds.length) {
                            const { error: e1 } = await supabase.from("reviews").delete().in("placeid", ownPlaceIds);
                            if (e1) throw e1;
                            const { error: e2 } = await supabase.from("restaurants").delete().in("id", ownPlaceIds);
                            if (e2) throw e2;
                          }
                          const { error: e3 } = await supabase.from("reviews").delete().eq("userid", id);
                          if (e3) throw e3;
                          const { error: e4 } = await supabase.from("users").delete().eq("id", id);
                          if (e4) throw e4;
                          const ownSet = new Set(ownPlaceIds.map(Number));
                          setReviews(prev => prev.filter(r => r.userid !== id && !ownSet.has(Number(r.placeid || r.placeId))));
                          setPlaces(prev => prev.filter(p => p.addedBy !== id));
                          setAdminUsers(prev => { const n = { ...prev }; delete n[id]; return n; });
                          setAdminContributorProfiles(prev => { const n = { ...prev }; delete n[id]; return n; });
                          setAdminUserDetail(null);
                        } catch (delErr) {
                          window.alert("No se pudo completar el borrado: " + (delErr?.message || delErr));
                        }
                      }} style={ADMIN_DEL_BTN}>🗑</button>
                    </div>
                    {expanded && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #1e1e1e" }}>
                        {userPlaces.length > 0 && (<>
                          <p style={{ margin: "0 0 6px", color: "#555", fontSize: 10, letterSpacing: 0.5 }}>LUGARES REGISTRADOS</p>
                          {userPlaces.map(p => (
                            <p key={p.id} style={{ margin: "0 0 4px", color: "#888", fontSize: 11 }}>📍 {p.name} {p.pending ? <span style={{ color: "#f59e0b" }}>(pendiente)</span> : null}</p>
                          ))}
                        </>)}
                        {userReviews.length > 0 && (<>
                          <p style={{ margin: "10px 0 6px", color: "#555", fontSize: 10, letterSpacing: 0.5 }}>RESEÑAS Y COMENTARIOS</p>
                          {userReviews.map(r => (
                            <p key={r.id} style={{ margin: "0 0 4px", color: "#888", fontSize: 11, lineHeight: 1.4 }}>💬 {places.find(p => Number(p.id) === Number(r.placeid || r.placeId))?.name || "?"} — {r.comentario || "(sin comentario)"} {r.pending ? <span style={{ color: "#f59e0b" }}>(pendiente)</span> : null}</p>
                          ))}
                        </>)}
                        {userPlaces.length === 0 && userReviews.length === 0 && <p style={{ margin: 0, color: "#555", fontSize: 11 }}>Sin actividad registrada</p>}
                      </div>
                    )}
                  </div>
                  );
                })}
                {Object.keys(adminUsers).length === 0 && <p style={{ color: "#555", fontSize: 13 }}>Sin usuarios registrados aún</p>}
              </div>
              <Paginador page={adminPageUsuarios} setPage={setAdminPageUsuarios} total={usuariosOrdenados.length} pageSize={ADMIN_PAGE_SIZE} />
            </>)}

            {adminTab === "testers" && <TestersAdminPanel adminUsers={adminUsers} />}

            {adminTab === "feedback" && <><AdminFeedbackPanel /><AdminReportsPanel places={places} /></>}

            {adminTab === "config" && (<>
              <p style={{ color: "#555", fontWeight: 700, fontSize: 11, letterSpacing: 1, margin: "0 0 12px" }}>DON D&#39;</p>
              <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 14, padding: "14px 16px", marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#fff" }}>Don D&#39; — Asistente IA</p>
                  <p style={{ margin: "3px 0 0", fontSize: 11, color: donDOnline ? "#4caf50" : "#e57373" }}>{donDOnline === null ? "● Cargando..." : donDOnline ? "● Online — respondiendo" : "● Offline — no responde"}</p>
                </div>
                <button onClick={async () => { const next = !donDOnline; const { error } = await supabase.from("settings").update({ value: String(next) }).eq("key", "don_d_online"); if (!error) setDonDOnline(next); }} style={{ background: donDOnline ? "#1a3a1a" : "#3a1a1a", border: "1px solid " + (donDOnline ? "#2d5a2d" : "#5a2d2d"), color: donDOnline ? "#4caf50" : "#e57373", borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  {donDOnline === null ? "..." : donDOnline ? "Poner Offline" : "Poner Online"}
                </button>
              </div>
              <AdminResourceUsagePanel />
            </>)}

          </div>
          )}
        </div>
        );
      })()}

      {adminEditReview && isAdmin && (
        <RevForm
          placeId={adminEditReview.placeid || adminEditReview.placeId}
          placeName={places.find(place => Number(place.id) === Number(adminEditReview.placeid || adminEditReview.placeId))?.name || ""}
          placeTipo={places.find(place => Number(place.id) === Number(adminEditReview.placeid || adminEditReview.placeId))?.tipo || ""}
          placeOptions={places.filter(place => !place.metadata?.rejected).sort((a, b) => a.name.localeCompare(b.name, "es"))}
          user={user}
          editMode
          zIndex={13000}
          initialValues={adminEditReview}
          onClose={() => setAdminEditReview(null)}
          onSubmit={saveOwnPendingAdminReview}
        />
      )}

      {/* ── Admin: modal edición de lugar ─────────────────── */}
      {adminEditPlace && isAdmin && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 12000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => { if (e.target === e.currentTarget) setAdminEditPlace(null); }}>
          <div style={{ background: "#0d0d0d", border: "1px solid #2a2a2a", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", padding: "24px 20px 40px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>✏️ Editar lugar</span>
              <button onClick={() => setAdminEditPlace(null)} style={{ background: "none", border: "none", color: "#555", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            {/* Foto de portada */}
            <p style={{ color: "#555", fontSize: 11, fontWeight: 700, letterSpacing: 1, margin: "0 0 8px" }}>FOTO DE PORTADA</p>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <img src={adminEditPlace.image} alt="" style={{ width: 64, height: 64, borderRadius: 10, objectFit: "cover", border: "1px solid #2a2a2a" }} />
              <label style={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, padding: "8px 14px", fontSize: 12, color: "#90caf9", cursor: "pointer", fontWeight: 700 }}>
                📷 Cambiar foto
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={async e => {
                  const file = e.target.files[0]; if (!file) return;
                  const fd = new FormData(); fd.append("file", file); fd.append("upload_preset", "venaqqi_reviews");
                  try {
                    const res = await fetch("https://api.cloudinary.com/v1_1/dgdtlohqw/image/upload", { method: "POST", body: fd });
                    const data = await res.json();
                    if (data.secure_url) { setAdminEditPlace(prev => ({ ...prev, image: data.secure_url })); }
                    else { console.error("Cloudinary error:", data); setSessionToast({ type: "error", text: "❌ " + (data.error?.message || "Error al subir foto") }); setTimeout(() => setSessionToast(null), 4000); }
                  } catch (err) { console.error("Upload error:", err); setSessionToast({ type: "error", text: "❌ No se pudo subir la foto" }); setTimeout(() => setSessionToast(null), 4000); }
                }} />
              </label>
            </div>
            {/* Campos */}
            {[
              { label: "NOMBRE", key: "name" },
              { label: "TIPO", key: "tipo" },
              { label: "DIRECCIÓN", key: "location" },
              { label: "LINK MAPS", key: "maps" },
            ].map(({ label, key }) => (
              <div key={key} style={{ marginBottom: 14 }}>
                <p style={{ color: "#555", fontSize: 11, fontWeight: 700, letterSpacing: 1, margin: "0 0 6px" }}>{label}</p>
                <input value={adminEditPlace[key] || ""} onChange={e => setAdminEditPlace(prev => ({ ...prev, [key]: e.target.value }))}
                  style={{ width: "100%", background: "#111", border: "1px solid #2a2a2a", borderRadius: 8, padding: "10px 12px", color: "#fff", fontSize: 13, boxSizing: "border-box", outline: "none" }} />
              </div>
            ))}
            {/* Tags */}
            <p style={{ color: "#555", fontSize: 11, fontWeight: 700, letterSpacing: 1, margin: "0 0 6px" }}>TAGS (separados por coma)</p>
            <input value={(adminEditPlace.tags || []).join(", ")}
              onChange={e => setAdminEditPlace(prev => ({ ...prev, tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) }))}
              style={{ width: "100%", background: "#111", border: "1px solid #2a2a2a", borderRadius: 8, padding: "10px 12px", color: "#fff", fontSize: 13, boxSizing: "border-box", outline: "none", marginBottom: 24 }} />
            {/* Guardar */}
            <button onClick={async () => {
              const { error } = await supabase.from("restaurants").update({
                name: adminEditPlace.name, tipo: adminEditPlace.tipo,
                location: adminEditPlace.location, maps: adminEditPlace.maps,
                tags: adminEditPlace.tags, image: adminEditPlace.image
              }).eq("id", adminEditPlace.id);
              if (!error) {
                setPlaces(prev => prev.map(pl => pl.id === adminEditPlace.id ? { ...pl, ...adminEditPlace } : pl));
                setAdminEditPlace(null);
                setSessionToast({ type: "success", text: "✅ Lugar actualizado" });
                setTimeout(() => setSessionToast(null), 3000);
              } else {
                setSessionToast({ type: "error", text: "❌ Error al guardar" });
                setTimeout(() => setSessionToast(null), 3000);
              }
            }} style={{ width: "100%", background: "#e53935", border: "none", borderRadius: 10, padding: "14px", color: "#fff", fontWeight: 900, fontSize: 14, cursor: "pointer", fontFamily: "'Arial Black',sans-serif" }}>
              GUARDAR CAMBIOS
            </button>
          </div>
        </div>
      )}

      {/* ONBOARDING — Bienvenida + GPS + Presupuesto */}
      {showWelcome && (() => {
        const finishOnboarding = () => {
          localStorage.setItem('venaqqi_onboarding_visto', '1');
          if (localStorage.getItem('venaqqi_gps') !== '1') deferLocationForSession();
          if (!user) {
            setAccessMode(ACCESS_MODES.GUEST);
            persistAccessMode(ACCESS_MODES.GUEST);
          }
          if (onboardingPresupuesto) {
            // B8 (ID 41) — guardar ya traducido a la escala 1-7 unificada (misma que perfil y reseñas)
            const MAP17 = { "1": "2", "2": "4", "3": "5", "4": "7", "0": "0" };
            localStorage.setItem('venaqqi_presupuesto', MAP17[onboardingPresupuesto] || onboardingPresupuesto);
            localStorage.setItem('venaqqi_presupuesto_v2', '1');
          }
          setShowWelcome(false);
          setOnboardingStep(0);
          startAutoReco(); // B12 (ID 44) — aviso 5-6s antes de la recomendación automática
        };
        const skipAll = () => setConfirmSkip(true); // B12 (ID 50) — confirmar antes de saltar
        if (confirmSkip) return (
          <div style={{ position: "fixed", inset: 0, background: "#080808", zIndex: 11500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div style={{ width: "100%", maxWidth: 340, background: "#0d0d0d", border: "1px solid #1e1e1e", borderRadius: 24, padding: "36px 24px", textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 14 }}>⚠️</div>
              <p style={{ fontSize: 17, color: "#fff", fontWeight: 700, margin: "0 0 10px" }}>¿Saltar la configuración?</p>
              <p style={{ fontSize: 13, color: "#aaa", margin: "0 0 24px", lineHeight: 1.6 }}>Sin tu ubicación y presupuesto, las recomendaciones serán menos precisas. Podrás configurarlos después desde tu perfil.</p>
              <button style={{ background: "#e53935", color: "#fff", border: "none", borderRadius: 28, padding: "13px 0", fontSize: 14, fontWeight: 700, width: "100%", cursor: "pointer", marginBottom: 10 }} onClick={() => setConfirmSkip(false)}>Mejor configurar</button>
              <button style={{ background: "transparent", color: "#666", border: "1px solid #2a2a2a", borderRadius: 28, padding: "11px 0", fontSize: 13, width: "100%", cursor: "pointer" }} onClick={() => { setConfirmSkip(false); finishOnboarding(); }}>Saltar de todos modos →</button>
            </div>
          </div>
        );
        const OB = { position: "fixed", inset: 0, background: "#080808", zIndex: 11000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
        const CARD = { position: "relative", width: "100%", maxWidth: 360, background: "#0d0d0d", border: "1px solid #1e1e1e", borderRadius: 24, padding: "40px 24px 32px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" };
        const BTN = { background: "#e53935", color: "#fff", border: "none", borderRadius: 28, padding: "13px 0", fontSize: 14, fontWeight: 700, width: "100%", cursor: "pointer", marginBottom: 10, letterSpacing: 1 };
        const BTN_GHOST = { background: "transparent", color: "#666", border: "1px solid #2a2a2a", borderRadius: 28, padding: "11px 0", fontSize: 13, width: "100%", cursor: "pointer", marginBottom: 10 };
        const DOTS = (active) => (
          <div style={{ display: "flex", gap: 6, marginBottom: 28 }}>
            {[1, 2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: active === i ? "#e53935" : "#2a2a2a" }} />)}
          </div>
        );

        if (onboardingStep === 0) return (
          <div style={OB}>
            <div style={CARD}>
              {adminTutorialOpen && <button aria-label="Cerrar tutorial Admin" onClick={() => { setAdminTutorialOpen(false); setShowWelcome(false); setOnboardingStep(0); }} style={{ position: "absolute", top: 16, right: 16, width: 36, height: 36, borderRadius: "50%", background: "#1e1e1e", border: "1px solid #444", color: "#fff", fontSize: 18, cursor: "pointer", zIndex: 1 }}>✕</button>}
              <div style={{ width: 72, height: 72, borderRadius: "50%", border: "2px solid #e53935", background: "#0d0d0d", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>
                <img className="don-d-logo-symbol" src={LOGO} alt="Venaqqi" style={{ width: 52, height: 52, objectFit: "contain" }} />
              </div>
              <p style={{ fontFamily: "'Bebas Neue','Arial Black',sans-serif", fontSize: 32, color: "#e53935", letterSpacing: 4, margin: "0 0 2px" }}>VENAQQI</p>
              <p style={{ fontSize: 10, color: "#555", letterSpacing: 2, margin: "0 0 28px" }}>come donde quieras</p>
              <div style={{ width: "100%", height: 1, background: "#1a1a1a", marginBottom: 24 }} />
              <p style={{ fontSize: 20, color: "#fff", fontWeight: 700, margin: "0 0 8px" }}>¡Bienvenido!</p>
              <p style={{ fontSize: 13, color: "#888", margin: "0 0 28px", lineHeight: 1.6 }}>Tu guía para comer bien en Puebla.</p>
              <button aria-label="Continuar con Google desde bienvenida" style={{ ...BTN, background: "#fff", color: "#1a1a1a", border: "1px solid #ddd", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }} onClick={() => runGoogleSignIn(message => { setSessionToast({ type: "error", text: message }); setTimeout(() => setSessionToast(null), 5000); })}><GoogleG /> Continuar con Google</button>
              <button style={BTN} onClick={() => { setRegisterFromOnboarding(true); setShowWelcome(false); setShowRegister(true); }}>Crear cuenta</button>
              <button style={{ ...BTN_GHOST, color: "#ccc", borderColor: "#444" }} onClick={() => { setAuthFromOnboarding(true); setShowWelcome(false); setShowAuth(true); }}>Iniciar sesión</button>
              <div style={{ width: "100%", height: 1, background: "#1a1a1a", margin: "4px 0 14px" }} />
              <button style={{ ...BTN_GHOST, color: "#666", marginBottom: 0 }} onClick={() => setOnboardingStep(-1)}>Hoy como invitado</button>
              <p style={{ margin: "14px 0 0", fontSize: 10, color: "#444", lineHeight: 1.6 }}>{/* B14 (IDs 16,17) — legal accesible sin sesión */}
                <a href="/terminos.html" target="_blank" rel="noopener noreferrer" style={{ color: "#666" }}>Términos</a> · <a href="/aviso-privacidad.html" target="_blank" rel="noopener noreferrer" style={{ color: "#666" }}>Aviso de Privacidad</a> · <a href="/politica-ugc.html" target="_blank" rel="noopener noreferrer" style={{ color: "#666" }}>Política de UGC</a>
              </p>
            </div>
          </div>
        );

        if (onboardingStep === -1) return (
          <div style={OB}>
            <div style={CARD}>
              <div style={{ fontSize: 36, marginBottom: 16 }}>🤝</div>
              <p style={{ fontSize: 18, color: "#fff", fontWeight: 700, margin: "0 0 12px", lineHeight: 1.3 }}>Antes de continuar</p>
              <p style={{ fontSize: 13, color: "#aaa", margin: "0 0 20px", lineHeight: 1.7, textAlign: "left" }}>
                Nos alegra tenerte por aquí. Para ofrecerte la mejor experiencia, Venaqqi guardará tus preferencias de uso — como tu rango de presupuesto y ubicación aproximada.
              </p>
              <p style={{ fontSize: 13, color: "#aaa", margin: "0 0 28px", lineHeight: 1.7, textAlign: "left" }}>
                Esta información nos ayuda a mejorar la app y entender mejor a nuestra comunidad. No la compartimos con terceros ni la usamos para fines publicitarios.
              </p>
              <button style={BTN} onClick={() => setOnboardingStep(1)}>Entendido, continuar</button>
              <button style={BTN_GHOST} onClick={() => setOnboardingStep(0)}>← Regresar</button>
            </div>
          </div>
        );

        if (onboardingStep === 1) return (
          <div style={OB}>
            <div style={CARD}>
              {DOTS(1)}
              <div style={{ fontSize: 42, marginBottom: 16 }}>📍</div>
              <p style={{ fontSize: 10, color: "#e53935", letterSpacing: 2, fontWeight: 700, margin: "0 0 10px" }}>PASO 1 DE 2</p>
              <p style={{ fontSize: 18, color: "#fff", fontWeight: 700, margin: "0 0 8px", lineHeight: 1.3 }}>¿Desde dónde buscamos?</p>
              <p style={{ fontSize: 12, color: "#888", margin: "0 0 12px", lineHeight: 1.6 }}>Con tu ubicación activa, podemos darte recomendaciones de lugares cerca de ti.</p>
              <p style={{ fontSize: 12, color: "#555", margin: "0 0 24px", lineHeight: 1.6 }}>Si prefieres no compartirla, no pasa nada — las recomendaciones serán generales para Puebla.</p>
              <button style={BTN} onClick={handleOnboardingGPS}>Activar ubicación</button>
              <button style={BTN_GHOST} onClick={() => { localStorage.setItem('venaqqi_gps', '0'); deferLocationForSession(); setOnboardingStep(2); }}>No por ahora</button>
              <button style={{ ...BTN_GHOST, color: "#444", marginBottom: 0 }} onClick={() => setOnboardingStep(-1)}>← Regresar</button>
              <p style={{ fontSize: 11, color: "#444", cursor: "pointer", marginTop: 8, textDecoration: "underline" }} onClick={skipAll}>Saltar todo e ir directo →</p>
            </div>
          </div>
        );

        if (onboardingStep === 2) return (
          <div style={OB}>
            <div style={CARD}>
              {DOTS(2)}
              <div style={{ fontSize: 42, marginBottom: 16 }}>💸</div>
              <p style={{ fontSize: 10, color: "#e53935", letterSpacing: 2, fontWeight: 700, margin: "0 0 10px" }}>PASO 2 DE 2</p>
              <p style={{ fontSize: 18, color: "#fff", fontWeight: 700, margin: "0 0 8px", lineHeight: 1.3 }}>¿Cuánto quieres gastar?</p>
              <p style={{ fontSize: 12, color: "#888", margin: "0 0 6px", lineHeight: 1.6 }}>Elige tu presupuesto máximo. Don D' te recomendará dentro de ese rango.</p>
              <p style={{ fontSize: 11, color: "#555", margin: "0 0 20px", lineHeight: 1.6 }}>Sin límite = cualquier precio. ✏️ Podrás editar este rango cuando quieras desde tu perfil.</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginBottom: 24 }}>
                {[["1","$ Económico"],["2","$$ Moderado"],["3","$$$ Especial"],["4","$$$$ Premium"],["0","Sin límite"]].map(([val, label]) => (
                  <div key={val} onClick={() => setOnboardingPresupuesto(val === onboardingPresupuesto ? null : val)}
                    style={{ background: onboardingPresupuesto === val ? "#1a0a0a" : "#161616", border: `1px solid ${onboardingPresupuesto === val ? "#e53935" : "#2a2a2a"}`, borderRadius: 20, padding: "8px 16px", fontSize: 12, color: onboardingPresupuesto === val ? "#e53935" : "#aaa", cursor: "pointer" }}>
                    {label}
                  </div>
                ))}
              </div>
              <button style={{ ...BTN, ...(onboardingPresupuesto ? {} : { background: "#2a2a2a", color: "#555", cursor: "not-allowed" }) }} onClick={finishOnboarding} disabled={!onboardingPresupuesto}>Entrar a Venaqqi →</button>
              <button style={{ ...BTN_GHOST, color: "#444", marginBottom: 0 }} onClick={() => setOnboardingStep(1)}>← Regresar</button>
              <p style={{ fontSize: 11, color: "#444", cursor: "pointer", marginTop: 8, textDecoration: "underline" }} onClick={skipAll}>Saltar todo e ir directo →</p>
            </div>
          </div>
        );

        return null;
      })()}

      {showPolicies && (
        <div role="dialog" aria-modal="true" aria-label="Políticas" style={{ position: "fixed", inset: 0, background: "#080808", zIndex: 12400, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <section style={{ width: "100%", maxWidth: 420, background: "#0d0d0d", border: "1px solid #2a2a2a", borderRadius: 20, padding: "22px 18px", position: "relative" }}>
            <button aria-label="Cerrar Políticas" onClick={() => { setShowPolicies(false); setLegalOpenedFromPolicies(false); }} style={{ position: "absolute", top: 14, right: 14, width: 36, height: 36, borderRadius: "50%", background: "#1a1a1a", border: "1px solid #333", color: "#fff", fontSize: 17, cursor: "pointer" }}>✕</button>
            <button aria-label="Regresar desde Políticas" onClick={() => { setShowPolicies(false); setLegalOpenedFromPolicies(false); }} style={{ background: "transparent", border: 0, color: "#e57373", fontSize: 13, cursor: "pointer", padding: "8px 0 18px" }}>← Atrás</button>
            <h2 style={{ margin: "0 0 8px", color: "#fff", fontSize: 24 }}>Políticas</h2>
            <p style={{ margin: "0 0 20px", color: "#888", fontSize: 13 }}>Consulta los documentos legales de Venaqqi.</p>
            {LEGAL_DOCUMENT_ORDER.map(key => {
              const document = LEGAL_DOCUMENTS[key];
              return (
                <button key={key} onClick={() => openLegalDocument(key, true)} style={{ width: "100%", background: "#161616", border: "1px solid #2a2a2a", borderRadius: 12, color: "#fff", padding: "14px 16px", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between", textAlign: "left", cursor: "pointer", fontSize: 14 }}>
                  <span>{document.title}</span><span aria-hidden="true" style={{ color: "#e53935" }}>→</span>
                </button>
              );
            })}
          </section>
        </div>
      )}

      {showFeedback && <FeedbackForm user={user} audience={feedbackAudience} origin={feedbackOrigin} context={{ screen: selected ? "place_detail" : chatOpen ? "don_d" : "home", access_mode: user ? "authenticated" : accessMode, place_id: selected?.id || null }} hasPreviousFeedback={feedbackOrigin === "manual_menu" && feedbackHasPrevious} onClose={() => setShowFeedback(false)} />}

      {showFeedbackInvitation && !showFeedback && !feedbackInvitationExclusion && (
        <div role="dialog" aria-modal="true" aria-label="Invitación de Feedback" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 12300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <section style={{ width: "100%", maxWidth: 360, background: "#0d0d0d", border: "1px solid #2a2a2a", borderRadius: 20, padding: "26px 20px", textAlign: "center" }}>
            <FeedbackBrand />
            <h2 style={{ margin: "0 0 8px", color: "#fff", fontSize: 21 }}>Venaqqi y Don D’ quieren saber qué opinas</h2>
            <p style={{ margin: "0 0 22px", color: "#888", fontSize: 13, lineHeight: 1.5 }}>Tu experiencia nos ayuda a mejorar. ¡Danos tu feedback!</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={rejectFeedbackInvitation} style={{ flex: 1, padding: 12, background: "transparent", border: "1px solid #333", borderRadius: 10, color: "#ccc", cursor: "pointer" }}>Ahora no</button>
              <button onClick={() => { acceptFeedbackInvitation(); setFeedbackOrigin("automatic_invitation"); setFeedbackHasPrevious(false); setShowFeedback(true); }} style={{ flex: 1, padding: 12, background: "#e53935", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, cursor: "pointer" }}>Dar feedback</button>
            </div>
          </section>
        </div>
      )}

      {legalDocument && LEGAL_DOCUMENTS[legalDocument] && (
        <div role="dialog" aria-modal="true" aria-label={LEGAL_DOCUMENTS[legalDocument].title} style={{ position: "fixed", inset: 0, background: "#080808", zIndex: 12500, display: "flex", flexDirection: "column" }}>
          <header style={{ minHeight: 58, padding: "10px 14px", borderBottom: "1px solid #2a2a2a", display: "flex", alignItems: "center", gap: 12, background: "#0d0d0d" }}>
            <button aria-label="Regresar" onClick={() => setLegalDocument(null)} style={{ width: 38, height: 38, borderRadius: "50%", background: "#1a1a1a", border: "1px solid #333", color: "#fff", fontSize: 20, cursor: "pointer" }}>←</button>
            <strong style={{ flex: 1, fontSize: 15 }}>{LEGAL_DOCUMENTS[legalDocument].title}</strong>
            <button aria-label="Cerrar documento" onClick={closeLegalDocument} style={{ width: 38, height: 38, borderRadius: "50%", background: "#1a1a1a", border: "1px solid #333", color: "#fff", fontSize: 17, cursor: "pointer" }}>✕</button>
          </header>
          <iframe title={LEGAL_DOCUMENTS[legalDocument].title} src={LEGAL_DOCUMENTS[legalDocument].path} style={{ flex: 1, width: "100%", border: 0, background: "#fff" }} />
        </div>
      )}

      <div style={{ padding: "6px 20px 12px", textAlign: "center", fontSize: 10, color: "#555" }}>
        Versión de prueba — Codex + GitHub + Vercel
      </div>

      {/* TUTORIAL — Tooltip "Pregúntale a Don D'" — aparece sobre el área del chat Don D' (que ocupa 51vh inferior) */}
      {showDondeHint && (
        <>
          <style>{`@keyframes vq-hint-fade { 0% { opacity: 0; transform: translate(-50%, 8px); } 15% { opacity: 1; transform: translate(-50%, 0); } 85% { opacity: 1; transform: translate(-50%, 0); } 100% { opacity: 0; transform: translate(-50%, -8px); } }`}</style>
          <div style={{ 
            position: "fixed", 
            top: "47vh", 
            left: "50%", 
            transform: "translateX(-50%)",
            background: "#fff", color: "#0d0d0d", 
            padding: "12px 20px", borderRadius: 14, 
            fontSize: 14, fontWeight: 500, 
            boxShadow: "0 6px 24px rgba(229,57,53,0.5)", 
            zIndex: 9700, 
            animation: "vq-hint-fade 3s ease forwards",
            pointerEvents: "none",
            whiteSpace: "nowrap"
          }}>
            ↓ Pregúntale a <span style={{ fontWeight: 700, color: "#e53935" }}>Don D'</span>
            <div style={{ 
              position: "absolute", left: "50%", bottom: -7,
              marginLeft: -7,
              width: 14, height: 14, 
              background: "#fff", 
              transform: "rotate(45deg)" 
            }}/>
          </div>
        </>
      )}
    </div>
  );
}

// applicator sentinel: onRegister={u => { setUser(u); setShowRegister(false); setTutorialStep(1); if (registerFromOnboarding) { setRegisterFromOnboarding(false); nextOnboarding(); setShowOnboarding(true); } }}
// applicator sentinel: const related = buildRelatedPlaces(place, places, 4);
